import { Component, ElementRef, HostListener, OnDestroy, ViewChild, NgZone, AfterViewInit, ApplicationRef, ComponentRef, EnvironmentInjector, createComponent } from '@angular/core';

// Prevents flooding zoom DOM/calculate hooks natively without needing lodash
function frameThrottle(func: Function, limit: number) {
  let inThrottle: boolean;
  return function (this: any, ...args: any[]) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }
}
import { SideNavigationComponent } from '../shared/layout/side-navigation/side-navigation.component';
import { TourAnchorDirective } from '../../features/learning-center/engine/tour-anchor.directive';
import { TourController } from '../../features/learning-center/engine/tour-controller.service';
import maplibregl, { Map, setMaxParallelImageRequests } from 'maplibre-gl';
import { FilterPageComponent } from '../shared/components/filter-page/filter-page.component';
import { WatchPageComponent } from '../shared/components/watch-page/watch-page.component';
import { MapOrientationMode, SubMenuService } from '../../services/submenu.service';
import { WaypointService } from '../../services/waypoint.service';
import { Observable, ReplaySubject, Subscription, forkJoin, of } from 'rxjs';
import { PollScheduler, latestObservationKey } from '../../services/poll-scheduler.service';
import { CommonModule } from '@angular/common';
import { TripComponent } from '../trip/trip.component';
import { WaypointsComponent } from '../shared/components/user-profile/components/waypoints/waypoints.component';
import { AppBannerComponent } from '../shared/reusable/app-banner/app-banner.component';
import { AppDownloadPopupComponent } from '../shared/reusable/app-download-popup/app-download-popup.component';
import { AuthService } from '../../core/auth/auth.service';
import { MatDialogModule, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MapFilterComponent } from '../shared/components/map-filter/map-filter.component';
import { MapFilterService } from '../../services/map-filter.service';
import { MapSkinService } from '../../services/map-skin.service';
import { VesselService } from '../../services/vessel.service';
import {
  ClusterableVessel,
  ClusterableWaypoint,
  MapCluster,
  MapClusterResult,
  MapClusteringService,
} from '../../services/map-clustering.service';
import { VesselSkinStore } from '../../services/vessel-skin-store.service';
import { MapLayerKey, MapLayerSelectionService } from '../../services/map-layer-selection.service';
import { CatchHeatMapService } from '../../services/catch-heat-map.service';
import { CatchHeatControlsComponent } from '../shared/components/catch-heat-controls/catch-heat-controls.component';
import { MapLayerSwitcherComponent } from '../shared/components/map-layer-switcher/map-layer-switcher.component';
import { isDesktopViewport, rasterizeSkinSprite, VESSEL_RING_OWN, vesselHullPx } from '../../shared/utils/vessel-sprite';
import { VESSEL_SKIN_ASSET, VesselSkin } from '../../shared/utils/vessel-skin';
import { CHAT_UI_ENABLED } from '../../shared/feature-flags';
import { CardIdentity, CardParams, CardUnits, ChatArgs } from '../../shared/utils/vessel-card.logic';
import { RideAlongRole } from '../trip/models/ride-along.model';
import {
  VesselCardComponent, VesselCardParticipantsRequest, VesselCardProfileRequest,
} from '../vessel-card/vessel-card.component';
import { VesselCardPopupService } from '../../services/vessel-card-popup.service';
import { VesselCardChatService } from '../../services/vessel-card-chat.service';
import { UserRolesService } from '../../services/user-roles.service';
import { TripService } from '../../services/trip.service';
import { ClarityService } from '../../services/clarity.service';
import { switchMap, catchError, map, auditTime } from 'rxjs/operators';
import { UserProfileService } from '../../services/user-profile.service';
import { SocialService } from '../../services/social.service';
import { ANONYMOUS_ALERT_ALIAS, getWaypointIconAsset, getWaypointPinAsset, isAlertWaypointType, resolveWaypointFishSubtype, resolveWaypointType } from '../../shared/utils/waypoint-icon.util';
import { MOMENT_ACCENT, bindWaypointPhotoCarousel, buildMomentPinHtml, buildWaypointPhotoCarouselHtml, getWaypointImageUrls, isFeedOnlyWaypoint, isMomentType } from '../../shared/utils/waypoint-photo.util';
import { ReplayService, ReplayState, ReplayFilterParams, ActiveBoatState } from '../../services/replay.service';
import { ReplayFilterModalComponent } from '../replay/replay-filter-modal/replay-filter-modal.component';
import { ReplayOverlayComponent } from '../replay/replay-overlay/replay-overlay.component';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ClusterMarkerComponent } from '../cluster-marker/cluster-marker.component';
import { ComboSliderCardComponent } from '../shared/components/combo/combo-slider-card.component';
import { ComboCursorBubbleComponent } from '../shared/components/combo/combo-cursor-bubble.component';
import { ComboWindArrowsComponent } from '../shared/components/combo/combo-wind-arrows.component';
import { ComboWindChartComponent } from '../shared/components/combo/combo-wind-chart.component';
import { ComboService, ComboState, defaultComboState, probeBounds } from '../shared/components/combo/combo.service';
import { ComboBackendService } from '../shared/components/combo/combo-backend.service';
import { RideAlongService } from '../../services/ride-along.service';
import { ToastService } from '../../services/toast.service';
import { RideAlongInviteComponent } from '../trip/components/ride-along-invite/ride-along-invite.component';
import { RideAlongFollowComponent } from '../trip/components/ride-along-follow/ride-along-follow.component';
import { RideAlongParticipantsComponent } from '../trip/components/ride-along-participants/ride-along-participants.component';
import { RideAlongWaypointsComponent } from '../trip/components/ride-along-waypoints/ride-along-waypoints.component';
import { ActiveRideAlong, RideAlongParticipant } from '../trip/models/ride-along.model';
import { cmax, cmin, currentView, currentWindFrameUrl, playStart, resolvedFrameIndex } from '../shared/components/combo/combo.service';
import { SkipperHideDuringShowDirective } from '../../features/skipper/presentation/skipper-hide-during-show.directive';
import { SkipperHelpButtonComponent } from '../../features/skipper/presentation/skipper-help-button/skipper-help-button.component';

interface BuoyMetricCard {
  label: string;
  value: string;
  iconSrc: string;
  iconAlt: string;
}

interface BuoyObservationView {
  timestampLabel: string;
  metricCards: BuoyMetricCard[];
}

interface BuoyDetailView {
  id: string;
  name: string;
  subtitle: string;
  currentObservation: BuoyObservationView | null;
}

interface TideEventView {
  type: string;
  level: string;
  timeLabel: string;
}

interface TideDetailView {
  id: string;
  name: string;
  subtitle: string;
  locationTag: string;
  dateLabel: string;
  events: TideEventView[];
  currentLevel: number | null;
  trend: number | null;
  levelTimestamp: string | null;
}

interface ClusterComponentRefEntry {
  key: string;
  marker: maplibregl.Marker;
  componentRef: ComponentRef<ClusterMarkerComponent>;
  clusterClickSub: Subscription;
}

// Matches Flutter kPinArtSize (map_markers.dart:130); tip sits at y≈43.4 of 54.
const WAYPOINT_PIN_SIZE_PX = 54;
const WAYPOINT_PIN_TIP_OFFSET_PX = 10;
const WAYPOINT_PAGE_SIZE = 100; // server max
const WAYPOINT_MAX_PAGES = 20;
const MAP_INIT_IDLE_TIMEOUT_MS = 1500; // max wait before a panel-route page builds its (hidden) map
const MAP_INIT_FALLBACK_DELAY_MS = 300; // browsers without requestIdleCallback
const VESSEL_TRACKING_POLL_MS = 7000; // latest-observation poll for map vessels and the dockmate card

@Component({
  selector: 'app-home-page',
  standalone: true,
  imports: [
    SkipperHideDuringShowDirective,
    SkipperHelpButtonComponent,
    VesselCardComponent,
    CatchHeatControlsComponent,
    MapLayerSwitcherComponent,
    SideNavigationComponent,
    FilterPageComponent,
    WatchPageComponent,
    WaypointsComponent,
    CommonModule,
    TripComponent,
    MatDialogModule,
    AppBannerComponent,
    AppDownloadPopupComponent,
    ReplayOverlayComponent,
    ComboSliderCardComponent,
    ComboCursorBubbleComponent,
    ComboWindArrowsComponent,
    ComboWindChartComponent,
    RideAlongInviteComponent,
    RideAlongFollowComponent,
    RideAlongParticipantsComponent,
    RideAlongWaypointsComponent,
    TourAnchorDirective,
  ],
  templateUrl: './home-page.component.html',
  styleUrl: './home-page.component.scss',
})
export class HomePageComponent implements AfterViewInit, OnDestroy {
  @ViewChild('tripComponent') tripComponent!: TripComponent;
  private map!: Map;
  // requestIdleCallback id or setTimeout id; cleared in ngOnDestroy so a destroyed page never builds a map.
  private deferredMapInitHandle: number | null = null;
  private subs = new Subscription();

  // ── Combo overlay state (parallel sibling to the ocean overlays) ───────────
  comboState: ComboState = defaultComboState();
  comboProbeAnchor: { x: number; y: number } | null = null;
  comboShowChart = false;
  private comboWasEnabled = false;
  private readonly comboValueSourceId = 'combo-value-source';
  private readonly comboValueLayerId = 'combo-value-layer';
  private readonly comboFrontsSourceId = 'combo-fronts-source';
  private readonly comboFrontsLayerId = 'combo-fronts-layer';
  private readonly comboWindSourceId = 'combo-wind-source';
  private readonly comboWindLayerId = 'combo-wind-layer';
  private readonly comboContourSourceId = 'combo-contour-source';
  private readonly comboContourLayerId = 'combo-contour-layer';
  private readonly comboContourUrl = `${environment.apiUrl}/observations/layers/contours/v3/{z}/{x}/{y}.png`;
  private comboValueKey = '';
  private comboFrontsKey = '';
  private comboWindKey = '';
  private comboValueObjUrl: string | null = null;
  private comboFrontsObjUrl: string | null = null;
  private comboPrevMaxZoom: number | null = null;
  /** Exposed to the wind-arrows overlay template (which needs the map). */
  get comboMap(): Map {
    return this.map;
  }
  get comboEnabled(): boolean {
    return this.comboState.config.enabled;
  }
  /**
   * The Combo control surface stays mounted (combo isn't disabled) but is hidden
   * while a full-screen panel is open — profile, watch, messages, the waypoint
   * manager, or the filter sheet — so it doesn't float over them. It springs back
   * when those close.
   */
  get comboControlsVisible(): boolean {
    return this.comboEnabled && this.mapChromeVisible;
  }

  /** The live layer, from the single-selection model (null = no layer on the map). */
  activeLayerKey: MapLayerKey | null = null;

  /** The card + switcher stack: shown for any live layer, under the same panel rules as the combo card. */
  get layerStackVisible(): boolean {
    return this.activeLayerKey != null && this.mapChromeVisible;
  }

  /**
   * Flutter `_catchHeatTakeover`: while the heatmap is live, trip lines/dots, ride-along,
   * every boat and the vessel clusters are suppressed so only the heat + catch pins read.
   * GL layers are hidden here; DOM markers (waypoints, boats, clusters) by the
   * `.heat-takeover` class (see scss). Layers drawn later are caught by the `idle` hook.
   */
  private static readonly HEAT_TAKEOVER_LAYERS = /^(home-trip-|ride-along-|vessel-cluster-|vessel-unclustered)/;
  private heatTakeoverHidden = new Set<string>();
  private heatTakeoverIdleBound = false;

  private applyHeatTakeover(): void {
    const map = this.map;
    if (!map || !this.isMapLoaded) return;
    const on = this.activeLayerKey === 'catch';
    map.getContainer().classList.toggle('heat-takeover', on);
    if (on) {
      if (!this.heatTakeoverIdleBound) {
        this.heatTakeoverIdleBound = true;
        map.on('idle', () => this.applyHeatTakeover());
      }
      for (const l of map.getStyle()?.layers ?? []) {
        if (!HomePageComponent.HEAT_TAKEOVER_LAYERS.test(l.id)) continue;
        if (map.getLayoutProperty(l.id, 'visibility') === 'none') continue;
        map.setLayoutProperty(l.id, 'visibility', 'none');
        this.heatTakeoverHidden.add(l.id);
      }
    } else if (this.heatTakeoverHidden.size) {
      for (const id of this.heatTakeoverHidden) {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'visible');
      }
      this.heatTakeoverHidden.clear();
      // Trip filters own the real visibility of those layers; re-assert it.
      this.applyTripFilters();
    }
  }

  private get mapChromeVisible(): boolean {
    return (
      !this.isProfileOpen &&
      !this.isWatchOpen &&
      !this.isMessagesOpen &&
      !this.isWaypointManagerOpen &&
      !this.isFilterOpen
    );
  }
  private userLocationMarker?: maplibregl.Marker;
  private vesselMarkers: globalThis.Map<string, maplibregl.Marker> = new globalThis.Map();
  private vesselTrackingSubs: globalThis.Map<string, Subscription> = new globalThis.Map();
  /** vesselId → latest observation id + position, so a tap on the WebGL boat icon
   *  (own/primary vessel + shared boats) can open its observation popup. */
  private vesselLatestObs = new globalThis.Map<string, { obsId: string | null; lng: number; lat: number }>();
  private dockmateMarkers: maplibregl.Marker[] = [];
  private dockmateDetailSub?: Subscription;
  private activeObsTripPopup: maplibregl.Popup | null = null;
  /**
   * Monotonic token for observation-popup requests. Bumped on every click and on
   * close, so a slow in-flight fetch never opens (or reopens) a popup the user has
   * already superseded or dismissed — the loading popup is shown instantly and only
   * the matching request is allowed to fill it in.
   */
  private _dockmateZoomPendingId = 0;
  private _suppressNextMapClick = false;
  private accuracyCircleId = 'user-accuracy-circle';
  private myUserIds: string[] = [];
  private myProfileNames: string[] = [];
  /** Current user's display name — shown as the author on their own vessel popup. */
  private myDisplayName = '';
  /** Ids of the current user's own vessels (from getVessels) — used to attribute the
   *  observation popup author to the current user when the payload lacks a valid id. */
  private myVesselIds = new Set<string>();
  /** Every id alias of my vessels → its name, so tapping my own boat can show it without a matched trip. */
  private myVesselNames = new globalThis.Map<string, string>();
  isWatchOpen = false;
  isFilterOpen = false;
  isProfileOpen = false;
  isMessagesOpen = false;
  isLayerOpen = false;
  isWaypointCreating = false;
  isWaypointManagerOpen = false;
  isDockmateCardOpen = false;
  isWeatherZonesLoading = false;
  private weatherZonesLoadCounter = 0;
  selectedDockmateVessel: any = null;
  /** Boat-card inputs for the open dockmate card; built once per open so OnPush inputs stay stable. */
  dockmateCardParams: CardParams | null = null;
  dockmateCardContext: { identity: CardIdentity; units: CardUnits; chatEnabled: boolean } | null = null;
  /** Vessel coordinate the dockmate detail card is anchored to, so the card can be
   *  repositioned next to the moving marker as the map pans/zooms. */
  private dockmateCardLngLat: [number, number] | null = null;
  activeWatchVesselId: string | null = null;
  activeWaypointManagerId: string | null = null;
  private allWaypoints: any[] = [];
  private displayedMarkers: maplibregl.Marker[] = [];
  /**
   * Waypoint the Create Waypoint tour is spotlighting. While it is set, that one
   * marker is exempt from clustering, viewport culling and presentation hiding —
   * otherwise the pin the user just saved is folded into a cluster (or detached
   * by the fly-to's moveend) and the "Waypoint created" spotlight rings an empty
   * patch of chart. See `spotlightTourWaypoint`.
   */
  private tourSpotlightWaypointId: string | null = null;
  /** Fraction of the viewport width/height to pad when culling off-screen markers. */
  private static readonly MARKER_VIEWPORT_PADDING = 0.25;
  private lastClusterCacheZoom: number = -1;
  /**
   * Fingerprint of everything that can change cluster membership (waypoint set,
   * vessel set + positions, filters, panel state). Mobile keys its cluster cache
   * on the same idea (`_waypointFilterSignature`); the web only keyed on zoom, so
   * hiding a captain or receiving new positions left the OLD bubbles and OLD
   * badge numbers on screen until the zoom happened to move by more than 0.05.
   * That is the "counts are just inconsistent / never track" report.
   */
  private lastClusterDataSignature: string | null = null;
  /** Previous value of `shouldHideWaypointPresentation()` — drives the re-aggregate on panel close. */
  private wasWaypointPresentationHidden = false;
  private allTrips: any[] = [];
  private sharedActiveTripMarkers: { [id: string]: maplibregl.Marker } = {};
  private latestObservation: any = null;
  private hasCenteredOnceAfterLogin = false;
  private primaryVesselName: string | null = null;
  private userSettings: any = null;
  private mapOrientationMode: MapOrientationMode = 'normal';
  private isNorthUpRotationLockActive = false;
  private isMapLoaded = false;
  private gpsWatchId: number | null = null;
  isAutoFollowEnabled = true;
  private lastKnownGps: { lat: number; lng: number } | null = null;
  private pendingWaypointFocus: { id: string; position?: any; create_ts?: string; attempts: number; highlight?: boolean; overrideFetchStarted?: boolean; waypointData?: any } | null = null;
  private pendingTripFocus: { id: string; tripData?: any; create_ts?: string; attempts: number } | null = null;
  private activeTripFocusId: string | null = null;
  private activeWaypointHighlightId: string | null = null;
  private focusedWaypointOverride: any | null = null;
  private activeTripWaypointHighlightTripId: string | null = null;
  private activeTripFocusWaypoints: any[] = [];
  private activeWaypointCardMarker: maplibregl.Marker | null = null;
  private activeWaypointCardWaypointId: string | null = null;
  private waypointCardOpenRequestSeq = 0;
  private readonly tripTrackSourceId = 'home-trip-track-source';
  private readonly tripTrackLineLayerId = 'home-trip-track-line';
  private readonly tripTrackPointLayerId = 'home-trip-track-points';
  private readonly tripTrackHighlightLineLayerId = 'home-trip-track-line-highlight';
  private readonly tripTrackHighlightPointLayerId = 'home-trip-track-points-highlight';

  private readonly tripLiveConnectionSourceId = 'home-trip-live-connection-source';
  private readonly tripLiveConnectionLayerId = 'home-trip-live-connection-layer';
  private readonly extendTrackPollingIntervalMs = 90000;
  /** Emits once the map canvas is revealed; background polls wait for it so they don't compete with first tiles. */
  private readonly mapFirstPaint$ = new ReplaySubject<void>(1);
  private tripTrackSourceDataCache: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
  private visibleTripIds = new Set<string>();
  private tripExtendTrackSub?: Subscription;
  private extendTrackTripId: string | null = null;
  private activeTripLiveAnchorCoord: number[] | null = null;
  private activeTripLivePath: number[][] = [];

  // ── Ride Along follow mode ───────────────────────────────────────────────
  // Faithful port of the Flutter _TripTrackMixin ride-along rendering. Decoupled
  // from the trip filter/allTrips system so following never mutates normal map
  // state. The visual model:
  //   • confirmed track  → green polyline (glow + solid) from extend_track's
  //     track.points (90s poll)
  //   • dotted connector → track tail → live boat (≤75km guard)
  //   • boat marker      → green circle + boat img rotated (cog + 30°), at the
  //     live observation position (observations/latest, 15s poll)
  //   • paused           → hide boat, show red dot at the track tail
  //   • observation dots → green dots at track points carrying an observation id
  private readonly rideAlongObsIntervalMs = 15000;
  private readonly rideAlongTrackIntervalMs = 90000;
  private readonly rideAlongTrackSourceId = 'ride-along-track-source';
  private readonly rideAlongTrackGlowLayerId = 'ride-along-track-glow';
  private readonly rideAlongTrackLineLayerId = 'ride-along-track-line';
  private readonly rideAlongConnectorSourceId = 'ride-along-connector-source';
  private readonly rideAlongConnectorLayerId = 'ride-along-connector-line';
  private readonly rideAlongPointsSourceId = 'ride-along-points-source';
  private readonly rideAlongPointsLayerId = 'ride-along-points-layer';
  private static readonly RIDE_ALONG_GREEN = '#34C759';
  private rideAlongBoatMarker?: maplibregl.Marker;
  private rideAlongPausedMarker?: maplibregl.Marker;
  private rideAlongObsSub?: Subscription;
  private rideAlongTrackSub?: Subscription;
  private rideAlongTripId: string | null = null;
  private rideAlongVesselId: string | null = null;
  private rideAlongTripName = '';
  private rideAlongLatestObsId: string | null = null;
  private rideAlongPointsClickBound = false;
  // Participants sheet (bound in template).
  rideAlongParticipantsOpen = false;
  rideAlongParticipantsTripId = '';
  /** Inline crew (captain + riders, with their vessels) from the followed trip. */
  rideAlongParticipantsList: RideAlongParticipant[] = [];
  /** Raw ride_along_participants from the last trip payload — carries rider vessels. */
  private rideAlongTripParticipantsRaw: any[] = [];
  // Added-waypoints modal (bound in template).
  rideAlongWaypointsOpen = false;
  rideAlongWaypointsTripId = '';
  // Added waypoints (mine + others) rendered on the map while following.
  private rideAlongWaypointMarkers: maplibregl.Marker[] = [];
  private rideAlongWaypointsSub?: Subscription;
  // Ids of OTHER mates' waypoints merged into allWaypoints (so they cluster via the
  // normal pipeline); removed again on leave.
  private rideAlongWaypointIds = new Set<string>();
  // Confirmed backend track ([lng,lat]) — separate from the live boat position.
  private rideAlongTrackPolyline: number[][] = [];
  // Live boat position/bearing from the latest observation.
  private rideAlongVesselPos: [number, number] | null = null;
  private rideAlongVesselBearing: number | null = null;
  // Observation dots ({ coord:[lng,lat], obsId }) drawn along the track.
  private rideAlongObsPoints: { coord: number[]; obsId: string }[] = [];
  private rideAlongStartTime: number | null = null;
  private rideAlongEndedHandled = false;
  // Bound to the follow card in the template.
  rideAlongFollowActive = false;
  rideAlongCaptainName = '';
  rideAlongVesselName = '';
  rideAlongStartTimeMs: number | null = null;
  /** SI values — the card owns unit conversion, exactly like the app's TripStatusCard. */
  rideAlongDistanceMeters: number | null = null;
  rideAlongSpeedMs: number | null = null;
  rideAlongWaterTempC: number | null = null;
  rideAlongLat: number | null = null;
  rideAlongLng: number | null = null;
  rideAlongPaused = false;

  get rideAlongDistanceUnit(): string {
    return this.userSettings?.distance_unit || 'nautical';
  }

  get rideAlongTemperatureUnit(): string {
    return this.userSettings?.temperature_unit || 'fahrenheit';
  }

  // ── Replay Mode ────────────────────────────────────────────────────────────
  isReplayMode = false;
  replayState: ReplayState | null = null;
  private _replayAutoFollow = false;
  private _replayNeedsInitialZoom = false;
  private _replayHadMyBoats = false;
  /** Filter state kept in sync so settings sheet can open with latest values. */
  _replayPresetIndex = 2;
  _replaySourceIndex = 0;
  _replayCustomRange: { start: Date; end: Date } | null = null;
  private _replayBoatMarkers: { [tripIndex: number]: maplibregl.Marker } = {};
  private readonly replayWaypointsSourceId = 'replay-waypoints-source';
  private readonly replayWaypointsLayerId = 'replay-waypoints-icons';
  private _replayWaypointClickHandler: ((e: any) => void) | null = null;
  private readonly _loadingWaypointImages = new Set<string>();
  private readonly replayPolylinesSourceId = 'replay-polylines-source';
  private readonly replayPolylinesLineLayerId = 'replay-polylines-line';
  private readonly replayPolylinesSharedLayerId = 'replay-polylines-shared';

  // ── Distance Measurement ──────────────────────────────────────────────────
  isMeasurementMode = false;
  showMeasureToast = false;
  measureDistance: string | null = null;
  measurePointASet = false;   // true once Point A is placed
  private measurePointA: maplibregl.Marker | null = null;
  private measurePointB: maplibregl.Marker | null = null;
  private measureHighlightedElA: HTMLElement | null = null;   // waypoint el highlighted for Point 1
  private measureHighlightedElB: HTMLElement | null = null;   // waypoint el highlighted for Point 2
  private readonly measureSourceId = 'measure-line-source';
  private readonly measureLayerId = 'measure-line-layer';

  // ── Active Map Overlay ─────────────────────────────────────────────────────
  activeOverlayConfigs: any[] = [];
  activeOverlayWidgetType: string | null = null;
  private readonly rasterOverlaySourceId = 'map-overlay-raster-source';
  private readonly rasterOverlayLayerId = 'map-overlay-raster-layer';
  // Tracks the sourceId of the most-recently applied raster overlay that is
  // currently performing its initial load. Prevents showing the global overlay
  // loader on ordinary tile fetches caused by map panning after the overlay
  // has already finished its initial load.
  private currentRasterOverlayInitialLoadSourceId: string | null = null;
  private readonly weatherZonesApiBaseUrl = `${environment.apiUrl}/observations/marine-zones/`;
  private readonly weatherZonesBboxApiBaseUrl = `${environment.apiUrl}/observations/marine-zones/bbox/`;
  private readonly weatherOverlayMinZoom = 7;
  private readonly acspoRegionBounds: Record<string, [[number, number], [number, number]]> = {
    east_coast: [[-83.5, 23.0], [-60.0, 47.5]],
    gulf_coast: [[-99.5, 17.5], [-79.0, 32.5]],
    bahamas: [[-82.5, 20.0], [-70.0, 29.5]],
  };
  private readonly acspoPointRegionBoundaryToleranceDeg = 0;
  private weatherOverlayPreviousMinZoom: number | null = null;
  private weatherOverlayPreviousMaxZoom: number | null = null;
  private weatherOverlayLockedMinZoom: number | null = null;
  private weatherOverlayLockedMaxZoom: number | null = null;
  private weatherViewportSnapped = false;
  private marineWeatherPopup: maplibregl.Popup | null = null;
  private readonly acspoPointApiUrl = `${environment.apiUrl}/observations/acspo/point/`;
  private acspoPointPopup: maplibregl.Popup | null = null;
  private acspoPointRequestSeq = 0;
  /**
   * Session-level overlay cache.
   * Key: composite string built by buildOverlayCacheKey()  →  type|source|region|date
   * Value: tile URL string (raster layers) OR GeoJSON FeatureCollection object (weather).
   * Lives for the lifetime of the page — cleared on refresh/reload.
   * NOTE: Plain object used intentionally to avoid conflict with maplibre-gl's Map import.
   */
  private overlayDataCache: { [key: string]: string | object } = {};
  activeMapLayerIds: string[] = [];
  private layersHiddenByZoom: string[] = [];
  private readonly layerAutoHideZoomThreshold = 6;
  private buoyMarkers: maplibregl.Marker[] = [];
  private buoysLoaded = false;
  private readonly buoyNearbyApiBaseUrl = `${environment.apiUrl}/observations/buoys/nearby/`;
  private lastBuoyFetchKey: string | null = null;
  private tideMarkers: maplibregl.Marker[] = [];
  private tidesLoaded = false;
  private readonly localTidesNearbyApiBaseUrl = `${environment.apiUrl}/observations/tides/nearby/`;
  private readonly coOpsBase = 'https://api.tidesandcurrents.noaa.gov/api/prod/datagetter';
  private lastTideFetchKey: string | null = null;
  private tidesRequestSeq = 0;
  private readonly tideMarkerMinZoom = 5;
  private readonly tideNearbyRadiusMinNm = 10;
  // Mobile parity: keep tide nearby queries bounded to 50nm.
  private readonly tideNearbyRadiusMaxNm = 50;
  private readonly tideMarkersRenderLimit = 100;
  private readonly buoyMarkersRenderLimit = 220;
  private readonly wreckMarkersRenderLimit = 260;
  private tideDetailRequestSeq = 0;
  private wreckMarkers: maplibregl.Marker[] = [];
  private wrecksLoaded = false;
  private readonly wreckNearbyApiBaseUrl = `${environment.apiUrl}/observations/wrecks/nearby/`;
  // Geographic area the currently-loaded wrecks cover (a padded superset of the
  // viewport they were fetched for). While the live viewport stays inside this
  // region — i.e. when zooming in or panning slightly — we reuse the loaded
  // wrecks instead of re-hitting the network, which prevents the flicker/reload
  // that happened when wrecks were re-fetched on every zoom level.
  private lastWreckFetchBounds: maplibregl.LngLatBounds | null = null;
  // How far beyond the viewport to fetch wrecks (fraction of viewport span per
  // side). 0.6 ≈ a 2.2× wider/taller area, so typical zoom-ins/pans hit cache.
  private readonly wreckFetchPadFactor = 0.6;
  private wreckMarkerIconUrlCache: string | null = null;
  private wreckMarkerIconPromise: Promise<string> | null = null;
  private wreckMarkersRequestSeq = 0;
  private activeWreckPopup: maplibregl.Popup | null = null;
  // Wrecks render as a GPU GeoJSON symbol layer (not DOM markers) so hundreds of
  // points stay smooth on pan/zoom — DOM markers re-project every frame and lag.
  private readonly wreckSourceId = 'wreck-points-source';
  private readonly wreckSymbolLayerId = 'wreck-points-symbol';
  private readonly wreckIconImageId = 'wreck-point-icon';
  private wreckIconNaturalWidth: number | null = null;
  private wreckIconImagePromise: Promise<boolean> | null = null;
  private wreckFeatures: any[] = [];
  private wreckInteractionsRegistered = false;
  private readonly waypointRasterHighlightCache = new globalThis.Map<string, Promise<string | null>>();
  private readonly placeMarkerBaseSizePx = 40;
  private readonly placeMarkerGlyphBaseSizePx = 36;
  // Wreck markers are ~30% smaller than other place-icon markers for less clutter.
  private readonly wreckMarkerBaseSizePx = 28;
  private readonly placeLabelFadeStartZoom = 8;
  private readonly placeLabelHiddenZoom = 6;
  isBuoyDetailOpen = false;
  buoyDetailLoading = false;
  buoyHistoryLoading = false;
  buoyHistoryExpanded = false;
  selectedBuoy: BuoyDetailView | null = null;
  buoyHistoryObservations: BuoyObservationView[] = [];
  private buoyDetailRequestSeq = 0;
  isTideDetailOpen = false;
  tideDetailLoading = false;
  tideSheetError: string | null = null;
  selectedTide: TideDetailView | null = null;

  /** True while a focus jump is waiting on tiles for a not-yet-loaded area. */
  isChartLoading = false;
  /** Guards against an older jump's `idle` clearing a newer jump's indicator. */
  private chartLoadToken = 0;

  // ── Watermark ──────────────────────────────────────────────────────────────
  showWatermark = false;
  watermarkEmail = '';
  watermarkArray = new Array(400);

  // ── Clustering ─────────────────────────────────────────────────────────────
  private clusterComponentRefs = new globalThis.Map<string, ClusterComponentRefEntry>();
  private clusterRemovalTimers = new globalThis.Map<string, ReturnType<typeof setTimeout>>();
  private isZoomAnimating = false;
  private clusterDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  /** Faded historical vessel icon shown after zooming into a dockmate */
  private dockmateObservationMarker: maplibregl.Marker | null = null;
  /** The regular dockmate marker hidden while its observation marker is visible */
  private dockmateObservationHiddenMarker: maplibregl.Marker | null = null;

  // ── Vessel Clustering ──────────────────────────────────────────────────────
  // The cluster circle/count GeoJSON layers below are legacy: every aggregate is
  // now a single Angular bubble marker (mobile parity), so these sources are only
  // kept alive — and fed an empty FeatureCollection — because the style and its
  // click handlers still reference the ids.
  private readonly vesselClusterSourceId = 'vessel-cluster-source-v3';
  private readonly vesselClusterCircleLayerId = 'vessel-cluster-circles-v3';
  private readonly vesselClusterCountLayerId = 'vessel-cluster-counts-v3';
  private readonly vesselUnclusteredSourceId = 'vessel-unclustered-source-v3';
  private readonly vesselUnclusteredLayerId = 'vessel-unclustered-layer-v3';
  /** Live vessel data store, keyed by vessel id */
  private liveVesselStore = new globalThis.Map<string, ClusterableVessel>();
  private vesselClusterSub?: Subscription;

  constructor(
    private submenuService: SubMenuService,
    private waypointService: WaypointService,
    private tripService: TripService,
    private mapFilterService: MapFilterService,
    private mapSkinService: MapSkinService,
    private authService: AuthService,
    private vesselService: VesselService,
    private userProfileService: UserProfileService,
    private socialService: SocialService,
    private dialog: MatDialog,
    private ngZone: NgZone,
    private pollScheduler: PollScheduler,
    public replayService: ReplayService,
    private userRoles: UserRolesService,
    private vesselCardPopup: VesselCardPopupService,
    private vesselCardChat: VesselCardChatService,
    private skins: VesselSkinStore,
    private layerSelection: MapLayerSelectionService,
    private catchHeatMap: CatchHeatMapService,
    private http: HttpClient,
    private appRef: ApplicationRef,
    private environmentInjector: EnvironmentInjector,
    private clustering: MapClusteringService,
    public combo: ComboService,
    private comboBackend: ComboBackendService,
    private clarityService: ClarityService,
    private rideAlongService: RideAlongService,
    private toast: ToastService,
    private tourController: TourController,
  ) {
    this.clarityService.setTag('page', 'home_map');
  }
  ngAfterViewInit(): void {
    const objId = this.authService.getUserObjectId();
    const tokenId = this.authService.getUserIdFromToken();
    const subId = tokenId || objId;
    this.myUserIds = [objId, tokenId].filter((id): id is string => !!id);
    this.mapFilterService.syncForUser(subId);

    this.refreshUserSettingsFromApi({ refreshWaypointMarkers: true });

    // Work out our live trip (own trip → status card, ride-along → follow) straight
    // away. It reads GET /trip/ directly, so it must not wait on the trips feed —
    // that wait is why the timer used to take so long to appear.
    this.resolveMyLiveTrip();

    // Pre-fetch phone GPS on home screen init — mirrors Flutter's _getCurrentLocation() in initState.
    // Stores the position so it's ready the moment the user opens "Create Waypoint".
    this.fetchAndStorePhoneGps();

    // ── Watermark check ──
    this.initWatermark();

    // Capture pathname before initMap — map init can take >10ms and allow async
    // URL-cosmetics timers (from UserProfileComponent's BehaviorSubject subscription)
    // to fire and overwrite window.location.pathname with '/home' before we read it.
    const initialPathname = window.location.pathname;

    const profilePanelFirstSegments = ['watch', 'dockmates', 'fleets', 'trips', 'waypoints', 'photos', 'posts', 'vessels', 'settings'];
    const startsOnPanel =
      initialPathname.startsWith('/profile') ||
      profilePanelFirstSegments.includes(initialPathname.split('/').filter(Boolean)[0] || '');

    if (startsOnPanel) {
      // The panel covers the map on these routes: let it paint first, then build the map
      // (style + tile work) when the browser is idle. Timeout bounds the wait so closing the
      // panel never reveals a map that was never started.
      const startMap = () => { if (!this.map) this.initMap(); };
      this.deferredMapInitHandle = typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(startMap, { timeout: MAP_INIT_IDLE_TIMEOUT_MS })
        : window.setTimeout(startMap, MAP_INIT_FALLBACK_DELAY_MS);
    } else {
      this.initMap();
    }

    // Start downloading the map's waypoint pins immediately — do NOT wait for the
    // map's 'load' event (which is delayed on slow connections while the map style
    // and tiles download). The data fetch needs no map; the pins are drawn as soon
    // as both the data has arrived and the map is ready (see map 'load' handler).
    this.loadInitialWaypoints();

    // If landing on a shared resource deep-link, open the profile panel on
    // the Watch tab and show the specific trip/waypoint inline there.
    const profileResourceMatch = initialPathname.match(/^\/(profile\/)?(trips|waypoints)\/([^/]+)/);
    if (profileResourceMatch) {
      const resourceType = profileResourceMatch[2];  // 'trips' or 'waypoints'
      const id = decodeURIComponent(profileResourceMatch[3]);
      this.submenuService.setUserProfileOpenState(true);
      // Delay so Watch tab mounts and its feed starts loading before we signal
      setTimeout(() => {
        this.submenuService.navigateToProfileTab('watch');
        if (resourceType === 'waypoints') {
          this.submenuService.navigateToWatchWaypoint(id);
        } else {
          this.submenuService.navigateToWatchTrip(id);
        }
      }, 150);
    } else if (startsOnPanel) {
      this.submenuService.setUserProfileOpenState(true);
    } else {
      this.submenuService.setUserProfileOpenState(false);
    }

    // Ride Along: react to the "which trip am I following" state. Entering starts
    // the boat/track polling; clearing tears it down. Also start the incoming
    // invitation poll (the invite card component also starts it — startInvitePolling
    // is idempotent).
    this.rideAlongService.startInvitePolling();
    this.subs.add(
      this.rideAlongService.activeRideAlong$.subscribe((active) => {
        if (active) {
          this.enterRideAlongFollow(active);
        } else {
          this.exitRideAlongFollow();
        }
      }),
    );
    // NOTE: we deliberately do NOT restore the ride-along from localStorage here.
    // That cache outlives the trip it names, so on the next load it would resurrect an
    // already-ended ride-along, fetch it, find trip_state='completed' and announce
    // "the trip you were riding along with has ended" — while the user is in fact on a
    // new, live trip. resolveMyLiveTrip() below asks the server what's live on every
    // load, which is strictly better information than the cache ever was.
    // Guardrail: if the user starts their own trip while following, stop following.
    this.subs.add(
      this.tripService.currentTripId$.subscribe((id) => {
        if (id && this.rideAlongService.getActiveRideAlong()) {
          this.rideAlongService.setActiveRideAlong(null);
        }
      }),
    );

    // Replay state — update map on every tick during playback
    this.subs.add(
      this.replayService.state$.subscribe((state) => {
        this.replayState = state;
        if (this.isReplayMode && this.isMapLoaded) {
          this.ngZone.runOutsideAngular(() => this._updateReplayMap(state));
        }
      })
    );

    this.subs.add(
      this.submenuService.replayMode$.subscribe((active) => {
        this.isReplayMode = active;
        if (!active) this._clearReplayMap();
        this.applyClusteringNow();
      })
    );

    // Replay button in side-nav requests filter sheet from here
    this.subs.add(
      this.submenuService.openReplay$.subscribe(() => this.openReplay())
    );

    this.subs.add(
      this.layerSelection.selected$.subscribe((key) => {
        this.activeLayerKey = key;
        this.applyHeatTakeover();
      }),
    );

    // Skins arrive from three payloads at different times; coalesce a burst into one redraw.
    this.subs.add(
      this.skins.changed$.pipe(auditTime(150)).subscribe(() => this.ngZone.runOutsideAngular(() => this.refreshBoatArt())),
    );

    this.subs.add(
      this.submenuService.userSettings$.subscribe((settings) => {
        if (!settings) return;
        this.userSettings = {
          ...(this.userSettings || {}),
          ...(settings || {}),
        };
        this.refreshWaypointMarkers();
      })
    );

    this.subs.add(
      this.submenuService.mapOrientationMode$.subscribe((mode) => {
        this.applyMapOrientationMode(mode);
      })
    );

    this.subs.add(
      this.submenuService.userProfileOpenState$.subscribe((state) => {
        this.isProfileOpen = state;
        if (state) {
          this.clearActiveWaypointSelection();
          this.closeLayerPopupsForProfileOpen();
        }
        if (state && this.isDockmateCardOpen) {
          this.closeDockmateCard();
        }
        if (state && this.isReplayMode) { this.exitReplay(); }
        if (!state) {
          this.refreshUserSettingsFromApi({ refreshWaypointMarkers: false });
        }
        this.syncWaypointPresentationVisibility();
      }),
    );
    this.subs.add(
      this.submenuService.messagesOpenState$.subscribe((state) => {
        this.isMessagesOpen = state;
      }),
    );
    this.subs.add(
      this.submenuService.watchPageModalState$.subscribe((state) => {
        this.isWatchOpen = state;
        if (state) {
          this.clearActiveWaypointSelection();
        }
        if (state && this.isReplayMode) { this.exitReplay(); }
        this.syncWaypointPresentationVisibility();
      }),
    );
    this.subs.add(
      this.submenuService.layerModalState$.subscribe((state) => {
        this.isLayerOpen = state;
        if (state) {
          this.clearActiveWaypointSelection();
        }
        this.syncWaypointPresentationVisibility();
      }),
    );
    this.subs.add(
      this.submenuService.waypointModalState$.subscribe((state) => {
        this.isWaypointCreating = state;
        if (state) {
          this.clearActiveWaypointSelection();
        }
        this.syncWaypointPresentationVisibility();
      }),
    );
    this.subs.add(
      this.submenuService.activeMapOverlays$.subscribe((configs) => {
        this.activeOverlayConfigs = configs;
        if (!configs.length) {
          this.activeOverlayWidgetType = null;
        } else if (!this.activeOverlayWidgetType || !configs.some(c => c.type === this.activeOverlayWidgetType)) {
          this.activeOverlayWidgetType = configs[0].type;
        }
        if (this.isMapLoaded) {
          this.syncMapOverlayLayers(configs);
        }
        if (!this.isAcspoPointQueryOverlayActive(configs)) {
          this.dismissAcspoPointPopup();
        }
      }),
    );
    this.subs.add(
      this.submenuService.activeMapLayers$.subscribe((layerIds) => {
        this.activeMapLayerIds = layerIds || [];
        if (this.isMapLoaded) {
          this.syncMapDataLayers();
        }
      }),
    );
    this.subs.add(
      this.submenuService.waypointManager$.subscribe((waypointId) => {
        this.activeWaypointManagerId = waypointId;
        this.isWaypointManagerOpen = !!waypointId;
        this.syncWaypointPresentationVisibility();
      }),
    );
    this.subs.add(
      this.submenuService.overlayApplied$.subscribe((type) => {
        this.setActiveOverlayWidgetType(type);
        // Mutual exclusion: activating any ocean/weather overlay turns Combo off.
        if (this.comboState.config.enabled) {
          this.combo.disable();
        }
      }),
    );

    // ── Combo overlay runtime — cubit-equivalent state drives the map layers ──
    this.subs.add(
      this.combo.state$.subscribe((s) => {
        const justEnabled = s.config.enabled && !this.comboWasEnabled;
        const justDisabled = !s.config.enabled && this.comboWasEnabled;
        this.comboWasEnabled = s.config.enabled;
        this.comboState = s;
        // Mutual exclusion: activating Combo clears any ocean/weather overlay.
        if (justEnabled && this.activeOverlayConfigs.length) {
          this.activeOverlayConfigs.slice().forEach((c) => this.submenuService.removeMapOverlay(c.type));
        }
        // Camera framing on activation (mirrors ContourConstants.comboActivationZoom /
        // comboMaxZoom): ease a deep camera back to z8 and clamp max zoom to 15.
        // Also explicitly release any stale Weather-overlay zoom lock so combo
        // Wind isn't stuck at the Weather overlay's minZoom=7 floor (a legit
        // web-only bug — mobile has no such lock, and users could otherwise hit
        // it if they'd had the old Weather overlay on before switching to combo,
        // or if a stale lock persisted).
        if (justEnabled && this.map) {
          this.releaseWeatherOverlayZoomLock();
          this.comboPrevMaxZoom = this.map.getMaxZoom();
          this.map.setMaxZoom(15);
          if (this.map.getZoom() > 8) {
            this.map.easeTo({ zoom: 8, duration: 650 });
          }
        }
        if (justDisabled && this.map && this.comboPrevMaxZoom != null) {
          this.map.setMaxZoom(this.comboPrevMaxZoom);
          this.comboPrevMaxZoom = null;
        }
        if (!s.config.enabled) this.comboShowChart = false;
        if (this.isMapLoaded) this.renderComboLayers(s);
        this.updateComboProbeAnchor();
      }),
    );

    this.subs.add(
      this.tripService.currentTripId$.subscribe(() => {
        if (this.allTrips.length > 0) {
          this.applyTripFilters();
        }
        this.syncActiveTripLiveTracking();
      })
    );

    // Listen for waypoint updates (from manage overlay) and update marker + popup in realtime
    this.subs.add(
      this.submenuService.waypointUpdated$.subscribe((wp) => {
        try {
          if (!wp || !this.map) return;
          const wpId = this.getWaypointId(wp);
          console.debug('[HomePage] waypointUpdated received', wpId);

          // Update allWaypoints array
          const existingIndex = this.allWaypoints.findIndex((w) => this.getWaypointId(w) === wpId);
          if (existingIndex >= 0) {
            this.allWaypoints[existingIndex] = this.normalizeWaypointForMap({
              ...this.allWaypoints[existingIndex],
              ...wp,
              details: {
                ...(this.allWaypoints[existingIndex]?.details || {}),
                ...(wp?.details || {}),
              },
            });
          } else {
            this.allWaypoints.push(this.normalizeWaypointForMap(wp));
          }

          // Update displayed marker if present. Remove + recreate marker to force a full re-render.
          const markerIndex = this.displayedMarkers.findIndex((m) => {
            const el = m.getElement();
            return el && el.getAttribute && el.getAttribute('data-wpid') === String(wpId).trim();
          });

          if (markerIndex >= 0) {
            try {
              const oldMarker = this.displayedMarkers[markerIndex];
              const wasPopupOpen = this.activeWaypointCardWaypointId === String(wpId).trim();
              const lngLat = oldMarker.getLngLat();
              this.closeWaypointCard(String(wpId).trim());

              // remove old marker
              try { oldMarker.remove(); } catch (e) { /* ignore */ }
              // remove from displayedMarkers list
              this.displayedMarkers.splice(markerIndex, 1);

              // recreate marker using updated waypoint data (ensure position exists)
              const currentWp = this.allWaypoints.find(w => this.getWaypointId(w) === wpId);
              const newWp = this.normalizeWaypointForMap({
                ...currentWp,
                ...wp,
                details: {
                  ...(currentWp?.details || {}),
                  ...(wp?.details || {}),
                },
              });
              if (newWp && (newWp.position || lngLat)) {
                // normalize position if missing
                if (!newWp.position && lngLat) {
                  newWp.position = { latitude: lngLat.lat, longitude: lngLat.lng };
                }
                this.addWaypointMarker(newWp, false);

                // Ensure the recreated marker's popup HTML reflects the updated waypoint
                try {
                  const recreated = this.displayedMarkers.find(m => {
                    const el = m.getElement();
                    return el && el.getAttribute && el.getAttribute('data-wpid') === String(wpId).trim();
                  });
                  if (recreated) {
                    if (wasPopupOpen && this.map) {
                      try {
                        this.openWaypointCard(recreated);
                      } catch (e) { /* ignore */ }
                    }
                  }
                } catch (e) {
                  // ignore popup update failures
                }
              }
            } catch (err) {
              console.error('Error recreating waypoint marker in realtime:', err);
            }
          }
        } catch (err) {
          console.error('[HomePage] waypointUpdated subscription handler error', err);
        }
      })
    );

    // Close map popup requests (when manage overlay requests the popup to be closed)
    this.subs.add(
      this.submenuService.waypointClosePopup$.subscribe((wpId) => {
        try {
          if (!this.map) return;
          if (!wpId) {
            // Close ALL popups unconditionally
            this.closeWaypointCard();
            // Also close buoys, tides, wrecks, etc. popups if available natively
            const allPopups = document.getElementsByClassName('maplibregl-popup');
            Array.from(allPopups).forEach(p => p.remove());
            return;
          }
          const marker = this.displayedMarkers.find((m) => {
            const el = m.getElement();
            return el && el.getAttribute && el.getAttribute('data-wpid') === String(wpId).trim();
          });
          if (marker) {
            this.closeWaypointCard(String(wpId).trim());
          }
        } catch (e) {
          console.error('[HomePage] error processing waypointClosePopup', e);
        }
      })
    );
    this.subs.add(
      this.submenuService.filterOpenState$.subscribe((state) => {
        this.isFilterOpen = state;
        this.syncWaypointPresentationVisibility();
      }),
    );
    this.subs.add(
      this.mapSkinService.selectedSkinId$.subscribe((skinId) => {
        if (this.map && this.isMapLoaded) {
          const styleUrl = this.mapSkinService.getStyleUrlFor(skinId);
          this.map.setStyle(styleUrl);
          this.map.once('style.load', () => {
            this.applyTripFilters();
            this.applyMapFilters();
            // A style swap drops every custom source/layer; the heat stack is rebuilt here.
            this.catchHeatMap.restyle();
            this.heatTakeoverHidden.clear();
            this.applyHeatTakeover();
          });
        }
      }),
    );
    this.subs.add(
      this.mapFilterService.filterState$.subscribe(() => {
        if (this.isMapLoaded && !this.isReplayMode) {
          this.applyTripFilters();
          this.applyMapFilters();
        }
      }),
    );

    this.subs.add(
      this.submenuService.waypointAdded$.subscribe((wp) => {
        if (wp) {
          const normalizedWp = this.normalizeWaypointForMap(wp);
          const existingIdx = this.allWaypoints.findIndex(w => this.getWaypointId(w) === this.getWaypointId(normalizedWp));
          if (existingIdx >= 0) {
            this.allWaypoints[existingIdx] = normalizedWp;
          } else {
            this.allWaypoints.push(normalizedWp);
          }
          // During the Create Waypoint guided tour, claim this waypoint BEFORE
          // its marker is built so the clustering pass below leaves it alone and
          // its pin stays on the chart under the tour's final spotlight.
          const isTourRun = this.tourController.state()?.tour.id === 'create_waypoint';
          if (isTourRun) {
            this.tourSpotlightWaypointId =
              String(this.getWaypointId(normalizedWp) || '').trim() || null;
          }
          this.addWaypointMarker(normalizedWp, false);
          this.updateWaypointAggregation();
          this.handleWaypointAddedForTripTrack(wp);
          if (isTourRun) {
            this.spotlightTourWaypoint(normalizedWp);
          }
        }
      }),
    );
    this.subs.add(
      this.submenuService.waypointDeleted$.subscribe((waypointId) => {
        try {
          if (!waypointId) return;
          this.removeWaypointFromMap(waypointId);
        } catch (err) {
          console.error('[HomePage] error processing waypointDeleted', err);
        }
      }),
    );
    this.subs.add(
      this.submenuService.watchItemHidden$.subscribe(({ tripId, waypointId }) => {
        try {
          if (waypointId) {
            this.removeWaypointFromMap(waypointId);
          }
          if (tripId) {
            this.allTrips = this.allTrips.filter((t) => this.getTripId(t) !== tripId);
            this.applyTripFilters();
          }
        } catch (err) {
          console.error('[HomePage] error processing watchItemHidden', err);
        }
      }),
    );
    this.subs.add(
      this.submenuService.mapFocus$.subscribe((data) => {
        if (!data) return;
        if (data.type === 'waypoint') {
          // Auto-highlight waypoints when the focus originates from Watch or Profile UI
          const origin = (data as any).origin || '';
          const shouldHighlight = origin === 'watch' || origin === 'profile';
          const waypointData = (data as any).waypointData || null;
          this.focusWaypointOnMap(data.id, data.position, data.create_ts, !!shouldHighlight, waypointData);
        } else if (data.type === 'trip') {
          this.focusTripOnMap(data.id, data.tripData, data.create_ts);
        }
      })
    );
  }
  private getIconSrc(wp: any): string {
    return this.getMapWaypointIconSrc(wp, this.isWaypointMine(wp));
  }

  // Same pin art + own-"dot" rule as Flutter map_markers.dart pinArt().
  private getMapWaypointIconSrc(wp: any, isMine: boolean): string {
    return `/${getWaypointPinAsset(wp, isMine)}`.replace('//assets', '/assets');
  }

  private getPlaceMapMarkerHtml(wp: any): string {
    const type = this.resolveWaypointTypeForMap(wp);
    if (this.shouldRenderPlaceAsTextLabel(wp)) {
      const label = this.getPlaceMapLabelText(wp);
      return `<span class="waypoint-place-label" style="display:inline-block;color:#1c2733;font-size:12px;line-height:1.1;font-style:italic;font-weight:500;letter-spacing:0.01em;white-space:nowrap;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;text-shadow:0 1px 3px rgba(255,255,255,0.9),0 -1px 3px rgba(255,255,255,0.9),1px 0 3px rgba(255,255,255,0.9),-1px 0 3px rgba(255,255,255,0.9);">${label}</span>`;
    }

    // Spot = Flutter's brown teardrop (canyon.png) with the X on a cover disc, same composite as the create screen.
    // Full 54px pin like every other waypoint (marker is bottom-anchored, see addWaypointMarker).
    if (type === 'canyon') {
      return `<span style="position:relative;display:block;width:${WAYPOINT_PIN_SIZE_PX}px;height:${WAYPOINT_PIN_SIZE_PX}px;">
        <img class="main-marker-image" src="/assets/waypoint_icons/pins/canyon.png" alt="Spot icon" style="position:absolute;inset:0;width:100%;height:100%;">
        <i style="position:absolute;left:16px;top:10.6px;width:22px;height:22px;border-radius:50%;background:#ac7f5e;"></i>
        <img src="/assets/waypoint_icons/spot_x_flutter.png" alt="" style="position:absolute;left:18px;top:12.7px;width:18px;height:18px;">
      </span>`;
    }

    const iconSrc = this.getPlaceMapMarkerIconSrc(type);
    const iconAlt = this.getWaypointTypeLabel(wp);

    // Wreck/Anchorage = Flutter's teardrop pins (same PNGs as assets/images/*.png), full pin size like Spot.
    if (type === 'wreck' || type === 'anchorage') {
      return `<span style="position:relative;display:block;width:${WAYPOINT_PIN_SIZE_PX}px;height:${WAYPOINT_PIN_SIZE_PX}px;">
        <img class="main-marker-image" src="${iconSrc}" alt="${iconAlt} icon" style="position:absolute;inset:0;width:100%;height:100%;">
      </span>`;
    }

    const shouldMuteIcon = !['inlet', 'canyon'].includes(type);
    const iconSize = type === 'inlet' ? 28 : 38;
    const iconOpacity = type === 'inlet' ? 1 : 0.92;

    return this.getSizedPlaceMapIconHtml(
      iconSrc,
      `${iconAlt} icon`,
      iconSize,
      shouldMuteIcon,
      '',
      iconOpacity
    );
  }

  private getPlaceMapMarkerIconSrc(type: string): string {
    const inlineIcon = this.getPlaceInlineIconSrc(type);
    if (inlineIcon) return inlineIcon;

    const iconMap: Record<string, string> = {
      canyon: '/assets/waypoint_icons/pins/canyon.png',
      wreck: '/assets/waypoint_icons/pins/wreck.png',
      inlet: '/assets/home/wavepoint/inlet_logo.svg',
      anchorage: '/assets/waypoint_icons/pins/anchorage.png',
      home: '/assets/waypoint_icons/pins/home.png',
    };

    return iconMap[type] || iconMap['canyon'];
  }

  private getSizedPlaceMapIconHtml(
    src: string,
    alt: string,
    size: number = 38,
    muted: boolean = true,
    extraStyle: string = '',
    opacity: number = 0.92
  ): string {
    const isInlineSvg = src.startsWith('data:image/svg+xml');
    const filter = muted
      ? 'grayscale(1) brightness(0.6) contrast(1.1) drop-shadow(0 5px 12px rgba(15, 23, 42, 0.16))'
      : isInlineSvg
        ? 'none'
        : 'drop-shadow(0 5px 12px rgba(15, 23, 42, 0.16))';
    return `<img src="${src}" alt="${alt}" style="width:${size}px;height:${size}px;display:block;object-fit:contain;opacity:${opacity};filter:${filter};${extraStyle}">`;
  }

  private getPlaceInlineIconSrc(type: string): string | null {
    const svg = this.getPlaceInlineIconSvg(type);
    if (!svg) return null;
    return this.svgToDataUri(svg);
  }

  private getPlaceInlineIconSvg(type: string): string | null {
    if (type === 'inlet') {
      return `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -2 20 20">
          <g transform="translate(-2 -4)">
            <path d="M21,11c-2.25,0-2.25,2-4.5,2s-2.25-2-4.5-2-2.25,2-4.5,2S5.25,11,3,11" fill="none" stroke="#555B66" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
            <path d="M3,5C5.25,5,5.25,7,7.5,7S9.75,5,12,5s2.26,2,4.51,2S18.75,5,21,5" fill="none" stroke="#555B66" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
            <path d="M21,17c-2.25,0-2.25,2-4.5,2s-2.25-2-4.5-2-2.25,2-4.5,2S5.25,17,3,17" fill="none" stroke="#555B66" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
          </g>
        </svg>
      `;
    }

    return null;
  }

  private svgToDataUri(svg: string): string {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg.trim())}`;
  }

  private resolveWaypointTypeForMap(wp: any): string {
    const resolved = resolveWaypointType(wp);
    if (resolved && resolved !== 'place' && resolved !== 'waypoint') return resolved;

    const placeCandidates = [
      wp?.place_type,
      wp?.details?.place_type,
      wp?.waypoint_ref?.place_type,
      Array.isArray(wp?.places) ? wp.places[0] : null,
    ];
    for (const candidate of placeCandidates) {
      const normalized = resolveWaypointType(String(candidate || ''));
      if (normalized && normalized !== 'place' && normalized !== 'waypoint') {
        return normalized;
      }
    }

    return resolved;
  }

  private normalizeWaypointForMap(wp: any): any {
    if (!wp || typeof wp !== 'object') return wp;
    const normalizedType = this.resolveWaypointTypeForMap(wp);
    const normalizedDetails = this.normalizeWaypointDetails(wp?.details);
    const fishSubtype = resolveWaypointFishSubtype({ ...wp, details: normalizedDetails });
    return {
      ...wp,
      ...(fishSubtype ? { fish_subtype: fishSubtype, subtype: wp?.subtype || fishSubtype } : {}),
      details: {
        ...normalizedDetails,
        ...(fishSubtype ? { fish_subtype: fishSubtype } : {}),
      },
      type: normalizedType || wp?.type || wp?.waypoint_type || wp?.event_type || wp?.place_type,
    };
  }

  private normalizeWaypointDetails(details: any): any {
    if (typeof details === 'string') {
      try {
        details = JSON.parse(details);
      } catch {
        details = {};
      }
    }
    if (!details || typeof details !== 'object') return details || {};

    let showIcon = details.show_icon;
    if (typeof showIcon === 'string') {
      const normalized = showIcon.trim().toLowerCase();
      if (normalized === 'true') showIcon = true;
      else if (normalized === 'false') showIcon = false;
    }

    return {
      ...details,
      ...(showIcon !== undefined ? { show_icon: !!showIcon } : {}),
    };
  }

  private shouldRenderPlaceAsTextLabel(wp: any): boolean {
    const type = this.resolveWaypointTypeForMap(wp);
    const showIcon = this.normalizeWaypointDetails(wp?.details)?.show_icon;
    return (type === 'canyon' || type === 'inlet') && showIcon === false;
  }

  private getPlaceMapLabelText(wp: any): string {
    return String(wp?.waypoint_name || wp?.name || wp?.title || '').trim() || this.getWaypointTypeLabel(wp);
  }

  private getPopupIconSrc(wp: any): string {
    return `/${getWaypointIconAsset(wp, 'popup')}`.replace('//assets', '/assets');
  }

  private formatCoordinate(val: number, type: 'lat' | 'lng'): string {
    if (val === undefined || val === null || isNaN(val)) return '--';
    const absVal = Math.abs(val);
    const degrees = Math.floor(absVal);
    const minutes = Math.floor((absVal - degrees) * 60);
    const seconds = ((absVal - degrees - minutes / 60) * 3600).toFixed(1);
    const direction =
      type === 'lat' ? (val >= 0 ? 'N' : 'S') : val >= 0 ? 'E' : 'W';
    return `${degrees}°${minutes}'${seconds}"${direction}`;
  }

  private formatPlaceCoordinate(val: number, type: 'lat' | 'lng'): string {
    if (val === undefined || val === null || isNaN(val)) return '--';
    const absVal = Math.abs(val);
    const degrees = Math.floor(absVal);
    const minutesFull = (absVal - degrees) * 60;
    const minutes = Math.floor(minutesFull);
    const seconds = ((minutesFull - minutes) * 60).toFixed(1);
    const direction =
      type === 'lat' ? (val >= 0 ? 'N' : 'S') : val >= 0 ? 'E' : 'W';
    return `${degrees}° ${minutes}' ${seconds}" ${direction}`;
  }

  private parseUtc(dateStr: string | null | undefined): Date | null {
    if (!dateStr) return null;
    try {
      const asStr = String(dateStr).trim();
      const normalized = asStr.replace(' ', 'T');
      if (/[zZ]$|[+\-]\d{2}:?\d{2}$/.test(normalized)) {
        return new Date(normalized);
      }
      return new Date(normalized + 'Z');
    } catch {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? null : d;
    }
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = this.parseUtc(dateStr);
    if (!date) return '';
    return date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
    });
  }

  private getWaypointDisplayName(type: string): string {
    const mapping: { [key: string]: string } = {
      fish_caught: 'Fish Caught',
      fish_mark: 'Fish Mark',
      bait_mark: 'Bait Mark',
      temp_break: 'Temp Break',
      fishcaught: 'Fish Caught',
      fishmark: 'Fish Mark',
      baitmark: 'Bait Mark',
      tempbreak: 'Temp Break',
    };
    return mapping[type.toLowerCase()] || 'Fishing';
  }

  /**
   * Opens the boat card for a track dot, ride-along dot or a vessel's latest reading.
   * The card loads and renders itself; this gathers only what the tap site knows — the
   * same arguments Flutter's `_showCompactObservationPopup` takes.
   */
  private fetchAndShowObservation(obsId: string, coords?: number[], tripId?: string, prefetchedObs?: any, vesselId?: string): void {
    if (!obsId && !prefetchedObs && !vesselId) return;
    const matchedTrip = tripId ? this.allTrips.find((t) => this.getTripId(t) === tripId) : null;
    const onRideAlongTrip = !!tripId && tripId === this.rideAlongTripId;
    const mine = (!!matchedTrip && this.isTripMine(matchedTrip)) || (!!vesselId && this.myVesselIds.has(vesselId));
    const myName = (this.myDisplayName || this.authService.getUserDisplayNameFromToken() || '').trim();

    const rawCreatorId = String(
      matchedTrip?.audit_data?.create_user_id
      || matchedTrip?.created_by?.identifier
      || matchedTrip?.created_by?.user_id
      || matchedTrip?.source_profile?.user_id
      || matchedTrip?.source_profile?._id
      || matchedTrip?.source_profile?.id
      || '',
    ).trim();
    // The API sometimes returns a placeholder id ("0"); treat it as no id so we never call /profile/0.
    const creatorId = rawCreatorId && rawCreatorId !== '0' ? rawCreatorId : '';

    this.openVesselCard(
      {
        vesselId: vesselId || null,
        observationId: obsId && obsId !== 'live' ? obsId : null,
        vesselName: String(
          matchedTrip?.vessel_name || matchedTrip?.vessel?.name || matchedTrip?.vessel_ref?.name
          || matchedTrip?.vessel_ref?.display_name || (onRideAlongTrip ? this.rideAlongVesselName : '')
          // My own boat has no matched trip when tapped on the map: use my loaded vessels, not "--".
          || (mine ? (this.myVesselNames.get(vesselId ?? '') || this.primaryVesselName || '') : '') || '',
        ).trim() || null,
        ownerName: String(
          matchedTrip?._vis?.captainName || matchedTrip?.owner_name || matchedTrip?.user_name
          || matchedTrip?.created_by?.name || matchedTrip?.audit_data?.create_user_name
          || (mine ? myName : '') || '',
        ).trim() || null,
        // Own boat with no creator id on the trip: the owner is me (keycloak id last in myUserIds).
        ownerUserId: creatorId || (mine ? this.myUserIds[this.myUserIds.length - 1] ?? null : null),
        tripName: onRideAlongTrip ? this.rideAlongTripName || null : null,
        rideAlongTripId: onRideAlongTrip ? tripId : null,
        observedTripId: tripId || null,
        fallbackLon: coords?.length === 2 ? coords[0] : null,
        fallbackLat: coords?.length === 2 ? coords[1] : null,
      },
      coords,
      prefetchedObs,
    );
  }

  /** Mounts the boat card in a map popup, replacing any other vessel UI (one at a time). */
  private openVesselCard(params: CardParams, coords?: number[] | null, prefetched?: any): void {
    if (!this.map) return;
    this.closeVesselCardPopup();
    this.closeDockmateCard();
    // Only one popup at a time: a track-dot tap opens this card, so close any waypoint card and
    // cancel a pending (deferred) one — otherwise a nearby waypoint's popup mounts on top.
    this.waypointCardOpenRequestSeq++;
    this.closeWaypointCard();

    const lngLat: [number, number] | null = coords && coords.length === 2
      ? [coords[0], coords[1]]
      : params.fallbackLon != null && params.fallbackLat != null
        ? [params.fallbackLon, params.fallbackLat]
        : null;

    this.activeObsTripPopup = this.vesselCardPopup.open(this.map, lngLat, {
      params,
      prefetched,
      ...this.vesselCardContext(),
      onParticipants: (req) => this.ngZone.run(() => this.openVesselCardParticipants(req)),
      onProfile: (req) => this.ngZone.run(() => this.openVesselCardProfile(req)),
      onChat: (args) => this.ngZone.run(() => void this.vesselCardChat.open(args, params, this.vesselCardContext().identity)),
      onClosed: () => { this.activeObsTripPopup = null; },
    });
  }

  private closeVesselCardPopup(): void {
    if (!this.activeObsTripPopup) return;
    try { this.activeObsTripPopup.remove(); } catch (e) { /* ignore */ }
    this.activeObsTripPopup = null;
  }

  /** Who "me" is, in every shape a payload might name me, plus unit prefs and the chat toggle. */
  private vesselCardContext(): { identity: CardIdentity; units: CardUnits; chatEnabled: boolean } {
    const names = [this.myDisplayName, this.authService.getUserDisplayNameFromToken() || '', ...this.myProfileNames]
      .map((n) => n.trim().toLowerCase())
      .filter(Boolean);
    return {
      identity: {
        userId: this.authService.getUserIdFromToken() || null,
        ids: [...this.myUserIds],
        names,
        activeTripId: this.tripService.getCurrentTripId() || null,
      },
      units: {
        speed: this.userSettings?.speed_unit,
        temperature: this.userSettings?.temperature_unit,
        depth: this.userSettings?.depth_unit,
      },
      chatEnabled: CHAT_UI_ENABLED,
    };
  }

  /** Crew row → the existing "On Board" sheet. */
  openVesselCardParticipants(req: VesselCardParticipantsRequest): void {
    this.rideAlongParticipantsList = req.participants.map((p) => ({
      profileId: p.profileId ?? '',
      keycloakId: p.profileId ?? '',
      name: p.name ?? '',
      imageUrl: p.imageUrl ?? undefined,
      vesselName: p.vesselName ?? undefined,
      role: (p.role === 'captain' ? 'captain' : 'participant') as RideAlongRole,
      waypointCount: 0,
    }));
    this.rideAlongParticipantsTripId = req.tripId ?? '';
    this.rideAlongParticipantsOpen = true;
  }

  /** Owner row → the profile panel: your own Trips tab for your boat, the captain's full profile otherwise. */
  openVesselCardProfile(req: VesselCardProfileRequest): void {
    this.closeVesselCardPopup();
    this.closeDockmateCard();
    if (req.isSelf) {
      this.submenuService.setUserProfileOpenState(true);
      this.submenuService.navigateToProfileTab('trips');
      return;
    }
    this.submenuService.requestOpenCaptainProfile({
      profileId: req.userId,
      profileObject: { _id: req.userId, id: req.userId, name: req.name, image_url: req.imageUrl },
    });
  }

  private waypointColors: { [key: string]: string } = {
    tempbreak: '#FF8D28',
    hazard: '#FF8D28',
    patrol: '#FF8D28',
    anchorage: '#CB30E0',
    home: '#CB30E0',
    canyon: '#AC7F5E',
    wreck: '#AC7F5E',
    inlet: '#AC7F5E',
    diver: '#FF383C',
    fog: '#8E8E93',
  };

  private getWaypointId(wp: any): string {
    return String(
      wp?.waypoint_id ||
      wp?._id ||
      wp?.id ||
      wp?.identifier ||
      wp?.reference_id ||
      ''
    ).trim();
  }

  /**
   * The trip a waypoint belongs to, resolved exactly like the app's
   * `GetWaypointModel.tripRef` (`get_waypoint_model.dart`): every key `Ref.fromJson`
   * accepts — `reference_id`, `ref_id`, `identifier`, `_id` — then the top-level
   * `trip_id` as the fallback the app applies when `trip_ref` carries only metadata.
   *
   * The web previously read just `identifier` and `reference_id`. A backend payload
   * that keys the ref as `ref_id`/`_id` therefore resolved to the empty string, and
   * an empty trip id means "standalone waypoint" to every caller — so those marks
   * sailed straight through the active-trips-only gate and stayed on the chart after
   * their trip ended. That is the "still seeing waypoints from past trips" report:
   * not a broken gate, a trip reference the gate could not see.
   */
  private getWaypointTripId(wp: any): string {
    const ref = wp?.trip_ref;
    return String(
      ref?.reference_id ||
      ref?.ref_id ||
      ref?.identifier ||
      ref?._id ||
      wp?.trip_id ||
      wp?.tripId ||
      ''
    ).trim();
  }

  private mergeWaypointData(waypoint: any): void {
    const waypointId = this.getWaypointId(waypoint);
    if (!waypointId) return;

    const existingIndex = this.allWaypoints.findIndex((item) => this.getWaypointId(item) === waypointId);
    if (existingIndex >= 0) {
      this.allWaypoints[existingIndex] = { ...this.allWaypoints[existingIndex], ...waypoint };
    } else {
      this.allWaypoints.push(waypoint);
    }
  }

  private normalizeWaypointResults(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.results)) return res.results;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.waypoints)) return res.waypoints;
    return [];
  }

  private getWaypointStatus(wp: any): string {
    const creatorId = wp.audit_data?.create_user_id;
    const privacy = wp.privacy;
    const isMine = creatorId && this.myUserIds.includes(creatorId);
    const sourceProfileName = wp.source_profile?.name || wp.creator_name || 'Unknown';

    if (isMine) {
      if (privacy === 'private') {
        return 'Private waypoint created by me';
      } else if (privacy === 'custom') {
        return 'Custom waypoint created by me (shared with selected users)';
      } else if (privacy === 'public') {
        return 'Public waypoint created by me';
      }
    } else {
      if (privacy === 'public') {
        return `Public waypoint shared by ${sourceProfileName}`;
      } else if (privacy === 'custom') {
        return `Waypoint shared with you by ${sourceProfileName}`;
      }
    }
    return '';
  }

  private getWaypointQuantity(wp: any): number {
    return Math.max(1, Math.min(99, Number(wp?.details?.fish_quantity || 1)));
  }

  private isPlaceWaypoint(wp: any): boolean {
    const normalizedType = this.resolveWaypointTypeForMap(wp);
    return ['canyon', 'wreck', 'inlet', 'anchorage', 'home'].includes(normalizedType);
  }

  private isBackendReachableForPlaceOverride(): boolean {
    if (typeof navigator === 'undefined') return true;
    return navigator.onLine !== false;
  }

  private getWaypointCardTitle(wp: any): string {
    if (this.resolveWaypointTypeForMap(wp) === 'canyon') {
      return this.getWaypointTypeLabel(wp);
    }

    const rawName = String(wp?.waypoint_name || wp?.name || wp?.title || '').trim();
    if (this.isPlaceWaypoint(wp) && rawName) {
      return rawName;
    }
    return rawName || 'Waypoint';
  }

  private getWaypointTypeLabel(wp: any): string {
    const normalizedType = this.resolveWaypointTypeForMap(wp);
    if (normalizedType === 'canyon') return 'Spot';
    if (normalizedType === 'fish_caught' && wp?.details?.fish_subtype) {
      return this.getFishCaughtDisplayTitle(wp.details.fish_subtype);
    }
    if (normalizedType) {
      return normalizedType.replace(/_/g, ' ').replace(/\b\w/g, (char: string) => char.toUpperCase());
    }
    return 'Waypoint';
  }

  private isWaypointMine(wp: any): boolean {
    const createUserId = String(wp?.audit_data?.create_user_id || '').trim();
    const sourceProfileId = String(
      wp?.source_profile?._id ||
      wp?.source_profile?.id ||
      wp?.source_profile?.user_id ||
      ''
    ).trim();

    if (
      (createUserId && this.myUserIds.includes(createUserId)) ||
      (sourceProfileId && this.myUserIds.includes(sourceProfileId))
    ) {
      return true;
    }

    const sourceProfileName = String(wp?.source_profile?.name || '').trim();
    if (!sourceProfileName) {
      return false;
    }

    const normalizedAuthor = this.normalizeWaypointOwnerName(sourceProfileName);
    return !!normalizedAuthor && this.myProfileNames.includes(normalizedAuthor);
  }

  private normalizeWaypointOwnerName(value: string): string {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  private buildMyProfileNameCandidates(profile: any): string[] {
    const displayName = String(profile?.name || '').trim();
    const firstName = String(profile?.first_name || '').trim();
    const lastName = String(profile?.last_name || '').trim();

    const candidates = new Set(
      [
        displayName,
        `${firstName} ${lastName}`.trim(),
        `${firstName}_${lastName}`.trim(),
        `${firstName}${lastName}`.trim(),
        firstName && lastName ? `${firstName.toLowerCase()}_${lastName.toLowerCase()}` : '',
      ]
        .map((value) => this.normalizeWaypointOwnerName(value))
        .filter((value) => !!value)
    );

    return Array.from(candidates);
  }

  private getPopupPrivacyBadgeHtml(wp: any): string {
    if (isAlertWaypointType(wp)) {
      return '';
    }

    const privacy = String(wp?.privacy || '').toLowerCase();

    if (privacy === 'public') {
      return `
        <span class="popup-privacy-chip popup-privacy-chip--public" title="Public waypoint">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
          </svg>
        </span>
      `;
    }

    if (privacy === 'custom') {
      return `
        <span class="popup-privacy-chip popup-privacy-chip--custom" title="Pier Circle waypoint">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M16.5 12c1.38 0 2.49-1.12 2.49-2.5S17.88 7 16.5 7C15.12 7 14 8.12 14 9.5s1.12 2.5 2.5 2.5zM9 11c1.66 0 2.99-1.34 2.99-3S10.66 5 9 5C7.34 5 6 6.34 6 8s1.34 3 3 3zm7.5 3c-1.83 0-5.5.92-5.5 2.75V19h11v-2.25c0-1.83-3.67-2.75-5.5-2.75zM9 13c-2.33 0-7 1.17-7 3.5V19h7v-2.25c0-.85.33-2.34 2.37-3.47C10.5 13.1 9.66 13 9 13z"/>
          </svg>
        </span>
      `;
    }

    return `
      <span class="popup-privacy-chip popup-privacy-chip--private" title="Private waypoint">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/>
        </svg>
      </span>
    `;
  }

  private getPlacePopupIconHtml(wp: any): string {
    const type = this.resolveWaypointTypeForMap(wp);
    const iconSrc = this.getPlaceMapMarkerIconSrc(type);
    const iconAlt = `${this.getWaypointTypeLabel(wp)} icon`;

    return `
      <span class="place-popup-icon place-popup-icon--${type}">
        <img src="${iconSrc}" alt="${iconAlt}">
      </span>
    `;
  }


  private getPopupSourceIconHtml(isAlertWaypoint: boolean): string {
    if (isAlertWaypoint) {
      return `
        <svg class="privacy-icon privacy-icon--shield" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true">
          <path d="M12 3.19l7 3.11V11c0 4.52-2.98 8.69-7 9.93-4.02-1.24-7-5.41-7-9.93V6.3l7-3.11M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
        </svg>
      `;
    }

    return `<img class="privacy-icon" src="/assets/home/wavepoint/user_icon.svg" alt="user">`;
  }

  private getPopupHtml(wp: any): string {
    const cardTitle = this.getWaypointCardTitle(wp);
    let typeLabel = cardTitle || 'Fish Caught';
    const fishQuantity = this.getWaypointQuantity(wp);
    const normalizedType = resolveWaypointType(wp).replace(/[-_ ]/g, '');
    const isPlace = this.isPlaceWaypoint(wp);
    const popupIconSrc = this.getPopupIconSrc(wp);
    const isAlertWaypoint = isAlertWaypointType(wp);

    let headerBgColor =
      this.waypointColors[normalizedType] ||
      'var(--Accents-Cyan, hsl(190, 100%, 45%))';

    if (normalizedType === 'fish_caught' && wp.details?.fish_subtype) {
      if (typeLabel.toLowerCase() === 'fish caught') {
        typeLabel = this.getFishCaughtDisplayTitle(wp.details.fish_subtype);
      }
      headerBgColor = 'var(--Accents-Cyan, hsl(190, 100%, 45%))'; // Fish caught is cyan
    }

    const latStr = isPlace
      ? this.formatPlaceCoordinate(wp.position.latitude, 'lat')
      : this.formatCoordinate(wp.position.latitude, 'lat');
    const lngStr = isPlace
      ? this.formatPlaceCoordinate(wp.position.longitude, 'lng')
      : this.formatCoordinate(wp.position.longitude, 'lng');
    const dateStr = this.formatDate(wp.audit_data?.create_ts || wp.created_at || new Date().toISOString());

    // Creator logic — mirrors Flutter WaypointTooltipPopup._buildSourceInfoText:
    // never show "Unknown"; when the author's name isn't available fall back to
    // the privacy label (Community / Private / Pier Circle).
    let userName = 'Community';
    const privacy = wp.privacy;
    const sourceProfile = wp.source_profile;
    const isMe = this.isWaypointMine(wp);
    // Resolve the author name from every shape the backend uses. The map
    // waypoint object often carries it under audit_data.create_user_name /
    // created_by.name rather than source_profile — the same keys the Watch and
    // Waypoints tabs already read — so checking only source_profile showed
    // "Unknown" here while the expanded Watch view showed the real author.
    const sourceName = String(
      sourceProfile?.name ||
      wp.creator_name ||
      wp.audit_data?.create_user_name ||
      wp.created_by?.name ||
      ''
    ).trim();

    if (isAlertWaypoint) {
      userName = ANONYMOUS_ALERT_ALIAS;
    } else if (isMe) {

      if (privacy === 'public') userName = 'Public';
      else if (privacy === 'custom') userName = 'Pier Circle';
      else userName = 'Private';
    } else {
      const name = sourceName;
      if (privacy === 'custom') {
        userName = name ? `Shared- ${name}` : 'Pier Circle';
      } else {
        userName = name || (privacy === 'private' ? 'Private' : 'Community');
      }
    }

    const placeTypeLabel = this.getWaypointTypeLabel(wp);
    const popupSourceIconHtml = this.getPopupSourceIconHtml(isAlertWaypoint);

    const waypointId = this.getWaypointId(wp);

    const expandBtn = `
        <button class="share-btn" data-action="expand" data-wpid="${waypointId}" title="Open Watch">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 3 21 3 21 9"/>
            <polyline points="9 21 3 21 3 15"/>
            <line x1="21" y1="3" x2="14" y2="10"/>
            <line x1="3" y1="21" x2="10" y2="14"/>
          </svg>
        </button>`;

    const shareBtn = isMe ? `
        <button class="share-btn" data-action="share" data-lat="${wp.position.latitude}" data-lng="${wp.position.longitude}" title="Share">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
            <polyline points="16 6 12 2 8 6"/>
            <line x1="12" y1="2" x2="12" y2="15"/>
          </svg>
        </button>` : '';

    const waypointTripId = this.getWaypointTripId(wp);
    const hideBtn = (!isMe && !isAlertWaypoint) ? `
        <button class="share-btn popup-hide-btn" data-action="hide-from-me"
          data-wpid="${waypointId}" data-tripid="${waypointTripId || ''}" title="Hide from me">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
            fill="none" stroke="#555" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
          </svg>
        </button>` : '';

    const actionsHtml = `<div class="popup-actions">${shareBtn}${this.isReplayMode ? '' : expandBtn}${hideBtn}</div>`;

    // Prioritize existing observation_summary from the waypoint object.
    // As per user request, we do NOT hit observation/latest when clicking a waypoint,
    // and if the summary is null, we don't show the summary details at all.
    let mergedObs = wp.observation_summary || null;
    let hasDataBridge = false;

    if (mergedObs && Object.keys(mergedObs).length > 0) {
      hasDataBridge = mergedObs.sog != null || mergedObs.cog != null || mergedObs.temp != null || mergedObs.depth != null;
    }

    // -- UNIT CONVERSIONS BASED ON USER SETTINGS --
    let displaySog = 'N/A';
    let displayCog = 'N/A';
    let displayTemp = 'N/A';
    let displayDepth = 'N/A';

    if (mergedObs) {
      const settings = this.userSettings || {};

      // SOG (Speed)
      if (mergedObs.sog != null) {
        const speedUnit = (settings.speed_unit || 'knots').toLowerCase();
        let val = Number(mergedObs.sog);
        // If the stored Sog is in m/s (from PGN) or knots (from summary), we need to know the base.
        // Usually observation_summary.sog is in the unit specified by sog_unit or defaults to knots.
        // Assuming base is knots for simplicity in summary, or m/s if coming from raw PGN.
        // For existing waypoints, sog is usually already in knots.
        if (speedUnit === 'mph') {
          displaySog = (val * 1.15078).toFixed(1) + ' mph';
        } else {
          displaySog = val.toFixed(1) + ' kts';
        }
      }

      // COG (Course)
      if (mergedObs.cog != null) {
        displayCog = Math.round(Number(mergedObs.cog)) + '°';
      }

      // TEMP (Temperature)
      if (mergedObs.temp != null) {
        const tempUnit = (settings.temperature_unit || 'fahrenheit').toLowerCase();
        let val = Number(mergedObs.temp); // Assumed Celsius based on user feedback
        if (tempUnit === 'fahrenheit') {
          displayTemp = (val * 9 / 5 + 32).toFixed(1) + ' °F';
        } else {
          displayTemp = val.toFixed(1) + ' °C';
        }
      }

      // DEPTH
      if (mergedObs.depth != null) {
        const depthUnit = (settings.depth_unit || 'meters').toLowerCase();
        const storedUnit = (mergedObs.depth_unit || 'meters').toLowerCase();
        let val = Number(mergedObs.depth);

        if (depthUnit === 'feet' && storedUnit === 'meters') {
          displayDepth = (val * 3.28084).toFixed(1) + ' ft';
        } else if (depthUnit === 'meters' && storedUnit === 'feet') {
          displayDepth = (val / 3.28084).toFixed(1) + ' m';
        } else {
          displayDepth = val.toFixed(1) + (depthUnit === 'feet' ? ' ft' : ' m');
        }
      }
    }

    if (isPlace) {
      return `
        <div class="waypoint-popup waypoint-popup--place" ${isMe ? `data-action="manage" data-wpid="${waypointId}"` : ''} style="cursor: ${isMe ? 'pointer' : 'default'};">
          <div class="place-popup-head">
            ${this.getPlacePopupIconHtml(wp)}
            <div class="place-popup-copy">
              <div class="place-popup-title-row">
                <span class="place-popup-title">${cardTitle}</span>
                ${this.getPopupPrivacyBadgeHtml(wp)}
              </div>
              <div class="place-popup-coords-inline">
                <span class="place-popup-coord-line">${latStr}</span>
                <span class="place-popup-coord-line">${lngStr}</span>
              </div>
              ${!isMe && sourceName ? `<span class="place-popup-source">${sourceName}</span>` : ''}
            </div>
            <div class="place-popup-actions">
              ${isMe ? `
              <button class="share-btn" data-action="share" data-lat="${wp.position.latitude}" data-lng="${wp.position.longitude}" title="Share">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3f3f4c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="18" cy="5" r="3"/>
                  <circle cx="6" cy="12" r="3"/>
                  <circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
              </button>` : ''}
              <button class="share-btn" data-action="expand" data-wpid="${waypointId}" title="Open Watch">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3f3f4c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="15 3 21 3 21 9"/>
                  <polyline points="9 21 3 21 3 15"/>
                  <line x1="21" y1="3" x2="14" y2="10"/>
                  <line x1="3" y1="21" x2="10" y2="14"/>
                </svg>
              </button>
              ${(!isMe && !isAlertWaypoint) ? `
              <button class="share-btn popup-hide-btn" data-action="hide-from-me"
                data-wpid="${waypointId}" data-tripid="${waypointTripId || ''}" title="Hide from me">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                  fill="none" stroke="#3f3f4c" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <circle cx="12" cy="12" r="3"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              </button>` : ''}
            </div>
          </div>
          <div class="place-popup-caret"></div>
        </div>
      `;
    }

    // Flutter WaypointTooltipPopup: photo block under the header, description + small coords
    // when there is a photo/moment, coords as the headline otherwise.
    const photoUrls = getWaypointImageUrls(wp);
    const isMoment = isMomentType(normalizedType);
    const hasPhotoBlock = photoUrls.length > 0 || isMoment;
    if (isMoment) {
      headerBgColor = MOMENT_ACCENT;
      typeLabel = 'Moment'; // stored name is often the generic "Photo"; Flutter titles these Moment
    }
    const description = this.escapeHtml(String(wp.description || wp.details?.description || wp.details?.caption || '').trim());
    const coordsHtml = `
          <div class="coords${hasPhotoBlock ? ' coords--small' : ''}">
            <div class="coord-line">${latStr}</div>
            <div class="coord-line">${lngStr}</div>
          </div>`;
    const editBtn = isMe ? `
        <button class="share-btn" data-action="manage" data-wpid="${waypointId}" title="Edit">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3c3c43" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/>
          </svg>
        </button>` : '';
    const popupActionsHtml = `<div class="popup-actions">${editBtn}${shareBtn}${this.isReplayMode ? '' : expandBtn}${hideBtn}</div>`;

    return `
      <div class="waypoint-popup" ${isMe ? `data-action="manage" data-wpid="${waypointId}"` : ''}>
        <div class="popup-header" style="background: ${headerBgColor} !important; cursor: ${isMe ? 'pointer' : 'default'};">
          <span class="header-icon-wrap">
            <img src="${popupIconSrc}" class="header-icon" alt="icon">
          </span>
          <div class="header-text-col">
            <span class="header-title">${typeLabel}</span>
          </div>
          ${normalizedType === 'fish_caught' && fishQuantity > 1 ? `<span style="margin-left:auto;padding:2px 7px;border-radius:10px;background:rgba(255,255,255,0.25);color:#fff;font-size:13px;font-weight:700;">x${fishQuantity}</span>` : ''}
        </div>
        ${buildWaypointPhotoCarouselHtml(photoUrls)}
        <div class="popup-body">
          ${hasDataBridge && mergedObs ? `
          <div class="observation-grid">
            <div class="obs-item">
              <span class="obs-icon-wrap">
                <img src="assets/home/profile/boaticon.svg" class="obs-icon">
              </span>
              <div class="obs-text">
                <span class="obs-label">SOG</span>
                <span class="obs-value">${displaySog}</span>
              </div>
            </div>
            <div class="obs-item">
              <span class="obs-icon-wrap">
                <img src="assets/home/profile/compassicon.svg" class="obs-icon">
              </span>
              <div class="obs-text">
                <span class="obs-label">COG</span>
                <span class="obs-value">${displayCog}</span>
              </div>
            </div>
            <div class="obs-item">
              <span class="obs-icon-wrap">
                <img src="assets/common_icons/watch_temp.svg" class="obs-icon">
              </span>
              <div class="obs-text">
                <span class="obs-label">TEMP</span>
                <span class="obs-value">${displayTemp}</span>
              </div>
            </div>
            <div class="obs-item">
              <span class="obs-icon-wrap">
                <img src="assets/home/profile/distanceicon.svg" class="obs-icon">
              </span>
              <div class="obs-text">
                <span class="obs-label">DEPTH</span>
                <span class="obs-value">${displayDepth}</span>
              </div>
            </div>
          </div>
          <div class="obs-divider"></div>
          ` : ''}
          ${normalizedType === 'fish_caught' && fishQuantity > 1 ? `<div class="time">${fishQuantity} fish logged</div>` : ''}
          ${hasPhotoBlock && description ? `<div class="popup-description">${description}</div>` : ''}
          ${coordsHtml}
          <div class="time">${dateStr}</div>
          <div class="privacy-row">
            ${popupSourceIconHtml}
            <span class="privacy-text">${userName}</span>
          </div>
        </div>
        ${popupActionsHtml}
      </div>
    `;
  }

  private getFishCaughtDisplayTitle(subtype: string): string {
    const normalized = String(subtype || '').trim().toLowerCase();
    if (!normalized) return 'Fish Caught';
    if (normalized.startsWith('general_')) {
      const category = normalized.replace(/^general_/, '').replace(/[_-]+/g, ' ');
      return `${category.replace(/\b\w/g, (char: string) => char.toUpperCase())} Caught`;
    }
    const pretty = normalized
      .replace(/bluemarlin/g, 'blue marlin')
      .replace(/whitemarlin/g, 'white marlin')
      .replace(/stripedbass/g, 'striped bass')
      .replace(/bluefish/g, 'bluefish')
      .replace(/seabass/g, 'sea bass')
      .replace(/[_-]+/g, ' ');
    return `${pretty.replace(/\b\w/g, (char: string) => char.toUpperCase())} Caught`;
  }

  private addWaypointMarker(wp: any, flyTo = false): void {
    if (!wp || !wp.position || !this.map) return;
    this.invalidateClusterCache();
    wp = this.normalizeWaypointForMap(wp);
    const waypointId = this.getWaypointId(wp);
    if (!waypointId) return;

    this.removeExistingWaypointMarker(waypointId);

    const lat = wp.position.latitude;
    const lng = wp.position.longitude;
    if (lat == null || lng == null || isNaN(Number(lat)) || isNaN(Number(lng))) return;
    const fishQuantity = this.getWaypointQuantity(wp);
    const isFishCaught = resolveWaypointType(wp) === 'fish_caught';
    const isPlace = this.isPlaceWaypoint(wp);
    const isPlaceLabel = this.shouldRenderPlaceAsTextLabel(wp);
    // Moments render as a photo thumbnail pin (Flutter PhotoMapBoxMarker); without a photo they fall back to the icon pin.
    const momentUrls = isMomentType(resolveWaypointType(wp)) ? getWaypointImageUrls(wp) : [];

    // Create a container for the icon and optional creator dot
    const el = document.createElement('div');
    el.className = 'waypoint-marker-container';
    el.style.cursor = 'pointer';
    el.setAttribute('data-wpid', waypointId);
    const waypointTripId = this.getWaypointTripId(wp);
    if (waypointTripId) {
      el.setAttribute('data-trip-id', waypointTripId);
    }

    // Spot is a full teardrop pin: treated as a 'pin' (bottom-anchored, fixed 54px), not a zoom-scaled place glyph.
    // Inlet and Home use the same Flutter pin art (pins/inlet.png, pins/home.png) instead of the old line glyphs.
    const placeType = this.resolveWaypointTypeForMap(wp);
    const isFlatPinPlace = placeType === 'inlet' || placeType === 'home';
    const isSpotPin = isPlace && !isPlaceLabel && (placeType === 'canyon' || isFlatPinPlace);
    if (isSpotPin) {
      el.setAttribute('data-marker-kind', 'pin');
      el.setAttribute('data-place-type', placeType);
      el.style.width = `${WAYPOINT_PIN_SIZE_PX}px`;
      el.style.height = `${WAYPOINT_PIN_SIZE_PX}px`;
      el.innerHTML = isFlatPinPlace
        ? `<img class="waypoint-icon main-marker-image" src="${this.getIconSrc(wp)}" alt="${this.getWaypointTypeLabel(wp)} icon" style="width:100%;height:100%;">`
        : this.getPlaceMapMarkerHtml(wp);
    } else if (isPlace) {
      el.classList.add(isPlaceLabel ? 'waypoint-marker-container--place-label' : 'waypoint-marker-container--place-icon');
      el.setAttribute('data-marker-kind', isPlaceLabel ? 'place-label' : 'place-icon');
      el.setAttribute('data-place-type', this.resolveWaypointTypeForMap(wp));
      const iconSrc = isPlaceLabel ? '' : this.getPlaceMapMarkerIconSrc(this.resolveWaypointTypeForMap(wp));
      if (iconSrc) {
        el.setAttribute('data-highlight-src', iconSrc);
      }
      el.innerHTML = this.getPlaceMapMarkerHtml(wp);
      if (!isPlaceLabel) {
        const placeVisual = el.querySelector('img, svg') as HTMLElement | null;
        if (placeVisual) {
          placeVisual.classList.add('main-marker-image');
        }
      }
    } else if (momentUrls.length) {
      el.setAttribute('data-marker-kind', 'moment');
      el.classList.add('waypoint-marker-container--moment');
      el.innerHTML = buildMomentPinHtml(momentUrls[0], momentUrls.length);
    } else {
      el.setAttribute('data-marker-kind', 'pin');
      el.style.width = `${WAYPOINT_PIN_SIZE_PX}px`;
      el.style.height = `${WAYPOINT_PIN_SIZE_PX}px`;
      const img = document.createElement('img');
      const iconSrc = this.getIconSrc(wp);
      el.setAttribute('data-highlight-src', iconSrc);
      // Append ?_v=2 to bust the browser cache for the old SVGs that had the drop-shadow
      // filter embedded — ensures the <img> phase always loads the filter-free version.
      img.src = iconSrc.toLowerCase().endsWith('.svg') ? iconSrc + '?_v=2' : iconSrc;
      img.className = 'waypoint-icon main-marker-image';
      img.style.width = '100%';
      img.style.height = '100%';
      el.appendChild(img);

      // If the icon is an SVG file, try to inline it so we can apply a precise stroke outline
      try {
        if (typeof iconSrc === 'string' && iconSrc.toLowerCase().endsWith('.svg')) {
          fetch(iconSrc + '?_v=2')
            .then((r) => r.ok ? r.text() : Promise.reject('fetch failed'))
            .then((svgText) => {
              const wrapper = document.createElement('div');
              wrapper.innerHTML = svgText.trim();
              const svgEl = wrapper.querySelector('svg');
              if (svgEl) {
                // Strip the drop-shadow filter regardless of what the cached SVG contains.
                const gWithFilter = svgEl.querySelector('g[filter]');
                if (gWithFilter) gWithFilter.removeAttribute('filter');
                const defs = svgEl.querySelector('defs');
                if (defs) defs.remove();

                svgEl.classList.add('main-marker-image');
                svgEl.style.width = '100%';
                svgEl.style.height = '100%';
                svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');
                svgEl.setAttribute('focusable', 'false');
                try {
                  el.replaceChild(svgEl, img);
                  if (el.classList.contains('waypoint-marker-highlight')) {
                    try {
                      this.setSvgOutline(el, true);
                    } catch (e) { /* ignore */ }
                    try {
                      this.updateWaypointFocusRing(el);
                    } catch (e) { /* ignore */ }
                  }
                } catch (e) { /* ignore */ }
              }
            })
            .catch(() => {
              // leave the <img> fallback in place
            });
        }
      } catch (e) { /* ignore */ }
    }

    if (isFishCaught && fishQuantity > 1) {
      const qtyBadge = document.createElement('div');
      qtyBadge.className = 'waypoint-qty-badge';
      qtyBadge.textContent = `${fishQuantity}`;
      qtyBadge.style.position = 'absolute';
      qtyBadge.style.top = '-1px';
      qtyBadge.style.right = '-1px';
      qtyBadge.style.padding = '2px 4px';
      qtyBadge.style.minWidth = '18px';
      qtyBadge.style.height = '16px';
      qtyBadge.style.borderRadius = '6px';
      qtyBadge.style.background = 'rgba(228, 46, 36, 0.92)';
      qtyBadge.style.border = '1px solid #ffffff';
      qtyBadge.style.display = 'inline-flex';
      qtyBadge.style.alignItems = 'center';
      qtyBadge.style.justifyContent = 'center';
      qtyBadge.style.color = '#ffffff';
      qtyBadge.style.fontSize = '9px';
      qtyBadge.style.fontWeight = '700';
      qtyBadge.style.lineHeight = '1';
      qtyBadge.style.boxShadow = '0 3px 8px rgba(0, 0, 0, 0.18)';
      qtyBadge.style.pointerEvents = 'none';
      qtyBadge.style.zIndex = '210';
      el.appendChild(qtyBadge);
    }

    const highlightSrc = String(el.getAttribute('data-highlight-src') || '').trim();
    if (highlightSrc) {
      const outerOutline = document.createElement('div');
      outerOutline.className = 'waypoint-highlight-outline waypoint-highlight-outline--outer';
      outerOutline.style.setProperty('--icon-url', `url("${highlightSrc}")`);
      el.appendChild(outerOutline);

      const innerOutline = document.createElement('div');
      innerOutline.className = 'waypoint-highlight-outline waypoint-highlight-outline--inner';
      innerOutline.style.setProperty('--icon-url', `url("${highlightSrc}")`);
      el.appendChild(innerOutline);

    }

    const focusRing = document.createElement('div');
    focusRing.className = 'waypoint-focus-ring';
    focusRing.setAttribute('aria-hidden', 'true');
    el.appendChild(focusRing);

    // Pin art is 54px with its tip at y≈43.4 (Flutter map_markers.dart), so ~10px of empty
    // space sits below the tip. Offset shifts the element down so the tip lands on the coordinate.
    const marker = new maplibregl.Marker({ element: el, anchor: isPlace && !isSpotPin ? 'center' : 'bottom', offset: (isPlace && !isSpotPin) || momentUrls.length ? [0, 0] : [0, WAYPOINT_PIN_TIP_OFFSET_PX] })
      .setLngLat([lng, lat])
      .addTo(this.map);

    this.updateZoomResponsiveMarkerSizes(marker);

    this.displayedMarkers.push(marker);
    this.syncWaypointPresentationVisibility(marker);

    if (flyTo) {
      // Force-attach the fly-to target: the viewport cull above ran against the
      // pre-fly bounds and may have detached it. The moveend after the fly will
      // re-evaluate, but the card/highlight need it attached immediately.
      this.setMarkerAttached(marker, true);
      this.flyToWaypointPresentation([lng, lat], 12, marker);
      this.openWaypointCard(marker);
      this.highlightWaypointMarker(marker);
    }
  }

  private shouldClusterWaypoint(wp: any): boolean {
    if (!wp) return false;
    if (this.isPlaceWaypoint(wp)) return false;
    const normalizedType = this.resolveWaypointTypeForMap(wp);
    return ['fish_mark', 'fish_caught', 'bait_mark', 'temp_break', 'hazard', 'diver', 'patrol', 'fog'].includes(normalizedType);
  }

  private clearClusterMarkers(): void {
    this.clusterRemovalTimers.forEach((timer) => clearTimeout(timer));
    this.clusterRemovalTimers.clear();
    const keys = Array.from(this.clusterComponentRefs.keys());
    keys.forEach((key) => this.destroyClusterMarkerEntry(key));
  }

  private destroyClusterMarkerEntry(key: string): void {
    const entry = this.clusterComponentRefs.get(key);
    if (!entry) return;
    this.clusterComponentRefs.delete(key);
    const timer = this.clusterRemovalTimers.get(key);
    if (timer) {
      clearTimeout(timer);
      this.clusterRemovalTimers.delete(key);
    }
    try { entry.clusterClickSub.unsubscribe(); } catch (e) { /* ignore */ }
    try { entry.marker.remove(); } catch (e) { /* ignore */ }
    try { this.appRef.detachView(entry.componentRef.hostView); } catch (e) { /* ignore */ }
    entry.componentRef.destroy();
  }

  private scheduleClusterMarkerRemoval(key: string): void {
    if (this.clusterRemovalTimers.has(key)) return;
    const entry = this.clusterComponentRefs.get(key);
    if (!entry) return;
    const shellEl = entry.marker.getElement().querySelector('.cluster-marker-shell') as HTMLElement | null;
    if (shellEl) {
      shellEl.classList.add('cluster-exit');
    }
    const timer = setTimeout(() => {
      this.clusterRemovalTimers.delete(key);
      this.destroyClusterMarkerEntry(key);
    }, 170);
    this.clusterRemovalTimers.set(key, timer);
  }

  private setWaypointMarkerClusterVisibility(marker: maplibregl.Marker, hidden: boolean): void {
    const el = marker.getElement();
    if (!el) return;
    // Record cluster-hidden state on the element so the unified attachment
    // reconcile (which also factors in viewport culling and presentation hiding)
    // can make the final attach/detach decision.
    if (hidden) {
      el.setAttribute('data-cluster-hidden', '1');
    } else {
      el.removeAttribute('data-cluster-hidden');
    }
    this.applyWaypointMarkerAttachment(marker);
  }

  /**
   * Attach or detach a marker from the map without recreating it.
   *
   * Detaching (marker.remove) takes the element out of MapLibre's per-frame
   * reposition loop entirely — unlike opacity/visibility hiding, which keeps the
   * DOM node on the map so MapLibre still rewrites its transform every animation
   * frame (the root cause of drag/zoom lag). The same Marker object, its element,
   * highlight outline, focus ring and naturalOpacity dataset all persist, so
   * re-attaching restores the exact prior visual state.
   */
  private setMarkerAttached(marker: maplibregl.Marker, attached: boolean): void {
    if (!this.map) return;
    const el = marker.getElement();
    const isAttached = !!el && el.isConnected;
    if (attached === isAttached) return;
    if (attached) {
      marker.addTo(this.map);
    } else {
      marker.remove();
    }
  }

  /**
   * Decide whether a single waypoint marker should be attached to the map by
   * combining cluster-hidden, presentation-hidden and viewport-cull state. This
   * is the single source of truth for waypoint marker attachment.
   */
  private applyWaypointMarkerAttachment(marker: maplibregl.Marker): void {
    const el = marker.getElement();
    if (!el) return;
    // The tour's spotlight target always stays on the map — the overlay needs a
    // laid-out element to cut its hole around.
    if (this.isTourSpotlightWaypoint(el.getAttribute('data-wpid'))) {
      this.setMarkerAttached(marker, true);
      return;
    }
    const clusterHidden = el.getAttribute('data-cluster-hidden') === '1';
    const presentationHidden = this.shouldHideWaypointPresentation();
    let inViewport = true;
    if (!clusterHidden && !presentationHidden) {
      const ll = marker.getLngLat();
      inViewport = this.isWithinPaddedViewport(ll.lng, ll.lat);
    }
    this.setMarkerAttached(marker, !clusterHidden && !presentationHidden && inViewport);
  }

  /**
   * Attach/detach an aggregation bubble. Bubbles were the one marker class with
   * no viewport culling, which mattered much more once the pure-cluster view
   * started emitting a bubble per grid cell at basin zoom: every off-screen one
   * stayed in MapLibre's per-frame reposition loop. Gating is the same as a
   * waypoint pin — panel state plus the padded viewport.
   */
  private applyClusterMarkerAttachment(marker: maplibregl.Marker): void {
    if (!this.map) return;
    const el = marker.getElement();
    if (!el) return;
    const presentationHidden = this.shouldHideWaypointPresentation();
    let inViewport = true;
    if (!presentationHidden) {
      const ll = marker.getLngLat();
      inViewport = this.isWithinPaddedViewport(ll.lng, ll.lat);
    }
    this.setMarkerAttached(marker, !presentationHidden && inViewport);
  }

  /**
   * Viewport-cull a vessel marker (dockmate / shared-trip). Like the waypoint
   * version but WITHOUT presentation gating — vessels were never tied to panel
   * open/close state, so we preserve that behavior. Cluster-hidden vessels stay
   * detached regardless of viewport.
   */
  private applyVesselMarkerViewportCull(marker: maplibregl.Marker): void {
    const el = marker.getElement();
    if (!el) return;
    const clusterHidden = el.getAttribute('data-cluster-hidden') === '1';
    let inViewport = true;
    if (!clusterHidden) {
      const ll = marker.getLngLat();
      inViewport = this.isWithinPaddedViewport(ll.lng, ll.lat);
    }
    this.setMarkerAttached(marker, !clusterHidden && inViewport);
  }

  /**
   * Re-evaluate attachment for every off-GPU HTML marker (waypoints + dockmate +
   * shared-trip vessels). Cheap (no clustering recompute) — used on pan to cull
   * markers that scrolled out of view so MapLibre stops repositioning them.
   */
  private reconcileMarkerAttachment(): void {
    this.displayedMarkers.forEach((m) => this.applyWaypointMarkerAttachment(m));
    this.clusterComponentRefs.forEach((entry) => this.applyClusterMarkerAttachment(entry.marker));
    this.dockmateMarkers.forEach((m) => this.applyVesselMarkerViewportCull(m));
    Object.values(this.sharedActiveTripMarkers || {}).forEach((m) => {
      if (m) this.applyVesselMarkerViewportCull(m);
    });
    // Data-layer HTML markers (buoys/tides) are spread over a wide radius around the
    // vessel; cull the off-screen ones so MapLibre stops repositioning all ~320 of
    // them every frame while those layers are on. (Wrecks are a GPU symbol layer.)
    this.buoyMarkers.forEach((m) => this.applyVesselMarkerViewportCull(m));
    this.tideMarkers.forEach((m) => this.applyVesselMarkerViewportCull(m));
  }

  /**
   * True if the given lng/lat falls inside the current viewport expanded by
   * MARKER_VIEWPORT_PADDING (so markers don't pop in/out right at the edge while
   * panning). Falls back to "visible" if bounds are unavailable or wrap the
   * antimeridian (rare for this app's coastal use).
   */
  private isWithinPaddedViewport(lng: number, lat: number): boolean {
    if (!this.map) return true;
    const b = this.map.getBounds();
    const west = b.getWest();
    const east = b.getEast();
    const south = b.getSouth();
    const north = b.getNorth();
    if (west > east) return true; // antimeridian wrap — skip culling
    const padW = (east - west) * HomePageComponent.MARKER_VIEWPORT_PADDING;
    const padH = (north - south) * HomePageComponent.MARKER_VIEWPORT_PADDING;
    // When the map has been dragged past the antimeridian the bounds' centre may
    // be outside [-180, 180] (e.g. west=-450, east=-270, centre=-360).  The stored
    // longitude is canonical [-180, 180] and will never match those bounds unless
    // we shift it to the same world copy first.
    const boundsCenter = (west + east) / 2;
    const diff = ((lng - boundsCenter + 540) % 360) - 180;
    const normalizedLng = boundsCenter + diff;
    return (
      normalizedLng >= west - padW && normalizedLng <= east + padW &&
      lat >= south - padH && lat <= north + padH
    );
  }

  private setMarkerPresentationVisibility(marker: maplibregl.Marker, hidden: boolean): void {
    const el = marker.getElement();
    if (!el) return;
    // Ensure CSS transition is active before the opacity change so the browser
    // animates between values.  This must be set before calling setOpacity().
    el.style.transition = 'opacity 200ms ease';
    if (hidden) {
      // Actively fade to invisible.
      // Use marker.setOpacity so MapLibre's internal _opacity tracks the value
      // and _updateOpacity (fired on every map move) does not reset it to 1.
      marker.setOpacity(0);
      el.style.pointerEvents = 'none';
      setTimeout(() => { el.style.visibility = 'hidden'; }, 210);
    } else {
      const vesselEl = this.getDockmateVesselElement(el);
      if (vesselEl?.classList.contains('dockmate-vessel')) {
        // Ring marker: outer div stays at full opacity so the ring and blue glow
        // are always visible. Only the inner boat-icon img carries the age-based fade.
        marker.setOpacity(1);
        const nat = vesselEl.dataset['naturalOpacity'];
        const iconImg = vesselEl.querySelector('.boat-icon img') as HTMLElement | null;
        if (iconImg) {
          iconImg.style.transition = 'opacity 200ms ease';
          iconImg.style.opacity = nat !== undefined ? nat : '1';
        }
      } else {
        // Non-ring marker (trip waypoint, own vessel, etc.): opacity on outer div.
        // Passing undefined resets to full opacity (correct for trip markers that
        // have no naturalOpacity stored).
        marker.setOpacity(el.dataset['naturalOpacity']);
      }
      el.style.pointerEvents = '';
      el.style.visibility = '';
    }
  }

  /**
   * Schedule a debounced cluster recompute after panning.
   * Prevents redundant recalculations during rapid pan gestures.
   */
  private scheduleClusterUpdate(): void {
    if (this.clusterDebounceTimer !== null) {
      clearTimeout(this.clusterDebounceTimer);
    }
    this.clusterDebounceTimer = setTimeout(() => {
      this.clusterDebounceTimer = null;
      this.updateWaypointAggregation();
    }, 80);
  }

  /**
   * Show a faded observation vessel marker at the given dockmate's position.
   * This appears after zooming into a dockmate vessel, giving the user a
   * visible translucent icon to interact with (click to open the card).
   */
  private showDockmateObservationMarker(lat: number, lng: number, vessel: any): void {
    this.clearDockmateObservationMarker();
    if (!this.map) return;

    const el = document.createElement('div');
    el.className = 'dockmate-observation-vessel';
    el.title = vessel.vessel_name || 'Dockmate vessel';
    el.setAttribute('data-vessel-id', String(vessel.vessel_id || vessel.id || '').trim());
    el.innerHTML = `<img src="${this.skins.iconSrcFor(String(vessel.vessel_id || vessel.id || ''))}" alt="vessel" />`;

    // Start transparent, then fade in.
    // Set inline style first so the element is invisible before MapLibre's
    // first _updateOpacity fires.  opacity: 0 in the constructor keeps
    // MapLibre's internal _opacity at 0, preventing a reset to 1 on map moves.
    el.style.opacity = '0';
    el.style.transition = 'opacity 500ms ease';

    el.addEventListener('click', (e) => {
      e.stopPropagation();
      this.openDockmateCard(vessel);
    });

    this.dockmateObservationMarker = new maplibregl.Marker({ element: el, anchor: 'center', opacity: 0 })
      .setLngLat([lng, lat])
      .addTo(this.map);

    // Hide the original dockmate marker for this vessel so only one icon is visible.
    const vesselId = String(vessel.vessel_id || vessel.id || '').trim();
    const originalMarker = this.findDockmateMarkerByVesselId(vesselId);
    if (originalMarker) {
      originalMarker.getElement().style.display = 'none';
      this.dockmateObservationHiddenMarker = originalMarker;
    }

    // Trigger fade-in on next frame via setOpacity so MapLibre tracks the target
    // value and does not override it back to 0 on subsequent map moves.
    requestAnimationFrame(() => {
      this.dockmateObservationMarker?.setOpacity(0.45);
    });
  }

  /** Remove the dockmate observation vessel marker if present. */
  private clearDockmateObservationMarker(): void {
    if (this.dockmateObservationMarker) {
      try { this.dockmateObservationMarker.remove(); } catch (e) { /* ignore */ }
      this.dockmateObservationMarker = null;
    }
    // Restore the original dockmate marker that was hidden.
    if (this.dockmateObservationHiddenMarker) {
      this.dockmateObservationHiddenMarker.getElement().style.display = '';
      this.dockmateObservationHiddenMarker = null;
    }
  }

  /**
   * Build vessel list for clustering using live marker positions as source of truth.
   * Prevents drift between liveVesselStore coords and HTML marker positions.
   */
  private getVesselsForClustering(): ClusterableVessel[] {
    // Build a vesselId -> dockmate marker lookup ONCE. Previously this did a
    // linear .find() over dockmateMarkers for every vessel — O(vessels × dockmates)
    // per call, and this runs twice per cluster render. The Map makes it O(1).
    const markerByVesselId = new globalThis.Map<string, maplibregl.Marker>();
    for (const marker of this.dockmateMarkers) {
      const id = String(marker.getElement()?.getAttribute('data-vessel-id') || '').trim();
      if (id) markerByVesselId.set(id, marker);
    }
    for (const marker of Object.values(this.sharedActiveTripMarkers || {})) {
      const id = String(marker.getElement()?.getAttribute('data-vessel-id') || '').trim();
      if (id) markerByVesselId.set(id, marker);
    }

    const vessels: ClusterableVessel[] = [];
    for (const stored of this.liveVesselStore.values()) {
      // Skin is read live from the store, so a hull learned after registration still reaches the bubble.
      const skin = this.skins.forVesselOrNull(stored.id);
      if (stored.isPrimary) {
        // Own boat: rendered by the WebGL symbol layer, never aggregated.
        vessels.push({ ...stored, skin });
        continue;
      }
      const marker = markerByVesselId.get(String(stored.id || '').trim());
      if (!marker) {
        // No icon on the map ⇒ nothing to fold into a bubble. The store also holds
        // vessels tracked purely for observations (ride-along, watch feed) which
        // draw no boat of their own; counting those inflated the badge with items
        // that could never be found by zooming in — one of the "totals don't track"
        // cases. Mobile clusters its live-marker LISTS for exactly this reason.
        continue;
      }
      const lngLat = marker.getLngLat();
      vessels.push({ ...stored, skin, lat: lngLat.lat, lng: lngLat.lng });
    }
    return vessels;
  }

  private findDockmateMarkerByVesselId(vesselId: string): maplibregl.Marker | undefined {
    const normalized = String(vesselId || '').trim();
    if (!normalized) return undefined;
    return this.dockmateMarkers.find((marker) => {
      const el = marker.getElement();
      return String(el?.getAttribute('data-vessel-id') || '').trim() === normalized;
    });
  }

  /**
   * Positional fallback for identifying a dockmate boat, used when the only
   * available id is synthetic and therefore cannot match. Mirrors mobile, which
   * falls back to position for boats whose author key is empty
   * (`_clusteredVesselPoints`) and matches its own boat on a 1e-5 window.
   *
   * The tolerance is deliberately wider than mobile's 1e-5 (~1.1 m): mobile's two
   * feeds carry the same observation, while the web compares a live dockmate fix
   * against the tail of a shared trip track, which lags by a sample. 1e-4 is
   * ~11 m — far below the ~84 px aggregation cell at any clustering zoom, so it
   * can only ever collapse what is genuinely the same boat.
   */
  private findDockmateMarkerNearPosition(lat: number, lng: number): maplibregl.Marker | undefined {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
    const TOLERANCE_DEG = 1e-4;
    return this.dockmateMarkers.find((marker) => {
      const pos = marker.getLngLat();
      if (!pos) return false;
      return Math.abs(pos.lat - lat) < TOLERANCE_DEG && Math.abs(pos.lng - lng) < TOLERANCE_DEG;
    });
  }

  /** Inner boat UI — MapLibre anchors the fixed-size outer root instead. */
  private getDockmateVesselElement(root: HTMLElement): HTMLElement | null {
    if (root.classList.contains('vessel-boat-marker')) return root;
    return root.querySelector('.vessel-boat-marker') as HTMLElement | null;
  }

  private createDockmateMarkerRoot(vesselId: string, opacity: number): {
    root: HTMLElement;
    vesselEl: HTMLElement;
  } {
    const root = document.createElement('div');
    root.className = 'vessel-marker-root';
    root.setAttribute('data-vessel-id', vesselId);

    const vesselEl = document.createElement('div');
    vesselEl.className = 'vessel-boat-marker dockmate-vessel';
    vesselEl.dataset['naturalOpacity'] = opacity.toString();
    vesselEl.innerHTML = `
      <div class="dockmate-vessel-ring">
        <div class="boat-icon">
          <img src="${this.skins.iconSrcFor(vesselId)}" alt="vessel" />
        </div>
      </div>
    `;
    root.appendChild(vesselEl);
    return { root, vesselEl };
  }

  private readonly ownSpritesRequested = new Set<string>();

  private ownSpriteImageId(skin: VesselSkin): string {
    return `boat-skin-own-${skin}-${isDesktopViewport() ? 'd' : 'm'}`;
  }

  /** Feature properties for the own-boat WebGL layer: skin sprite when it is registered, else the legacy PNG. */
  private ownBoatFeatureProps(v: ClusterableVessel): Record<string, unknown> {
    const spriteId = v.skin ? this.ownSpriteImageId(v.skin) : '';
    const hasSprite = !!spriteId && !!this.map?.hasImage(spriteId);
    return {
      id: v.id,
      isPrimary: true,
      skinIcon: hasSprite ? spriteId : '',
      // Skins are bow-up; the legacy PNG's bow is 30° off north.
      heading: (v.heading ?? v.cog ?? 0) + (hasSprite ? 0 : 30),
      opacity: this.getMarkerOpacity(v.lastSeen, true),
    };
  }

  /**
   * Rasterizes each skin (hull + the blue "mine" ownership disc) and registers it with the map.
   * Idempotent; re-aggregates once a sprite lands so the own boat swaps from the PNG fallback.
   */
  private refreshOwnBoatSprites(): void {
    const map = this.map;
    if (!map) return;
    const dpr = window.devicePixelRatio || 1;
    const hullPx = vesselHullPx(isDesktopViewport());
    (Object.keys(VESSEL_SKIN_ASSET) as VesselSkin[]).forEach((skin) => {
      const id = this.ownSpriteImageId(skin);
      if (this.ownSpritesRequested.has(id) || map.hasImage(id)) return;
      this.ownSpritesRequested.add(id);
      // `requested` tracks in-flight work only: a style reload drops registered images, and the
      // next call must be able to register them again.
      rasterizeSkinSprite(skin, hullPx, dpr, VESSEL_RING_OWN)
        .then((data) => {
          if (this.map !== map || map.hasImage(id)) return;
          map.addImage(id, data, { pixelRatio: dpr });
          this.invalidateClusterCache();
          this.updateWaypointAggregation();
        })
        .catch(() => { /* fallback PNG stays; the next call retries */ })
        .finally(() => this.ownSpritesRequested.delete(id));
    });
  }

  /** Rotates a boat image so its bow lands on `heading`; skins are bow-up, the legacy PNG is 30° off. */
  private rotateBoatIcon(iconEl: HTMLElement, vesselId: string, heading: number): void {
    iconEl.dataset['heading'] = String(heading);
    const mapBrg = this.map?.getBearing() ?? 0;
    iconEl.style.transform = `rotate(${heading + this.skins.headingOffsetFor(vesselId) - mapBrg}deg)`;
  }

  /**
   * A skin arrived after its marker was drawn (live-positions and the vessel list race): swap the
   * art and re-aim it, then let aggregation re-read the skins for bubble hulls.
   */
  private refreshBoatArt(): void {
    const redraw = (marker: maplibregl.Marker): void => {
      const root = marker.getElement();
      const vesselId = String(root?.getAttribute('data-vessel-id') || '').trim();
      const img = root?.querySelector('img') as HTMLImageElement | null;
      if (!vesselId || !img) return;
      const src = this.skins.iconSrcFor(vesselId);
      if (!img.src.endsWith(src)) img.src = src;
      const iconEl = img.closest('.boat-icon') as HTMLElement | null;
      const heading = Number(iconEl?.dataset['heading']);
      if (iconEl && Number.isFinite(heading)) this.rotateBoatIcon(iconEl, vesselId, heading);
    };
    this.dockmateMarkers.forEach(redraw);
    // Shared-trip boats are a bare <img> (no .boat-icon wrapper): swap art and re-aim in place.
    Object.values(this.sharedActiveTripMarkers || {}).forEach((marker) => {
      const root = marker.getElement();
      const vesselId = String(root?.getAttribute('data-vessel-id') || '').trim();
      const img = root?.querySelector('img') as HTMLImageElement | null;
      if (!vesselId || !img) return;
      img.src = this.skins.iconSrcFor(vesselId);
      const heading = Number(img.dataset['heading']);
      if (Number.isFinite(heading)) {
        const mapBrg = this.map?.getBearing() ?? 0;
        img.style.transform = `rotate(${heading + this.skins.headingOffsetFor(vesselId) - mapBrg}deg)`;
      }
    });
    this.refreshOwnBoatSprites();
    this.invalidateClusterCache();
    this.updateWaypointAggregation();
  }

  private isVesselHiddenInCluster(vesselId: string, hiddenIds: Set<string>): boolean {
    return hiddenIds.has(vesselId);
  }

  /**
   * Waypoints eligible for aggregation, in the engine's input shape.
   *
   * Mobile aggregates event + alert types only (`_TripTrackMixin._eventTypes` /
   * `_alertTypes`); `shouldClusterWaypoint` mirrors that list. Positions come from
   * the live marker so the bubble can never drift from the pin it replaces.
   */
  private getClusterableWaypointEntries(): ClusterableWaypoint[] {
    // Index once — this used to run a linear scan of allWaypoints per marker,
    // i.e. O(markers × waypoints) on every recompute.
    const waypointById = new globalThis.Map<string, any>();
    for (const wp of this.allWaypoints) {
      const id = this.getWaypointId(wp);
      if (id) waypointById.set(id, wp);
    }

    const entries: ClusterableWaypoint[] = [];
    for (const marker of this.displayedMarkers) {
      const el = marker.getElement();
      const waypointId = String(el?.getAttribute('data-wpid') || '').trim();
      if (!waypointId) continue;
      // The profile/Watch-focused waypoint DOES take part in aggregation (mobile
      // adds it to the candidate set explicitly) so it is counted like any other
      // and never floats alone beside the bubble that should contain it.
      // Only the tour's spotlight target is exempt — that is a web-only feature
      // and the overlay needs a laid-out pin to cut its hole around.
      if (this.isTourSpotlightWaypoint(waypointId)) continue;
      const waypoint = waypointById.get(waypointId);
      if (!waypoint || !this.shouldClusterWaypoint(waypoint)) continue;
      const lngLat = marker.getLngLat();
      const lat = Number(lngLat?.lat);
      const lng = Number(lngLat?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      entries.push({
        id: waypointId,
        lat,
        lng,
        iconUrl: this.getIconSrc(waypoint),
        isMine: this.isWaypointMine(waypoint),
      });
    }
    return entries;
  }

  private upsertClusterComponentMarker(clusterKey: string, cluster: MapCluster): void {
    if (!this.map) return;
    const existingEntry = this.clusterComponentRefs.get(clusterKey);
    if (existingEntry) {
      const removalTimer = this.clusterRemovalTimers.get(clusterKey);
      if (removalTimer) {
        clearTimeout(removalTimer);
        this.clusterRemovalTimers.delete(clusterKey);
      }
      const shellEl = existingEntry.marker.getElement().querySelector('.cluster-marker-shell') as HTMLElement | null;
      if (shellEl) {
        shellEl.classList.remove('cluster-exit');
      }
      existingEntry.componentRef.setInput('cluster', cluster);
      existingEntry.componentRef.changeDetectorRef.detectChanges();
      existingEntry.marker.setLngLat([cluster.centerLng, cluster.centerLat]);
      this.syncWaypointPresentationVisibility(existingEntry.marker);
      this.applyClusterMarkerAttachment(existingEntry.marker);
      return;
    }
    const componentRef = createComponent(ClusterMarkerComponent, {
      environmentInjector: this.environmentInjector,
    });
    this.appRef.attachView(componentRef.hostView);
    componentRef.setInput('cluster', cluster);
    componentRef.changeDetectorRef.detectChanges();

    const marker = new maplibregl.Marker({
      element: componentRef.location.nativeElement,
      anchor: 'center',
    })
      .setLngLat([cluster.centerLng, cluster.centerLat])
      .addTo(this.map);

    // Read the CURRENT cluster off the component ref rather than closing over the
    // first one: the marker is reused across recomputes, so a captured centre goes
    // stale as soon as membership shifts and the tap flies to the wrong place.
    const clusterClickSub = componentRef.instance.clusterClick.subscribe((target) => {
      if (!this.map) return;
      if (!Number.isFinite(target.lat) || !Number.isFinite(target.lng)) return;
      this._suppressNextMapClick = true;
      this.clearDockmateObservationMarker();
      this.map.easeTo({
        center: [target.lng, target.lat],
        zoom: this.clustering.getClusterClickZoom(this.map.getZoom()),
        duration: 500,
      });
    });
    const shellEl = marker.getElement().querySelector('.cluster-marker-shell') as HTMLElement | null;
    if (shellEl) {
      shellEl.classList.add('cluster-enter');
      requestAnimationFrame(() => shellEl.classList.remove('cluster-enter'));
    }

    this.clusterComponentRefs.set(clusterKey, {
      key: clusterKey,
      marker,
      componentRef,
      clusterClickSub,
    });
    this.syncWaypointPresentationVisibility(marker);
    this.applyClusterMarkerAttachment(marker);
  }

  private renderUnifiedClusterResult(result: MapClusterResult): void {
    if (!this.map || !this.isMapLoaded) return;
    const hiddenVesselIds = result.hiddenVesselIds;
    const allVessels = this.getVesselsForClustering();
    // Legacy circle layer — every aggregate is an Angular bubble marker now.
    const clusterSource = this.map.getSource(this.vesselClusterSourceId) as any;
    if (clusterSource) {
      clusterSource.setData({ type: 'FeatureCollection', features: [] });
    }

    // GeoJSON unclustered layer: primary vessel only. Dockmates use HTML markers
    // so we never duplicate or drift positions between two render paths.
    const unclusteredFeatures: any[] = this.isReplayMode ? [] : allVessels
      .filter((v) => v.isPrimary && !hiddenVesselIds.has(v.id))
      .map((v) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [v.lng, v.lat],
        },
        properties: this.ownBoatFeatureProps(v),
      }));
    const unclusteredSource = this.map.getSource(this.vesselUnclusteredSourceId) as any;
    if (unclusteredSource) {
      unclusteredSource.setData({ type: 'FeatureCollection', features: unclusteredFeatures });
    }

    // Dockmate HTML markers: hidden exactly when a RENDERED bubble absorbed them,
    // otherwise shown at their own lat/lng. Never reposition.
    // Do NOT use display:none — MapLibre anchors with translate(-50%,-50%) and
    // zero-size elements shift the marker downward when shown again after zoom.
    const processMarkerVisibility = (marker: maplibregl.Marker) => {
      const vid = String(marker.getElement().getAttribute('data-vessel-id') || '').trim();
      const hidden = this.isVesselHiddenInCluster(vid, hiddenVesselIds);
      const el = marker.getElement();
      if (hidden) {
        // Tag + detach (not just hide) so MapLibre stops repositioning it every
        // frame; the viewport cull below also keeps it detached when off-screen.
        el.setAttribute('data-cluster-hidden', '1');
      } else {
        el.removeAttribute('data-cluster-hidden');
        marker.setOpacity(1);
        el.style.pointerEvents = '';
        el.style.visibility = 'visible';
        const vesselEl = this.getDockmateVesselElement(el);
        const iconImg = (vesselEl || el).querySelector('.boat-icon img') as HTMLElement | null;
        if (iconImg) {
          const nat = (vesselEl || el).dataset['naturalOpacity'];
          iconImg.style.opacity = nat !== undefined ? nat : '1';
        }
      }
      // Attach only if not cluster-hidden AND in the (padded) viewport.
      this.applyVesselMarkerViewportCull(marker);
    };

    this.dockmateMarkers.forEach(processMarkerVisibility);
    Object.values(this.sharedActiveTripMarkers || {}).forEach(processMarkerVisibility);

    // If the faded observation icon belongs to a vessel a bubble just absorbed,
    // drop it — otherwise it sits on top of the bubble and steals its clicks.
    if (this.dockmateObservationMarker) {
      const obsVesselId = String(
        this.dockmateObservationMarker.getElement()?.getAttribute('data-vessel-id') || ''
      ).trim();
      if (obsVesselId && hiddenVesselIds.has(obsVesselId)) {
        this.clearDockmateObservationMarker();
      }
    }

    // A waypoint pin hides when a bubble that is actually DRAWN absorbed it. The
    // engine already applied the zoom rule when building hiddenWaypointIds, so no
    // second zoom test is needed — the old extra `zoom < 9` guard is what left
    // orphaned bubbles standing next to their own un-hidden pins between z9 and z12.
    //
    // Below the pure-cluster threshold mobile emits bubbles and NOTHING else, so
    // place markers (spot/wreck/inlet/anchorage/home) — which never aggregate,
    // since "only event and alert types are meaningful at low zoom" — come off the
    // chart too. Leaving them drawn is why the web looked so much busier than the
    // app at basin zoom.
    // A pin hides ONLY when a bubble that is actually drawn absorbed it — i.e.
    // when it is genuinely aggregated with at least one other entity.
    //
    // This used to also blanket-hide EVERY pin below the pure-cluster threshold,
    // on the reading that mobile draws bubbles and nothing else down there. That
    // is not what mobile does: `_buildWaypointSymbolData`'s `addWaypoint` gate
    // keeps PLACE waypoints rendering individually in pure-cluster view (they
    // never aggregate), and with singletons no longer bubbling at all there is
    // nothing left for a blanket hide to do except make lone items vanish.
    const hiddenWaypointIds = result.hiddenWaypointIds;
    this.displayedMarkers.forEach((marker) => {
      const waypointId = String(marker.getElement()?.getAttribute('data-wpid') || '').trim();
      if (!waypointId) return;
      this.setWaypointMarkerClusterVisibility(marker, hiddenWaypointIds.has(waypointId));
    });

    const nextClusterKeys = new Set<string>();
    result.renderedClusters.forEach((cluster) => {
      nextClusterKeys.add(cluster.id);
      this.upsertClusterComponentMarker(cluster.id, cluster);
    });
    Array.from(this.clusterComponentRefs.keys()).forEach((existingKey) => {
      if (!nextClusterKeys.has(existingKey)) {
        this.scheduleClusterMarkerRemoval(existingKey);
      }
    });
  }

  /** Synchronous clustering apply — avoids RAF lag after zoom/pan. */
  private applyClusteringNow(): void {
    if (!this.map) {
      this.clearClusterMarkers();
      return;
    }
    // Run all clustering + rendering OUTSIDE Angular's zone so a pan/zoom end does
    // not trigger a full app-wide change-detection pass on this large component.
    // The background result$ path already renders outside the zone — this makes
    // the direct moveend/zoomend path consistent.
    this.ngZone.runOutsideAngular(() => this.applyClusteringNowInner());
  }

  /**
   * Fingerprint of every input that can change cluster membership or a badge
   * number. Mirrors mobile's `_waypointFilterSignature`, which exists for the
   * same reason: without it the cache is keyed on zoom alone, so a filter change,
   * a new fetch or a moved boat leaves the previous bubbles — and their counts —
   * frozen on screen until the zoom happens to shift by more than 0.05.
   */
  private computeClusterDataSignature(
    waypoints: ClusterableWaypoint[],
    vessels: ClusterableVessel[],
  ): string {
    // FNV-1a over ids + positions rounded to ~1 m. A rolling hash keeps this cheap
    // enough to run on every moveend even with thousands of markers.
    let h = 0x811c9dc5;
    const mixNum = (v: number) => {
      h ^= v | 0;
      h = Math.imul(h, 0x01000193);
    };
    const mixStr = (s: string) => {
      for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
      }
    };

    let wpCount = 0;
    for (const wp of waypoints) {
      mixStr(wp.id);
      mixNum(Math.round(wp.lat * 1e5));
      mixNum(Math.round(wp.lng * 1e5));
      wpCount++;
    }
    let vesselCount = 0;
    for (const v of vessels) {
      if (v.isPrimary) continue;
      mixStr(v.id);
      mixStr(v.skin ?? '');
      mixNum(Math.round(v.lat * 1e5));
      mixNum(Math.round(v.lng * 1e5));
      vesselCount++;
    }
    return `${wpCount}:${vesselCount}:${h >>> 0}`;
  }

  private applyClusteringNowInner(): void {
    const map = this.map;
    if (!map) return;

    const rawZoom = map.getZoom();
    const clusteringActive = MapClusteringService.isClusteringActive(rawZoom);
    const cached = this.clustering.getLastResult();

    // ── Past the cut-off: nothing aggregates ──────────────────────────────────
    // Tear the bubbles down once, then keep re-rendering the empty result so the
    // viewport cull still runs without paying for a marker sweep every pan.
    if (!clusteringActive) {
      const alreadyOff =
        !!cached &&
        this.lastClusterCacheZoom >= 0 &&
        !MapClusteringService.isClusteringActive(this.lastClusterCacheZoom);
      if (alreadyOff) {
        this.renderUnifiedClusterResult(cached);
        return;
      }
      this.clearClusterMarkers();
      const empty = this.clustering.computeAndStore([], [], rawZoom);
      this.lastClusterCacheZoom = rawZoom;
      this.lastClusterDataSignature = null;
      this.renderUnifiedClusterResult(empty);
      return;
    }

    // Aggregation runs regardless of which panel is open. It used to be skipped
    // whenever `shouldHideWaypointPresentation()` was true, which destroyed every
    // bubble on open and never rebuilt them on close — the "aggregation breaks
    // when coming back from the profile screen" report. Bubbles are hidden while a
    // panel is up by `syncWaypointPresentationVisibility`, exactly like the pins,
    // so they simply reappear intact when it closes.
    const vessels = this.getVesselsForClustering();
    const waypoints = this.getClusterableWaypointEntries();
    const signature = this.computeClusterDataSignature(waypoints, vessels);

    // Aggregation is pan-invariant at a fixed zoom: panning translates every
    // marker equally, so groupings and cluster centres don't change. Reuse the
    // last result only when BOTH the zoom and the underlying data are unchanged.
    // We still re-render so viewport culling re-applies.
    if (
      cached &&
      this.lastClusterCacheZoom >= 0 &&
      Math.abs(rawZoom - this.lastClusterCacheZoom) < 0.05 &&
      this.lastClusterDataSignature === signature
    ) {
      this.renderUnifiedClusterResult(cached);
      return;
    }

    const result = this.clustering.computeAndStore(waypoints, vessels, rawZoom);
    this.lastClusterCacheZoom = rawZoom;
    this.lastClusterDataSignature = signature;
    this.renderUnifiedClusterResult(result);
  }

  /** Force the next `applyClusteringNow()` to recompute instead of reusing cache. */
  private invalidateClusterCache(): void {
    this.lastClusterCacheZoom = -1;
    this.lastClusterDataSignature = null;
  }

  private updateWaypointAggregation(): void {
    if (!this.map) {
      this.clearClusterMarkers();
      return;
    }

    // Skip full recompute mid-zoom animation; zoomend will apply fresh clustering.
    if (this.isZoomAnimating) {
      return;
    }

    this.applyClusteringNow();
  }

  private openWaypointCard(marker: maplibregl.Marker): void {
    if (!this.map) return;

    const requestSeq = ++this.waypointCardOpenRequestSeq;
    let handled = false;
    const openCard = () => {
      if (handled) return;
      handled = true;
      if (requestSeq !== this.waypointCardOpenRequestSeq) return;

      const markerEl = marker.getElement();
      const waypointId = String(markerEl?.getAttribute('data-wpid') || '').trim();
      if (!waypointId) return;

      const activeMarker = this.displayedMarkers.find((m) => {
        const el = m.getElement();
        return String(el?.getAttribute('data-wpid') || '').trim() === waypointId;
      });
      if (activeMarker !== marker) return;

      // Waypoint markers now use the custom overlay card path in
      // mountWaypointCard(). Some callers still come through openWaypointCard(),
      // so route them into the active presentation system instead of the legacy
      // marker.togglePopup() flow, which no longer has an attached popup.
      this.mountWaypointCard(marker);
    };

    this.map.once('moveend', openCard);
    this.map.once('idle', openCard);

    setTimeout(openCard, 900);
  }

  private mountWaypointCard(marker: maplibregl.Marker, waypointOverride?: any): void {
    if (!this.map) return;

    const markerEl = marker.getElement();
    const waypointId = String(markerEl?.getAttribute('data-wpid') || '').trim();
    if (!waypointId) return;

    const waypoint =
      waypointOverride ||
      this.allWaypoints.find((wp) => this.getWaypointId(wp) === waypointId) ||
      (this.getFocusedWaypointOverrideId() === waypointId ? this.focusedWaypointOverride : null);
    if (!waypoint) return;

    this.closeWaypointCard();
    // Mutually exclusive with the observation popup: opening a waypoint card
    // closes any obs popup so the two never stack.
    if (this.activeObsTripPopup) {
      try { this.activeObsTripPopup.remove(); } catch (e) { /* ignore */ }
      this.activeObsTripPopup = null;
    }

    const isPlace = this.isPlaceWaypoint(waypoint);
    const wrapper = document.createElement('div');
    wrapper.className = `waypoint-popup-overlay${isPlace ? ' waypoint-popup-overlay--place' : ''}`;
    wrapper.innerHTML = `${this.getPopupHtml(waypoint)}${isPlace ? '' : '<div class="waypoint-popup-overlay-tail" aria-hidden="true"></div>'}`;

    const expandEl = wrapper.querySelector('[data-action="expand"]') as HTMLElement | null;
    if (expandEl) {
      expandEl.style.display = this.isReplayMode ? 'none' : '';
    }

    bindWaypointPhotoCarousel(wrapper);

    wrapper.addEventListener('click', (e: MouseEvent) => {
      const btn = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
      if (!btn) return;

      const action = btn.dataset['action'];
      if (action === 'photo-open') {
        e.stopPropagation();
        window.open(btn.dataset['url'] || '', '_blank', 'noopener');
      } else if (action === 'share') {
        e.stopPropagation();
        const shareLat = btn.dataset['lat'];
        const shareLng = btn.dataset['lng'];
        const shareUrl = `${window.location.origin}/map?lat=${shareLat}&lng=${shareLng}`;
        if (navigator.share) {
          navigator.share({ title: 'Waypoint', url: shareUrl }).catch(() => { });
        } else {
          navigator.clipboard.writeText(shareUrl).then(() => alert('Link copied!')).catch(() => { });
        }
      } else if (action === 'manage') {
        e.stopPropagation();
        const wpId = btn.dataset['wpid'];
        if (wpId) {
          this.ngZone.run(() => {
            this.submenuService.openWaypointManager(wpId);
          });
        }
      } else if (action === 'expand') {
        e.stopPropagation();
        const wpId = btn.dataset['wpid'];
        if (wpId) {
          this.ngZone.run(() => {
            this.submenuService.setUserProfileOpenState(true);
            this.submenuService.navigateToProfileTab('watch');
            this.submenuService.navigateToWatchPost(wpId);
          });
        }
      } else if (action === 'hide-from-me') {
        e.stopPropagation();
        const wpId = btn.dataset['wpid'] || '';
        const tripId = btn.dataset['tripid'] || '';
        // Replace actions row with inline confirm UI
        const actionsEl = (wrapper.querySelector('.popup-actions') || wrapper.querySelector('.place-popup-actions')) as HTMLElement | null;
        if (actionsEl) {
          actionsEl.innerHTML = `
            <span class="popup-hide-label">Delete from your map?</span>
            <button class="share-btn popup-hide-yes" data-action="hide-confirm"
              data-wpid="${wpId}" data-tripid="${tripId}">Yes</button>
            <button class="share-btn popup-hide-no" data-action="hide-cancel">No</button>`;
        }
      } else if (action === 'hide-confirm') {
        e.stopPropagation();
        const wpId = btn.dataset['wpid'] || '';
        const tripId = btn.dataset['tripid'] || '';
        this.ngZone.run(() => {
          const wid = wpId || undefined;
          const tid = tripId || undefined;
          // Signal map to remove immediately
          this.submenuService.notifyWatchItemHidden({ tripId: tid, waypointId: wid });
          // Close the popup card
          this.closeWaypointCard(wpId);
          // Persist hide on backend
          if (wid || tid) {
            this.waypointService.hideForMe(tid, wid).subscribe({ error: () => { } });
          }
        });
      } else if (action === 'hide-cancel') {
        e.stopPropagation();
        // Restore original actions by re-opening the card on the same waypoint
        const waypointId2 = this.activeWaypointCardWaypointId;
        if (waypointId2) {
          const marker = this.displayedMarkers.find((m) => {
            const el = m.getElement();
            return el?.getAttribute('data-wpid') === waypointId2;
          });
          if (marker) {
            this.closeWaypointCard(waypointId2);
            this.openWaypointCard(marker);
          }
        }
      }
    });

    const cardMarker = new maplibregl.Marker({
      element: wrapper,
      anchor: 'bottom',
      offset: this.getWaypointPopupOffset(marker),
    })
      .setLngLat(marker.getLngLat())
      .addTo(this.map);

    this.activeWaypointCardMarker = cardMarker;
    this.activeWaypointCardWaypointId = waypointId;
    this.syncWaypointPresentationVisibility();

    // Flip the card below the icon when it would otherwise hide behind the top
    // edge of the map (now that it is mounted we can measure its real height).
    this.applyWaypointCardFlip(marker);
  }

  private closeWaypointCard(waypointId?: string | null): void {
    this.waypointCardOpenRequestSeq++;
    const normalizedId = String(waypointId || '').trim();
    if (normalizedId && this.activeWaypointCardWaypointId !== normalizedId) return;

    if (this.activeWaypointCardMarker) {
      try {
        this.activeWaypointCardMarker.remove();
      } catch (e) {
        // ignore overlay cleanup errors
      }
    }

    this.activeWaypointCardMarker = null;
    this.activeWaypointCardWaypointId = null;

    if (this.displayedMarkers) {
      this.displayedMarkers.forEach((marker) => {
        const popup = marker.getPopup();
        if (popup && popup.isOpen()) popup.remove();
      });
    }
  }

  private isFocusedOverrideInCurrentFilter(): boolean {
    const wp = this.focusedWaypointOverride;
    if (!wp) return true;
    const filterState = this.mapFilterService.getFilterState();
    if (!filterState) return true;
    if (!filterState.waypointsDateRange?.start && !filterState.waypointsDateRange?.end) return true;
    const createdDateStr = wp.audit_data?.create_ts || wp.created_at;
    const createdDate = createdDateStr ? new Date(createdDateStr) : new Date();
    const startDate = filterState.waypointsDateRange.start || HomePageComponent.NO_LOWER_DATE_BOUND;
    const endDate = filterState.waypointsDateRange.end || new Date();
    const checkDate = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate()).getTime();
    const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()).getTime();
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()).getTime();
    return checkDate >= start && checkDate <= end;
  }

  /**
   * Reveals the map canvas immediately, bypassing the initial-load reveal gate.
   * Assigned in initMap; a no-op before the map exists.
   */
  private forceRevealMapCanvas: () => void = () => { /* set in initMap */ };

  private flyToWaypointPresentation(target: maplibregl.LngLatLike, zoom: number = 14, marker?: maplibregl.Marker | null): void {
    if (!this.map) return;
    // The user has explicitly asked to look somewhere — never leave them staring at
    // the hidden-canvas sea-blue while the initial-load gate waits.
    this.forceRevealMapCanvas();
    const container = this.map.getContainer();
    const markerKind = String(marker?.getElement()?.getAttribute('data-marker-kind') || '').trim();
    // Place markers are compact icons/labels, so they need less camera offset
    // to keep the popup visually attached after toggling label/icon mode.
    const verticalOffset = markerKind === 'place-icon' || markerKind === 'place-label'
      ? Math.round(Math.min(container.clientHeight * 0.04, 32))
      : Math.round(Math.min(container.clientHeight * 0.16, 120));

    // A highlight must land on loaded tiles, not on the bare #d8e8f1 sea-blue.
    //
    // flyTo() arcs the camera out to a low zoom and back in, and MapLibre requests
    // tiles for EVERY intermediate viewport along that path. On a long jump those
    // throwaway requests saturate the request pool (setMaxParallelImageRequests(32)),
    // so the tiles that actually matter — the destination's — are queued last and the
    // user stares at empty blue with the highlighted pin floating on it. The app never
    // has this problem because _focusWaypointFromProfile snaps the camera outright
    // (`_mapController.move(offsetCenter, zoom)`), making the destination tiles the
    // only ones ever requested.
    //
    // So: snap when the move is a real relocation, and keep the short animation only
    // when the target is already on screen at a similar zoom (where the intermediate
    // viewports overlap what is already loaded and cost nothing).
    const targetLngLat = maplibregl.LngLat.convert(target as any);
    const isOnScreen = this.map.getBounds().contains(targetLngLat);
    const zoomDelta = Math.abs(this.map.getZoom() - zoom);

    if (isOnScreen && zoomDelta <= 2) {
      this.map.easeTo({
        center: target,
        zoom,
        duration: 450,
        offset: [0, verticalOffset],
      });
      return;
    }

    // easeTo with duration 0 rather than jumpTo: `offset` is an AnimationOptions
    // field, so jumpTo would silently ignore it and drop the pin dead-centre instead
    // of below centre (where the tooltip anchors). duration 0 still resolves in a
    // single frame — no intermediate viewports, no throwaway tile requests.
    this.map.easeTo({
      center: target,
      zoom,
      duration: 0,
      offset: [0, verticalOffset],
    });
    // The destination is off-screen, so its tiles are necessarily still in flight.
    // Say so instead of leaving the user looking at bare sea-blue wondering whether
    // the highlight worked.
    this.beginChartLoadIndicator();
  }

  /**
   * Show the "loading chart" pill until the tiles under the current camera have
   * finished arriving.
   *
   * Only meaningful after a deliberate relocation — panning always keeps some
   * loaded tiles on screen, but a snap to an area never visited has nothing to
   * draw and the style paints its flat background colour. That blank frame is
   * indistinguishable from a broken map, which is what the report describes.
   * MapLibre's own `idle` is the authoritative "everything for this viewport is
   * loaded and rendered" signal.
   */
  private beginChartLoadIndicator(): void {
    const map = this.map;
    if (!map) return;
    if (map.areTilesLoaded()) return;

    const token = ++this.chartLoadToken;
    this.ngZone.run(() => { this.isChartLoading = true; });

    const finish = () => {
      if (token !== this.chartLoadToken) return; // superseded by a newer jump
      clearTimeout(timer);
      map.off('idle', finish);
      this.ngZone.run(() => { this.isChartLoading = false; });
    };
    // Never let a stalled/failed tile request pin the indicator up forever.
    const timer = setTimeout(finish, 15000);
    map.on('idle', finish);
  }

  /**
   * Put a freshly-created waypoint under the Create Waypoint tour's last step.
   *
   * That step spotlights `home.mapCenter` — a fixed box at the map's viewport
   * centre, exactly like the app's `home.boatMarkerAnchor`. So all this has to
   * do is centre the chart on the new waypoint and keep its pin visible there:
   * claim it as the tour target (which exempts it from clustering, viewport
   * culling and the presentation-hiding the still-closing sheet applies), then
   * fly it to centre and pulse it.
   */
  private spotlightTourWaypoint(wp: any): void {
    if (!this.map) return;
    const waypointId = String(this.getWaypointId(wp) || '').trim();
    if (!waypointId) return;
    this.tourSpotlightWaypointId = waypointId;

    const revealMarker = () => {
      const marker = this.displayedMarkers.find(
        (m) => String(m.getElement()?.getAttribute('data-wpid') || '').trim() === waypointId,
      );
      if (!marker) return null;
      // Undo any presentation hiding the still-closing sheet applied, and force
      // the marker onto the map so the pin actually sits inside the spotlight.
      const el = marker.getElement();
      if (el) {
        el.style.opacity = '';
        el.style.visibility = '';
        el.style.pointerEvents = '';
      }
      this.setMarkerAttached(marker, true);
      this.highlightWaypointMarker(marker);
      return marker;
    };

    revealMarker();

    const lat = Number(wp?.position?.latitude);
    const lng = Number(wp?.position?.longitude);
    if (isNaN(lat) || isNaN(lng)) return;
    // No offset: the coordinate must land on the map container's exact centre,
    // which is where `.tour-map-center` (the step's anchor) sits.
    this.map.flyTo({ center: [lng, lat], zoom: Math.max(this.map.getZoom(), 13), speed: 1.4 });
    // The fly-to's moveend re-runs clustering and viewport culling; re-assert
    // the marker afterwards in case it was rebuilt in between.
    this.map.once('moveend', () => revealMarker());
  }

  /**
   * True while [waypointId] is the pin the Create Waypoint tour is spotlighting.
   * Self-clears once the tour ends so normal clustering/culling resumes on the
   * next map interaction.
   */
  private isTourSpotlightWaypoint(waypointId: string | null | undefined): boolean {
    if (!this.tourSpotlightWaypointId) return false;
    if (this.tourController.state()?.tour.id !== 'create_waypoint') {
      this.tourSpotlightWaypointId = null;
      return false;
    }
    return String(waypointId || '').trim() === this.tourSpotlightWaypointId;
  }

  private highlightWaypointMarker(marker: maplibregl.Marker): void {
    const el = marker.getElement();
    if (!el) return;

    // Highlighting implies user focus, so make sure the marker is attached even
    // if viewport culling or clustering had detached it (the fly-to/moveend will
    // re-evaluate, but this avoids a brief gap where the highlight isn't visible).
    this.setMarkerAttached(marker, true);

    // Clear any previous waypoint highlight and set this as active
    this.clearWaypointHighlight(true);
    const wpId = el.getAttribute && el.getAttribute('data-wpid');
    if (wpId) this.activeWaypointHighlightId = String(wpId).trim();

    // Trigger the shared highlight treatment for every marker type.
    el.classList.remove('waypoint-marker-highlight');
    void el.offsetWidth;
    el.classList.add('waypoint-marker-highlight');

    try {
      this.setSvgOutline(el, true);
    } catch (e) { /* ignore */ }
    try {
      this.setRasterHighlightedIcon(el, true);
    } catch (e) { /* ignore */ }
    try {
      this.updateWaypointFocusRing(el);
    } catch (e) { /* ignore */ }
  }

  private clearWaypointHighlight(preserveFocusedOverride: boolean = false): void {
    const shouldClearFocusedOverride = !preserveFocusedOverride && !!this.focusedWaypointOverride;
    this.activeWaypointHighlightId = null;

    this.displayedMarkers.forEach((marker) => {
      const pel = marker.getElement();
      if (!pel) return;

      pel.classList.remove('waypoint-marker-highlight');
      try {
        const focus = pel.querySelector && (pel.querySelector('.waypoint-focus-ring') as HTMLElement | null);
        if (focus) {
          focus.style.opacity = '0';
          focus.style.display = 'none';
        }
      } catch (e) { /* ignore */ }
      try {
        this.setSvgOutline(pel, false);
      } catch (e) { /* ignore */ }
      try {
        this.setRasterHighlightedIcon(pel, false);
      } catch (e) { /* ignore */ }
    });

    if (shouldClearFocusedOverride) {
      this.focusedWaypointOverride = null;
      if (this.map && this.isMapLoaded) {
        this.applyMapFilters();
      }
    }
  }

  private clearActiveWaypointSelection(): void {
    this.clearWaypointHighlight();
    this.closeWaypointCard();
  }

  private isSvgAsset(src: string): boolean {
    return /^data:image\/svg\+xml/i.test(src) || /\.svg(?:$|\?)/i.test(src);
  }

  private setRasterHighlightedIcon(container: HTMLElement, enable: boolean): void {
    const img = container.querySelector('.main-marker-image') as HTMLImageElement | null;
    if (!img || img.tagName.toLowerCase() !== 'img') return;

    const currentSrc = String(img.getAttribute('src') || '').trim();
    const originalSrcAttr = String(img.getAttribute('data-original-src') || '').trim();
    const baseSrc = originalSrcAttr || currentSrc;
    if (!baseSrc || this.isSvgAsset(baseSrc)) return;

    if (!originalSrcAttr) {
      img.setAttribute('data-original-src', baseSrc);
    }

    if (!enable) {
      if (currentSrc !== baseSrc) {
        img.src = baseSrc;
      }
      img.removeAttribute('data-highlighted-src');
      return;
    }

    const highlightedSrcAttr = String(img.getAttribute('data-highlighted-src') || '').trim();
    if (highlightedSrcAttr) {
      if (currentSrc !== highlightedSrcAttr) {
        img.src = highlightedSrcAttr;
      }
      return;
    }

    this.getRasterHighlightDataUrl(baseSrc).then((dataUrl) => {
      if (!dataUrl || !container.isConnected || !img.isConnected) return;
      img.setAttribute('data-highlighted-src', dataUrl);
      const latestOriginal = String(img.getAttribute('data-original-src') || '').trim();
      if (latestOriginal === baseSrc && container.classList.contains('waypoint-marker-highlight')) {
        img.src = dataUrl;
      }
    }).catch(() => {
      // ignore highlight generation failures and keep the original icon
    });
  }

  private getRasterHighlightDataUrl(src: string): Promise<string | null> {
    const cached = this.waypointRasterHighlightCache.get(src);
    if (cached) return cached;

    const promise = new Promise<string | null>((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const width = img.naturalWidth || img.width;
          const height = img.naturalHeight || img.height;
          if (!width || !height) {
            resolve(null);
            return;
          }

          const pad = Math.max(6, Math.round(Math.max(width, height) * 0.16));
          const tintedCanvas = document.createElement('canvas');
          tintedCanvas.width = width;
          tintedCanvas.height = height;
          const tintedCtx = tintedCanvas.getContext('2d');
          if (!tintedCtx) {
            resolve(null);
            return;
          }

          tintedCtx.drawImage(img, 0, 0, width, height);
          tintedCtx.globalCompositeOperation = 'source-in';
          tintedCtx.fillStyle = '#6D28D9';
          tintedCtx.fillRect(0, 0, width, height);

          const outCanvas = document.createElement('canvas');
          outCanvas.width = width + pad * 2;
          outCanvas.height = height + pad * 2;
          const outCtx = outCanvas.getContext('2d');
          if (!outCtx) {
            resolve(null);
            return;
          }

          const radius = Math.max(3, Math.round(Math.max(width, height) * 0.08));
          for (let dx = -radius; dx <= radius; dx++) {
            for (let dy = -radius; dy <= radius; dy++) {
              if (dx === 0 && dy === 0) continue;
              if ((dx * dx) + (dy * dy) > radius * radius) continue;
              outCtx.drawImage(tintedCanvas, pad + dx, pad + dy, width, height);
            }
          }

          outCtx.drawImage(img, pad, pad, width, height);

          resolve(outCanvas.toDataURL('image/png'));
        } catch (e) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = src;
    });

    this.waypointRasterHighlightCache.set(src, promise);
    return promise;
  }

  private setSvgOutline(container: HTMLElement, enable: boolean): void {
    try {
      const svg = container.querySelector && (container.querySelector('svg') as SVGElement | null);
      if (!svg) return;
      const shapes = svg.querySelectorAll('path, circle, rect, polygon, polyline');
      shapes.forEach((s) => {
        const el = s as SVGElement;
        if (enable) {
          if (el.getAttribute('data-old-stroke') == null) {
            el.setAttribute('data-old-stroke', el.getAttribute('stroke') || '');
            el.setAttribute('data-old-stroke-width', el.getAttribute('stroke-width') || '');
            el.setAttribute('data-old-stroke-linejoin', el.getAttribute('stroke-linejoin') || '');
            el.setAttribute('data-old-stroke-linecap', el.getAttribute('stroke-linecap') || '');
          }
          // Thicker, sharper outline
          el.setAttribute('stroke', '#6D28D9');
          el.setAttribute('stroke-width', '3');
          el.setAttribute('stroke-linejoin', 'miter');
          el.setAttribute('stroke-linecap', 'butt');
          el.setAttribute('stroke-miterlimit', '2.5');
        } else {
          const oldStroke = el.getAttribute('data-old-stroke');
          const oldW = el.getAttribute('data-old-stroke-width');
          const oldJoin = el.getAttribute('data-old-stroke-linejoin');
          const oldCap = el.getAttribute('data-old-stroke-linecap');
          if (oldStroke != null) {
            if (oldStroke) el.setAttribute('stroke', oldStroke); else el.removeAttribute('stroke');
            el.removeAttribute('data-old-stroke');
          }
          if (oldW != null) {
            if (oldW) el.setAttribute('stroke-width', oldW); else el.removeAttribute('stroke-width');
            el.removeAttribute('data-old-stroke-width');
          }
          if (oldJoin != null) {
            if (oldJoin) el.setAttribute('stroke-linejoin', oldJoin); else el.removeAttribute('stroke-linejoin');
            el.removeAttribute('data-old-stroke-linejoin');
          }
          if (oldCap != null) {
            if (oldCap) el.setAttribute('stroke-linecap', oldCap); else el.removeAttribute('stroke-linecap');
            el.removeAttribute('data-old-stroke-linecap');
          }
          el.removeAttribute('stroke-miterlimit');
        }
      });
      // Keep SVG shadow minimal for a crisper look; use a subtle ring rather than blur
      if (enable) {
        svg.style.filter = 'drop-shadow(0 0 0 rgba(0,0,0,0))';
      } else {
        svg.style.filter = '';
      }
    } catch (e) {
      // ignore
    }
  }

  private updateWaypointFocusRing(container: HTMLElement): void {
    const focus = container.querySelector('.waypoint-focus-ring') as HTMLElement | null;
    if (!focus) return;

    const markerKind = String(container.getAttribute('data-marker-kind') || 'pin').trim();
    if (markerKind !== 'place-label') {
      focus.style.opacity = '0';
      focus.style.display = 'none';
      return;
    }

    const visualEl =
      container.querySelector('.waypoint-icon') as HTMLElement | null ||
      container.querySelector('svg') as HTMLElement | null ||
      container.querySelector('img') as HTMLElement | null ||
      container;

    const containerRect = container.getBoundingClientRect();
    const visualRect = visualEl.getBoundingClientRect();
    const visualWidth = visualRect.width || containerRect.width || 40;
    const visualHeight = visualRect.height || containerRect.height || 40;
    const visualLeft = visualEl === container ? 0 : (visualEl.offsetLeft || 0);
    const visualTop = visualEl === container ? 0 : (visualEl.offsetTop || 0);
    let width = containerRect.width + 12;
    let height = containerRect.height + 10;
    let centerX = containerRect.width / 2;
    let centerY = containerRect.height / 2;
    let borderRadius = '999px';
    let boxShadow = '0 0 0 3px rgba(255, 255, 255, 0.96), 0 0 0 5px rgba(109, 40, 217, 0.72), 0 8px 18px rgba(109, 40, 217, 0.18)';

    if (markerKind === 'place-label') {
      width = containerRect.width + 8;
      height = containerRect.height + 6;
      borderRadius = '999px';
      boxShadow = '0 0 0 2px rgba(255, 255, 255, 0.94), 0 0 0 4px rgba(109, 40, 217, 0.5), 0 8px 18px rgba(109, 40, 217, 0.12)';
    }

    focus.style.display = 'block';
    focus.style.position = 'absolute';
    focus.style.left = `${Math.round(centerX)}px`;
    focus.style.top = `${Math.round(centerY)}px`;
    focus.style.width = `${Math.round(width)}px`;
    focus.style.height = `${Math.round(height)}px`;
    focus.style.transform = 'translate(-50%, -50%)';
    focus.style.borderRadius = borderRadius;
    focus.style.boxShadow = boxShadow;
    focus.style.pointerEvents = 'none';
    focus.style.zIndex = '1';
    focus.style.opacity = '1';
    focus.style.background = 'transparent';
  }

  private clearMarkers(): void {
    this.invalidateClusterCache();
    this.clearWaypointHighlight(true);
    this.closeWaypointCard();
    this.displayedMarkers.forEach((m) => m.remove());
    this.displayedMarkers = [];
    this.clearClusterMarkers();
  }


  private shouldHideWaypointPresentation(): boolean {
    return this.isProfileOpen || this.isWatchOpen || this.isFilterOpen || this.isLayerOpen || this.isWaypointCreating || this.isWaypointManagerOpen;
  }

  private syncWaypointPresentationVisibility(targetMarker?: maplibregl.Marker | HTMLElement): void {
    if (!targetMarker) {
      this.invalidateClusterCache();
    }
    const shouldHide = this.shouldHideWaypointPresentation();
    const applyVisibility = (marker: maplibregl.Marker | HTMLElement | null | undefined) => {
      const el = marker instanceof HTMLElement ? marker : marker?.getElement();
      if (el) {
        // The tour's spotlight target stays visible even while a panel is open —
        // the sheet is still closing when the "Waypoint created" step resolves.
        const hide = shouldHide && !this.isTourSpotlightWaypoint(el.getAttribute('data-wpid'));
        el.style.opacity = hide ? '0' : '';
        el.style.visibility = hide ? 'hidden' : '';
        el.style.pointerEvents = hide ? 'none' : '';
      }
    };

    if (targetMarker) {
      applyVisibility(targetMarker);
      if (targetMarker instanceof maplibregl.Marker) {
        this.applyWaypointMarkerAttachment(targetMarker);
      }
      return;
    }

    this.displayedMarkers.forEach((m) => {
      applyVisibility(m);
      this.applyWaypointMarkerAttachment(m);
    });
    this.clusterComponentRefs.forEach((entry) => {
      applyVisibility(entry.marker);
      this.applyClusterMarkerAttachment(entry.marker);
    });
    const cardEl = this.activeWaypointCardMarker?.getElement();
    if (cardEl) {
      cardEl.style.opacity = shouldHide ? '0' : '';
      cardEl.style.visibility = shouldHide ? 'hidden' : '';
      cardEl.style.pointerEvents = shouldHide ? 'none' : '';
    }

    // Closing a panel (profile, watch, filters, layers, …) returns the user to the
    // chart. Waypoints and vessels may have been added, removed or re-filtered
    // while it was up, so re-aggregate before the bubbles become visible again —
    // otherwise the map shows the pre-panel grouping with pre-panel counts.
    if (!shouldHide && this.wasWaypointPresentationHidden) {
      this.applyClusteringNow();
    }
    this.wasWaypointPresentationHidden = shouldHide;
  }

  private clearTripWaypointMarkerHighlights(): void {
    this.displayedMarkers.forEach((marker) => {
      const el = marker.getElement();
      if (!el) return;
      el.classList.remove('waypoint-marker-trip-highlight');
    });
  }

  private updateTripWaypointMarkerHighlights(): void {
    const targetTripId = String(this.activeTripWaypointHighlightTripId || '').trim();

    if (!targetTripId) {
      this.clearTripWaypointMarkerHighlights();
      return;
    }

    this.displayedMarkers.forEach((marker) => {
      const el = marker.getElement();
      if (!el) return;

      const markerTripId = String(el.getAttribute('data-trip-id') || '').trim();
      const shouldHighlight = !!targetTripId && markerTripId === targetTripId;
      el.classList.toggle('waypoint-marker-trip-highlight', shouldHighlight);
    });
  }

  private clearTripFocusState(): void {
    this.activeTripFocusId = null;
    this.activeTripWaypointHighlightTripId = null;
    this.activeTripFocusWaypoints = [];
    this.clearTripWaypointMarkerHighlights();
  }

  private removeExistingWaypointMarker(waypointId: string): void {
    const normalizedId = String(waypointId || '').trim();
    if (!normalizedId) return;

    this.invalidateClusterCache();
    const markerIndex = this.displayedMarkers.findIndex((marker) => {
      const el = marker.getElement();
      return el && el.getAttribute && el.getAttribute('data-wpid') === normalizedId;
    });

    if (markerIndex < 0) return;

    const marker = this.displayedMarkers[markerIndex];
    this.closeWaypointCard(normalizedId);

    try {
      marker.remove();
    } catch (e) {
      // ignore marker cleanup errors
    }

    this.displayedMarkers.splice(markerIndex, 1);
    this.updateWaypointAggregation();
  }

  private getFocusedWaypointOverrideId(): string {
    return this.getWaypointId(this.focusedWaypointOverride);
  }

  private getCachedWaypointById(waypointId: string): any | null {
    const normalizedId = String(waypointId || '').trim();
    if (!normalizedId) return null;
    return this.allWaypoints.find((wp) => this.getWaypointId(wp) === normalizedId) || null;
  }

  private ensureFocusedWaypointOverride(waypointId: string, position?: any, create_ts?: string, waypointData?: any): boolean {
    const normalizedId = String(waypointId || '').trim();
    if (!normalizedId) return false;

    const cachedWaypoint = this.getCachedWaypointById(normalizedId);
    if (cachedWaypoint) {
      this.focusedWaypointOverride = cachedWaypoint;
      return true;
    }

    // Use the full waypoint_details from the watch post when available — mirrors Flutter's
    // approach of passing the embedded GetWaypointModel directly, bypassing any date filter
    // or pagination gap that would otherwise leave the old waypoint invisible on the map.
    if (waypointData && typeof waypointData === 'object') {
      // Watch API returns position as { lat, lon }; addWaypointMarker needs { latitude, longitude }
      const rawPos = waypointData.position;
      const normalizedPos = rawPos ? {
        latitude: rawPos.latitude ?? rawPos.lat,
        longitude: rawPos.longitude ?? rawPos.lon,
      } : undefined;
      this.focusedWaypointOverride = this.normalizeWaypointForMap({
        ...waypointData,
        ...(normalizedPos ? { position: normalizedPos } : {}),
        waypoint_id: waypointData.waypoint_id || waypointData._id || waypointData.id || normalizedId,
        _id: waypointData._id || waypointData.id || normalizedId,
        id: waypointData.id || normalizedId,
      });
      return true;
    }

    const lat = position?.latitude ?? position?.lat;
    const lng = position?.longitude ?? position?.lon;
    if (lat == null || lng == null) {
      return false;
    }

    this.focusedWaypointOverride = {
      waypoint_id: normalizedId,
      _id: normalizedId,
      id: normalizedId,
      position: {
        latitude: lat,
        longitude: lng,
      },
      audit_data: create_ts ? { create_ts } : undefined,
      created_at: create_ts,
    };

    return true;
  }

  private removeWaypointFromMap(waypointId: string): void {
    const normalizedId = String(waypointId).trim();
    if (!normalizedId) return;

    this.invalidateClusterCache();
    this.allWaypoints = this.allWaypoints.filter((wp) => this.getWaypointId(wp) !== normalizedId);

    const markerIndex = this.displayedMarkers.findIndex((marker) => {
      const el = marker.getElement();
      return el && el.getAttribute && el.getAttribute('data-wpid') === normalizedId;
    });

    if (markerIndex >= 0) {
      const marker = this.displayedMarkers[markerIndex];
      this.closeWaypointCard(normalizedId);
      try {
        marker.remove();
      } catch (e) {
        // ignore marker cleanup errors
      }
      this.displayedMarkers.splice(markerIndex, 1);
    }

    if (this.activeWaypointHighlightId === normalizedId) {
      this.activeWaypointHighlightId = null;
    }

    if (this.activeWaypointManagerId === normalizedId) {
      this.activeWaypointManagerId = null;
      this.isWaypointManagerOpen = false;
    }

    this.updateWaypointAggregation();
  }

  // ── Pill-label → internal waypoint type mapping (used by applyMapFilters) ──
  private static readonly PILL_TO_WAYPOINT_TYPES: Record<string, string[]> = {
    // Event pills
    'Tuna Caught': ['fish_caught'],
    'Billfish Caught': ['fish_caught'],
    'Pelagic Caught': ['fish_caught'],
    'Inshore Caught': ['fish_caught'],
    'Fish Mark': ['fish_mark'],
    'Bait Mark': ['bait_mark'],
    'Temp Break': ['temp_break'],
    // Place pills
    'Spot': ['canyon'],
    'Wreck': ['wreck'],
    'Inlet': ['inlet'],
    'Anchorage': ['anchorage'],
    'Home': ['home'],
    // Alert pills
    'Hazard': ['hazard'],
    'Diver': ['diver'],
    'Patrol': ['patrol'],
    'Fog': ['fog'],
  };

  /**
   * Stand-in start date for "the range has no lower bound". Replaces a
   * hard-coded `new Date('2026-01-01')` that silently clipped anything older
   * than 2026 even though the filter calendar allows picking back to 2020. The
   * app has no such floor.
   */
  private static readonly NO_LOWER_DATE_BOUND = new Date(0);

  // Maps event pill labels to the fish_caught category keys they represent
  private static readonly EVENT_PILL_TO_FISH_CATEGORY: Record<string, string> = {
    'Tuna Caught': 'tuna',
    'Billfish Caught': 'billfish',
    'Pelagic Caught': 'pelagic',
    'Inshore Caught': 'inshore',
  };

  // Maps tuna/billfish/pelagic/inshore sub-type pill labels to the fish_subtype key
  private static readonly SUBTYPE_PILL_TO_KEY: Record<string, string> = {
    'Yellowfin': 'yellowfin', 'Bluefin': 'bluefin', 'Bigeye': 'bigeye', 'Albacore': 'albacore',
    'Blue Marlin': 'bluemarlin', 'White Marlin': 'whitemarlin', 'Sailfish': 'sailfish', 'Swordfish': 'swordfish',
    'Mahi': 'mahi', 'Wahoo': 'wahoo',
    'Striped Bass': 'stripedbass', 'Fluke': 'fluke', 'Sea Bass': 'seabass', 'Bluefish': 'bluefish', 'Tautog': 'tautog',
  };

  private static readonly FISH_CATEGORY_FOR_SUBTYPE: Record<string, string> = {
    yellowfin: 'tuna', bluefin: 'tuna', bigeye: 'tuna', albacore: 'tuna',
    bluemarlin: 'billfish', whitemarlin: 'billfish', sailfish: 'billfish', swordfish: 'billfish',
    mahi: 'pelagic', wahoo: 'pelagic',
    stripedbass: 'inshore', fluke: 'inshore', seabass: 'inshore', bluefish: 'inshore', tautog: 'inshore',
  };

  private buildEnabledWaypointTypes(filterState: any): Set<string> {
    const types = new Set<string>();
    const allPillSets = [
      filterState.selectedEvents,
      filterState.selectedPlaces,
      filterState.selectedAlerts,
    ];
    for (const pills of allPillSets) {
      if (!pills) continue;
      for (const pill of pills) {
        const mapped = HomePageComponent.PILL_TO_WAYPOINT_TYPES[pill];
        if (mapped) mapped.forEach((t: string) => types.add(t));
      }
    }
    return types;
  }

  private getWaypointFishSubtype(wp: any): string {
    return resolveWaypointFishSubtype(wp);
  }

  private inferFishCategory(wp: any): string {
    const subtype = this.getWaypointFishSubtype(wp);
    if (subtype && HomePageComponent.FISH_CATEGORY_FOR_SUBTYPE[subtype]) {
      return HomePageComponent.FISH_CATEGORY_FOR_SUBTYPE[subtype];
    }
    // Fallback: check waypoint name/type for category hints
    const name = String(wp?.name || wp?.title || wp?.type || '').toLowerCase();
    if (name.includes('tuna')) return 'tuna';
    if (name.includes('billfish') || name.includes('marlin') || name.includes('sailfish') || name.includes('swordfish')) return 'billfish';
    if (name.includes('pelagic') || name.includes('mahi') || name.includes('wahoo')) return 'pelagic';
    if (name.includes('inshore') || name.includes('striped') || name.includes('fluke') || name.includes('seabass') || name.includes('bluefish') || name.includes('tautog')) return 'inshore';
    return 'tuna'; // default category
  }

  private applyMapFilters(): void {
    this.invalidateClusterCache();
    const filterState = this.mapFilterService.getFilterState();
    if (!filterState) {
      this.clearMarkers();
      this.getDedupedWaypoints(this.allWaypoints).forEach((wp) => this.addWaypointMarker(wp, false));
      this.updateWaypointAggregation();
      return;
    }

    if (filterState.waypointsEnabled === false) {
      this.clearMarkers();
      // Still try to resolve any pending watch/profile focus even with waypoints hidden globally
      this.tryPendingWaypointFocus();
      return;
    }

    // Build the set of enabled internal waypoint type keys from filter pills.
    // When a pill array is undefined/null in the state, it means "all enabled"
    // (initial state before user ever touches it).
    const enabledTypes = this.buildEnabledWaypointTypes(filterState);
    const hasTypeFilter = (filterState.selectedEvents || filterState.selectedPlaces || filterState.selectedAlerts) != null;

    // Build enabled fish category set from selected event pills
    const enabledFishCategories = new Set<string>();
    const eventPills: string[] | undefined = filterState.selectedEvents;
    if (eventPills) {
      for (const pill of eventPills) {
        const cat = HomePageComponent.EVENT_PILL_TO_FISH_CATEGORY[pill];
        if (cat) enabledFishCategories.add(cat);
      }
      // Fish Mark, Bait Mark, Temp Break don't have categories — they're separate types
    }
    const hasEventFilter = !!eventPills;

    // Per-category subtype filter sets — each category checked independently
    // so deselecting e.g. "Yellowfin" only affects tuna, not pelagic/billfish.
    const buildCatSet = (pills: string[] | undefined) => {
      if (!pills) return null;
      const keys = new Set<string>();
      let hasOther = false;
      for (const pill of pills) {
        if (pill === 'Other') { hasOther = true; continue; }
        const key = HomePageComponent.SUBTYPE_PILL_TO_KEY[pill];
        if (key) keys.add(key);
      }
      return { keys, hasOther };
    };
    const tunaFilter = buildCatSet(filterState.selectedTunaTypes);
    const billfishFilter = buildCatSet(filterState.selectedBillfish);
    const pelagicFilter = buildCatSet(filterState.selectedPelagic);
    const inshoreFilter = buildCatSet(filterState.selectedInshore);

    // Unified captain/group visibility sets (mirrors the mobile app's gate).
    const capSets = this.buildCaptainGateSets(filterState);

    const filteredWaypoints = this.getDedupedWaypoints(this.allWaypoints).filter((wp) => {
      const createdDateStr = wp.audit_data?.create_ts || wp.created_at;
      const createdDate = createdDateStr ? new Date(createdDateStr) : new Date();
      const wpType = this.resolveWaypointTypeForMap(wp);
      const isAlertWp = isAlertWaypointType(wp);
      const isPlaceWp = this.isPlaceWaypoint(wp);

      // ── Alert age gate (12h) ──
      if (isAlertWp) {
        const ageMs = Date.now() - createdDate.getTime();
        const twelveHoursMs = 12 * 60 * 60 * 1000;
        if (!Number.isNaN(createdDate.getTime()) && ageMs >= twelveHoursMs) {
          return false;
        }
      }

      // ── "Active trips only" gate ──
      // Hides waypoints belonging to a trip that has ended, which is what keeps the
      // app's map clean — a finished trip takes its marks off the map with it.
      // Standalone waypoints (no trip) are never touched by this filter.
      //
      // PLACES ARE EXEMPT, exactly as in the app's gate
      // (`if (_activeTripsOnly && !_placeTypes.contains(wpType))`): an anchorage, home,
      // wreck, inlet or spot is a persistent saved location, not trip data, so it stays
      // on the map regardless of whether the trip it happened to be created on has
      // ended. Web was hiding those, which is why places vanished here but not on
      // mobile. (The date-range place override further down could never rescue them —
      // this gate had already returned false.)
      if (this.isActiveTripsOnly(filterState) && !isPlaceWp && !this.isWaypointOnLiveTrip(wp)) {
        return false;
      }

      // ── Waypoint type filter ──
      if (hasTypeFilter && enabledTypes.size > 0 && !enabledTypes.has(wpType)) {
        return false;
      }

      // ── Fish-caught category + per-category sub-type filter ──
      if (wpType === 'fish_caught') {
        const category = this.inferFishCategory(wp);
        if (hasEventFilter && !enabledFishCategories.has(category)) {
          return false;
        }
        // Select only the filter set for this waypoint's category
        const catFilter =
          category === 'tuna' ? tunaFilter :
            category === 'billfish' ? billfishFilter :
              category === 'pelagic' ? pelagicFilter :
                category === 'inshore' ? inshoreFilter : null;
        if (catFilter) {
          const subtype = this.getWaypointFishSubtype(wp);
          if (subtype && HomePageComponent.FISH_CATEGORY_FOR_SUBTYPE[subtype] === category) {
            // Known subtype for this category — must be explicitly selected
            if (!catFilter.keys.has(subtype)) return false;
          } else {
            // Unknown / blank subtype — only show when "Other" is selected for this category
            if (!catFilter.hasOther) return false;
          }
        }
      }

      // ── Date range ──
      let inDateRange = true;
      if (filterState.waypointsDateRange.start || filterState.waypointsDateRange.end) {
        const startDate = filterState.waypointsDateRange.start || HomePageComponent.NO_LOWER_DATE_BOUND;
        const endDate = filterState.waypointsDateRange.end || new Date();

        const checkDate = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate()).getTime();
        const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()).getTime();
        const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()).getTime();

        inDateRange = checkDate >= start && checkDate <= end;
      }

      // ── User visibility (mine / captain / group / public) ──
      const creatorId = wp.audit_data?.create_user_id;
      const isMine = filterState.selectedUsers.mine && creatorId && this.myUserIds.includes(creatorId);

      if (isMine) {
        // Mine — pass through, check date range below
      } else if (creatorId && !this.myUserIds.includes(creatorId)) {
        if (!filterState.selectedUsers.shared) return false;

        // Alerts are anonymous/public: bypass per-captain & group gating, but
        // still respect the Public master.
        if (isAlertWp) {
          if (!capSets.publicOn) return false;
        } else {
          // Unified captain/group/public gate (identical to trips & the app).
          const wpFleetIds = wp.share_meta?.shared_with_fleet_ids || wp.shared_with?.fleets || [];
          let communityId: string | null = null;
          for (const fid of wpFleetIds) {
            const fleetId = typeof fid === 'string' ? fid : (fid?._id || fid?.id || fid?.community_id || '');
            if (fleetId) { communityId = String(fleetId); break; }
          }
          if (!this.isCaptainVisibleOnMap(creatorId, communityId, capSets)) return false;
        }
      } else {
        // No creator ID and not mine — respect mine toggle
        if (!filterState.selectedUsers.mine) return false;
      }

      // Place override: when online, place waypoints stay visible regardless
      // of the selected date range. Offline, fall through to the normal
      // time-window check so only synced-range places are shown.
      if (isPlaceWp && this.isBackendReachableForPlaceOverride()) {
        return true;
      }

      return inDateRange;
    });

    this.logWaypointTripGate(filteredWaypoints);

    // Diff update: remove only markers no longer in the filter, add only new ones.
    // This avoids clearing all markers at once which causes a visible flicker.
    const filteredIdSet = new Set(filteredWaypoints.map((wp) => this.getWaypointId(wp)));
    const focusedOverrideId = this.getFocusedWaypointOverrideId();
    const tripFocusIds = new Set(this.activeTripFocusWaypoints.map((w) => this.getWaypointId(w)));

    const keepMarkers: maplibregl.Marker[] = [];
    for (const m of this.displayedMarkers) {
      const id = m.getElement()?.getAttribute('data-wpid') || '';
      if (filteredIdSet.has(id) || id === focusedOverrideId || tripFocusIds.has(id)) {
        keepMarkers.push(m);
      } else {
        m.remove();
      }
    }
    this.displayedMarkers = keepMarkers;
    this.clearClusterMarkers();

    const shownIds = new Set(keepMarkers.map((m) => m.getElement()?.getAttribute('data-wpid') || ''));
    filteredWaypoints.forEach((wp) => {
      if (!shownIds.has(this.getWaypointId(wp))) this.addWaypointMarker(wp, false);
    });

    if (this.focusedWaypointOverride && focusedOverrideId &&
      !filteredIdSet.has(focusedOverrideId) && !shownIds.has(focusedOverrideId)) {
      this.addWaypointMarker(this.focusedWaypointOverride, false);
    }
    this.activeTripFocusWaypoints.forEach((wp) => {
      const id = this.getWaypointId(wp);
      if (!filteredIdSet.has(id) && !shownIds.has(id)) this.addWaypointMarker(wp, false);
    });
    if (this.activeTripFocusWaypoints.length) this.updateTripWaypointMarkerHighlights();
    this.tryPendingWaypointFocus();
    this.updateWaypointAggregation();
  }

  private getDedupedWaypoints(waypoints: any[]): any[] {
    const deduped = new globalThis.Map<string, any>();

    waypoints.forEach((wp) => {
      // Text posts are feed-only (Flutter TextPost.isFeedOnly): never drawn on the map.
      if (isFeedOnlyWaypoint(wp)) return;
      const waypointId = this.getWaypointId(wp);
      if (!waypointId) return;

      const existing = deduped.get(waypointId);
      if (!existing) {
        deduped.set(waypointId, wp);
        return;
      }

      const existingTs = existing?.audit_data?.create_ts || existing?.created_at || '';
      const incomingTs = wp?.audit_data?.create_ts || wp?.created_at || '';
      deduped.set(waypointId, incomingTs >= existingTs ? wp : existing);
    });

    return Array.from(deduped.values());
  }

  focusWaypointOnMap(id: string, position?: any, create_ts?: string, highlight: boolean = true, waypointData?: any) {
    if (!id) return;

    this.isAutoFollowEnabled = false;
    this.clearTripFocusState();
    this.updateTripFocusHighlight();
    const normalizedId = String(id).trim();
    if (this.getFocusedWaypointOverrideId() !== normalizedId) {
      this.focusedWaypointOverride = null;
    }
    const ensuredOverride = this.ensureFocusedWaypointOverride(normalizedId, position, create_ts, waypointData);
    this.pendingWaypointFocus = { id, position, create_ts, attempts: 0, highlight, overrideFetchStarted: false, waypointData };

    // Fly directly to coordinates immediately, matching Flutter's _focusWaypointFromProfile
    const lat = position?.latitude ?? position?.lat;
    const lng = position?.longitude ?? position?.lon;
    if (lat != null && lng != null && this.map && this.isMapLoaded) {
      this.flyToWaypointPresentation([lng, lat], 14);
    }

    if (ensuredOverride && this.map && this.isMapLoaded) {
      this.applyMapFilters();
      return;
    }
    this.tryPendingWaypointFocus();
  }

  private tryPendingWaypointFocus(): void {
    if (!this.pendingWaypointFocus || !this.map || !this.isMapLoaded) return;

    const { id, position, create_ts, highlight, waypointData } = this.pendingWaypointFocus;

    // Try to find an existing filtered marker first; if it is outside the
    // current date range, the focused waypoint override path below will
    // reveal it without mutating the user's active filters.
    const findAndShow = () => {
      const pending = this.pendingWaypointFocus;
      if (!pending) return;

      const marker = this.displayedMarkers.find(m => {
        const el = m.getElement();
        return el && el.getAttribute('data-wpid') === String(id).trim();
      });

      if (marker) {
        this.pendingWaypointFocus = null;
        this.flyToWaypointPresentation(marker.getLngLat(), 14, marker);
        if (highlight !== false) {
          this.highlightWaypointMarker(marker);
        } else {
          this.clearWaypointHighlight();
        }
      } else if (this.ensureFocusedWaypointOverride(String(id).trim(), position, create_ts, waypointData)) {
        this.applyMapFilters();
      } else if (position && (position.latitude || position.lat) && (position.longitude || position.lon)) {
        // Fallback to coordinate flyTo
        const lat = position.latitude || position.lat;
        const lng = position.longitude || position.lon;
        this.flyToWaypointPresentation([lng, lat], 14);
        if (!pending.overrideFetchStarted) {
          this.pendingWaypointFocus = { ...pending, overrideFetchStarted: true };
          this.waypointService.getWaypointById(String(id).trim()).subscribe({
            next: (res: any) => {
              const waypoint = res?.data || res;
              if (!waypoint) return;
              this.focusedWaypointOverride = waypoint;
              this.mergeWaypointData(waypoint);
              this.applyMapFilters();
            },
            error: () => {
              // ignore and keep coordinate fallback behavior
            },
          });
        }
        if (pending.attempts >= 10) {
          this.pendingWaypointFocus = null;
        }
      } else if (!pending.overrideFetchStarted) {
        this.pendingWaypointFocus = { ...pending, overrideFetchStarted: true };
        this.waypointService.getWaypointById(String(id).trim()).subscribe({
          next: (res: any) => {
            const waypoint = res?.data || res;
            if (!waypoint) return;
            this.focusedWaypointOverride = waypoint;
            this.mergeWaypointData(waypoint);
            this.applyMapFilters();
          },
          error: () => {
            // ignore and continue retrying visible markers
          },
        });
      } else if (pending.attempts >= 10) {
        console.warn('Waypoint focus target was not found on the map:', id);
        this.pendingWaypointFocus = null;
      } else {
        this.pendingWaypointFocus = {
          ...pending,
          attempts: pending.attempts + 1,
        };
        setTimeout(() => this.tryPendingWaypointFocus(), 200);
      }
    };

    // Small delay to allow marker rendering if filters were just applied
    setTimeout(findAndShow, 100);
  }

  private loadTripTracks(): void {
    if (!this.map || !this.isMapLoaded) return;
    this.fetchTripsPage(1, null, []);
    this.loadTripsFromPosts();
  }

  /**
   * Fetch and render the map's waypoint pins independently of (and before) the
   * vessel/observation chain and the heavy trip data. Firing this first means the
   * pins request gets a network connection immediately instead of waiting in the
   * queue behind the multi-megabyte trip pages, so pins appear quickly.
   */
  // API clamps page_size to 100 (waypoint/views.py), so page 1 tells us total_page and the rest
  // are fetched in parallel; capped so a huge fleet cannot flood the browser.
  private fetchAllWaypoints$(): Observable<any[]> {
    const extract = (res: any): any[] => {
      if (Array.isArray(res)) return res;
      if (Array.isArray(res?.data)) return res.data;
      if (Array.isArray(res?.results)) return res.results;
      return [];
    };
    return this.waypointService.getWaypoint('', 1, WAYPOINT_PAGE_SIZE).pipe(
      switchMap((first: any) => {
        const lastPage = Math.min(Number(first?.total_page) || 1, WAYPOINT_MAX_PAGES);
        if (lastPage <= 1) return of(extract(first));
        const rest = Array.from({ length: lastPage - 1 }, (_, i) =>
          this.waypointService.getWaypoint('', i + 2, WAYPOINT_PAGE_SIZE).pipe(catchError(() => of(null))),
        );
        return forkJoin(rest).pipe(
          map((pages) => {
            // Offset paging over a mutating set can repeat rows across pages.
            const byId = new globalThis.Map<string, any>();
            [first, ...pages].flatMap(extract).forEach((wp) => byId.set(this.getWaypointId(wp) || String(byId.size), wp));
            return [...byId.values()];
          }),
        );
      }),
    );
  }

  private loadInitialWaypoints(): void {
    this.fetchAllWaypoints$().subscribe({
      next: (waypoints: any[]) => {
        this.allWaypoints = [...this.allWaypoints, ...waypoints];
        // The map may still be loading its style/tiles when this data arrives.
        // Render now only if the map is ready; otherwise the map 'load' handler
        // will draw the already-fetched pins.
        if (this.isMapLoaded) {
          this.applyMapFilters();
          this.tryPendingWaypointFocus();
        }
      },
      error: (err) => console.error('Error loading initial waypoints:', err),
    });
  }

  private fetchTripsPage(page: number, cursor: string | null, collected: any[]): void {
    this.tripService.getAllTrip('', page, 100, cursor).subscribe({
      next: (res: any) => {
        const results = this.normalizeTripResults(res);
        const merged = [...collected, ...results];
        const nextCursor = res?.next_cursor || res?.nextCursor || null;

        if (nextCursor) {
          this.fetchTripsPage(page + 1, nextCursor, merged);
          return;
        }

        const sharedTrips = this.allTrips.filter((trip) => trip?._fromPosts === true);
        this.allTrips = [];
        merged.forEach((trip) => this.mergeTripData(trip));
        sharedTrips.forEach((trip) => this.mergeTripData(trip));
        this.applyTripFilters();
        this.handleActiveTrips();
        this.tryPendingTripFocus();
      },
      error: (err) => {
        console.error('Error loading trip tracks:', err);
      },
    });
  }

  private loadTripsFromPosts(): void {
    this.fetchTripsFromPostsState('in_progress', 1, []);
    this.fetchTripsFromPostsState('completed', 1, []);
  }

  private liveTripResolutionRan = false;

  /**
   * Decides, once per session, what the user's live trip is and which of the two
   * mutually exclusive modes to show: their own trip's status card (captain), or the
   * green ride-along overlay (rider). Every in-progress trip gets a timer, exactly
   * like the app.
   *
   * Both jobs are done here on purpose. They were previously two independent
   * heuristics that could disagree, and they did: the captain's own trip was not
   * recognised as theirs (so no timer) yet was still accepted as a ride-along (so a
   * green track) — the same trip classified both ways at once.
   *
   * The inputs are chosen to be authoritative rather than convenient:
   *   - GET /trip/ returns exactly the trips we own (write ACL) plus the ride-along
   *     trips we're a participant on, straight from mongo — so `audit_data` is intact.
   *     `allTrips` cannot be used for this: it comes from /trip/from-posts/, whose
   *     normalised shape drops the creator id, which is what made every ownership
   *     check silently fail.
   *   - The trip mates roster names the captain. That's the only trustworthy way to
   *     tell captain from rider: the participants endpoint returns 200 for the captain
   *     too (is_active_participant is true for them), so "the call succeeded" proves
   *     only that we're on the trip, never which side of it we're on.
   */
  private resolveMyLiveTrip(): void {
    if (this.liveTripResolutionRan) return;
    if (!this.myUserIds.length) return;
    this.liveTripResolutionRan = true;

    // page_size stays small on purpose: GET /trip/ computes waypoint counts, reaction
    // context and the ride-along participants map for every trip in the page, so a big
    // page costs seconds for nothing. Trips come back newest-first and a live trip is
    // by definition one of the newest, so the first page always contains it.
    this.tripService.getAllTrip(undefined, 1, 10).pipe(catchError(() => of(null))).subscribe((res: any) => {
      if (!res) {
        // Request failed (e.g. fired before the token was ready). Do NOT read this as
        // "no live trips" — that would clear a perfectly good status card. Allow the
        // later call from the trips feed to try again.
        this.liveTripResolutionRan = false;
        return;
      }
      const rows = this.normalizeTripResults(res) || [];
      const live = rows.filter((t: any) => this.isTripLive(t));
      console.debug('[HomePage] live trips from /trip/:', live.length);

      if (!live.length) {
        // Nothing live: drop a status card left over from a trip that has since been
        // ended elsewhere (e.g. on the phone), which would otherwise tick forever.
        if (this.tripService.getCurrentTripId()) {
          console.debug('[HomePage] no live trip — clearing stale current trip');
          this.tripService.setCurrentTripStartTime(null);
          this.tripService.setCurrentTripId('');
        }
        if (this.rideAlongService.getActiveRideAlong()) this.rideAlongService.setActiveRideAlong(null);
        return;
      }

      // This same response already carries `ride_along_participants` per trip
      // ({profile_id, display_name, image_url, role}), so captain-vs-rider is decided
      // from data we're holding — no second request, no error path that could leave a
      // live trip unclassified (and therefore with no timer), and no dependence on
      // audit_data surviving serialisation.
      const ownTrip = live.find((t: any) => this.isTripCaptainedByMe(t));
      if (ownTrip) { this.adoptOwnLiveTrip(ownTrip); return; }

      // Not the captain of any live trip → we're a rider on one.
      const trip = live[0];
      const captain = this.getTripCaptainRow(trip);
      console.debug('[RideAlong] following', this.getTripId(trip), 'captained by', captain?.display_name);
      this.tripService.setCurrentTripStartTime(null);
      this.tripService.setCurrentTripId('');
      this.rideAlongService.setActiveRideAlong({
        tripId: this.getTripId(trip),
        captainName: captain?.display_name || '',
        vesselName: trip?.vessel_name || '',
      });
    });
  }

  private getTripCaptainRow(trip: any): any {
    const parts = Array.isArray(trip?.ride_along_participants) ? trip.ride_along_participants : [];
    return parts.find((p: any) => String(p?.role || '').toLowerCase() === 'captain') || null;
  }

  /**
   * True when this live trip is ours to command rather than ride.
   *
   * GET /trip/ only ever returns trips we own or ride along on, so "no ride-along
   * captain named" means it's a plain trip of ours.
   */
  private isTripCaptainedByMe(trip: any): boolean {
    if (this.isTripMine(trip)) return true;
    const captain = this.getTripCaptainRow(trip);
    if (!captain) return true;
    const captainIds = [captain.profile_id, captain.keycloak_id, captain.user_id]
      .map((v: any) => String(v || '').trim())
      .filter(Boolean);
    if (!captainIds.length) return true;
    return captainIds.some((id: string) => this.myUserIds.includes(id));
  }

  /** Shows the normal status card for a trip we own, ticking from its real start. */
  private adoptOwnLiveTrip(trip: any): void {
    const tripId = this.getTripId(trip);
    if (!tripId) return;
    if (this.rideAlongService.getActiveRideAlong()) this.rideAlongService.setActiveRideAlong(null);
    if (this.tripService.getCurrentTripId() === tripId) return;

    const start = trip?.start_time || trip?.audit_data?.create_ts;
    const startMs = start ? Date.parse(this.withZ(String(start))) : NaN;
    this.tripService.setCurrentTripStartTime(isNaN(startMs) ? null : startMs);
    console.debug('[HomePage] adopting own live trip', tripId);
    this.tripService.setCurrentTripId(tripId);

    this.renderOwnLiveTripTrackNow(tripId);
  }

  /**
   * Draws our own live trip's track straight away, without waiting for the trips feed.
   *
   * The teal track system is fed by `allTrips`, which is populated from
   * /trip/from-posts/ — several MB across several pages. On a busy account that took
   * minutes, so the boat sat on the map with no track behind it long after the timer
   * appeared. GET /trip/ can't help here (it strips `track` from list responses), but
   * GET /trip/{id}/ returns the full trip including geometry, so fetch just the one
   * trip we care about and merge it in. The feed later merges the same trip by id —
   * mergeTripData dedupes — so this only ever front-runs it.
   */
  private renderOwnLiveTripTrackNow(tripId: string): void {
    this.tripService.getTripById(tripId).pipe(catchError(() => of(null))).subscribe((full: any) => {
      const t = full?.trip || full?.data || full;
      if (!t || this.tripService.getCurrentTripId() !== tripId) return;
      this.mergeTripData(t);
      if (this.isMapLoaded) {
        this.applyTripFilters();
        // The waypoint gate keys off the live trip, so its marks can appear too.
        this.applyMapFilters();
      }
    });
  }

  private fetchTripsFromPostsState(state: 'in_progress' | 'completed', page: number, collected: any[]): void {
    const pageSize = 100;
    this.tripService.getTripsFromPosts(state, page, pageSize).subscribe({
      next: (res: any) => {
        const pageTrips = this.normalizeTripsFromPostsResults(res, state);
        const merged = [...collected, ...pageTrips];
        const totalCount = Number(res?.count ?? res?.total_count ?? res?.totalCount ?? 0);
        const hasNext = !!(res?.next || res?.next_page || res?.nextPage || res?.next_cursor || res?.nextCursor);
        const hasMoreByCount = totalCount > 0 && merged.length < totalCount;
        const hasMoreByPageSize = totalCount <= 0 && hasNext && pageTrips.length > 0;

        // Render THIS page's trips immediately so trips appear progressively as
        // each page arrives, instead of staring at an empty map until every page
        // (potentially several MB) has finished downloading. mergeTripData dedupes
        // by id, so rendering incrementally is safe.
        pageTrips.forEach((trip) => this.mergeTripData(trip));
        this.applyTripFilters();

        // Which live trip we're on (and whether we're its captain or a rider) is
        // resolved from GET /trip/, not from this feed — see resolveMyLiveTrip.
        if (state === 'in_progress') this.resolveMyLiveTrip();

        if (hasMoreByCount || hasMoreByPageSize) {
          this.fetchTripsFromPostsState(state, page + 1, merged);
        } else if (this.isMapLoaded) {
          // Waypoints finish loading before the trips do, so the "active trips only"
          // gate can't know a waypoint's trip has ended on the first pass. Re-apply
          // once, when the trip list is complete. Done here rather than per page to
          // avoid rebuilding every marker on each page of trips.
          this.applyMapFilters();
        }
      },
      error: (err) => console.error(`Error loading ${state} trips from posts:`, err),
    });
  }

  private normalizeTripsFromPostsResults(res: any, state: 'in_progress' | 'completed'): any[] {
    const items = this.normalizeTripResults(res);
    return items
      .map((item: any) => {
        const trip = item?.trip_details || item?.trip || item?.trip_ref || item?.trip_data || item;
        if (!trip || typeof trip !== 'object') return null;
        const actionType = item?.action_type || trip?.action_type || 'shared';
        return {
          ...trip,
          trip_state: trip?.trip_state || trip?.state || trip?.status || state,
          action_type: actionType,
          _fromPosts: true,
          _fromPostsState: state,
          // Preserve the backend's visibility classification (who + via what) so the
          // map filter can build the Captains list and run the Public/Group cascade.
          _vis: this.buildVisClassification(item, trip, actionType, true),
        };
      })
      .filter((trip: any) => !!trip && !!this.getTripId(trip));
  }

  private normalizeTripResults(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.results)) return res.results;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.trips)) return res.trips;
    if (Array.isArray(res?.items)) return res.items;
    return [];
  }

  private handleActiveTrips(): void {
    // Find the first active trip owned by the user
    for (const trip of this.allTrips) {
      const state = (trip?.trip_state || trip?.status || '').toLowerCase();
      if (state === 'in_progress' || state === 'paused') {
        if (this.isTripMine(trip)) {
          const tripId = this.getTripId(trip);
          if (tripId) {
            console.debug('[HomePage] Auto-navigating to active trip:', tripId);
            // 1. Open the trip UI
            if (this.tripComponent) {
              this.tripComponent.onStartTrip(tripId);
            }
            // 2. Focus the map — on the BOAT (its latest position), NOT the whole
            // trip. A trip can span a huge area (e.g. SF to mid-continent); fitBounds
            // to it centred the camera on the United States before the vessel-follow
            // zoomed back in — that double camera move is the "clunky" load. Jump
            // straight to the boat instead (instant; the map is still hidden here).
            if (this.map) {
              // Do not auto-highlight active trip in purple on login auto-focus.
              this.clearTripFocusState();
              this.applyTripFilters();
              const boatCoord = this.getTripFocusCoord(trip);
              if (boatCoord) {
                this.map.jumpTo({ center: boatCoord, zoom: Math.max(this.map.getZoom(), 13) });
                this.hasCenteredOnceAfterLogin = true;
              } else {
                const bounds = this.getTripBounds(trip);
                if (bounds) {
                  this.map.fitBounds(bounds, {
                    padding: { top: 80, right: 80, bottom: 80, left: 80 },
                    duration: 1200,
                  });
                }
              }
            }
          }
        }
        // If an IN_PROGRESS trip is NOT owned by the user, we do nothing.
        // It will just be rendered in blue naturally by getTripLineFeatures logic.
      }
    }
    this.syncActiveTripLiveTracking();
  }

  private getTripId(trip: any): string {
    return trip?._id || trip?.id || trip?.trip_id || trip?.identifier || '';
  }

  private getTripDateValue(trip: any): string | null {
    return (
      trip?.start_ts ||
      trip?.start_time ||
      trip?.start_date ||
      trip?.audit_data?.create_ts ||
      trip?.created_at ||
      trip?.created_ts ||
      null
    );
  }

  /**
   * The trip's LATEST activity timestamp — the app's `_watchTripLatestObservationUtc`
   * anchor. Prefers an explicit end time, then the newest track-point timestamp, and
   * only falls back to the start when a trip carries neither.
   *
   * Needed because a trip is an interval, not an instant: matching the date range on
   * the start alone hides any multi-day trip that began before the window but ran
   * into it. See isTripVisibleByFilter.
   */
  private getTripAnchorDateValue(trip: any): string | null {
    const explicitEnd = trip?.end_ts || trip?.end_time || trip?.end_date || null;
    if (explicitEnd) return explicitEnd;

    // Newest ts on the track, if the geometry carries per-point timestamps.
    const features = trip?.track?.features || trip?.track_simplified?.features;
    if (Array.isArray(features)) {
      let newest: string | null = null;
      for (const feature of features) {
        const ts = feature?.properties?.ts_utc || feature?.properties?.ts || null;
        if (ts && (!newest || String(ts) > newest)) newest = String(ts);
      }
      if (newest) return newest;
    }

    return this.getTripDateValue(trip);
  }

  /** Undefined means "on" — the app's `_activeTripsOnly` defaults to true. */
  private isActiveTripsOnly(filterState: any): boolean {
    return filterState?.activeTripsOnly !== false;
  }

  /**
   * Whether a trip is still running. Single source of truth for the map's
   * active-trips-only behaviour: which tracklines survive, which boats draw, and
   * which waypoints the gate keeps.
   *
   * `stop_proposed` counts as live — the app treats it as in-progress everywhere
   * (`TripCardViewModel.tripStatusBadgeFromState`, `formatTripDuration`,
   * `_isTripStateLive`); it is a trip the client has *proposed* ending, not one that
   * has ended, and it reverts to in_progress if the captain dismisses the prompt.
   * Omitting it here made a trip drop off the map — trackline, boat and all its
   * waypoints — while it was still under way, and the web's own
   * `isTripOngoingState` already disagreed with this function on exactly that value.
   */
  private isTripLive(trip: any): boolean {
    const state = String(trip?.trip_state || trip?.status || '').trim().toLowerCase();
    return this.isTripOngoingState(state);
  }

  /**
   * True when a waypoint isn't tied to a trip at all, or is tied to one that's still
   * running. Anything tied to a finished trip is hidden under "active trips only".
   *
   * An unresolvable trip id counts as NOT live. `allTrips` is the shared/watch feed and
   * simply won't contain most finished trips, so treating "unknown" as live left every
   * old mark on the map — which is the whole complaint. The live trip we're actually on
   * is checked directly rather than via that feed, so the marks that matter never
   * depend on it.
   */
  private isWaypointOnLiveTrip(wp: any): boolean {
    const tripId = this.getWaypointTripId(wp);
    if (!tripId) {
      // Standalone mark. NOTE: a waypoint created on the web before trip binding was
      // fixed also lands here (it was stored with no trip at all) and is therefore
      // indistinguishable from a genuine standalone mark — see logWaypointTripGate.
      return true;
    }
    if (tripId === this.tripService.getCurrentTripId()) return true;
    if (tripId === this.rideAlongTripId) return true;
    const trip = this.allTrips.find((t) => this.getTripId(t) === tripId);
    return !!trip && this.isTripLive(trip);
  }

  private waypointGateLogged = false;

  /**
   * One-shot diagnostic for the "active trips only" waypoint gate. Prints why each
   * mark on the map survived, so a lingering old waypoint can be attributed to either
   * a filter bug (it has a trip, and we wrongly judged that trip live) or to orphaned
   * data (it has no trip id at all, which no client-side filter can distinguish from a
   * standalone mark).
   */
  private logWaypointTripGate(shown: any[]): void {
    if (this.waypointGateLogged || !shown.length) return;
    this.waypointGateLogged = true;
    const rows = shown.map((wp) => {
      const tripId = this.getWaypointTripId(wp);
      const trip = tripId ? this.allTrips.find((t) => this.getTripId(t) === tripId) : null;
      return {
        name: wp?.name || this.resolveWaypointTypeForMap(wp),
        tripId: tripId || '(none — orphaned or standalone)',
        tripState: trip ? (trip.trip_state || trip.status) : (tripId ? '(trip not in feed)' : '-'),
      };
    });
    console.debug('[WaypointGate] marks currently shown:', rows);
  }

  private isTripMine(trip: any): boolean {
    const creatorId = trip?.audit_data?.create_user_id || trip?.created_by?.identifier || trip?.created_by?.user_id;
    return !!creatorId && this.myUserIds.includes(String(creatorId));
  }

  private hasSharedAudience(trip: any): boolean {
    const sharedWith = trip?.shared_with || {};
    const friends = sharedWith?.friends || trip?.friends || [];
    const fleets = sharedWith?.fleets || trip?.fleets || [];

    const sharedIds = new Set<string>();
    [...friends, ...fleets].forEach((entry: any) => {
      const id = entry?.user_id || entry?.friend_id || entry?.community_id || entry?._id || entry?.id;
      if (id) {
        sharedIds.add(String(id));
      }
    });

    if ([...sharedIds].some((id) => this.myUserIds.includes(id))) {
      return true;
    }

    const privacy = (trip?.privacy || '').toLowerCase();
    const actionType = (trip?.action_type || '').toLowerCase();
    return privacy === 'custom' || privacy === 'public' || actionType === 'shared' || !!trip?.shared_with;
  }

  private isTripVisibleByFilter(trip: any, filterState: any): boolean {
    const creatorId = trip?.audit_data?.create_user_id || trip?.created_by?.identifier || trip?.created_by?.user_id;
    const isMine = !!creatorId && this.myUserIds.includes(String(creatorId));
    const isShared = !isMine && this.hasSharedAudience(trip);

    const dateValue = this.getTripDateValue(trip);
    let inDateRange = true;

    // A LIVE trip is never date-filtered — it is happening now, so when it started is
    // irrelevant. This mirrors the app, where the date range is applied only to
    // `_watchTripEntries` (completed trips); `_refilterWatchTripsInMemory` states it
    // outright: "Live shared tracklines now filter at build time from the authoritative
    // _watchLiveEntries polylines. Do not rebuild them here."
    //
    // Without this exemption a trip started before the window (a multi-day passage, or
    // simply one begun yesterday under the default 24h window) lost BOTH its trackline
    // and its boat here, since applyTripFilters feeds this same list to
    // updateSharedActiveTripMarkers.
    const isLive = this.isTripLive(trip);

    if (!isLive && dateValue && (filterState?.trackLinesDateRange?.start || filterState?.trackLinesDateRange?.end)) {
      const tripStart = new Date(dateValue);
      if (!isNaN(tripStart.getTime())) {
        const startDate = filterState.trackLinesDateRange.start || HomePageComponent.NO_LOWER_DATE_BOUND;
        const endDate = filterState.trackLinesDateRange.end || new Date();

        // Range in local whole days: [start 00:00:00, end 23:59:59.999].
        const rangeStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()).getTime();
        const rangeEnd = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999).getTime();

        // A trip is an INTERVAL, not an instant. Match the app's
        // _watchTripIntersectsDateRange: the trip must have begun no later than the end
        // of the range, and its latest activity must fall inside the range. Testing the
        // start alone (the previous behaviour) dropped every trip that began before the
        // window but ran into it.
        const anchorValue = this.getTripAnchorDateValue(trip);
        const anchorDate = anchorValue ? new Date(anchorValue) : tripStart;
        const anchor = isNaN(anchorDate.getTime()) ? tripStart.getTime() : anchorDate.getTime();

        inDateRange = tripStart.getTime() <= rangeEnd && anchor >= rangeStart && anchor <= rangeEnd;
      }
    }

    const mineEnabled = !!filterState?.selectedUsers?.mine;
    const sharedEnabled = !!filterState?.selectedUsers?.shared;

    // Unified captain/group/public gate for shared trips (identical to waypoints).
    if (!isMine && creatorId) {
      const sharedWith = trip?.shared_with || {};
      const fleets = sharedWith?.fleets || trip?.fleets || [];
      let communityId: string | null = null;
      for (const fleet of fleets) {
        const fleetId = typeof fleet === 'string' ? fleet : (fleet?.community_id || fleet?._id || fleet?.id || fleet?.identifier || '');
        if (fleetId) { communityId = String(fleetId); break; }
      }
      const capSets = this.buildCaptainGateSets(filterState);
      if (!this.isCaptainVisibleOnMap(String(creatorId), communityId, capSets)) {
        return false;
      }
    }

    return inDateRange && ((mineEnabled && isMine) || (sharedEnabled && isShared));
  }

  private mergeTripData(trip: any): any {
    const tripId = this.getTripId(trip);
    if (!tripId) return trip;

    const existingIndex = this.allTrips.findIndex((item) => this.getTripId(item) === tripId);
    if (existingIndex >= 0) {
      this.allTrips[existingIndex] = { ...this.allTrips[existingIndex], ...trip };
      return this.allTrips[existingIndex];
    }

    this.allTrips = [...this.allTrips, trip];
    return trip;
  }

  private extractTripCoordinates(trackData: any): number[][] {
    const coords: number[][] = [];

    const addCoord = (candidate: any) => {
      const coord = this.normalizeTripCoordinate(candidate);
      if (!coord) return;
      const prev = coords[coords.length - 1];
      if (prev && prev[0] === coord[0] && prev[1] === coord[1]) return;
      coords.push(coord);
    };

    const addGeometry = (geometry: any) => {
      if (!geometry) return;
      if (geometry.type === 'LineString' && Array.isArray(geometry.coordinates)) {
        geometry.coordinates.forEach((coord: any) => addCoord(coord));
      } else if (geometry.type === 'MultiLineString' && Array.isArray(geometry.coordinates)) {
        geometry.coordinates.forEach((segment: any) => {
          if (Array.isArray(segment)) {
            segment.forEach((coord: any) => addCoord(coord));
          }
        });
      } else if (geometry.type === 'Point') {
        addCoord(geometry.coordinates);
      }
    };

    if (!trackData) {
      return coords;
    }

    if (trackData.type === 'FeatureCollection' && Array.isArray(trackData.features)) {
      trackData.features.forEach((feature: any) => {
        if (feature?.geometry) {
          addGeometry(feature.geometry);
        } else if (feature?.type === 'Feature' && feature?.coordinates) {
          addCoord(feature.coordinates);
        }
      });
    } else if (trackData.type === 'Feature') {
      addGeometry(trackData.geometry);
    } else if (trackData.type === 'LineString' || trackData.type === 'MultiLineString' || trackData.type === 'Point') {
      addGeometry(trackData);
    } else if (Array.isArray(trackData.coordinates)) {
      trackData.coordinates.forEach((coord: any) => addCoord(coord));
    } else if (Array.isArray(trackData)) {
      trackData.forEach((coord: any) => addCoord(coord));
    }

    return coords;
  }

  private normalizeTripCoordinate(candidate: any): number[] | null {
    if (!candidate) return null;
    if (Array.isArray(candidate) && candidate.length >= 2) {
      const lng = Number(candidate[0]);
      const lat = Number(candidate[1]);
      if (Number.isNaN(lng) || Number.isNaN(lat)) return null;
      return [lng, lat];
    }
    if (candidate.longitude != null && candidate.latitude != null) {
      const lng = Number(candidate.longitude);
      const lat = Number(candidate.latitude);
      if (Number.isNaN(lng) || Number.isNaN(lat)) return null;
      return [lng, lat];
    }
    if (candidate.lng != null && candidate.lat != null) {
      const lng = Number(candidate.lng);
      const lat = Number(candidate.lat);
      if (Number.isNaN(lng) || Number.isNaN(lat)) return null;
      return [lng, lat];
    }
    if (candidate.lon != null && candidate.lat != null) {
      const lng = Number(candidate.lon);
      const lat = Number(candidate.lat);
      if (Number.isNaN(lng) || Number.isNaN(lat)) return null;
      return [lng, lat];
    }
    if (candidate.coordinates && Array.isArray(candidate.coordinates)) {
      return this.normalizeTripCoordinate(candidate.coordinates);
    }
    return null;
  }

  private createGeoJsonCircle(center: [number, number], radiusKm: number, points = 64): GeoJSON.Feature<GeoJSON.Polygon> {
    const coords: number[][] = [];
    const [lng, lat] = center;
    const R = 6371; // Earth's radius in km
    const d = Math.max(0.0001, radiusKm);

    const latRad = (lat * Math.PI) / 180;
    const lngRad = (lng * Math.PI) / 180;

    for (let i = 0; i <= points; i++) {
      const bearing = (i * 2 * Math.PI) / points;
      const lat2 = Math.asin(
        Math.sin(latRad) * Math.cos(d / R) + Math.cos(latRad) * Math.sin(d / R) * Math.cos(bearing)
      );
      const lng2 = lngRad + Math.atan2(
        Math.sin(bearing) * Math.sin(d / R) * Math.cos(latRad),
        Math.cos(d / R) - Math.sin(latRad) * Math.sin(lat2)
      );
      coords.push([(lng2 * 180) / Math.PI, (lat2 * 180) / Math.PI]);
    }

    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [coords],
      },
    };
  }

  private extractPosDetails(pos: any): { lat: number; lng: number } | null {
    if (!pos) return null;
    if (pos.latitude != null && pos.longitude != null) {
      return { lat: Number(pos.latitude), lng: Number(pos.longitude) };
    }
    if (pos.lat != null && pos.lon != null) {
      return { lat: Number(pos.lat), lng: Number(pos.lon) };
    }
    if (Array.isArray(pos.coordinates) && pos.coordinates.length >= 2 &&
      pos.coordinates[0] != null && pos.coordinates[1] != null) {
      return { lat: Number(pos.coordinates[1]), lng: Number(pos.coordinates[0]) };
    }
    return null;
  }

  // Bearing (degrees, 0=N clockwise) between two lat/lng points — matches Flutter's _bearingBetween
  private bearingBetween(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const toRad = Math.PI / 180;
    const φ1 = lat1 * toRad;
    const φ2 = lat2 * toRad;
    const Δλ = (lon2 - lon1) * toRad;
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
  }

  private getTripLineFeatures(trips: any[]): GeoJSON.FeatureCollection {
    const lineFeatures: GeoJSON.Feature<GeoJSON.LineString>[] = [];
    const pointFeatures: GeoJSON.Feature<GeoJSON.Point>[] = [];

    trips.forEach((trip: any) => {
      const tripId = this.getTripId(trip);
      const fullTrackData = trip?.track || trip?.trip_track || trip?.track_data;
      const simplifiedTrackData = trip?.track_simplified || fullTrackData;
      const lineTrackData = simplifiedTrackData;
      const pointTrackData = fullTrackData || simplifiedTrackData;
      const coords = this.extractTripCoordinates(lineTrackData);
      const pointCoords = this.extractTripCoordinates(pointTrackData);

      const state = (trip?.trip_state || '').trim().toLowerCase();
      const isOngoing = state === 'in_progress' || state === 'paused' || state === 'stop_proposed' || tripId === this.tripService.getCurrentTripId();

      if (!coords.length) {
        const startPos = this.extractPosDetails(trip?.start_position);
        const endPos = this.extractPosDetails(trip?.end_position);
        if (startPos && endPos) {
          coords.push([startPos.lng, startPos.lat]);
          if (startPos.lat !== endPos.lat || startPos.lng !== endPos.lng) {
            coords.push([endPos.lng, endPos.lat]);
          }
        }
      }

      if (coords.length < 2) return;

      lineFeatures.push({
        type: 'Feature',
        properties: {
          tripId,
          name: trip?.name || '',
          privacy: trip?.privacy || '',
          isOngoing: isOngoing ? 1 : 0,
          isMine: this.isTripMine(trip) ? 1 : 0,
          kind: 'trip-line',
        },
        geometry: {
          type: 'LineString',
          coordinates: coords,
        },
      });

      const pointCoordSet = new Set<string>();
      const addTripPoint = (coord: number[], extraProps: Record<string, any> = {}) => {
        const normalized = this.normalizeTripCoordinate(coord);
        if (!normalized) return;
        const key = `${normalized[0]},${normalized[1]} `;
        if (pointCoordSet.has(key)) return;
        pointCoordSet.add(key);
        pointFeatures.push({
          type: 'Feature',
          properties: {
            tripId,
            isOngoing: isOngoing ? 1 : 0,
            isMine: this.isTripMine(trip) ? 1 : 0,
            kind: 'trip-point',
            ...extraProps,
          },
          geometry: {
            type: 'Point',
            coordinates: normalized,
          },
        });
      };

      let hasExplicitTripPoints = false;
      if (pointTrackData && pointTrackData.type === 'FeatureCollection' && Array.isArray(pointTrackData.features)) {
        pointTrackData.features.forEach((feature: any) => {
          if (!feature || !feature.geometry) return;
          if (feature.geometry.type === 'Point') {
            const coord = this.normalizeTripCoordinate(feature.geometry.coordinates) || null;
            if (!coord) return;
            hasExplicitTripPoints = true;
            const props: any = {};
            const p = feature.properties || {};
            props.obsId = p._id || p.observation_id || p.obs_id || p.id || p.identifier || null;
            if (p.ts_utc) props.ts_utc = p.ts_utc;
            addTripPoint(coord, props);
          }
        });
      }

      // For ongoing trips, render every GPS coordinate as a dot (live tracking dots).
      // For completed trips, only the explicit observation Point features above are used —
      // raw GPS coordinates are not added so the default view is clean and no
      // observation endpoints are queried per zoom level.
      if (isOngoing) {
        if (!hasExplicitTripPoints) {
          const markerCoords = pointCoords.length ? pointCoords : coords;
          markerCoords.forEach((coord: number[]) => {
            addTripPoint(coord);
          });
        }
      }
    });

    return {
      type: 'FeatureCollection',
      features: [...lineFeatures, ...pointFeatures],
    };
  }

  private ensureTripTrackLayers(): void {
    if (!this.map || !this.isMapLoaded) return;

    const sourceData = this.getTripLineFeatures([]);
    this.tripTrackSourceDataCache = sourceData;
    if (!this.map.getSource(this.tripTrackSourceId)) {
      this.map.addSource(this.tripTrackSourceId, {
        type: 'geojson',
        data: sourceData,
      });
    }

    if (!this.map.getLayer(this.tripTrackLineLayerId)) {
      this.map.addLayer({
        id: this.tripTrackLineLayerId,
        type: 'line',
        source: this.tripTrackSourceId,
        filter: ['==', ['get', 'kind'], 'trip-line'],
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': [
            'case',
            ['==', ['get', 'isOngoing'], 0],
            '#a7adb5', // All Finished: Gray
            ['==', ['get', 'isMine'], 0],
            '#2EC4B6', // Others Active: Teal
            '#2f54eb'  // Mine Active: Blue
          ],
          'line-width': 3.2,
          'line-opacity': 0.92,
        },
      });
    }

    const shadowLayerId = `${this.tripTrackLineLayerId} -shadow`;
    if (!this.map.getLayer(shadowLayerId)) {
      this.map.addLayer({
        id: shadowLayerId,
        type: 'line',
        source: this.tripTrackSourceId,
        filter: ['==', ['get', 'kind'], 'trip-line'],
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': [
            'case',
            ['==', ['get', 'isOngoing'], 1],
            'rgba(47, 84, 235, 0.4)',
            '#d8dee6'
          ],
          'line-width': 5.4,
          'line-opacity': 0.22,
        },
      }, this.tripTrackLineLayerId);
    }

    if (!this.map.getLayer(this.tripTrackPointLayerId)) {
      this.map.addLayer({
        id: this.tripTrackPointLayerId,
        type: 'circle',
        source: this.tripTrackSourceId,
        // Observation points on completed trips are hidden by default.
        // They appear via tripTrackHighlightPointLayerId when the trip is selected.
        filter: ['all', ['==', ['get', 'kind'], 'trip-point'], ['==', ['get', 'isOngoing'], 1]],
        paint: {
          'circle-radius': 3,
          'circle-color': [
            'case',
            ['==', ['get', 'isMine'], 0],
            '#2EC4B6', // Others Active: Teal
            '#2f54eb'  // Mine Active: Blue
          ],
          'circle-opacity': 0.95,
          'circle-stroke-width': 1.2,
          'circle-stroke-color': '#ffffff',
        },
      });
    }

    if (!this.map.getLayer(this.tripTrackHighlightLineLayerId)) {
      this.map.addLayer({
        id: this.tripTrackHighlightLineLayerId,
        type: 'line',
        source: this.tripTrackSourceId,
        filter: ['==', ['get', 'tripId'], ''],
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          // Purple — the app's focus-highlight colour (home_screen_overlay_sync
          // `_lineFeature(focusPts, Color(0xFF6D28D9), 4.5)`). The web had grey
          // #6b7280 at width 5.8, which is mobile's *dots-on-demand* style
          // (Color(0xFF656566), 5.8) — a different highlight that mobile keeps
          // deliberately grey and explicitly does NOT use for profile focus.
          // Copying that one here is why a focused trip read as grey.
          'line-color': '#6D28D9',
          'line-width': 4.5,
          'line-opacity': 0.96,
        },
      });
    }

    if (!this.map.getLayer(this.tripTrackHighlightPointLayerId)) {
      this.map.addLayer({
        id: this.tripTrackHighlightPointLayerId,
        type: 'circle',
        source: this.tripTrackSourceId,
        filter: ['==', ['get', 'tripId'], ''],
        paint: {
          // highlight radius slightly larger than base points
          'circle-radius': 5,
          // Purple to match the focused line, like mobile's focused track dots.
          'circle-color': '#6D28D9',
          'circle-opacity': 0.95,
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff',
        },
      });
    }

    // Add live vessel connection layer
    if (!this.map.getSource(this.tripLiveConnectionSourceId)) {
      this.map.addSource(this.tripLiveConnectionSourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    if (!this.map.getLayer(this.tripLiveConnectionLayerId)) {
      this.map.addLayer({
        id: this.tripLiveConnectionLayerId,
        type: 'line',
        source: this.tripLiveConnectionSourceId,
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          // SOLID continuous blue line (matches the base trip-track colour) so the
          // captain's live track reads as one continuous line, like the app — not
          // the old dotted "leash".
          'line-color': '#2f54eb',
          'line-width': 3.2,
          'line-opacity': 0.92,
        },
      }, this.tripTrackPointLayerId); // insert underneath the points layer
    }

    // Trip track lines are often created after the vessel icon layers (track
    // data arrives async), which would leave the track rendering on top of the
    // boat. Keep the vessel icons above the track so the boat stays visible.
    this.moveVesselLayersAboveTrack();
  }

  /** Ensure vessel icon layers always render above the trip track lines. */
  private moveVesselLayersAboveTrack(): void {
    if (!this.map) return;
    const vesselLayerIds = [
      this.vesselClusterCircleLayerId,
      this.vesselClusterCircleLayerId + '-icon',
      this.vesselClusterCircleLayerId + '-badge',
      this.vesselClusterCountLayerId,
      this.vesselUnclusteredLayerId,
    ];
    vesselLayerIds.forEach((id) => {
      try {
        if (this.map!.getLayer(id)) this.map!.moveLayer(id);
      } catch (_) { /* ignore */ }
    });
  }

  private updateTripTrackSource(trips: any[]): void {
    if (!this.map || !this.isMapLoaded) return;
    this.ensureTripTrackLayers();

    this.tripTrackSourceDataCache = this.getTripLineFeatures(trips);
    const source = this.map.getSource(this.tripTrackSourceId) as any;
    if (source) {
      source.setData(this.tripTrackSourceDataCache as any);
    }
    this.updateTripFocusHighlight();
  }

  private applyTripFilters(): void {
    if (!this.map || !this.isMapLoaded) return;
    this.invalidateClusterCache();

    const filterState = this.mapFilterService.getFilterState();

    // When tracklines are disabled, hide all trip tracks
    if (filterState.tracklinesEnabled === false) {
      this.visibleTripIds = new Set<string>();
      this.updateTripTrackSource([]);
      this.updateSharedActiveTripMarkers([]);
      return;
    }

    let trips = this.allTrips.filter((trip) => this.isTripVisibleByFilter(trip, filterState));

    // "Active trips only" (app default): drop tracklines for trips that have ended, so
    // the map isn't littered with every past track that still falls inside the date
    // window. Boats are unaffected — updateSharedActiveTripMarkers only ever draws
    // live trips anyway, matching the app's rule that this filter never hides vessels.
    if (this.isActiveTripsOnly(filterState)) {
      trips = trips.filter((t) => this.isTripLive(t));
    }

    // Always include the actively focused trip in the track source so its track
    // is visible even when it falls outside the current date or user filter
    // (e.g. a Watch-feed trip from another user or a past-date trip).
    if (this.activeTripFocusId) {
      const focusedTrip = this.allTrips.find((t) => this.getTripId(t) === this.activeTripFocusId);
      if (focusedTrip && !trips.some((t) => this.getTripId(t) === this.activeTripFocusId)) {
        trips = [...trips, focusedTrip];
      }
    }

    // While following a ride-along, the followed trip is rendered by the dedicated
    // green ride-along layers/boat — exclude it here so it isn't ALSO drawn by the
    // normal (teal) trip-track + shared-boat system (which caused the dual render).
    if (this.rideAlongFollowActive && this.rideAlongTripId) {
      trips = trips.filter((t) => this.getTripId(t) !== this.rideAlongTripId);
    }

    this.visibleTripIds = new Set(trips.map((trip) => this.getTripId(trip)).filter((id) => !!id));
    this.updateTripTrackSource(trips);
    this.updateSharedActiveTripMarkers(trips);
    this.syncActiveTripLiveTracking();
    this.updateTripWaypointMarkerHighlights();
  }

  private isTripOngoingState(state: string): boolean {
    return state === 'in_progress' || state === 'paused' || state === 'stop_proposed';
  }

  private findTripLineFeature(tripId: string): GeoJSON.Feature<GeoJSON.LineString> | null {
    const line = this.tripTrackSourceDataCache.features.find((feature: any) =>
      feature?.geometry?.type === 'LineString' &&
      feature?.properties?.['kind'] === 'trip-line' &&
      String(feature?.properties?.['tripId']) === tripId
    );
    return (line as GeoJSON.Feature<GeoJSON.LineString>) || null;
  }

  private getLineCoordinatesFromFeature(feature: GeoJSON.Feature<GeoJSON.LineString> | null): number[][] {
    if (!feature?.geometry || !Array.isArray(feature.geometry.coordinates)) return [];
    return feature.geometry.coordinates as number[][];
  }

  private coordinatesMatch(a: number[] | null, b: number[] | null): boolean {
    if (!a || !b || a.length < 2 || b.length < 2) return false;
    return Number(a[0]) === Number(b[0]) && Number(a[1]) === Number(b[1]);
  }

  private buildObservationPointFeature(tripId: string, coord: number[], obsId?: string | null): GeoJSON.Feature<GeoJSON.Point> {
    const trip = this.allTrips.find((t) => this.getTripId(t) === tripId);
    const state = (trip?.trip_state || trip?.status || '').toLowerCase();
    return {
      type: 'Feature',
      properties: {
        tripId,
        kind: 'trip-point',
        isMine: trip ? (this.isTripMine(trip) ? 1 : 0) : 0,
        isOngoing: this.isTripOngoingState(state) ? 1 : 0,
        ...(obsId ? { obsId } : {})
      },
      geometry: {
        type: 'Point',
        coordinates: [coord[0], coord[1]]
      }
    };
  }

  private ensureTripLineFeatureForCoords(tripId: string, coordsToAppend: number[][]): GeoJSON.Feature<GeoJSON.LineString> | null {
    let lineFeature = this.findTripLineFeature(tripId);
    if (lineFeature) return lineFeature;
    if (!coordsToAppend.length || !this.visibleTripIds.has(tripId)) return null;

    const trip = this.allTrips.find((t) => this.getTripId(t) === tripId);
    if (!trip) return null;
    const state = (trip?.trip_state || trip?.status || '').trim().toLowerCase();
    const existingCoords = this.extractTripCoordinates(trip?.track || trip?.track_simplified);
    const seedCoords = [...existingCoords];
    coordsToAppend.forEach((coord) => {
      const prev = seedCoords[seedCoords.length - 1];
      if (!this.coordinatesMatch(prev || null, coord)) {
        seedCoords.push([coord[0], coord[1]]);
      }
    });

    if (seedCoords.length < 2) return null;

    lineFeature = {
      type: 'Feature',
      properties: {
        tripId,
        name: trip?.name || '',
        privacy: trip?.privacy || '',
        isOngoing: this.isTripOngoingState(state) ? 1 : 0,
        isMine: this.isTripMine(trip) ? 1 : 0,
        kind: 'trip-line',
      },
      geometry: {
        type: 'LineString',
        coordinates: seedCoords,
      },
    };
    this.tripTrackSourceDataCache.features.push(lineFeature);
    return lineFeature;
  }

  private appendCoordsToTripLine(tripId: string, coords: number[][]): boolean {
    if (!coords.length) return false;

    const normalized = coords
      .map((coord) => this.normalizeTripCoordinate(coord))
      .filter((coord): coord is number[] => !!coord);
    if (!normalized.length) return false;

    const lineFeature = this.ensureTripLineFeatureForCoords(tripId, normalized);
    if (!lineFeature) return false;

    const lineCoords = this.getLineCoordinatesFromFeature(lineFeature);
    let changed = false;
    normalized.forEach((coord) => {
      const last = lineCoords[lineCoords.length - 1] || null;
      if (!this.coordinatesMatch(last, coord)) {
        lineCoords.push([coord[0], coord[1]]);
        changed = true;
      }
    });
    return changed;
  }

  private appendTripPointIfMissing(tripId: string, coord: number[], obsId?: string | null): boolean {
    const existingPoint = this.tripTrackSourceDataCache.features.find((feature: any) => {
      if (feature?.geometry?.type !== 'Point') return false;
      if (feature?.properties?.['kind'] !== 'trip-point') return false;
      if (String(feature?.properties?.['tripId']) !== tripId) return false;
      const c = this.normalizeTripCoordinate(feature?.geometry?.coordinates);
      return !!c && this.coordinatesMatch(c, coord);
    });
    if (existingPoint) return false;
    this.tripTrackSourceDataCache.features.push(this.buildObservationPointFeature(tripId, coord, obsId));
    return true;
  }

  private commitTripSourceCache(): void {
    if (!this.map || !this.isMapLoaded) return;
    const source = this.map.getSource(this.tripTrackSourceId) as maplibregl.GeoJSONSource | null;
    if (!source) return;
    source.setData(this.tripTrackSourceDataCache as any);
    this.updateTripFocusHighlight();
  }

  private getTripTrackAnchorCoord(tripId: string): number[] | null {
    const tripPoints = this.tripTrackSourceDataCache.features
      .filter((feature: any) =>
        feature?.geometry?.type === 'Point' &&
        feature?.properties?.['kind'] === 'trip-point' &&
        String(feature?.properties?.['tripId']) === tripId
      ) as GeoJSON.Feature<GeoJSON.Point>[];

    if (tripPoints.length > 0) {
      const pointCoord = this.normalizeTripCoordinate(tripPoints[tripPoints.length - 1].geometry?.coordinates);
      if (pointCoord) return pointCoord;
    }

    const line = this.findTripLineFeature(tripId);
    const coords = this.getLineCoordinatesFromFeature(line);
    return coords.length ? coords[coords.length - 1] : null;
  }

  private pushLivePathCoords(coords: number[][]): void {
    coords.forEach((coord) => {
      const normalized = this.normalizeTripCoordinate(coord);
      if (!normalized) return;
      const prev = this.activeTripLivePath[this.activeTripLivePath.length - 1] || null;
      if (!this.coordinatesMatch(prev, normalized)) {
        this.activeTripLivePath.push([normalized[0], normalized[1]]);
      }
    });
  }

  private updateLiveTrackDottedPathSource(currentVessel?: [number, number]): void {
    if (!this.map || !this.isMapLoaded) return;
    const source = this.map.getSource(this.tripLiveConnectionSourceId) as maplibregl.GeoJSONSource | null;
    if (!source) return;

    // This layer carries the active trip's LIVE path (anchor → appended positions →
    // current boat). It is now drawn SOLID (see the layer's paint) so the captain's
    // track reads as one continuous blue line — matching the app — and it lives in
    // its own source so a feed-reload rebuild of the base trip-track layer can't
    // blank it out.
    const coords: number[][] = [];
    if (this.activeTripLiveAnchorCoord) {
      coords.push([this.activeTripLiveAnchorCoord[0], this.activeTripLiveAnchorCoord[1]]);
    }
    this.activeTripLivePath.forEach((coord) => coords.push([coord[0], coord[1]]));
    if (currentVessel) {
      const last = coords[coords.length - 1] || null;
      if (!this.coordinatesMatch(last, currentVessel)) {
        coords.push([currentVessel[0], currentVessel[1]]);
      }
    }

    if (coords.length < 2) {
      source.setData({ type: 'FeatureCollection', features: [] });
      return;
    }

    source.setData({
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coords,
        },
      }],
    } as any);
  }

  private clearLiveTrackDottedPath(): void {
    this.activeTripLiveAnchorCoord = null;
    this.activeTripLivePath = [];
    this.updateLiveTrackDottedPathSource();
  }

  private syncActiveTripLiveTracking(): void {
    const currentTripId = this.tripService.getCurrentTripId();
    if (!currentTripId) {
      this.stopExtendTrackPolling(true);
      return;
    }

    const activeTrip = this.allTrips.find((trip) => this.getTripId(trip) === currentTripId);
    if (!activeTrip) {
      this.stopExtendTrackPolling(false);
      return;
    }

    const state = (activeTrip?.trip_state || activeTrip?.status || '').toLowerCase();
    if (this.isTripMine(activeTrip) && this.isTripOngoingState(state)) {
      this.startExtendTrackPolling(currentTripId);
      return;
    }

    this.stopExtendTrackPolling(true);
  }

  private startExtendTrackPolling(tripId: string): void {
    if (!tripId) return;
    if (this.tripExtendTrackSub && this.extendTrackTripId === tripId) return;

    this.stopExtendTrackPolling(false);
    this.extendTrackTripId = tripId;
    this.activeTripLiveAnchorCoord = this.getTripTrackAnchorCoord(tripId);
    this.activeTripLivePath = [];
    this.updateLiveTrackDottedPathSource();

    this.tripExtendTrackSub = this.pollScheduler
      .poll$(() => this.tripService.extendTrack(tripId).pipe(catchError(() => of(null))), this.extendTrackPollingIntervalMs, { startAfter$: this.mapFirstPaint$ })
      .subscribe((response: any) => this.handleExtendTrackResponse(tripId, response));
  }

  private stopExtendTrackPolling(clearLivePath: boolean): void {
    if (this.tripExtendTrackSub) {
      this.tripExtendTrackSub.unsubscribe();
      this.tripExtendTrackSub = undefined;
    }
    this.extendTrackTripId = null;
    if (clearLivePath) {
      this.clearLiveTrackDottedPath();
    }
  }

  private extractTripCoordsFromExtendResponse(response: any, tripId: string): number[][] {
    if (!response) return [];

    const extractFromArray = (arr: any[]): number[][] =>
      arr.map((entry) => this.normalizeTripCoordinate(entry)).filter((coord): coord is number[] => !!coord);

    const directArrays = [
      response?.positions,
      response?.new_positions,
      response?.coordinates,
      response?.track_positions,
      response?.live_track_positions,
      response?.results,
      response?.data?.positions,
      response?.data?.new_positions,
      response?.data?.coordinates,
      response?.trip?.positions,
      response?.trip?.new_positions,
      response?.trip?.coordinates,
    ].filter((arr) => Array.isArray(arr));

    for (const arr of directArrays) {
      const coords = extractFromArray(arr);
      if (coords.length) return coords;
    }

    const updatedTrip = response?.trip || response?.data?.trip || response?.data || response;
    const updatedTrack = updatedTrip?.track || updatedTrip?.track_simplified;
    const updatedCoords = this.extractTripCoordinates(updatedTrack);
    if (!updatedCoords.length) return [];

    const currentLine = this.findTripLineFeature(tripId);
    const currentCoords = this.getLineCoordinatesFromFeature(currentLine);
    if (!currentCoords.length) return updatedCoords;

    let startAt = 0;
    for (let index = 0; index < updatedCoords.length; index++) {
      if (this.coordinatesMatch(updatedCoords[index], currentCoords[currentCoords.length - 1])) {
        startAt = index + 1;
      }
    }
    return updatedCoords.slice(startAt);
  }

  private handleExtendTrackResponse(tripId: string, response: any): void {
    if (!response || !tripId) return;

    const mergedTrip = response?.trip || response?.data?.trip || null;
    if (mergedTrip && this.getTripId(mergedTrip) === tripId) {
      this.mergeTripData(mergedTrip);
    }

    const extendedCoords = this.extractTripCoordsFromExtendResponse(response, tripId);
    if (!extendedCoords.length) return;

    const changed = this.appendCoordsToTripLine(tripId, extendedCoords);
    this.pushLivePathCoords(extendedCoords);
    this.activeTripLiveAnchorCoord = this.activeTripLiveAnchorCoord || this.getTripTrackAnchorCoord(tripId);
    this.updateLiveTrackDottedPathSource();

    if (changed) {
      this.commitTripSourceCache();
    }
  }

  private handleWaypointAddedForTripTrack(waypoint: any): void {
    const tripId = String(
      waypoint?.trip_id ||
      waypoint?.trip_ref?.identifier ||
      waypoint?.trip_ref?.reference_id ||
      ''
    ).trim();
    if (!tripId) return;

    const coord = this.normalizeTripCoordinate(waypoint?.position);
    if (!coord) return;

    let changed = false;
    changed = this.appendCoordsToTripLine(tripId, [coord]) || changed;
    changed = this.appendTripPointIfMissing(
      tripId,
      coord,
      String(waypoint?._id || waypoint?.id || waypoint?.identifier || '').trim() || null
    ) || changed;

    if (this.extendTrackTripId && this.extendTrackTripId === tripId) {
      this.activeTripLiveAnchorCoord = coord;
      this.activeTripLivePath = [];
      this.updateLiveTrackDottedPathSource();
    }

    if (changed) {
      this.commitTripSourceCache();
    }
  }

  private updateSharedActiveTripMarkers(visibleTrips: any[]): void {
    if (!this.map || !this.isMapLoaded) return;

    const activeTripIds = new Set<string>();

    visibleTrips.forEach(trip => {
      const state = (trip?.trip_state || trip?.status || '').toLowerCase();
      const isOngoing = state === 'in_progress' || state === 'paused';

      const creatorId = trip?.audit_data?.create_user_id || trip?.created_by?.identifier || trip?.created_by?.user_id;
      const isMine = !!creatorId && this.myUserIds.includes(String(creatorId));

      if (isOngoing && !isMine) {
        const tripId = this.getTripId(trip);
        const trackData = trip.track_simplified || trip.track || trip.trip_track || trip.track_data;
        const coords = this.extractTripCoordinates(trackData);

        let lastPos: number[] | null = null;
        if (coords.length > 0) {
          lastPos = coords[coords.length - 1];
        } else if (trip.end_position) {
          const ep = this.extractPosDetails(trip.end_position);
          if (ep) lastPos = [ep.lng, ep.lat];
        } else if (trip.start_position) {
          const sp = this.extractPosDetails(trip.start_position);
          if (sp) lastPos = [sp.lng, sp.lat];
        }

        if (lastPos && tripId) {
          activeTripIds.add(tripId);

          // Compute COG from last two track points — matches Flutter's _bearingBetween fallback
          let cog = 0;
          if (coords.length >= 2) {
            const p1 = coords[coords.length - 2]; // [lng, lat]
            const p2 = coords[coords.length - 1]; // [lng, lat]
            cog = this.bearingBetween(p1[1], p1[0], p2[1], p2[0]);
          }
          const mapBrg = this.map?.getBearing() ?? 0;

          const resolvedVesselId = String(
            trip?.vessel_id || trip?.vessel_ref?.vessel_id || trip?.vessel_ref?.reference_id || ''
          ).trim();
          const vesselId = resolvedVesselId || `trip-${tripId}`;

          // A boat reaching the map on BOTH feeds — the dockmate endpoint and the
          // tail of its own shared active trip — must be one entity, or the
          // aggregation engine buckets it twice and a lone vessel picks up a "2"
          // bubble that resolves back to a single boat when you zoom in (mobile
          // dedupes across its two live-position feeds for exactly this reason).
          //
          // Matching on vessel id alone is not enough: a trip that carries no
          // vessel reference falls back to the synthetic `trip-<id>` key, which can
          // never equal the dockmate's real vessel id, so the guard below used to
          // miss precisely the case it existed to catch. Fall back to position for
          // those, the same handle mobile uses for its keyless boats.
          const dockmateOwns = !!(
            this.findDockmateMarkerByVesselId(vesselId) ||
            (!resolvedVesselId && this.findDockmateMarkerNearPosition(lastPos[1], lastPos[0]))
          );
          if (!resolvedVesselId && dockmateOwns) {
            // Synthetic id + a dockmate already drawing this boat ⇒ drop it
            // entirely. Registering it would add a second store entry a few
            // metres from the dockmate's, and the engine counts distinct
            // positions — that is the inflated badge.
            const stale = this.sharedActiveTripMarkers[tripId];
            if (stale) {
              stale.remove();
              delete this.sharedActiveTripMarkers[tripId];
            }
            this.liveVesselStore.delete(vesselId);
            activeTripIds.delete(tripId);
            return;
          }

          // `trip/live/feed` items carry the vessel's `boat_skin` at the top level.
          this.skins.remember(vesselId, trip?.boat_skin);

          const existing = this.liveVesselStore.get(vesselId);
          const cv: ClusterableVessel = existing ? {
            ...existing,
            lat: lastPos[1],
            lng: lastPos[0],
            heading: cog,
            cog: cog,
            lastSeen: trip?.updated_at || trip?.created_at || existing.lastSeen,
          } : {
            id: vesselId,
            lat: lastPos[1],
            lng: lastPos[0],
            heading: cog,
            speed: null,
            isPrimary: false,
            cog: cog,
            lastSeen: trip?.updated_at || trip?.created_at || new Date().toISOString(),
          };
          this.registerVesselForClustering(cv);

          // If a dockmate DOM marker already owns this vessel, skip creating a
          // second shared-trip marker — it would duplicate the vessel in clustering
          // and show a second icon at the (possibly stale) trip track position.
          if (!this.sharedActiveTripMarkers[tripId] && !dockmateOwns) {
            const el = document.createElement('div');
            el.className = 'dockmate-marker';
            el.setAttribute('data-vessel-id', vesselId);
            const boatPx = vesselHullPx(isDesktopViewport());
            el.style.width = `${boatPx}px`;
            el.style.height = `${boatPx}px`;
            el.style.cursor = 'pointer';

            const img = document.createElement('img');
            img.src = this.skins.iconSrcFor(vesselId);
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.objectFit = 'contain';
            img.style.cursor = 'pointer';
            img.dataset['heading'] = String(cog);
            img.style.transform = `rotate(${cog + this.skins.headingOffsetFor(vesselId) - mapBrg}deg)`;
            el.appendChild(img);

            // Clicking the boat opens the observation popup (if any observations exist).
            // When there are no logged observations yet the handler is a safe no-op.
            const clickTripId = tripId;
            el.addEventListener('click', (ev) => {
              ev.stopPropagation();
              const m = this.sharedActiveTripMarkers[clickTripId];
              const ll = m?.getLngLat();
              const coord = ll ? [ll.lng, ll.lat] : [lastPos![0], lastPos![1]];
              this.ngZone.run(() => this.openSharedActiveTripObservation(clickTripId, coord));
            });

            const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
              .setLngLat([lastPos[0], lastPos[1]])
              .addTo(this.map);

            this.sharedActiveTripMarkers[tripId] = marker;
          } else {
            // `dockmateOwns` can be true while no shared marker was ever created,
            // in which case there is nothing to update here. Reading `.getElement()`
            // off the undefined slot threw, and because this runs inside a forEach
            // the throw abandoned every remaining trip in the batch — leaving their
            // boats unregistered, which is one way markers stopped aggregating.
            const marker = this.sharedActiveTripMarkers[tripId];
            if (marker) {
              marker.getElement().setAttribute('data-vessel-id', vesselId);
              marker.setLngLat([lastPos[0], lastPos[1]]);
              const img = marker.getElement().querySelector('img');
              if (img) {
                // Art offset: +30 for boat_other_user.png (the app uses the same constant), 0 for a
                // bow-up skin. Draw and update must agree or the boat jumps off-heading on first move.
                img.src = this.skins.iconSrcFor(vesselId);
                img.dataset['heading'] = String(cog);
                img.style.transform = `rotate(${cog + this.skins.headingOffsetFor(vesselId) - mapBrg}deg)`;
              }
            }
          }
        }
      }
    });

    Object.keys(this.sharedActiveTripMarkers).forEach(id => {
      if (!activeTripIds.has(id)) {
        const removedMarker = this.sharedActiveTripMarkers[id];
        const removedVesselId = String(removedMarker.getElement()?.getAttribute('data-vessel-id') || '').trim();
        removedMarker.remove();
        delete this.sharedActiveTripMarkers[id];
        // Remove the ghost entry from the clustering store unless a dockmate
        // marker still needs it (real vessel_id shared with updateDockmateMarkers).
        if (removedVesselId && !this.findDockmateMarkerByVesselId(removedVesselId)) {
          this.liveVesselStore.delete(removedVesselId);
        }
      }
    });
  }

  /** Returns true if the trip's track GeoJSON contains at least one explicit
   *  Point feature carrying an observation id. Kept for use by other callers;
   *  vessel marker creation no longer requires this — all live trips with a known
   *  position show a vessel icon on the map. */
  private tripHasObservationPoints(trip: any): boolean {
    const trackData = trip?.track || trip?.trip_track || trip?.track_data;
    if (!trackData || trackData.type !== 'FeatureCollection' || !Array.isArray(trackData.features)) {
      return false;
    }
    return trackData.features.some((f: any) => {
      if (f?.geometry?.type !== 'Point') return false;
      const p = f.properties || {};
      return !!(p._id || p.observation_id || p.obs_id || p.id || p.identifier);
    });
  }

  /**
   * Opens the observation popup for a shared active-trip boat when its icon is clicked.
   * The boat sits on the trip's most recent track point, so we locate that trip's
   * rendered points and use the one carrying an observation id nearest the boat.
   * Mirrors the map-click behaviour (fetchAndShowObservation) but works when the
   * click lands on the boat marker element itself rather than the tiny 3px point.
   */
  private openSharedActiveTripObservation(tripId: string, coord: number[]): void {
    if (!this.map || !tripId) return;
    this.closeWaypointCard();

    const pointLayers = [this.tripTrackPointLayerId, this.tripTrackHighlightPointLayerId]
      .filter((layerId) => !!this.map!.getLayer(layerId));
    if (pointLayers.length === 0) return;

    let obsId: string | null = null;
    let obsCoord: number[] = coord;
    try {
      // Query the whole viewport for this trip's points that carry an observation id,
      // then pick the one geographically closest to the boat's current position.
      const features = this.map.queryRenderedFeatures({ layers: pointLayers } as any) as any[];
      const mine = (features || []).filter(
        (f) => String(f?.properties?.tripId || '') === String(tripId) && f?.properties?.obsId
      );
      let bestDist = Infinity;
      for (const f of mine) {
        const c = f.geometry?.coordinates;
        if (!Array.isArray(c) || c.length < 2) continue;
        const d = (Number(c[0]) - coord[0]) ** 2 + (Number(c[1]) - coord[1]) ** 2;
        if (d < bestDist) {
          bestDist = d;
          obsId = String(f.properties.obsId);
          obsCoord = [Number(c[0]), Number(c[1])];
        }
      }
    } catch (_) { /* ignore query errors */ }

    if (obsId) {
      this.fetchAndShowObservation(obsId, obsCoord, tripId);
    }
  }

  private updateTripFocusHighlight(): void {
    if (!this.map || !this.isMapLoaded) return;
    this.invalidateClusterCache();
    const targetId = this.activeTripFocusId || '';
    const lineFilter = ['all', ['==', ['get', 'kind'], 'trip-line'], ['==', ['get', 'tripId'], targetId]] as any;
    const pointFilter = ['all', ['==', ['get', 'kind'], 'trip-point'], ['==', ['get', 'tripId'], targetId]] as any;

    if (this.map.getLayer(this.tripTrackHighlightLineLayerId)) {
      this.map.setFilter(this.tripTrackHighlightLineLayerId, lineFilter);
    }
    if (this.map.getLayer(this.tripTrackHighlightPointLayerId)) {
      this.map.setFilter(this.tripTrackHighlightPointLayerId, pointFilter);
    }

    this.updateTripWaypointMarkerHighlights();
  }

  private focusTripOnMap(id: string, tripData?: any, create_ts?: string): void {
    if (!id) return;
    this.clearWaypointHighlight();
    this.activeTripWaypointHighlightTripId = id;
    this.activeTripFocusWaypoints = [];
    this.pendingTripFocus = { id, tripData, create_ts, attempts: 0 };
    this.tripService.getWaypointsByTrip(id).subscribe({
      next: (res: any) => {
        const tripWaypoints = this.normalizeWaypointResults(res);
        if (!tripWaypoints.length) return;
        // Stamp trip_id so updateTripWaypointMarkerHighlights can match markers
        // by data-trip-id, and so the override bypass in applyMapFilters works.
        const stamped = tripWaypoints.map((wp: any) => ({ ...wp, trip_id: id }));
        stamped.forEach((waypoint: any) => this.mergeWaypointData(waypoint));
        this.activeTripFocusWaypoints = stamped;
        this.applyMapFilters();
      },
      error: () => {
        // ignore: trip focus still works from already-loaded track data
      },
    });
    if (tripData) {
      this.submenuService.pendingTripData = tripData;
      this.mergeTripData(tripData);
    }
    // Do NOT call ensureTripDateInFilter — that mutates the global tracklines
    // date filter and changes which other trips are visible on the map.
    // The highlight layers filter by trip ID directly and need no date change.
    this.tryPendingTripFocus();
  }

  private ensureTripDateInFilter(create_ts?: string): void {
    if (!create_ts) return;

    const tripDate = new Date(create_ts);
    if (isNaN(tripDate.getTime())) return;

    const filterState = this.mapFilterService.getFilterState();
    let changed = false;
    const range = filterState.trackLinesDateRange;

    if (!range.start || tripDate < range.start) {
      range.start = new Date(tripDate.getFullYear(), tripDate.getMonth(), tripDate.getDate());
      changed = true;
    }
    if (!range.end || tripDate > range.end) {
      range.end = new Date(tripDate.getFullYear(), tripDate.getMonth(), tripDate.getDate(), 23, 59, 59);
      changed = true;
    }

    if (changed) {
      this.mapFilterService.setFilterState(filterState);
    }
  }

  private getTripBounds(trip: any): maplibregl.LngLatBounds | null {
    const coords = this.extractTripCoordinates(trip?.track_simplified || trip?.track);
    if (!coords.length) return null;

    const bounds = new maplibregl.LngLatBounds();
    coords.forEach(([lng, lat]) => bounds.extend([lng, lat]));
    return bounds.isEmpty() ? null : bounds;
  }

  /**
   * Best-known position of a trip's boat for INITIAL framing: the live primary
   * vessel if we're already tracking it, else the trip's end position, else the
   * last track coordinate. Login focuses on the boat with this — not the middle of
   * a track that can stretch across the country (which fitBounds would centre on).
   */
  private getTripFocusCoord(trip: any): [number, number] | null {
    const primary = Array.from(this.liveVesselStore.values())
      .find((v: any) => v.isPrimary && v.lat != null && v.lng != null) as any;
    if (primary) return [primary.lng, primary.lat];
    const end = this.normalizeTripCoordinate(trip?.end_position ?? trip?.endPosition);
    if (end) return [end[0], end[1]];
    const coords = this.extractTripCoordinates(trip?.track_simplified || trip?.track);
    if (coords.length) {
      const last = coords[coords.length - 1];
      return [last[0], last[1]];
    }
    return null;
  }

  private tryPendingTripFocus(): void {
    if (!this.pendingTripFocus || !this.map || !this.isMapLoaded) return;

    const pending = this.pendingTripFocus;
    const candidate = this.allTrips.find((trip) => this.getTripId(trip) === pending.id) || pending.tripData;
    const trip = candidate ? this.mergeTripData(candidate) : null;

    if (!trip) {
      if (pending.attempts >= 12) {
        this.pendingTripFocus = null;
      } else {
        this.pendingTripFocus = { ...pending, attempts: pending.attempts + 1 };
        setTimeout(() => this.tryPendingTripFocus(), 200);
      }
      return;
    }

    const bounds = this.getTripBounds(trip);
    if (bounds) {
      this.activeTripFocusId = this.getTripId(trip);
      this.applyTripFilters();
      this.map.fitBounds(bounds, {
        padding: { top: 80, right: 80, bottom: 80, left: 80 },
        duration: 1200,
      });
      this.updateTripFocusHighlight();
      this.pendingTripFocus = null;
    } else if (pending.attempts >= 12) {
      this.pendingTripFocus = null;
    } else {
      this.pendingTripFocus = { ...pending, attempts: pending.attempts + 1 };
      setTimeout(() => this.tryPendingTripFocus(), 200);
    }
  }

  openUserProfile(): void {
    if (this.isDockmateCardOpen) {
      this.closeDockmateCard();
    }
    this.submenuService.setUserProfileOpenState(true);
  }

  openWatchPage(): void {
    if (this.isFilterOpen) return; // safety guard
    this.submenuService.setWatchPageModalState(true);
  }

  openOverlayWithSelection(type: string): void {
    if (!type) return;
    this.submenuService.openOverlayWithSelection(type);
  }

  /**
   * True when the map isn't showing everything — drives the blue cue on the
   * filter button, mirroring the app's `filtersActive` FAB shade (e.g. it turns
   * blue as soon as a non-default date range is applied).
   */
  get mapFiltersActive(): boolean {
    return this.mapFilterService.filtersAreActive;
  }

  openMapFilter(initialTab: 'filters' | 'styles' = 'filters'): void {
    this.submenuService.openFilterModal(initialTab);
  }

  /**
   * Classify WHY a single visible item (trip/waypoint post) is visible, using the
   * fields the backend already computes on the feed:
   *   - reason 'public' : action_type 'created' OR privacy 'public'
   *   - reason 'group'  : shared via a fleet (community_ref present) — captures groupId
   *   - reason 'direct' : shared directly with me (shared, no community_ref)
   */
  private buildVisClassification(item: any, resource: any, actionType: any, isTrip: boolean = false): any {
    const sp = item?.source_profile || {};
    const captainId = String(
      sp.user_id || resource?.audit_data?.create_user_id || item?.audit_data?.create_user_id || ''
    ).trim();
    const groupId = String(item?.community_ref?.identifier || '').trim();
    const privacy = String(resource?.privacy || item?.resource_privacy || '').toLowerCase();
    const action = String(actionType || item?.action_type || '').toLowerCase();

    let reason: 'public' | 'group' | 'direct';
    if (action === 'created' || privacy === 'public') {
      reason = 'public';
    } else if (groupId) {
      reason = 'group';
    } else {
      reason = 'direct';
    }

    const vis = {
      captainId,
      captainName: sp.name || item?.creator_name || resource?.creator_name || '',
      captainImage: sp.image_url || null,
      captainVessel: sp.vessel_name || resource?.vessel_name || null,
      groupId: groupId || null,
      reason,
    };
    // Accumulate the author into the persisted feed-captain cache as the feed
    // streams in (mirrors the app's _harvestFeedCaptains + Hive persistence) so
    // the Captains roster keeps every captain ever seen, not just the current load.
    // IMPORTANT: the app harvests captains ONLY from trips (_watchTripEntries /
    // _watchLiveEntries), never from waypoints — so we gate this on isTrip to
    // avoid extra people + wrong vessel names leaking in from waypoint authors.
    if (isTrip) this.accumulateFeedCaptain(vis);
    return vis;
  }

  // ── Feed-captain cache (persists across sessions, per account, capped 2000) ──
  private feedCaptainCache: Record<string, { id: string; name: string; vessel_name: string | null; image_url: string | null }> | null = null;
  // v2: scoped to trips only (v1 wrongly included waypoint authors) — bumping the
  // key discards any polluted v1 cache already in the browser.
  private readonly FEED_CAPTAIN_CACHE_KEY = 'map_filter_feed_captains_v2';

  private currentOwnerId(): string {
    return String(this.authService.getUserIdFromToken() || this.authService.getUserObjectId() || '').trim();
  }

  private loadFeedCaptainCache(): Record<string, any> {
    if (this.feedCaptainCache) return this.feedCaptainCache;
    this.feedCaptainCache = {};
    try {
      const raw = localStorage.getItem(this.FEED_CAPTAIN_CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const owner = String(parsed?.ownerId || '');
        if (owner && owner === this.currentOwnerId() && parsed?.captains && typeof parsed.captains === 'object') {
          this.feedCaptainCache = parsed.captains;
        }
      }
    } catch { /* ignore corrupt cache */ }
    return this.feedCaptainCache!;
  }

  private saveFeedCaptainCache(): void {
    try {
      const cache = this.feedCaptainCache || {};
      let entries = Object.values(cache);
      if (entries.length > 2000) entries = entries.slice(entries.length - 2000);
      const capped: Record<string, any> = {};
      for (const e of entries) capped[(e as any).id.toLowerCase()] = e;
      this.feedCaptainCache = capped;
      localStorage.setItem(this.FEED_CAPTAIN_CACHE_KEY, JSON.stringify({ ownerId: this.currentOwnerId(), captains: capped }));
    } catch { /* ignore quota errors */ }
  }

  private accumulateFeedCaptain(vis: any): void {
    const id = String(vis?.captainId || '').trim();
    if (!id) return;
    const lid = id.toLowerCase();
    if ((this.myUserIds || []).some((u) => String(u).trim().toLowerCase() === lid)) return;
    const cache = this.loadFeedCaptainCache();
    const prev = cache[lid] || { id, name: '', vessel_name: null, image_url: null };
    const vessel = String(vis.captainVessel || '').trim();
    cache[lid] = {
      id,
      name: String(vis.captainName || '').trim() || prev.name,
      vessel_name: (vessel && vessel !== 'No Vessel') ? vessel : prev.vessel_name,
      image_url: vis.captainImage || prev.image_url,
    };
  }

  /**
   * Build the feed arm of the Captains roster — the people whose trip data has been
   * on your map. Mirrors the app's _feedDerivedCaptains EXACTLY: a per-account set
   * that PERSISTS across sessions (the app keeps up to 2000 in Hive) and is merged
   * with whoever is on the map now. The app therefore lists every captain whose
   * trip/live data has appeared on your map — not only authors of trips loaded in
   * the current session, which is why the app's list is larger and stable.
   * (Trips only — never waypoints — matching the app.)
   *
   * NOTE: the app also harvests LIVE-boat owners (_watchLiveEntries); the web does
   * not yet, because that endpoint identifies owners by name, not id, and needs the
   * name→id resolver the app has. A captain whose ONLY presence is a moving boat
   * (no trip ever seen) will still be missing until that resolver is ported.
   */
  buildVisibleCaptains(): any[] {
    const norm = (v: any) => String(v ?? '').trim().toLowerCase();
    const mine = new Set((this.myUserIds || []).map(norm));

    // Fold the currently-loaded trips into the persisted per-account cache, then
    // persist — so a captain seen this session is remembered next session too.
    (this.allTrips || []).forEach((t: any) => {
      const v = t?._vis;
      if (v && v.captainId) this.accumulateFeedCaptain(v);
    });
    this.saveFeedCaptainCache();

    // Return the whole accumulated set (minus me) — the app's persisted roster arm.
    const out = Object.values(this.loadFeedCaptainCache())
      .filter((c: any) => c && c.id && !mine.has(norm(c.id)))
      .map((c: any) => ({
        id: c.id,
        name: c.name || 'Unknown',
        image_url: c.image_url || null,
        vessel_name: c.vessel_name || null,
      }));

    // ── TEMP DIAGNOSTICS (remove after debugging) ───────────────────────────
    try {
      const tripAuthors = (this.allTrips || []).filter((t: any) => t?._vis?.captainId).length;
      const tripsNoVis = (this.allTrips || []).filter((t: any) => !t?._vis).length;
      console.log('%c[MapFilter] buildVisibleCaptains', 'color:#34C759;font-weight:bold', {
        allTrips: (this.allTrips || []).length,
        tripsWithCaptain: tripAuthors,
        tripsMissingVis: tripsNoVis,
        cacheSize: Object.keys(this.loadFeedCaptainCache()).length,
        returned: out.length,
        names: out.map((c) => c.name),
      });
    } catch (e) { /* diagnostics only */ }

    return out;
  }

  // ── Unified captain/group visibility gate (mirrors the mobile app) ──────────
  private buildCaptainGateSets(filterState: any): {
    hiddenCaptains: Set<string>; hiddenGroups: Set<string>;
    shown: Set<string>; connections: Set<string>; publicOn: boolean;
  } {
    const norm = (v: any) => String(v ?? '').trim().toLowerCase();
    return {
      hiddenCaptains: new Set<string>((filterState?.hiddenCaptainUserIds ?? []).map(norm)),
      hiddenGroups: new Set<string>((filterState?.hiddenGroupIds ?? filterState?.hiddenFleetIds ?? []).map(norm)),
      shown: new Set<string>((filterState?.shownCaptainUserIds ?? []).map(norm)),
      connections: new Set<string>((filterState?.connectionUserIds ?? []).map(norm)),
      publicOn: filterState?.publicEnabled ?? true,
    };
  }

  /**
   * Single source of truth for whether a non-mine item is visible, identical to
   * the app's `_isCaptainVisible`:
   *  1. an explicitly-hidden captain is never shown (wins over everything);
   *  2. a hidden group's content is hidden unless the captain is re-checked (pierce);
   *  3. a non-connection (not a dockmate / fleet member) is hidden when Public is off.
   */
  private isCaptainVisibleOnMap(
    creatorId: string | null | undefined,
    communityId: string | null | undefined,
    sets: { hiddenCaptains: Set<string>; hiddenGroups: Set<string>; shown: Set<string>; connections: Set<string>; publicOn: boolean }
  ): boolean {
    const id = String(creatorId ?? '').trim().toLowerCase();
    const hasAuthor = id.length > 0;

    if (hasAuthor && sets.hiddenCaptains.has(id)) return false;

    const cid = String(communityId ?? '').trim().toLowerCase();
    if (cid && sets.hiddenGroups.has(cid) && !(hasAuthor && sets.shown.has(id))) return false;

    const isConnection = hasAuthor && sets.connections.has(id);
    if (!isConnection && !sets.publicOn) return false;

    return true;
  }

  closeWaypointManager(): void {
    this.submenuService.closeWaypointManager();
  }

  private refreshUserSettingsFromApi(options: { refreshWaypointMarkers: boolean }): void {
    const userId = this.authService.getUserIdFromToken() || this.authService.getUserObjectId();
    if (!userId) {
      this.applyCurrentMapOrientation();
      return;
    }

    this.userProfileService.getProfileById(userId).subscribe({
      next: (res: any) => {
        this.userSettings = res?.settings || null;
        this.myProfileNames = this.buildMyProfileNameCandidates(res);
        this.myDisplayName = String(
          res?.name || res?.display_name
          || `${res?.first_name || ''} ${res?.last_name || ''}`.trim()
          || ''
        ).trim();
        this.publishMapOrientationFromSettings(this.userSettings);
        this.clarityService.setUserProfileTags({
          name: res?.name || res?.full_name || this.myProfileNames?.[0],
          email: res?.email,
          vesselName: this.primaryVesselName || res?.primary_vessel?.name || res?.vessel_name,
          distanceUnit: this.userSettings?.distance_unit,
          speedUnit: this.userSettings?.speed_unit,
          gpsType: this.userSettings?.gps_type,
          friendsCount: res?.friends_count ?? res?.friendsCount,
          communitiesCount: res?.communities_count ?? res?.communitiesCount,
          activitiesCount: res?.activities_count ?? res?.activitiesCount,
        });
        console.debug('[HomePage] User settings refreshed', this.userSettings);
        if (options.refreshWaypointMarkers) {
          this.refreshWaypointMarkers();
        }
      },
      error: (err) => {
        console.error('[HomePage] Error refreshing profile settings', err);
        this.applyCurrentMapOrientation();
      },
    });
  }

  private isNorthUpMapOrientation(): boolean {
    return this.isNorthUpRotationLockActive;
  }

  private publishMapOrientationFromSettings(settings: any): void {
    const mode = settings?.map_orientation?.mode;
    if (mode === 'normal' || mode === 'north_up') {
      this.submenuService.setMapOrientationMode(mode);
    }
  }

  private lockMapBearingNorth(): void {
    if (!this.map) return;
    if (Math.abs(this.map.getBearing()) > 0.01) {
      this.map.setBearing(0);
    }
  }

  private applyMapOrientationMode(mode: MapOrientationMode): void {
    this.mapOrientationMode = mode;
    this.isNorthUpRotationLockActive = mode === 'north_up';

    if (!this.map) return;

    if (this.isNorthUpRotationLockActive) {
      this.lockMapBearingNorth();
      this.map.dragRotate.disable();
      this.map.touchZoomRotate.disableRotation();
      this.map.keyboard.disableRotation();
      return;
    }

    this.map.dragRotate.enable();
    this.map.touchZoomRotate.enable();
    this.map.touchZoomRotate.enableRotation();
    this.map.keyboard.enable();
    this.map.keyboard.enableRotation();
  }

  private applyCurrentMapOrientation(): void {
    this.applyMapOrientationMode(this.submenuService.getCurrentMapOrientationMode());
  }

  private applyMapOrientationSettings(): void {
    this.applyCurrentMapOrientation();
  }

  private initMap(): void {
    // Create the map OUTSIDE Angular's zone. MapLibre's render loop
    // (requestAnimationFrame) and its pointer/move DOM listeners would otherwise
    // run as zone-patched tasks, firing a full ApplicationRef.tick() on every
    // animation frame during pan/zoom — the root cause of the "claggy" dragging.
    // Discrete map events that need Angular re-enter the zone explicitly via
    // this.ngZone.run(...) in their individual handlers below.
    // Inject the Keycloak Bearer on requests to our own API host — MapLibre
    // tile fetches (raster contour tiles etc.) don't go through Angular's
    // HttpClient interceptor, so without this they'd 401 on any authed layer.
    // Anything hitting a different host is left untouched so cross-origin tile
    // sources (MapTiler etc.) still work correctly.
    const apiHost = (() => {
      try { return new URL(environment.apiUrl).host; } catch { return ''; }
    })();
    const authService = this.authService;
    const transformRequest = (url: string, resourceType?: string) => {
      try {
        if (apiHost && new URL(url, window.location.href).host === apiHost) {
          const token = authService.getAccessToken();
          if (token) {
            return { url, headers: { Authorization: `Bearer ${token}` } } as any;
          }
        }
      } catch { /* ignore malformed URLs */ }
      return { url } as any;
    };
    setMaxParallelImageRequests(32);
    this.map = this.ngZone.runOutsideAngular(() => new maplibregl.Map({
      container: 'map',
      transformRequest,
      style: this.mapSkinService.getCurrentStyleUrl(),
      center: [-85, 32],
      zoom: 3,
      minZoom: 3,
      pitch: 0,
      bearing: 0,
      fadeDuration: 0,
      refreshExpiredTiles: false,
      // Retain far more parent tiles than the default 5 zoom levels' worth.
      //
      // When the camera snaps from the world view to a waypoint at z14, MapLibre
      // looks for an already-loaded ancestor tile to stretch over the destination
      // while the real tiles download. With the default cache the z3/z5 ancestors
      // have long been evicted, it finds nothing, and it paints the style's bare
      // background — the flat sea-blue the tester sees under the highlighted pin.
      // Keeping the ancestors resident means the jump lands on a blurry chart that
      // sharpens, instead of on nothing, and a second visit to the same area is
      // instant. The cost is memory for tiles that are cheap to hold.
      maxTileCacheZoomLevels: 20,
    }));

    // Hide the canvas until all tiles are fully rendered so they appear all at
    // once instead of popping in tile by tile. This must stay hidden through the
    // initial auto-center on the primary vessel below (which recenters/rezooms
    // the map once vessel data arrives) — otherwise the world view reveals first,
    // then visibly jumps and reloads tiles for the vessel's location a moment later.
    const canvas = this.map.getCanvas();
    canvas.style.opacity = '0';
    const revealMap = () => { canvas.style.transition = 'opacity 200ms ease'; canvas.style.opacity = '1'; };
    let revealed = false;
    let pendingInitialCenter = true;
    const finishReveal = () => {
      if (revealed) return;
      revealed = true;
      this.mapFirstPaint$.next();
      clearTimeout(fallbackTimer);
      this.map!.off('idle', tryReveal);
      revealMap();
    };
    const tryReveal = () => {
      if (pendingInitialCenter) return;
      finishReveal();
    };
    const fallbackTimer = setTimeout(finishReveal, 4000);
    // An explicit camera focus must never sit behind this gate. `pendingInitialCenter`
    // only clears inside the initial vessel-centring callback below, so a highlight pan
    // arriving before that (or when it never fires at all — no primary vessel, failed
    // request) left the canvas at opacity 0 until the 4s fallback: a flat #d8e8f1 blue
    // with the highlighted DOM marker floating on top, which is precisely the reported
    // symptom. Once the user has asked to go somewhere, a partially-painted chart beats
    // an invisible one.
    this.forceRevealMapCanvas = () => {
      pendingInitialCenter = false;
      finishReveal();
    };
    this.map.on('idle', tryReveal);

    this.map.doubleClickZoom.disable();
    this.applyMapOrientationSettings();

    this.map.on('rotate', () => {
      if (this.isNorthUpMapOrientation()) {
        this.lockMapBearingNorth();
      }
      // Marker elements are viewport-aligned, so a heading only stays truthful if the
      // icon is counter-rotated as the map turns. The ride-along boat used to be
      // re-rotated solely by the 15s observation poll, leaving it pointing the wrong
      // way for up to 15s after any rotation. Pure DOM writes — deliberately kept
      // outside the Angular zone, like the other hot map handlers.
      if (this.rideAlongFollowActive) this.renderRideAlongBoat();
    });

    // Disable auto-follow when the user manually pans the map
    this.map.on('dragstart', () => {
      this.ngZone.run(() => {
        this.isAutoFollowEnabled = false;
        this._replayAutoFollow = false;
      });
    });

    this.map.on('dblclick', (e) => this.ngZone.run(() => {
      const coords = e.lngLat;
      this.submenuService.setWaypointModalState(true, {
        lat: coords.lat,
        lng: coords.lng,
      });
    }));

    this.map.on('dataloading', (e: any) => {
      // Only show the overlay loader during the *initial* load of the raster
      // overlay we most recently applied. Ignore ordinary tile requests (e.g.
      // panning) after the initial load completes.
      if (!this.currentRasterOverlayInitialLoadSourceId) return;
      if ((e.dataType === 'source' || e.dataType === 'tile') && String(e.sourceId) === this.currentRasterOverlayInitialLoadSourceId) {
        this.ngZone.run(() => this.submenuService.setOverlayLoading(true));
      }
    });

    this.map.on('data', (e: any) => {
      if (!this.currentRasterOverlayInitialLoadSourceId) return;
      if ((e.dataType === 'source' || e.dataType === 'tile') && e.isSourceLoaded && String(e.sourceId) === this.currentRasterOverlayInitialLoadSourceId) {
        // debounce slightly to avoid harsh flashes
        setTimeout(() => this.ngZone.run(() => this.submenuService.setOverlayLoading(false)), 300);
        // initial load for this overlay finished — stop tracking it so subsequent
        // tile loads (from panning/zoom) don't re-trigger the loader.
        this.currentRasterOverlayInitialLoadSourceId = null;
      }
    });

    this.map.on('load', () => {
      this.isMapLoaded = true;
      // Combo overlay — probe on bare-map tap + keep the probe bubble anchored.
      this.setupComboInteractions();
      if (this.comboState.config.enabled) this.renderComboLayers(this.comboState);
      // Render any waypoint pins that finished downloading before the map became
      // ready. The fetch itself is kicked off early in ngAfterViewInit (not here),
      // so by now the data is often already in this.allWaypoints.
      this.applyMapFilters();
      this.tryPendingWaypointFocus();
      this.loadTripTracks();
      // If replay mode was already active when the map loaded, restore layers
      if (this.isReplayMode) this._ensureReplayLayers();
      // Same for ride-along: follow mode can start (from the persisted active
      // ride-along) before the style is ready, and every render call bails out while
      // !isMapLoaded. Without this replay the green track only appeared on the next
      // 90s poll, which is what made the track look like it took minutes to load.
      if (this.rideAlongFollowActive) this.renderRideAlongAll();
      // Initialize vessel clustering GeoJSON layers
      this.initVesselClusterLayers();
      // After the vessel layer exists so the heat stack is inserted beneath the own boat.
      this.catchHeatMap.attach(this.map!);
      this.applyHeatTakeover();
      // Fetch Vessel -> Latest Observation -> Waypoints (page=1, page_size=50)
      this.vesselService.getVessels().pipe(
        map((res: any) => {
          return (res && res.results) || res || [];
        }),
        switchMap((vessels: any[]) => {
          if (!vessels || vessels.length === 0) {
            this.startDockmatesTracking();
            return of(null);
          }

          const primaryVessel = vessels.find((v: any) => v.is_primary === true);
          if (primaryVessel) {
            try { this.primaryVesselName = primaryVessel.name || primaryVessel.display_name || primaryVessel.vessel_name || null; } catch (e) { this.primaryVesselName = null; }
          }

          vessels.forEach(vessel => {
            const vId = vessel._id || vessel.id;
            // Every vessel returned by getVessels() belongs to the current user — record
            // their ids so the observation popup can attribute the author to us even when
            // the observation's create_user_id is a placeholder (e.g. "0").
            const vesselDisplayName = String(vessel.name || vessel.display_name || vessel.vessel_name || '').trim();
            [vessel._id, vessel.id, vessel.identifier, vessel.vessel_id, vessel.reference_id]
              .forEach((idVal) => {
                const s = String(idVal || '').trim();
                if (!s) return;
                this.myVesselIds.add(s);
                if (vesselDisplayName) this.myVesselNames.set(s, vesselDisplayName);
              });
            // `vessel/` carries `boat_skin` on each vessel.
            this.skins.remember(vId, vessel.boat_skin);
            // Only show the primary vessel on the map — skip non-primary vessels.
            if (vId && vessel.is_primary === true) {
              this.startVesselTracking(vId, true);
            }
          });

          this.startDockmatesTracking();

          if (primaryVessel) {
            const pId = primaryVessel._id || primaryVessel.id;
            return this.vesselService.getLatestObservation(pId).pipe(
              catchError(() => of(null))
            );
          }
          return of(null);
        }),
        switchMap((obs: any) => {
          if (obs) {
            this.latestObservation = this.parseObservation(obs);
            // Auto-center on load if we have coordinates — but never yank the camera off
            // the captain's boat when we're riding along.
            if (this.latestObservation.lat != null && this.latestObservation.lng != null && this.map
              && !this.rideAlongFollowActive) {
              console.debug('[HomePage] Auto-centering on initial vessel observation');
              this.map.setCenter([this.latestObservation.lng, this.latestObservation.lat]);
              this.map.setZoom(12);
              this.hasCenteredOnceAfterLogin = true;
            }
          }
          // Waypoint pins are fetched separately/earlier via loadInitialWaypoints();
          // this chain now only handles vessel tracking + auto-centering.
          return of(null);
        }),
        catchError((err) => {
          console.error('Error fetching vessel/observations:', err);
          return of(null);
        })
      ).subscribe(() => {
        // Whether or not we actually had a vessel to center on, the initial
        // auto-center attempt is now resolved — safe to reveal the map. But if we
        // just recentred on the vessel, wait for THAT view's tiles before fading in
        // (the still-attached 'idle' listener reveals once they finish) so the reveal
        // never flashes the world view or a half-loaded frame. If nothing moved the
        // camera, tiles are already loaded and we reveal immediately.
        pendingInitialCenter = false;
        if (!this.map || this.map.areTilesLoaded()) tryReveal();
      });

      // Fetch and display watch waypoints
      this.waypointService.getWatch(1, 1000).subscribe({
        next: (res: any) => {
          let items = [];
          if (Array.isArray(res)) {
            items = res;
          } else if (res && res.results && Array.isArray(res.results)) {
            items = res.results;
          }

          const newWaypoints: any[] = [];
          items.forEach((item: any) => {
            if (item.post_type === 'waypoint' && item.waypoint_details) {
              const wd = item.waypoint_details;
              // Normalize watch waypoint to match standard waypoint structure
              const normalizedWp = {
                ...wd,
                type:
                  wd.type ||
                  wd.waypoint_type ||
                  wd.event_type ||
                  wd.place_type ||
                  item.waypoint_ref?.type ||
                  item.waypoint_ref?.waypoint_type ||
                  item.waypoint_ref?.event_type ||
                  item.waypoint_ref?.place_type,
                name:
                  wd.name ||
                  wd.waypoint_name ||
                  item.waypoint_ref?.name ||
                  item.waypoint_ref?.title,
                waypoint_name:
                  wd.waypoint_name ||
                  wd.name ||
                  item.waypoint_ref?.name ||
                  item.waypoint_ref?.title,
                position: {
                  latitude: wd.position?.lat,
                  longitude: wd.position?.lon,
                },
                source_profile: item.source_profile,
                audit_data: item.audit_data,
                created_at: item.audit_data?.create_ts,
                // Preserve the backend's visibility classification for the map filter.
                _vis: this.buildVisClassification(item, wd, item.action_type),
              };
              newWaypoints.push(normalizedWp);
            }
          });
          this.allWaypoints = [...this.allWaypoints, ...newWaypoints];
          this.applyMapFilters();
          this.tryPendingWaypointFocus();
        },
        error: (err) => console.error('Error loading watch waypoints:', err),
      });

      // Click handler: clicking a trip track should toggle highlight for that trip,
      // clicking anywhere else clears the active trip highlight.
      this.map.on('click', (e) => this.ngZone.run(() => {
        try {
          // Cluster circle was just clicked — zoom is already handled; skip all
          // other map-click logic so the vessel popup doesn't open underneath.
          if (this._suppressNextMapClick) {
            this._suppressNextMapClick = false;
            return;
          }

          // ── Measurement mode: intercept click for A/B point placement ──────
          if (this.isMeasurementMode) {
            // Check if a waypoint marker was clicked — if so, snap to its coords and highlight it
            const origEvtM = (e && (e as any).originalEvent) as MouseEvent | undefined;
            let clickedWPElM: HTMLElement | null = null;
            try {
              let tgtM: HTMLElement | null = null;
              if (origEvtM && origEvtM.target && (origEvtM.target as HTMLElement).closest) {
                tgtM = (origEvtM.target as HTMLElement).closest('[data-wpid]');
              }
              if (!tgtM && origEvtM && typeof origEvtM.clientX === 'number') {
                const nodeM = document.elementFromPoint(origEvtM.clientX, origEvtM.clientY) as HTMLElement | null;
                tgtM = nodeM && nodeM.closest ? nodeM.closest('[data-wpid]') as HTMLElement | null : null;
              }
              if (tgtM) clickedWPElM = tgtM;
            } catch (_) { /* ignore */ }

            if (clickedWPElM) {
              // Find the Leaflet/MapLibre Marker that owns this element to get the lngLat
              const wpIdM = clickedWPElM.getAttribute('data-wpid') || '';
              const markerM = this.displayedMarkers.find(m => {
                const mel = m.getElement();
                return mel && mel.getAttribute && mel.getAttribute('data-wpid') === wpIdM;
              });
              if (markerM) {
                const lngLatM = markerM.getLngLat();
                this.ngZone.run(() => {
                  if (!this.measurePointA) {
                    this.applyMeasureSelectionToWaypoint(clickedWPElM!, 1);
                    this.measureHighlightedElA = clickedWPElM;
                    // Ghost (invisible) marker just to track lngLat for line/distance calcs
                    this.measurePointA = this.createGhostMeasureMarker(lngLatM);
                    this.measurePointASet = true;
                  } else if (!this.measurePointB) {
                    this.applyMeasureSelectionToWaypoint(clickedWPElM!, 2);
                    this.measureHighlightedElB = clickedWPElM;
                    // Ghost marker for position tracking
                    this.measurePointB = this.createGhostMeasureMarker(lngLatM);
                    this.drawMeasureLine(this.measurePointA!.getLngLat(), this.measurePointB.getLngLat());
                    const dist = this.haversineNauticalMiles(this.measurePointA!.getLngLat(), this.measurePointB.getLngLat());
                    this.measureDistance = dist.toFixed(2) + ' nmi';
                  }
                });
                return;
              }
            }

            this.ngZone.run(() => this.handleMeasurementClick(e.lngLat));
            return;
          }

          // ── Outside-click dismiss ─────────────────────────────────────────
          // A click on the map background closes any open detail card / side
          // panel (vessel detail card + shared-posts panel). Vessel/waypoint
          // markers stop event propagation, so clicking them does not reach
          // here; the waypoint detail card is closed further below.
          if (this.isDockmateCardOpen) {
            this.closeDockmateCard();
          }
          if (this.isWatchOpen) {
            this.submenuService.setWatchPageModalState(false);
          }

          // ── A click on the map always drops the previous highlight ─────────
          // Waypoint/vessel markers stopPropagation, so reaching this handler at
          // all means the user clicked something that is NOT the highlighted pin.
          // Clearing here rather than in the "hit nothing" branch far below is
          // deliberate: that branch is only reached if none of the boat, WebGL
          // vessel, nearest-icon or trip-line paths returned early first, so a
          // click on a boat (or anywhere those paths claimed) left the old
          // highlight burning on screen — the reported "highlighted item not
          // disappearing after clicking on the map".
          //
          // Anything that legitimately re-highlights (opening a pin's card, a
          // trip focus) does so after this point, so it still wins.
          this.clearWaypointHighlight(true);

          // Clearing the highlight *styling* above is only half of it: the pin
          // itself can outlive it. `focusedWaypointOverride` force-renders a
          // waypoint that the current filters would otherwise hide (that is what
          // makes a focused pin visible at all when it falls outside the date
          // range), and dropping it used to live ONLY in the "clicked empty
          // water" branch far below. Every other path — a boat, a trip line,
          // another pin, the WebGL vessel query — returns before reaching it, so
          // the focused pin stayed burning on the map: the reported "highlighted
          // item not disappearing after clicking on the map".
          //
          // Do it here, on every click, with the same guard the old branch used:
          // a waypoint the filters would show anyway simply stays put, and
          // anything that legitimately re-focuses does so after this point.
          if (this.focusedWaypointOverride && !this.isFocusedOverrideInCurrentFilter()) {
            this.focusedWaypointOverride = null;
            this.pendingWaypointFocus = null;
            this.applyMapFilters();
          }

          const originalEvt = (e && (e as any).originalEvent) as MouseEvent | undefined;

          // Priority between an overlapping boat and a waypoint pin is TOPMOST-wins:
          // whichever the cursor is actually over should open. Scan the stack under
          // the click from the top down — if a waypoint pin comes first, the user
          // clicked the pin (open the waypoint, below); if a boat comes first, open
          // the boat. `wpOnTop` is also consulted by the WebGL vessel query below so a
          // pin sitting over the own-vessel icon opens the waypoint, not the vessel.
          let wpOnTop = false;
          try {
            const bx = originalEvt && (originalEvt as any).clientX;
            const by = originalEvt && (originalEvt as any).clientY;
            if (typeof bx === 'number' && typeof by === 'number') {
              const stack = (document.elementsFromPoint(bx, by) || []) as HTMLElement[];
              let boatEl: HTMLElement | null = null;
              for (const node of stack) {
                if (node?.closest?.('[data-wpid]')) { wpOnTop = true; break; }
                const hit = node?.closest?.('.ride-along-boat, .replay-boat-wrapper, [data-vessel-id]') as HTMLElement | null;
                if (hit) { boatEl = hit; break; }
              }
              if (boatEl) {
                this.clearTripFocusState();
                // Each boat/vessel element carries its own click handler (ride-along
                // obs popup, or dockmate card) — replay it so the right one opens.
                boatEl.click();
                return;
              }
            }
          } catch (boatErr) {
            // ignore stack inspection errors
          }

          // Nearest overlapping point icon (waypoint pin / place / spot / wreck),
          // computed up front so the own-vessel query below can yield to a closer one.
          const nearestIcon = this.pickNearestClickable(e);

          // WebGL boat icons (own/primary vessel + shared boats) are drawn on the map
          // canvas, not as DOM markers, so the DOM scan above can't reach them. Query
          // the vessel symbol layer directly and, when a boat is under the click, open
          // its observation popup — this is what makes the user's OWN vessel clickable.
          try {
            const vHit: [[number, number], [number, number]] = [
              [e.point.x - 8, e.point.y - 8],
              [e.point.x + 8, e.point.y + 8],
            ];
            const vFeats = this.map.getLayer(this.vesselUnclusteredLayerId)
              ? this.map.queryRenderedFeatures(vHit, { layers: [this.vesselUnclusteredLayerId] })
              : [];
            const vf: any = (vFeats && vFeats.length) ? vFeats[0] : null;
            // Nearest-wins: a waypoint/wreck closer to the click beats the own-vessel
            // icon, and a waypoint pin directly under the cursor always wins. Otherwise
            // the ±8px box would open the boat popup for a click aimed at a neighbour.
            let vDist = Infinity;
            const vCoordArr = vf?.geometry?.coordinates as number[] | undefined;
            if (Array.isArray(vCoordArr) && vCoordArr.length >= 2 && originalEvt && typeof (originalEvt as any).clientX === 'number') {
              const cvRect = this.map.getCanvas().getBoundingClientRect();
              const p = this.map.project({ lng: vCoordArr[0], lat: vCoordArr[1] } as any);
              vDist = Math.hypot(p.x + cvRect.left - (originalEvt as any).clientX, p.y + cvRect.top - (originalEvt as any).clientY);
            }
            if (vf && !wpOnTop && (!nearestIcon || vDist <= nearestIcon.dist)) {
              const f: any = vf;
              const vesselId = String(f?.properties?.['id'] || '').trim();
              const coord = (f?.geometry?.coordinates as number[] | undefined);
              const tripId = this.tripService.getCurrentTripId() || undefined;
              this.clearTripFocusState();
              // The card opens instantly and loads the vessel's latest reading itself
              // (observations/latest?vessel_id=), showing its own loading/error state.
              this.fetchAndShowObservation('', coord, tripId, undefined, vesselId || undefined);
              return;
            }
          } catch (vErr) {
            // ignore vessel query errors
          }

          // Open the SINGLE nearest overlapping icon (waypoint pin / place / spot /
          // wreck) resolved above, so clicking an icon opens exactly that icon at any
          // zoom — never a neighbour sitting a few pixels away. Wrecks are handled
          // here too; their own greedy layer-click handler was removed so it can no
          // longer hijack a waypoint click.
          if (nearestIcon) {
            // Do NOT set the persistent waypoint highlight here — that should only
            // happen for programmatic focus from the Watch UI. Just open the card.
            this.clearTripFocusState();
            if (nearestIcon.kind === 'waypoint') {
              this.openWaypointCard(nearestIcon.marker);
              this.updateTripFocusHighlight();
            } else {
              this.closeWaypointCard();
              this.openWreckPopup(nearestIcon.row, nearestIcon.coord);
            }
            return;
          }

          // Not a waypoint DOM click: check for trip features using a ±10px bbox
          // so small 3px-radius circle points are reliably hit-tested.
          const hitBox: [[number, number], [number, number]] = [
            [e.point.x - 10, e.point.y - 10],
            [e.point.x + 10, e.point.y + 10],
          ];
          const features = this.map.queryRenderedFeatures(hitBox, {
            layers: [
              this.tripTrackLineLayerId,
              this.tripTrackPointLayerId,
              this.tripTrackHighlightLineLayerId,
              this.tripTrackHighlightPointLayerId,
            ],
          });

          // Prefer Point features over LineString so clicking near a track point
          // opens the observation popup rather than toggling the line highlight.
          const sortedFeatures = (features || []).slice().sort((a: any, b: any) => {
            const aIsPoint = a.geometry?.type === 'Point' ? 0 : 1;
            const bIsPoint = b.geometry?.type === 'Point' ? 0 : 1;
            return aIsPoint - bIsPoint;
          });
          const tripFeature = sortedFeatures.find((f: any) => f && f.properties && f.properties['tripId']);
          let didOpenObsPopup = false;
          if (tripFeature && tripFeature.properties && tripFeature.properties['tripId']) {
            const props = (tripFeature.properties || {}) as any;
            const clickedId = String(props['tripId']);
            const geomType = tripFeature.geometry && (tripFeature.geometry as any).type;
            const isPoint = geomType === 'Point';
            const isLine = geomType === 'LineString' || (props['kind'] === 'trip-line');
            const obsId = props['obsId'] || props['_id'] || props['observation_id'] || null;

            // If this feature is a trip-point and carries an observation id, fetch and show observation popup
            if (isPoint && obsId) {
              try {
                this.closeWaypointCard();
                const coords = (tripFeature.geometry as any).coordinates;
                (this as any).fetchAndShowObservation(String(obsId), coords, clickedId);
                didOpenObsPopup = true;
              } catch (e) {
                // ignore
              }
              // Do NOT toggle trip highlight when tapping an observation point.
            } else {
              // Line click (or non-observation point): toggle trip selection,
              // but only for completed trips — ongoing trips are not highlighted.
              const isOngoing = props['isOngoing'] === 1;
              if (!isOngoing) {
                if (this.activeTripFocusId === clickedId) {
                  this.clearTripFocusState();
                  this.updateTripFocusHighlight();
                  this.updateTripWaypointMarkerHighlights();
                } else {
                  this.activeTripFocusId = clickedId;
                  this.updateTripFocusHighlight();
                }
              }
            }
          } else {
            // clicked outside any trip or waypoint -> clear both highlights
            const hadTripFocus = !!this.activeTripFocusId;
            this.closeWaypointCard();
            this.clearTripFocusState();
            this.clearWaypointHighlight(true);
            // If the focused override waypoint is outside the current date range
            // filter, clicking away should also remove its icon from the map.
            if (this.focusedWaypointOverride && !this.isFocusedOverrideInCurrentFilter()) {
              this.focusedWaypointOverride = null;
              this.pendingWaypointFocus = null;
              this.applyMapFilters();
            } else if (hadTripFocus) {
              this.applyTripFilters();
              this.applyMapFilters();
            }
          }

          // If ACSPO overlay is active, allow tap-to-query SST/zone for this point.
          // Keep waypoint/trip-point popups higher priority.
          if (!didOpenObsPopup) {
            this.tryShowAcspoPointPopup(e.lngLat);
          }
          this.updateTripFocusHighlight();
        } catch (err) {
          // ignore query errors
        }
      }));

      this.map.on('moveend', () => this.ngZone.run(() => {
        // Buoy and Local Tides markers are fetched once on layer apply (vessel position + fixed radius).
        // Do NOT re-call ensureBuoyMarkers / ensureTideMarkers here so map panning never triggers a new API call.
        if (this.activeMapLayerIds.includes('wrecks')) {
          this.ensureWreckMarkers();
        }
        // Broadcast current map center for side navigation + waypoint fallback
        const center = this.map.getCenter();
        this.submenuService.setMapCenter(center.lat, center.lng);
        // Cull markers that scrolled out of view immediately (cheap, no cluster
        // recompute) so panning detaches off-screen markers without waiting for
        // the debounced cluster update below. Pure DOM work — keep it out of the
        // Angular zone so it doesn't trigger a change-detection pass.
        this.ngZone.runOutsideAngular(() => this.reconcileMarkerAttachment());
        this.scheduleClusterUpdate();
      }));

      // Track zoom animation state so we never recompute clusters mid-animation.
      this.map.on('zoomstart', () => {
        this.isZoomAnimating = true;
      });

      // NOTE: We intentionally do NOT resize markers on every 'zoom' tick.
      // Writing each DOM marker's width/height mid-animation interleaved with
      // MapLibre's per-frame position reads caused forced reflows (confirmed via
      // DevTools: Scripting ~76% of frame time, "Forced reflow" insight) and made
      // zoom feel laggy. Markers keep their current size during the zoom gesture
      // and are resized once below in 'zoomend' when the animation settles.

      this.map.on('zoomend', () => this.ngZone.run(() => {
        this.isZoomAnimating = false;
        this.updateZoomResponsiveMarkerSizes();
        this.handleZoomBasedLayerVisibility();
        // Apply clustering synchronously so markers hide/show immediately at zoom threshold.
        this.applyClusteringNow();
      }));

      this.map.on('move', () => {
        if (this.isMeasurementMode && this.measurePointA && this.measurePointB) {
          this.drawMeasureLine(
            this.measurePointA.getLngLat(),
            this.measurePointB.getLngLat()
          );
        }
        // Keep the dockmate detail card stuck to its vessel as the map pans/zooms.
        // Runs outside Angular's zone (map created via runOutsideAngular) and only
        // writes inline styles, so it does not trigger change detection.
        if (this.isDockmateCardOpen) {
          this.positionDockmateCard();
        }
      });

    });
  }

  private fetchAndStorePhoneGps(): void {
    if (!navigator.geolocation) return;
    // Use watchPosition (like Flutter's continuous _getCurrentLocation) so position
    // stays fresh and is always available when a waypoint sheet opens.
    this.gpsWatchId = navigator.geolocation.watchPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        this.lastKnownGps = { lat, lng };
        this.submenuService.setUserGpsPosition(lat, lng);

        // Auto-follow: only center on GPS if the user has NO primary vessel observation.
        // If a primary vessel is tracked or has a latestObservation, let the vessel
        // tracking code handle map centering.
        if (this.isAutoFollowEnabled && this.isMapLoaded && this.map) {
          const trackedVessels = Array.from(this.liveVesselStore.values());
          const hasPrimaryVessel = trackedVessels.some(v => v.isPrimary && v.lat != null && v.lng != null);
          const hasLatestObservation = this.latestObservation?.lat != null && this.latestObservation?.lng != null;

          if (!hasPrimaryVessel && !hasLatestObservation) {
            if (this.hasCenteredOnceAfterLogin) {
              this.map.easeTo({ center: [lng, lat], duration: 800 });
            } else {
              // First fix after login: snap instantly (no cross-map animation from the
              // initial world view) so the revealed map is already on the location.
              this.map.jumpTo({ center: [lng, lat], zoom: Math.max(this.map.getZoom(), 12) });
              this.hasCenteredOnceAfterLogin = true;
            }
          }
        }

        // Update blue-dot marker position in real time
        if (this.isMapLoaded && this.map) {
          if (!this.userLocationMarker) {
            const el = document.createElement('div');
            el.className = 'user-location-marker';
            this.userLocationMarker = new maplibregl.Marker({ element: el })
              .setLngLat([lng, lat])
              .addTo(this.map);
          } else {
            this.userLocationMarker.setLngLat([lng, lat]);
          }
        }
      },
      () => { /* silently ignore — GPS not available or denied on this device */ },
      { enableHighAccuracy: true, timeout: 30000 }
    );
  }

  // ── Vessel Clustering: GeoJSON Layer Initialization ─────────────────────────
  private initVesselClusterLayers(): void {
    if (!this.map || !this.isMapLoaded) return;

    // ── Cluster source + layers ──
    if (!this.map.getSource(this.vesselClusterSourceId)) {
      this.map.addSource(this.vesselClusterSourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    if (!this.map.getLayer(this.vesselClusterCircleLayerId)) {
      this.map.addLayer({
        id: this.vesselClusterCircleLayerId,
        type: 'circle',
        source: this.vesselClusterSourceId,
        paint: {
          'circle-color': '#ffffff',
          'circle-radius': 22,
          'circle-stroke-width': 0,
          'circle-blur': 0.1,
        },
      });
    }

    const vesselClusterIconLayerId = this.vesselClusterCircleLayerId + '-icon';
    if (!this.map.getLayer(vesselClusterIconLayerId)) {
      this.map.addLayer({
        id: vesselClusterIconLayerId,
        type: 'symbol',
        source: this.vesselClusterSourceId,
        layout: {
          'icon-image': 'boat-other-icon',
          'icon-size': 0.75,
          'icon-allow-overlap': true,
          'icon-rotation-alignment': 'viewport',
        }
      });
    }

    const vesselClusterBadgeLayerId = this.vesselClusterCircleLayerId + '-badge';
    if (!this.map.getLayer(vesselClusterBadgeLayerId)) {
      this.map.addLayer({
        id: vesselClusterBadgeLayerId,
        type: 'circle',
        source: this.vesselClusterSourceId,
        paint: {
          'circle-color': '#ef4444',
          'circle-radius': 9,
          'circle-translate': [12, -12],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });
    }

    if (!this.map.getLayer(this.vesselClusterCountLayerId)) {
      this.map.addLayer({
        id: this.vesselClusterCountLayerId,
        type: 'symbol',
        source: this.vesselClusterSourceId,
        layout: {
          'text-field': ['get', 'countLabel'],
          'text-size': 10,
          'text-font': ['Open Sans Bold'],
          'text-offset': [1.3, -1.3],
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': '#ffffff',
        },
      });
    }

    // ── Unclustered vessel source + layer ──
    if (!this.map.getSource(this.vesselUnclusteredSourceId)) {
      this.map.addSource(this.vesselUnclusteredSourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    if (!this.map.getLayer(this.vesselUnclusteredLayerId)) {
      // Load vessel icons into the map style
      const boatSelfImg = new Image(50, 50);
      boatSelfImg.src = '/assets/common_icons/boat_self.png';
      boatSelfImg.onload = () => {
        if (!this.map!.hasImage('boat-self-icon')) {
          this.map!.addImage('boat-self-icon', boatSelfImg);
        }
      };
      const boatOtherImg = new Image(32, 32);
      boatOtherImg.src = '/assets/common_icons/boat_other_user.png';
      boatOtherImg.onload = () => {
        if (!this.map!.hasImage('boat-other-icon')) {
          this.map!.addImage('boat-other-icon', boatOtherImg);
        }
      };

      this.map.addLayer({
        id: this.vesselUnclusteredLayerId,
        type: 'symbol',
        source: this.vesselUnclusteredSourceId,
        layout: {
          // A rasterized skin sprite (hull + ownership ring, pre-sized in CSS px) wins; the legacy PNGs
          // remain the fallback while sprites load or when the vessel has no skin yet.
          'icon-image': ['case', ['!=', ['get', 'skinIcon'], ''], ['get', 'skinIcon'],
            ['case', ['get', 'isPrimary'], 'boat-self-icon', 'boat-other-icon']],
          'icon-size': ['case', ['!=', ['get', 'skinIcon'], ''], 1,
            ['case', ['get', 'isPrimary'], isDesktopViewport() ? 1.8 : 1.3, 1]],
          'icon-anchor': 'center',
          'icon-allow-overlap': true,
          'icon-rotation-alignment': 'map',
          'icon-rotate': ['get', 'heading'],
        },
        paint: {
          'icon-opacity': ['get', 'opacity'],
        },
      });
    }

    this.refreshOwnBoatSprites();

    // ── Subscribe to clustering results (data-driven updates via scheduleRecompute) ──
    this.vesselClusterSub?.unsubscribe();
    this.vesselClusterSub = this.clustering.result$.subscribe((result) => {
      if (this.isZoomAnimating) return;
      this.ngZone.runOutsideAngular(() => {
        this.renderUnifiedClusterResult(result);
      });
    });

    // ── Cluster click interaction (tap-to-expand) ──
    this.map.on('click', this.vesselClusterCircleLayerId, (e) => {
      if (!e.features || e.features.length === 0) return;
      const coords = (e.features[0].geometry as any).coordinates;
      this.map!.easeTo({
        center: coords,
        zoom: this.clustering.getClusterClickZoom(this.map!.getZoom()),
        duration: 500,
      });
    });

    // Pointer cursor on cluster hover
    this.map.on('mouseenter', this.vesselClusterCircleLayerId, () => {
      if (this.map) this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', this.vesselClusterCircleLayerId, () => {
      if (this.map) this.map.getCanvas().style.cursor = '';
    });

    // Pointer cursor for the WebGL-rendered boat icons (own/primary vessel + trip
    // points that open the observation popup). These live on the map canvas, so CSS
    // can't reach them — bind the same enter/leave cursor pattern used for clusters.
    [this.vesselUnclusteredLayerId, this.tripTrackPointLayerId, this.tripTrackHighlightPointLayerId]
      .forEach((layerId) => {
        this.map!.on('mouseenter', layerId, () => {
          if (this.map) this.map.getCanvas().style.cursor = 'pointer';
        });
        this.map!.on('mouseleave', layerId, () => {
          if (this.map) this.map.getCanvas().style.cursor = '';
        });
      });
  }

  // ── Vessel Clustering: Trigger Recomputation ───────────────────────────────
  private triggerVesselClustering(): void {
    if (!this.map || !this.isMapLoaded) return;
    this.updateWaypointAggregation();
  }

  // ── Vessel Clustering: Register a vessel in the live store ─────────────────
  private registerVesselForClustering(vessel: ClusterableVessel): void {
    this.liveVesselStore.set(vessel.id, vessel);
    this.triggerVesselClustering();
  }

  // ── Ride Along follow mode ───────────────────────────────────────────────

  private enterRideAlongFollow(active: ActiveRideAlong): void {
    // Running your own trip and riding along are mutually exclusive. This is the last
    // line of defence for every entry point (detection, the persisted restore, an
    // in-flight response landing late): a captain must get their own trip's status
    // card, never the green follow overlay.
    if (this.tripService.getCurrentTripId()) {
      console.debug('[RideAlong] refusing follow — own trip is active');
      this.rideAlongService.setActiveRideAlong(null);
      return;
    }
    // Same refusal if the trip is simply ours. This also self-heals a persisted
    // ride_along_active pointing at our own trip, which an earlier build could write.
    const knownTrip = this.allTrips.find((t) => this.getTripId(t) === active.tripId);
    if (knownTrip && this.isTripMine(knownTrip)) {
      console.debug('[RideAlong] refusing follow — trip is our own', active.tripId);
      this.rideAlongService.setActiveRideAlong(null);
      return;
    }
    // Already following this trip — nothing to do.
    if (this.rideAlongFollowActive && this.rideAlongTripId === active.tripId) return;
    // Switching trips: tear the previous one down first.
    if (this.rideAlongFollowActive) this.exitRideAlongFollow();

    this.rideAlongTripId = active.tripId;
    this.rideAlongVesselId = active.vesselId || null;
    this.rideAlongCaptainName = active.captainName || '';
    this.rideAlongVesselName = active.vesselName || '';
    this.rideAlongFollowActive = true;
    this.rideAlongPaused = false;
    this.rideAlongEndedHandled = false;
    this.rideAlongTrackPolyline = [];
    this.rideAlongVesselPos = null;
    this.rideAlongVesselBearing = null;
    this.rideAlongObsPoints = [];
    this.rideAlongStartTime = null;
    this.rideAlongDistanceMeters = null;
    this.rideAlongWaterTempC = null;
    this.rideAlongSpeedMs = null;
    this.rideAlongLat = null;
    this.rideAlongLng = null;
    this.rideAlongStartTimeMs = null;
    this.isAutoFollowEnabled = true;

    // Only the captain's vessel is polled/followed while riding along — see
    // suspendOwnVesselTracking for why hitting both endpoints fought the camera.
    this.suspendOwnVesselTracking();

    this.ensureRideAlongLayers();

    // Suppress the normal (teal) rendering + shared boat for THIS trip so only the
    // green ride-along track/boat show it (avoids the dual-render seen on public trips).
    this.applyTripFilters();

    // 1. Initial track fetch via extend_track — same as the app's _refreshRideAlongTrack
    //    (reads trip.track.points + vessel_ref). Falls back to getTripById only if
    //    extend_track comes back without a track (participant edge case).
    this.refreshRideAlongTrack(true);

    // 2. Slow track polling (90s) + fast observation polling (15s) — matches the app.
    this.rideAlongTrackSub = this.pollScheduler
      .poll$(() => this.rideAlongService.extendTrack(active.tripId).pipe(catchError(() => of(null))), this.rideAlongTrackIntervalMs, { skipInitialFetch: true })
      .subscribe((res: any) => this.applyRideAlongTrackResponse(res, false));

    this.rideAlongObsSub = this.pollScheduler
      .poll$(() => this.fetchRideAlongObservation(), this.rideAlongObsIntervalMs, { startAfter$: this.mapFirstPaint$ })
      .subscribe((obs: any) => this.applyRideAlongObservation(obs));

    // Added waypoints: merge OTHER mates' waypoints into the normal waypoint set so
    // they render + CLUSTER through the standard pipeline (the user's own waypoints
    // are already loaded). This avoids the earlier separate-marker duplication and
    // fixes the aggregation/count when zooming out.
    this.rideAlongWaypointsSub = this.pollScheduler
      .poll$(() => this.rideAlongService.getRideAlongWaypoints(active.tripId).pipe(catchError(() => of([]))), this.rideAlongTrackIntervalMs, { startAfter$: this.mapFirstPaint$ })
      .subscribe((wps: any[]) => this.mergeRideAlongWaypointsIntoMap(wps));
  }

  private mergeRideAlongWaypointsIntoMap(wps: any[]): void {
    if (!Array.isArray(wps) || !wps.length) return;
    let changed = false;
    wps.forEach((wp) => {
      const creatorId = wp?.audit_data?.create_user_id || wp?.source_profile?.id || wp?.creator;
      const isMine = !!creatorId && this.myUserIds.includes(String(creatorId));
      if (isMine) return; // already present via the normal waypoint load
      const id = this.getWaypointId(wp);
      if (!id || this.rideAlongWaypointIds.has(id)) return;
      this.mergeWaypointData(wp);
      this.rideAlongWaypointIds.add(id);
      changed = true;
    });
    if (changed && this.isMapLoaded) this.applyMapFilters();
  }

  private exitRideAlongFollow(): void {
    this.rideAlongObsSub?.unsubscribe();
    this.rideAlongObsSub = undefined;
    this.rideAlongTrackSub?.unsubscribe();
    this.rideAlongTrackSub = undefined;
    this.rideAlongWaypointsSub?.unsubscribe();
    this.rideAlongWaypointsSub = undefined;

    this.rideAlongBoatMarker?.remove();
    this.rideAlongBoatMarker = undefined;
    this.rideAlongPausedMarker?.remove();
    this.rideAlongPausedMarker = undefined;
    this.clearRideAlongWaypoints();
    this.rideAlongParticipantsOpen = false;
    this.removeRideAlongLayers();

    this.rideAlongFollowActive = false;
    this.rideAlongTripId = null;
    this.rideAlongVesselId = null;
    this.rideAlongCaptainName = '';
    this.rideAlongVesselName = '';
    this.rideAlongStartTimeMs = null;
    this.rideAlongStartTime = null;
    this.rideAlongDistanceMeters = null;
    this.rideAlongWaterTempC = null;
    this.rideAlongSpeedMs = null;
    this.rideAlongLat = null;
    this.rideAlongLng = null;
    this.rideAlongPaused = false;
    this.rideAlongTrackPolyline = [];
    this.rideAlongVesselPos = null;
    this.rideAlongVesselBearing = null;
    this.rideAlongObsPoints = [];
    // Deliberately NOT re-running resolveMyLiveTrip here: leaving a ride-along must
    // not immediately re-detect and rejoin the same trip.

    // Our own boat goes back to being tracked/followed now that we've stopped.
    this.resumeOwnVesselTracking();

    // Restore the trip to normal (teal) rendering now that we've stopped following.
    if (this.isMapLoaded) this.applyTripFilters();
  }

  /** Called by the follow card's Leave button. */
  onRideAlongLeave(): void {
    const tripId = this.rideAlongTripId;
    if (!tripId) {
      this.rideAlongService.setActiveRideAlong(null);
      return;
    }
    this.rideAlongService.leaveRideAlong(tripId).subscribe({
      next: () => this.rideAlongService.setActiveRideAlong(null),
      error: () => {
        // Tear down locally even if the server call fails so the user isn't stuck.
        this.rideAlongService.setActiveRideAlong(null);
      },
    });
  }

  /** Redraws every ride-along layer/marker from the current in-memory state. */
  private renderRideAlongAll(): void {
    this.ensureRideAlongLayers();
    this.renderRideAlongTrack();
    this.renderRideAlongObsPoints();
    this.renderRideAlongBoat();
    this.recomputeRideAlongConnector();
  }

  private refreshRideAlongTrack(initial: boolean): void {
    const tripId = this.rideAlongTripId;
    if (!tripId) return;

    // On the first fetch, race extend_track against getTripById instead of waiting for
    // extend_track to come back empty before falling back. extend_track recomputes the
    // track server-side and is by far the slower of the two, so serialising them was
    // the main reason the green line and the trip card sat empty for so long.
    // applyRideAlongTrackResponse only ever grows the polyline, so whichever lands
    // first draws and the other tops it up.
    if (initial) {
      this.tripService.getTripById(tripId).pipe(catchError(() => of(null))).subscribe((trip: any) => {
        if (trip && this.rideAlongTripId === tripId) this.applyRideAlongTrackResponse(trip, true);
      });
    }

    this.rideAlongService.extendTrack(tripId).pipe(catchError(() => of(null))).subscribe((res: any) => {
      if (this.rideAlongTripId !== tripId) return;
      this.applyRideAlongTrackResponse(res, initial);
    });
  }

  /** Mirrors Flutter _refreshRideAlongTrack: parse the extend_track TripResult. */
  private applyRideAlongTrackResponse(res: any, initial: boolean): void {
    if (!res || !this.rideAlongFollowActive) return;
    const trip = res?.trip || res?.data?.trip || res?.data || res;
    const state = (trip?.trip_state || trip?.status || '').toLowerCase();
    console.debug('[RideAlong] track response — state:', state, 'trackKeys:', Object.keys(trip?.track || {}));

    if (state === 'ended' || state === 'completed') {
      this.handleRideAlongTripEnded(trip?.name);
      return;
    }

    // Discover the captain's vessel id so the observation poll can run.
    const vesselId =
      trip?.vessel_ref?.reference_id ||
      trip?.vessel_ref?.vessel_id ||
      trip?.vessel_ref?.identifier ||
      trip?.vessel_id ||
      this.rideAlongVesselId;
    if (vesselId) this.rideAlongVesselId = String(vesselId);

    if (trip?.name) this.rideAlongTripName = trip.name;
    if (trip?.vessel_name && !this.rideAlongVesselName) this.rideAlongVesselName = trip.vessel_name;
    // Keep the inline crew (captain + riders, each with their vessel) so the On
    // Board sheet can show rider vessels instead of the vessel-less roster call.
    if (Array.isArray(trip?.ride_along_participants) && trip.ride_along_participants.length) {
      this.rideAlongTripParticipantsRaw = trip.ride_along_participants;
    }
    this.rideAlongPaused = state === 'paused';

    const start = trip?.start_time || trip?.audit_data?.create_ts;
    const startMs = start ? Date.parse(this.withZ(String(start))) : NaN;
    if (!isNaN(startMs)) {
      this.rideAlongStartTime = startMs;
      this.rideAlongStartTimeMs = startMs;
    } else if (this.rideAlongStartTimeMs == null) {
      this.rideAlongStartTimeMs = Date.now();
    }

    // Confirmed backend track. Prefer the full `track` (all points, like the app's
    // trip.track.points) and fall back to the simplified track only if absent.
    const poly = this.extractTripCoordinates(trip?.track || trip?.track_simplified).map((c) => [c[0], c[1]]);
    console.debug('[RideAlong] parsed track points:', poly.length);
    // Never shrink the track: the initial extend_track / getTripById race means the
    // two responses can arrive in either order, and the shorter one must not clobber
    // the longer. Distance grows monotonically too (matches the app's guard).
    if (poly.length && poly.length >= this.rideAlongTrackPolyline.length) {
      this.rideAlongTrackPolyline = poly;
      const distMeters = this.polylineDistanceKm(poly) * 1000;
      if (this.rideAlongDistanceMeters == null || distMeters > this.rideAlongDistanceMeters) {
        this.rideAlongDistanceMeters = distMeters;
      }
    }

    // Observation dots = track points carrying an observation id.
    const obsPoints = this.extractRideAlongObsPoints(trip?.track);
    if (obsPoints.length >= this.rideAlongObsPoints.length) this.rideAlongObsPoints = obsPoints;

    this.renderRideAlongTrack();
    this.renderRideAlongObsPoints();

    // If we have no live boat yet, fall back to the track tail for the marker.
    if (!this.rideAlongVesselPos && this.rideAlongTrackPolyline.length) {
      this.rideAlongVesselPos = this.rideAlongTrackPolyline[this.rideAlongTrackPolyline.length - 1] as [number, number];
    }
    this.renderRideAlongBoat();
    this.recomputeRideAlongConnector();

    // Initial framing: center on the captain's vessel (like the app, which moves
    // the camera to the boat), not on the middle of the whole track.
    if (initial && this.map) {
      const target = this.rideAlongVesselPos
        || (this.rideAlongTrackPolyline.length
          ? (this.rideAlongTrackPolyline[this.rideAlongTrackPolyline.length - 1] as [number, number])
          : null);
      if (target) {
        if (this.hasCenteredOnceAfterLogin) {
          this.map.easeTo({ center: target, zoom: Math.max(this.map.getZoom(), 13), duration: 900 });
        } else {
          // First framing on load: snap to the boat instantly (no animation from the
          // world view) — the reveal then shows the boat directly.
          this.map.jumpTo({ center: target, zoom: Math.max(this.map.getZoom(), 13) });
          this.hasCenteredOnceAfterLogin = true;
        }
      }
    }

    // Kick an immediate observation fetch once the vessel id is known.
    if (this.rideAlongVesselId && !this.rideAlongVesselPos) {
      this.fetchRideAlongObservation().subscribe((obs) => this.applyRideAlongObservation(obs));
    }
  }

  private fetchRideAlongObservation() {
    const tripId = this.rideAlongTripId;
    const vesselId = this.rideAlongVesselId;
    if (!tripId || !vesselId) return of(null);
    return this.rideAlongService
      .latestObservationForVesselAndTrip(vesselId, tripId)
      .pipe(catchError(() => of(null)));
  }

  /** Mirrors Flutter _refreshRideAlongObservation: parse PGNs for the live boat. */
  private applyRideAlongObservation(obs: any): void {
    if (!this.rideAlongFollowActive) return;
    const obsId = obs?._id || obs?.id || obs?.identifier;
    if (obsId) this.rideAlongLatestObsId = String(obsId);
    const parsed = this.parseRideAlongObservation(obs);
    if (parsed.waterTempC != null) this.rideAlongWaterTempC = parsed.waterTempC;

    if (parsed.pos) {
      const prevPos = this.rideAlongVesselPos;
      // Accumulate live distance beyond the track tail (like the app).
      if (
        prevPos &&
        (prevPos[0] !== parsed.pos[0] || prevPos[1] !== parsed.pos[1])
      ) {
        const meters = this.haversineKm(
          prevPos[1], prevPos[0], parsed.pos[1], parsed.pos[0],
        ) * 1000;
        if (!isNaN(meters) && meters > 0.5) {
          this.rideAlongDistanceMeters = (this.rideAlongDistanceMeters ?? 0) + meters;
        }
      }
      this.rideAlongVesselPos = parsed.pos;
      this.rideAlongLng = parsed.pos[0];
      this.rideAlongLat = parsed.pos[1];
      if (parsed.cog != null) {
        this.rideAlongVesselBearing = parsed.cog;
      } else if (prevPos) {
        // No COG in this observation — derive heading from how far the boat moved.
        const moved = this.computeBearingDeg(prevPos, parsed.pos);
        if (moved != null) this.rideAlongVesselBearing = moved;
      }
      if (parsed.sogMs != null) this.rideAlongSpeedMs = parsed.sogMs;
    } else if (!this.rideAlongVesselPos && this.rideAlongTrackPolyline.length) {
      this.rideAlongVesselPos = this.rideAlongTrackPolyline[this.rideAlongTrackPolyline.length - 1] as [number, number];
    }

    this.renderRideAlongBoat();
    this.recomputeRideAlongConnector();

    if (this.rideAlongVesselPos && this.isAutoFollowEnabled && this.isMapLoaded && this.map && !this.rideAlongPaused) {
      this.map.easeTo({ center: this.rideAlongVesselPos, duration: 800 });
    }
  }

  /** Extracts position / COG / SOG from an observation's PGNs (129029/129025/129026). */
  private parseRideAlongObservation(obs: any): {
    pos: [number, number] | null;
    cog: number | null;
    sogMs: number | null;
    waterTempC: number | null;
  } {
    const result: {
      pos: [number, number] | null;
      cog: number | null;
      sogMs: number | null;
      waterTempC: number | null;
    } = { pos: null, cog: null, sogMs: null, waterTempC: null };
    const pgns = Array.isArray(obs?.pgns) ? obs.pgns : [];
    if (!pgns.length) return result;

    const findPgn = (nums: number[], names: string[]) =>
      pgns.find((p: any) => p && (nums.includes(p.pgn) || names.includes(String(p.name).trim())));

    const posPgn =
      findPgn([129029], ['GNSSPositionData', 'GNSS Position Data']) ||
      findPgn([129025], ['PositionRapidUpdate', 'Position Rapid Update', 'Position, Rapid Update']);
    if (posPgn) {
      const f = posPgn.fields || {};
      const lat = Number(f.latitude_deg ?? f.lat_deg ?? f.latitude ?? f.lat);
      const lng = Number(f.longitude_deg ?? f.lon_deg ?? f.longitude ?? f.lon);
      if (isFinite(lat) && isFinite(lng)) result.pos = [lng, lat];
    }

    const cogSog = findPgn([129026], ['COGSOGRapidUpdate', 'COG/SOGRapidUpdate', 'COG/SOG Rapid Update']);
    if (cogSog) {
      const f = cogSog.fields || {};
      const cog = Number(f.cog_deg_true ?? f.cog_true_deg ?? f.cog_deg ?? f.cog);
      if (isFinite(cog)) result.cog = cog;
      // NB: despite the `sog_knots` field name, the payload carries metres/second —
      // the app reads the same field and formats it via speedFromMs. Treating it as
      // knots here is what made the web read ~half the app's speed.
      const sog = Number(f.sog_knots ?? f.sog_kts ?? f.sog);
      if (isFinite(sog)) result.sogMs = sog;
    }

    // Water temperature from the environmental PGNs — mirrors the app's tempPgn lookup.
    const tempPgn = findPgn(
      [130310, 130311, 130312],
      [
        'EnvironmentalParameters', 'Environmental Parameters',
        'Temperature', 'SeaTemperature', 'Sea Temperature',
        'WaterTemperature', 'Water Temperature',
      ],
    );
    if (tempPgn) {
      const f = tempPgn.fields || {};
      const t = Number(f.water_temp_c ?? f.sea_temp_c ?? f.temperature_c ?? f.temp_c ?? f.temp);
      if (isFinite(t)) result.waterTempC = t;
    }
    return result;
  }

  private extractRideAlongObsPoints(track: any): { coord: number[]; obsId: string }[] {
    const out: { coord: number[]; obsId: string }[] = [];
    if (!track || track.type !== 'FeatureCollection' || !Array.isArray(track.features)) return out;
    track.features.forEach((f: any) => {
      if (f?.geometry?.type !== 'Point') return;
      const p = f.properties || {};
      const obsId = p._id || p.observation_id || p.obs_id || p.id || p.identifier;
      const coord = this.normalizeTripCoordinate(f.geometry.coordinates);
      if (obsId && coord) out.push({ coord, obsId: String(obsId) });
    });
    return out;
  }

  private handleRideAlongTripEnded(tripName?: string): void {
    if (this.rideAlongEndedHandled) return;
    this.rideAlongEndedHandled = true;
    const name = tripName || this.rideAlongTripName || 'The trip';
    // setActiveRideAlong(null) triggers exitRideAlongFollow via the subscription.
    this.rideAlongService.setActiveRideAlong(null);
    this.toast.info(`"${name}" you were riding along with has ended.`);
  }

  // ── Ride Along rendering ─────────────────────────────────────────────────

  private ensureRideAlongLayers(): void {
    if (!this.map || !this.isMapLoaded) return;
    const green = HomePageComponent.RIDE_ALONG_GREEN;

    if (!this.map.getSource(this.rideAlongTrackSourceId)) {
      this.map.addSource(this.rideAlongTrackSourceId, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    }
    if (!this.map.getLayer(this.rideAlongTrackGlowLayerId)) {
      this.map.addLayer({
        id: this.rideAlongTrackGlowLayerId,
        type: 'line',
        source: this.rideAlongTrackSourceId,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': green, 'line-width': 7, 'line-opacity': 0.19 },
      });
    }
    if (!this.map.getLayer(this.rideAlongTrackLineLayerId)) {
      this.map.addLayer({
        id: this.rideAlongTrackLineLayerId,
        type: 'line',
        source: this.rideAlongTrackSourceId,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': green, 'line-width': 3.5, 'line-opacity': 1 },
      });
    }

    // Dotted connector: track tail → live boat.
    if (!this.map.getSource(this.rideAlongConnectorSourceId)) {
      this.map.addSource(this.rideAlongConnectorSourceId, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    }
    if (!this.map.getLayer(this.rideAlongConnectorLayerId)) {
      this.map.addLayer({
        id: this.rideAlongConnectorLayerId,
        type: 'line',
        source: this.rideAlongConnectorSourceId,
        // Round line-cap + [0, gap] dash array renders as round DOTS — a dotted
        // leash from the confirmed track tail to the live boat (matches the app).
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': green, 'line-width': 4, 'line-opacity': 1, 'line-dasharray': [0, 2] },
      });
    }

    // Observation dots along the track.
    if (!this.map.getSource(this.rideAlongPointsSourceId)) {
      this.map.addSource(this.rideAlongPointsSourceId, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    }
    if (!this.map.getLayer(this.rideAlongPointsLayerId)) {
      this.map.addLayer({
        id: this.rideAlongPointsLayerId,
        type: 'circle',
        source: this.rideAlongPointsSourceId,
        paint: {
          'circle-radius': 5,
          'circle-color': green,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1.5,
        },
      });
    }

    // Tapping an observation dot opens that observation's popup (vessel details),
    // which itself carries a "Participants" row — matches the app.
    if (!this.rideAlongPointsClickBound) {
      this.rideAlongPointsClickBound = true;
      this.map.on('click', this.rideAlongPointsLayerId, (e: any) => {
        const f = e?.features?.[0];
        const obsId = f?.properties?.obsId;
        const c = f?.geometry?.coordinates;
        if (obsId) this.ngZone.run(() => this.fetchAndShowObservation(String(obsId), c, this.rideAlongTripId || undefined));
        else this.ngZone.run(() => this.openRideAlongBoatObservation());
      });
      this.map.on('mouseenter', this.rideAlongPointsLayerId, () => { this.map.getCanvas().style.cursor = 'pointer'; });
      this.map.on('mouseleave', this.rideAlongPointsLayerId, () => { this.map.getCanvas().style.cursor = ''; });
    }
  }


  /** Opens the ride-along participants sheet (captain + riders) for the followed trip. */
  openRideAlongParticipants(): void {
    if (!this.rideAlongTripId) return;
    // Build the crew from the trip's inline participants (carries each rider's
    // vessel); fall back to the roster endpoint inside the sheet if we have none.
    const { captain, participants } = this.rideAlongService.participantsFromTrip({
      ride_along_participants: this.rideAlongTripParticipantsRaw,
      vessel_name: this.rideAlongVesselName,
    });
    this.rideAlongParticipantsList = [...(captain ? [captain] : []), ...participants];
    this.rideAlongParticipantsTripId = this.rideAlongTripId;
    this.rideAlongParticipantsOpen = true;
  }

  closeRideAlongParticipants(): void {
    this.rideAlongParticipantsOpen = false;
  }

  /** Opens the ride-along Added Waypoints modal (Mine / Others) for the followed trip. */
  openRideAlongWaypoints(): void {
    if (!this.rideAlongTripId) return;
    this.rideAlongWaypointsTripId = this.rideAlongTripId;
    this.rideAlongWaypointsOpen = true;
  }

  closeRideAlongWaypoints(): void {
    this.rideAlongWaypointsOpen = false;
  }

  /** A waypoint was removed from the Added Waypoints sheet — drop its pin from the map. */
  onRideAlongWaypointRemoved(waypointId: string): void {
    const id = String(waypointId || '').trim();
    if (!id) return;
    this.allWaypoints = this.allWaypoints.filter((wp) => this.getWaypointId(wp) !== id);
    this.rideAlongWaypointIds.delete(id);
    if (this.isMapLoaded) this.applyMapFilters();
  }

  private removeRideAlongLayers(): void {
    if (!this.map) return;
    const layers = [
      this.rideAlongTrackGlowLayerId,
      this.rideAlongTrackLineLayerId,
      this.rideAlongConnectorLayerId,
      this.rideAlongPointsLayerId,
    ];
    const sources = [this.rideAlongTrackSourceId, this.rideAlongConnectorSourceId, this.rideAlongPointsSourceId];
    layers.forEach((id) => { try { if (this.map.getLayer(id)) this.map.removeLayer(id); } catch { /* ignore */ } });
    sources.forEach((id) => { try { if (this.map.getSource(id)) this.map.removeSource(id); } catch { /* ignore */ } });
  }

  private renderRideAlongTrack(): void {
    if (!this.map || !this.isMapLoaded) return;
    this.ensureRideAlongLayers();
    const source = this.map.getSource(this.rideAlongTrackSourceId) as maplibregl.GeoJSONSource | null;
    if (!source) return;
    const coords = this.rideAlongTrackPolyline;
    source.setData({
      type: 'FeatureCollection',
      features: coords.length >= 2 ? [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords } }] : [],
    } as any);
  }

  private renderRideAlongObsPoints(): void {
    if (!this.map || !this.isMapLoaded) return;
    const source = this.map.getSource(this.rideAlongPointsSourceId) as maplibregl.GeoJSONSource | null;
    if (!source) return;
    source.setData({
      type: 'FeatureCollection',
      features: this.rideAlongObsPoints.map((p) => ({
        type: 'Feature',
        properties: { obsId: p.obsId },
        geometry: { type: 'Point', coordinates: p.coord },
      })),
    } as any);
  }

  /**
   * Renders the trip's added waypoints (mine + others) as icon markers on the
   * map while following — web equivalent of the app's Added Waypoints overlay.
   * "Mine" get a blue ring, "others" a grey ring.
   */
  private renderRideAlongWaypoints(waypoints: any[]): void {
    if (!this.map) return;
    this.clearRideAlongWaypoints();
    if (!Array.isArray(waypoints) || !waypoints.length) return;

    waypoints.forEach((wp) => {
      const pos = this.extractPosDetails(wp?.position) || this.extractPosDetails(wp?.geometry);
      if (!pos) return;

      // Only render OTHER mates' waypoints here — the current user's own waypoints
      // are already drawn by the normal waypoint layer, so rendering them again
      // caused the duplicate icons cluttering the boat.
      const creatorId = wp?.audit_data?.create_user_id || wp?.source_profile?.id || wp?.creator;
      const isMine = !!creatorId && this.myUserIds.includes(String(creatorId));
      if (isMine) return;

      const el = document.createElement('div');
      el.className = 'ride-along-waypoint';
      el.style.zIndex = '2'; // below the boat
      const img = document.createElement('img');
      try {
        img.src = `/${getWaypointIconAsset(wp, 'map')}`.replace('//assets', '/assets');
      } catch {
        img.src = '/assets/common_icons/waypoint_pin_white.svg';
      }
      // Plain waypoint icon (like the normal waypoint markers / the app) — no heavy
      // white disc that hides the boat.
      img.style.width = '30px';
      img.style.height = '30px';
      img.style.objectFit = 'contain';
      img.style.filter = 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))';
      el.appendChild(img);

      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([pos.lng, pos.lat]).addTo(this.map);
      this.rideAlongWaypointMarkers.push(marker);
    });
  }

  private clearRideAlongWaypoints(): void {
    this.rideAlongWaypointMarkers.forEach((m) => m.remove());
    this.rideAlongWaypointMarkers = [];
    // Remove the other mates' waypoints we merged into the normal set. Dropping them
    // from `allWaypoints` is not enough on its own — their markers stay on the map
    // until the pins are re-rendered, which is why a previous captain's waypoints
    // survived the trip ending and reappeared over the next ride-along.
    if (this.rideAlongWaypointIds.size) {
      this.allWaypoints = this.allWaypoints.filter((wp) => !this.rideAlongWaypointIds.has(this.getWaypointId(wp)));
      this.rideAlongWaypointIds.clear();
      if (this.isMapLoaded) this.applyMapFilters();
    }
  }

  private recomputeRideAlongConnector(): void {
    if (!this.map || !this.isMapLoaded) return;
    const source = this.map.getSource(this.rideAlongConnectorSourceId) as maplibregl.GeoJSONSource | null;
    if (!source) return;

    const empty = () => { source.setData({ type: 'FeatureCollection', features: [] } as any); };
    if (!this.rideAlongTrackPolyline.length || !this.rideAlongVesselPos) { empty(); return; }

    const anchor = this.rideAlongTrackPolyline[this.rideAlongTrackPolyline.length - 1];
    const boat = this.rideAlongVesselPos;
    if (anchor[0] === boat[0] && anchor[1] === boat[1]) { empty(); return; }

    // 75km guard, identical to the app.
    const meters = this.haversineKm(anchor[1], anchor[0], boat[1], boat[0]) * 1000;
    if (isNaN(meters) || meters > 75000) { empty(); return; }

    console.debug('[RideAlong] connector drawn — gap(m):', Math.round(meters));
    source.setData({
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: [anchor, boat] } }],
    } as any);
  }

  /**
   * Renders the boat marker at the live observation position (green circle + boat
   * image rotated by cog+30). When the captain is paused, the moving boat is
   * replaced by a static red dot bound to the FROZEN track tail (app FE-1c).
   */
  private renderRideAlongBoat(): void {
    if (!this.map) return;

    // Paused → red dot at the track tail, no moving boat.
    if (this.rideAlongPaused && this.rideAlongTrackPolyline.length) {
      this.rideAlongBoatMarker?.remove();
      this.rideAlongBoatMarker = undefined;
      const tail = this.rideAlongTrackPolyline[this.rideAlongTrackPolyline.length - 1];
      if (!this.rideAlongPausedMarker) {
        const el = document.createElement('div');
        el.className = 'ride-along-paused-dot';
        el.style.width = '18px';
        el.style.height = '18px';
        el.style.borderRadius = '50%';
        el.style.background = '#FF3B30';
        el.style.border = '3px solid #ffffff';
        el.style.boxShadow = '0 0 0 2px rgba(255,59,48,0.4)';
        this.rideAlongPausedMarker = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([tail[0], tail[1]]).addTo(this.map);
      } else {
        this.rideAlongPausedMarker.setLngLat([tail[0], tail[1]]);
      }
      return;
    }

    // Active → remove paused dot, draw/update the moving boat.
    this.rideAlongPausedMarker?.remove();
    this.rideAlongPausedMarker = undefined;
    if (!this.rideAlongVesselPos) return;

    const mapBrg = this.map.getBearing() ?? 0;
    // COG is the preferred heading, but observations often arrive without it, which
    // left the boat frozen facing "up". Fall back to the direction of travel (the
    // last track segment) so the icon always points where the boat is going.
    const bearing = this.rideAlongVesselBearing ?? this.rideAlongTrackTailBearing() ?? 0;
    const rot = bearing + this.skins.headingOffsetFor(this.rideAlongVesselId) - mapBrg;

    if (!this.rideAlongBoatMarker) {
      const el = document.createElement('div');
      el.className = 'ride-along-boat';
      // Larger on desktop, where the phone-sized 48px disc reads as tiny.
      const discPx = isDesktopViewport() ? 72 : 48;
      el.style.width = `${discPx}px`;
      el.style.height = `${discPx}px`;
      el.style.borderRadius = '50%';
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';
      el.style.background = 'rgba(52,199,89,0.25)';
      el.style.border = '2px solid rgba(52,199,89,0.6)';
      el.style.cursor = 'pointer';
      const img = document.createElement('img');
      img.src = this.skins.iconSrcFor(this.rideAlongVesselId);
      const hullPx = Math.round(discPx * 0.73);
      img.style.width = `${hullPx}px`;
      img.style.height = `${hullPx}px`;
      img.style.objectFit = 'contain';
      img.style.transform = `rotate(${rot}deg)`;
      el.appendChild(img);
      // Tapping the boat opens its observation popup (vessel details), which has a
      // "Participants" row — matches the app.
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        this.ngZone.run(() => this.openRideAlongBoatObservation());
      });
      this.rideAlongBoatMarker = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat(this.rideAlongVesselPos).addTo(this.map);
    } else {
      this.rideAlongBoatMarker.setLngLat(this.rideAlongVesselPos);
      const img = this.rideAlongBoatMarker.getElement().querySelector('img');
      if (img) {
        img.src = this.skins.iconSrcFor(this.rideAlongVesselId);
        img.style.transform = `rotate(${rot}deg)`;
      }
    }
  }

  /** Great-circle initial bearing (degrees, 0–360) from one lng/lat to another; null if identical. */
  private computeBearingDeg(from: [number, number], to: [number, number]): number | null {
    const [lon1, lat1] = from;
    const [lon2, lat2] = to;
    if (lon1 === lon2 && lat1 === lat2) return null;
    const toRad = Math.PI / 180;
    const φ1 = lat1 * toRad;
    const φ2 = lat2 * toRad;
    const Δλ = (lon2 - lon1) * toRad;
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    const θ = Math.atan2(y, x) / toRad;
    return (θ + 360) % 360;
  }

  /** Heading of the last drawn track segment — the fallback boat direction when COG is missing. */
  private rideAlongTrackTailBearing(): number | null {
    const p = this.rideAlongTrackPolyline;
    if (!p || p.length < 2) return null;
    const last = p[p.length - 1] as [number, number];
    for (let i = p.length - 2; i >= 0; i--) {
      const b = this.computeBearingDeg(p[i] as [number, number], last);
      if (b != null) return b;
    }
    return null;
  }

  /** Opens the followed boat's observation popup (latest obs, or nearest track dot). */
  private openRideAlongBoatObservation(): void {
    const coord = this.rideAlongVesselPos || undefined;
    if (this.rideAlongLatestObsId) {
      this.ngZone.run(() => this.fetchAndShowObservation(this.rideAlongLatestObsId!, coord, this.rideAlongTripId || undefined));
      return;
    }
    // Fall back to the nearest observation dot if we don't have a live obs id yet.
    if (coord && this.rideAlongObsPoints.length) {
      let best = this.rideAlongObsPoints[0];
      let bestD = Infinity;
      for (const p of this.rideAlongObsPoints) {
        const d = (p.coord[0] - coord[0]) ** 2 + (p.coord[1] - coord[1]) ** 2;
        if (d < bestD) { bestD = d; best = p; }
      }
      this.ngZone.run(() => this.fetchAndShowObservation(best.obsId, best.coord, this.rideAlongTripId || undefined));
    }
  }

  private polylineDistanceKm(coords: number[][]): number {
    if (coords.length < 2) return 0;
    let km = 0;
    for (let i = 1; i < coords.length; i++) {
      km += this.haversineKm(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0]);
    }
    return km;
  }

  private haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  private fitMapToCoords(coords: number[][]): void {
    if (!this.map || coords.length === 0) return;
    if (coords.length === 1) {
      this.map.easeTo({ center: [coords[0][0], coords[0][1]], zoom: Math.max(this.map.getZoom(), 12), duration: 900 });
      return;
    }
    const bounds = new maplibregl.LngLatBounds();
    coords.forEach(([lng, lat]) => bounds.extend([lng, lat]));
    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds, { padding: { top: 80, right: 80, bottom: 200, left: 80 }, duration: 1000 });
    }
  }

  /** Append a trailing 'Z' if a timestamp lacks a timezone, so Date.parse treats it as UTC. */
  private withZ(ts: string): string {
    if (!ts) return ts;
    return /[zZ]|[+-]\d{2}:?\d{2}$/.test(ts) ? ts : `${ts}Z`;
  }

  ngOnDestroy(): void {
    if (this.deferredMapInitHandle != null) {
      if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(this.deferredMapInitHandle);
      window.clearTimeout(this.deferredMapInitHandle);
      this.deferredMapInitHandle = null;
    }
    this.submenuService.setDockmateAndFleetOverlay(false);
    this.exitRideAlongFollow();
    this.rideAlongService.stopInvitePolling();
    // Combo: stop playback timers and revoke any object URLs we created.
    this.combo.pause();
    this.removeComboLayers();
    if (this.gpsWatchId !== null) {
      navigator.geolocation.clearWatch(this.gpsWatchId);
      this.gpsWatchId = null;
    }
    this.clearWaypointHighlight(true);
    this.vesselTrackingSubs.forEach((sub: Subscription) => sub.unsubscribe());
    this.vesselTrackingSubs.clear();
    this.vesselClusterSub?.unsubscribe();
    this.clustering.reset();
    this.liveVesselStore.clear();
    this.dockmateDetailSub?.unsubscribe();
    this.stopExtendTrackPolling(true);
    this.vesselMarkers.forEach((m: maplibregl.Marker) => m.remove());
    this.vesselMarkers.clear();
    this.dockmateMarkers.forEach(m => m.remove());
    this.clearBuoyMarkers();
    this.clearTideMarkers();
    this.clearWreckMarkers();
    this.replayService.reset();
    this._clearReplayMap();
    this.map?.remove();
    this.subs.unsubscribe();
  }
  zoomIn(): void {
    this.map.zoomIn();
  }

  zoomOut(): void {
    if (this.isWeatherOverlayMinZoomReached()) return;
    this.map.zoomOut();
  }

  goToCurrentLocation(): void {
    // Re-enable auto-follow
    this.isAutoFollowEnabled = true;

    // While riding along, "locate me" means the trip we're following — the captain's
    // boat is the only thing being tracked, so recentre on it (matches the app).
    if (this.rideAlongFollowActive && this.rideAlongVesselPos && this.map) {
      this.map.flyTo({
        center: this.rideAlongVesselPos,
        zoom: Math.max(this.map.getZoom(), 13),
        speed: 1.4,
      });
      return;
    }

    // Prefer centering on the primary vessel if available in tracking store
    const trackedVessels = Array.from(this.liveVesselStore.values());
    const primaryVessel = trackedVessels.find(v => v.isPrimary);

    if (primaryVessel && primaryVessel.lat != null && primaryVessel.lng != null && this.map) {
      this.map.flyTo({ center: [primaryVessel.lng, primaryVessel.lat], zoom: Math.max(this.map.getZoom(), 13), speed: 1.4 });
      return;
    }

    // Fallback to latestObservation from initial vessel load
    if (this.latestObservation && this.latestObservation.lat != null && this.latestObservation.lng != null && this.map) {
      this.map.flyTo({ center: [this.latestObservation.lng, this.latestObservation.lat], zoom: Math.max(this.map.getZoom(), 13), speed: 1.4 });
      return;
    }

    // Fallback to cached device GPS fix
    if (this.lastKnownGps && this.map) {
      const { lat, lng } = this.lastKnownGps;
      this.map.flyTo({ center: [lng, lat], zoom: Math.max(this.map.getZoom(), 13), speed: 1.4 });
      return;
    }

    if (!navigator.geolocation) {
      console.warn('[HomePage] Geolocation is not supported by this browser');
      alert('Geolocation is not supported by your browser.');
      return;
    }

    console.debug('[HomePage] Requesting browser geolocation…');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lng = position.coords.longitude;
        const lat = position.coords.latitude;
        const accuracy = position.coords.accuracy; // meters

        console.debug(`[HomePage] Got user location: ${lat}, ${lng} (±${accuracy}m)`);

        this.lastKnownGps = { lat, lng };

        // Fly to user's actual location
        this.map.flyTo({
          center: [lng, lat],
          zoom: 13,
          speed: 1.2,
        });

        // ----- BLUE DOT MARKER -----
        if (!this.userLocationMarker) {
          const el = document.createElement('div');
          el.className = 'user-location-marker';

          this.userLocationMarker = new maplibregl.Marker({ element: el })
            .setLngLat([lng, lat])
            .addTo(this.map);
        } else {
          this.userLocationMarker.setLngLat([lng, lat]);
        }

        // Accuracy circle
        const meters = Math.max(accuracy || 0, 10);
        const circle = this.createGeoJsonCircle([lng, lat], meters / 1000); // km
        const geojson = {
          type: 'FeatureCollection',
          features: [circle],
        } as GeoJSON.FeatureCollection;

        try {
          if (this.map.getLayer(this.accuracyCircleId)) {
            this.map.removeLayer(this.accuracyCircleId);
          }
        } catch (e) { /* ignore */ }
        try {
          if (this.map.getSource(this.accuracyCircleId)) {
            this.map.removeSource(this.accuracyCircleId as any);
          }
        } catch (e) { /* ignore */ }

        this.map.addSource(this.accuracyCircleId, {
          type: 'geojson',
          data: geojson,
        });

        this.map.addLayer({
          id: this.accuracyCircleId,
          type: 'fill',
          source: this.accuracyCircleId,
          paint: {
            'fill-color': '#2563eb',
            'fill-opacity': 0.15,
          },
        });
      },
      (error) => {
        console.error('[HomePage] Geolocation error:', error.message);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            alert('Location access was denied. Please allow location access in your browser settings and try again.');
            break;
          case error.POSITION_UNAVAILABLE:
            alert('Your location could not be determined. Please try again.');
            break;
          case error.TIMEOUT:
            alert('Location request timed out. Please try again.');
            break;
          default:
            alert('Unable to get your location. Please try again.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 30000,
      }
    );
  }


  onSearch(): void {
    console.log('Open search modal');
  }

  private parseObservation(obs: any): any {
    const parsed: any = {};
    if (obs && Array.isArray(obs.pgns)) {
      obs.pgns.forEach((p: any) => {
        const payload = typeof p.payload === 'string' ? JSON.parse(p.payload) : (p.payload || p.fields);
        if (!payload) return;

        // Sometimes the API returns 'fields' directly on the PGN object, sometimes inside 'payload'
        const f = p.fields || payload.fields || payload;
        if (!f) return;

        switch (p.name) {
          case 'GNSSPositionData':
          case 'GNSS Position Data':
            // Sometimes SOG/COG is here, but usually it's in COGSOGRapidUpdate
            if (f.sog_ms != null && parsed.sog == null) parsed.sog = f.sog_ms * 1.94384;
            if (f.cog_deg != null && parsed.cog == null) parsed.cog = f.cog_deg;
            if (f.lat_deg != null) parsed.lat = Number(f.lat_deg);
            if (f.lon_deg != null) parsed.lng = Number(f.lon_deg);
            break;
          case 'COGSOGRapidUpdate':
            if (f.cog_deg != null) parsed.cog = f.cog_deg;
            if (f.sog != null) parsed.sog = f.sog * 1.94384;
            if (f.sog_ms != null) parsed.sog = f.sog_ms * 1.94384;
            break;
          case 'Temperature':
          case 'EnvironmentalParameters':
          case 'EnvironmentalParametersV2':
          case 'Environmental Parameters':
            if (f.temp_c != null) parsed.temp = (f.temp_c * 9 / 5) + 32;
            break;
          case 'WaterDepth':
          case 'Water Depth':
            if (f.depth_m != null) parsed.depth = f.depth_m * 3.28084;
            break;
        }
      });
    }
    return parsed;
  }

  private refreshWaypointMarkers(): void {
    if (!this.displayedMarkers.length) return;
    console.debug('[HomePage] Refreshing all waypoint markers with user settings');
    this.displayedMarkers.forEach(marker => {
      const el = marker.getElement();
      const wpid = el.getAttribute('data-wpid');
      if (wpid) {
        const wp = this.allWaypoints.find(w => this.getWaypointId(w) === wpid);
        if (wp && this.activeWaypointCardWaypointId === String(wpid).trim()) {
          this.mountWaypointCard(marker, wp);
        }
      }
    });
  }

  /**
   * Own-vessel tracking is suspended for the whole time we're riding along.
   *
   * The app only ever watches one vessel at a time, so while following a captain the
   * web must not also poll /observations/latest for the user's own boat: both streams
   * call easeTo/flyTo and the camera ping-pongs between the two vessels. We remember
   * which vessels were being tracked so they can be resumed on leave.
   */
  private suspendedVesselTrackingIds: string[] = [];

  private suspendOwnVesselTracking(): void {
    if (!this.vesselTrackingSubs.size) return;
    this.suspendedVesselTrackingIds = Array.from(this.vesselTrackingSubs.keys());
    this.vesselTrackingSubs.forEach((sub) => sub.unsubscribe());
    this.vesselTrackingSubs.clear();
    console.debug('[RideAlong] suspended own-vessel tracking for', this.suspendedVesselTrackingIds);
  }

  private resumeOwnVesselTracking(): void {
    const ids = this.suspendedVesselTrackingIds;
    this.suspendedVesselTrackingIds = [];
    ids.forEach((id) => this.startVesselTracking(id, true));
  }

  private startVesselTracking(vesselId: string, isPrimary: boolean = true): void {
    // While riding along we follow the captain's vessel only (see suspendOwnVesselTracking).
    if (this.rideAlongFollowActive) {
      if (!this.suspendedVesselTrackingIds.includes(vesselId)) this.suspendedVesselTrackingIds.push(vesselId);
      return;
    }
    if (this.vesselTrackingSubs.has(vesselId)) {
      this.vesselTrackingSubs.get(vesselId)?.unsubscribe();
      this.vesselTrackingSubs.delete(vesselId);
    }

    console.debug('[HomePage] Starting real-time vessel tracking for', vesselId, 'isPrimary:', isPrimary);

    const sub = this.pollScheduler
      .poll$(() => this.vesselService.getLatestObservation(vesselId).pipe(catchError(() => of(null))), VESSEL_TRACKING_POLL_MS, {
        skipInitialFetch: true,
        startAfter$: this.mapFirstPaint$,
        changeKey: latestObservationKey,
      })
      .subscribe((obs: any) => {
        if (!obs || !obs.pgns || !this.map) return;

        let lat: number | null = null;
        let lon: number | null = null;
        let heading: number | null = null;
        let ts_utc: string | null = null;
        const latestObsId = String(obs?._id || obs?.id || obs?.identifier || '').trim() || null;

        for (const p of obs.pgns) {
          const f = p.fields || {};
          if ((p.name === 'GNSSPositionData' || p.pgn === 129029) && f.lat_deg != null && f.lon_deg != null) {
            lat = Number(f.lat_deg);
            lon = Number(f.lon_deg);
            ts_utc = p.ts_utc || obs.ts_utc || null;
          }
          if ((p.name === 'VesselHeading' || p.pgn === 127250) && f.heading_deg != null) {
            heading = Number(f.heading_deg);
          }
          // Fallback heading from COG if heading is missing
          if (heading === null && (p.name === 'COGSOGRapidUpdate' || p.pgn === 129026) && f.cog_deg != null) {
            heading = Number(f.cog_deg);
          }
        }

        if (!ts_utc) ts_utc = obs.ts_utc || null;

        if (lat !== null && lon !== null) {
          this.updateVesselMarker(vesselId, lat, lon, heading, latestObsId, ts_utc, isPrimary);
        }
      });

    this.vesselTrackingSubs.set(vesselId, sub);
  }

  private updateVesselMarker(vesselId: string, lat: number, lon: number, heading: number | null, latestObsId: string | null = null, ts_utc: string | null = null, isPrimary: boolean = true): void {
    if (!this.map) return;

    // Remember the latest obs id + position so a tap on this vessel's WebGL icon
    // can open its observation popup (own/primary vessel included).
    if (vesselId) this.vesselLatestObs.set(vesselId, { obsId: latestObsId, lng: lon, lat });

    if (isPrimary) {
      this.integrateLatestObservationIntoActiveTrip(lat, lon, latestObsId);
      this.updateLiveVesselConnection(lat, lon);
    }

    if (isPrimary) {
      this.followActiveTripVesselIfNeeded(lon, lat);

      // Auto-follow: if location snap is active, smoothly keep the map centered on the primary vessel.
      if (this.isAutoFollowEnabled && this.isMapLoaded && this.map) {
        if (this.hasCenteredOnceAfterLogin) {
          this.map.easeTo({ center: [lon, lat], duration: 800 });
        } else {
          // First fix after login: SNAP straight to the boat. Animating (easeTo) from
          // the initial [-85,32] world view is the "camera zooms to Central America then
          // to the vessel" jank — a jump lands us on the boat before the map is revealed.
          this.map.jumpTo({ center: [lon, lat], zoom: Math.max(this.map.getZoom(), 12) });
          this.hasCenteredOnceAfterLogin = true;
        }
      }
    }

    // Register vessel in the clustering store
    this.registerVesselForClustering({
      id: vesselId,
      lat,
      lng: lon,
      heading,
      speed: null,
      isPrimary,
      cog: heading,
      lastSeen: ts_utc,
    });
  }

  private integrateLatestObservationIntoActiveTrip(lat: number, lon: number, obsId: string | null): void {
    const currentTripId = this.tripService.getCurrentTripId();
    if (!currentTripId) return;

    const trip = this.allTrips.find((item) => this.getTripId(item) === currentTripId);
    if (!trip || !this.isTripMine(trip)) return;

    const state = (trip?.trip_state || trip?.status || '').toLowerCase();
    if (!this.isTripOngoingState(state)) return;

    const coord: number[] = [Number(lon), Number(lat)];
    const lineChanged = this.appendCoordsToTripLine(currentTripId, [coord]);
    const pointChanged = this.appendTripPointIfMissing(currentTripId, coord, obsId || undefined);

    if (!lineChanged && !pointChanged) return;

    // ACCUMULATE the live position (don't reset the path) so the solid live-track
    // line keeps the whole path. Resetting it here erased the line on every
    // observation — which, with frequent obs polling, left the captain with no
    // visible track at all. The anchor is the track tail set when polling started.
    if (!this.activeTripLiveAnchorCoord) this.activeTripLiveAnchorCoord = coord;
    this.pushLivePathCoords([coord]);
    this.commitTripSourceCache();
    this.updateLiveTrackDottedPathSource([lon, lat]);
  }

  private updateLiveVesselConnection(lat: number, lon: number): void {
    if (!this.map || !this.isMapLoaded) return;

    const source = this.map.getSource(this.tripLiveConnectionSourceId) as any;
    if (!source) return;

    const currentTripId = this.tripService.getCurrentTripId();
    if (!currentTripId) {
      source.setData({ type: 'FeatureCollection', features: [] });
      return;
    }

    const myActiveTrip = this.allTrips.find((t) => this.getTripId(t) === currentTripId && this.isTripMine(t));

    if (!myActiveTrip) {
      source.setData({ type: 'FeatureCollection', features: [] });
      return;
    }

    const state = (myActiveTrip?.trip_state || myActiveTrip?.status || '').toLowerCase();
    if (!this.isTripOngoingState(state)) {
      source.setData({ type: 'FeatureCollection', features: [] });
      return;
    }

    if (this.extendTrackTripId === currentTripId && this.activeTripLiveAnchorCoord) {
      this.updateLiveTrackDottedPathSource([lon, lat]);
      return;
    }

    const line = this.findTripLineFeature(currentTripId);
    const coords = this.getLineCoordinatesFromFeature(line);
    const lastCoord = coords.length ? coords[coords.length - 1] : null;
    if (!lastCoord) {
      source.setData({ type: 'FeatureCollection', features: [] });
      return;
    }

    source.setData({
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [lastCoord, [lon, lat]]
        }
      }]
    });
  }

  private followActiveTripVesselIfNeeded(lon: number, lat: number): void {
    if (!this.map || !this.isMapLoaded) return;
    if (this.hasCenteredOnceAfterLogin) return;

    const currentTripId = this.tripService.getCurrentTripId();
    if (!currentTripId) return;

    const trip = this.allTrips.find((t) => this.getTripId(t) === currentTripId);
    if (!trip || !this.isTripMine(trip)) return;

    const state = (trip?.trip_state || trip?.status || '').toLowerCase();
    if (!this.isTripOngoingState(state)) return;

    this.map.easeTo({
      center: [lon, lat],
      zoom: this.map.getZoom(),
      duration: 750,
      essential: true,
    });
    this.hasCenteredOnceAfterLogin = true;
  }

  private syncMapOverlayLayers(configs: any[]): void {
    if (!this.map) return;

    const knownTypes = ['sst', 'chlorophyll', 'wind'];
    let renderConfigs = configs;

    if (configs.length > 1) {
      const selectedType = this.activeOverlayWidgetType && configs.some(c => c.type === this.activeOverlayWidgetType)
        ? this.activeOverlayWidgetType
        : configs[0].type;
      this.activeOverlayWidgetType = selectedType;
      renderConfigs = configs.filter(c => c.type === selectedType);
    }

    const renderTypes = new Set(renderConfigs.map(c => c.type));

    // Remove layers/sources that are not currently selected for rendering
    knownTypes.forEach(type => {
      if (!renderTypes.has(type)) {
        this.removeMapOverlayLayer(type);
      }
    });

    // Add or update the selected overlay only
    renderConfigs.forEach(config => {
      this.applyMapOverlayLayer(config);
    });

    this.syncWeatherOverlayZoomLock(renderTypes.has('wind'));

    // Overlay layers are added on top — push replay lines back above them
    this._liftReplayLayers();
  }

  /** Move replay layers to the top of the stack so they render above any overlay. */
  private _liftReplayLayers(): void {
    if (!this.map) return;
    try {
      // Ensure vessel cluster layers render above any overlays. These should
      // always float on top of raster overlays so users can interact with
      // clusters regardless of overlay visibility.
      const clusterLayerIds = [
        this.vesselClusterCircleLayerId,
        this.vesselClusterCircleLayerId + '-icon',
        this.vesselClusterCircleLayerId + '-badge',
        this.vesselClusterCountLayerId,
        this.vesselUnclusteredLayerId,
      ];
      clusterLayerIds.forEach((id) => {
        if (this.map!.getLayer(id)) this.map!.moveLayer(id);
      });

      // Replay layers still need to be moved above overlays when replay is active.
      if (this.isReplayMode) {
        if (this.map.getLayer(this.replayPolylinesLineLayerId)) this.map.moveLayer(this.replayPolylinesLineLayerId);
        if (this.map.getLayer(this.replayPolylinesSharedLayerId)) this.map.moveLayer(this.replayPolylinesSharedLayerId);
        if (this.map.getLayer(this.replayWaypointsLayerId)) this.map.moveLayer(this.replayWaypointsLayerId);
      }
    } catch (_) { }
  }

  /**
   * Builds a composite cache key from the overlay config.
   * Format: "type|source|region|date" — uniquely identifies a layer+date combination.
   */
  private buildOverlayCacheKey(config: any): string {
    const date = config.date instanceof Date
      ? config.date.toISOString().slice(0, 10)
      : String(config.date ?? '').slice(0, 10);

    let bboxSuffix = '';
    if (config.type === 'wind' && this.map) {
      const b = this.map.getBounds();
      bboxSuffix = `| ${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}| Z${Math.round(this.map.getZoom())} `;
    }

    const rutgersSuffix = config?.source === 'Rutgers Satellite Imagery' && config?.rutgersImageId
      ? `| IMG:${String(config.rutgersImageId)} `
      : '';
    return `${config.type}| ${config.source ?? ''}| ${config.region ?? ''}| ${date}${rutgersSuffix}${bboxSuffix} `;
  }

  private isSstOrChlorophyllOverlaySourceId(sourceId: any): boolean {
    const id = String(sourceId ?? '');
    return id.includes(`${this.rasterOverlaySourceId} -sst `)
      || id.includes(`${this.rasterOverlaySourceId} -chlorophyll `);
  }

  private applyMapOverlayLayer(config: any): void {
    if (!this.map) { console.warn('[Overlay] map not ready'); return; }

    const sourceId = `${this.rasterOverlaySourceId} -${config.type} `;
    const layerId = `${this.rasterOverlayLayerId} -${config.type} `;

    if (config.type === 'wind') {
      this.applyWeatherOverlayLayer(config, false);
      return;
    }

    const shouldTrackRasterLoad = config.type === 'sst' || config.type === 'chlorophyll';
    if (shouldTrackRasterLoad) {
      this.submenuService.setOverlayLoading(true);
      // Mark this raster source as performing its initial load so map
      // dataloading/data handlers only show the loader for that initial work.
      this.currentRasterOverlayInitialLoadSourceId = sourceId;
    }

    // Map source names to API dataset codes
    const sourceCodeMap: Record<string, string> = {
      'VIIRS SNPP Chlorophyll-a': 'chl_snpp',
      'MODIS Aqua Chlorophyll-a': 'chl_aqua',
      'MUR SST (Global)': 'sst_mur',
      'ACSPO GOES-19 (Regional)': 'acspo',
    };

    const rutgersRegionCodeMap: Record<string, string> = {
      'Cape Hatteras': 'capehat',
      'Hudson Canyon': 'hudsoncyn',
      'Mid-Atlantic Bight': 'midatl',
      'South New England': 'sne',
    };

    const sourceCode = sourceCodeMap[config.source]
      ?? (config.type === 'chlorophyll' ? 'chl_snpp' : 'sst_mur');

    // Format date as YYYY-MM-DD
    const dateStr = config.date instanceof Date
      ? config.date.toISOString().slice(0, 10)
      : String(config.date ?? '').slice(0, 10);

    if (config.source === 'Rutgers Satellite Imagery') {
      const selectedImageUrl = typeof config.rutgersImageUrl === 'string' ? config.rutgersImageUrl : '';
      const b = config.rutgersImageBounds;
      const bounds = b && [b.west, b.east, b.south, b.north].every((v: any) => Number.isFinite(Number(v)))
        ? { west: Number(b.west), east: Number(b.east), south: Number(b.south), north: Number(b.north) }
        : null;
      if (!selectedImageUrl || !bounds) {
        this.removeMapOverlayLayer(config.type);
        if (shouldTrackRasterLoad) {
          this.submenuService.setOverlayLoading(false);
        }
        return;
      }

      const regionCode = config.rutgersRegionCode ?? rutgersRegionCodeMap[config.region] ?? 'capehat';
      const cacheKey = this.buildOverlayCacheKey(config);
      const cachedImageUrl = this.overlayDataCache[cacheKey] as string | undefined;

      const base = environment.apiUrl.replace('/api/v1', '');
      const proxyUrl = `${base}/api/v1/observations/rutgers/image/?url=${encodeURIComponent(selectedImageUrl)}&region=${encodeURIComponent(regionCode)}`;
      const imageUrl = cachedImageUrl ?? proxyUrl;
      if (!cachedImageUrl) this.overlayDataCache[cacheKey] = imageUrl;

      const coordinates: [[number, number], [number, number], [number, number], [number, number]] = [
        [bounds.west, bounds.north],
        [bounds.east, bounds.north],
        [bounds.east, bounds.south],
        [bounds.west, bounds.south],
      ];

      try {
        const existingType = (this.map.getStyle()?.sources as any)?.[sourceId]?.type;
        if (existingType && existingType !== 'image') {
          this.removeMapOverlayLayer(config.type);
        }

        if (this.map.getSource(sourceId)) {
          const src = this.map.getSource(sourceId) as any;
          if (typeof src.updateImage === 'function') {
            src.updateImage({ url: imageUrl, coordinates });
          } else {
            this.removeMapOverlayLayer(config.type);
            this.map.addSource(sourceId, { type: 'image', url: imageUrl, coordinates });
          }
        } else {
          this.map.addSource(sourceId, { type: 'image', url: imageUrl, coordinates });
        }

        if (!this.map.getLayer(layerId)) {
          // Attempt to place overlay below trip tracks
          let beforeId: string | undefined = undefined;
          if (this.map.getLayer(`${this.tripTrackLineLayerId} -shadow`)) {
            beforeId = `${this.tripTrackLineLayerId} -shadow`;
          } else if (this.map.getLayer(this.tripTrackLineLayerId)) {
            beforeId = this.tripTrackLineLayerId;
          }

          this.map.addLayer({
            id: layerId,
            type: 'raster',
            source: sourceId,
            paint: {
              'raster-opacity': (config.opacity ?? 70) / 100,
            },
          }, beforeId);
        } else {
          this.map.setPaintProperty(layerId, 'raster-opacity', (config.opacity ?? 70) / 100);
        }

        // Ensure the selected Rutgers image is visible immediately.
        this.map.fitBounds(
          [
            [bounds.west, bounds.south],
            [bounds.east, bounds.north],
          ],
          { padding: 24, maxZoom: 10, duration: 650 }
        );
      } catch (err) {
        console.error('[Overlay] Failed to add/update Rutgers image overlay:', err);
        if (shouldTrackRasterLoad) {
          this.submenuService.setOverlayLoading(false);
        }
      }
      return;
    }

    // ── Session cache check ───────────────────────────────────────────────────
    const cacheKey = this.buildOverlayCacheKey(config);
    const cachedTileUrl = this.overlayDataCache[cacheKey] as string | undefined;
    const hasMalformedTemplate = (url: string): boolean =>
      /\{\s+[xyz]\s*\}|\{[xyz]\s+\}/i.test(url) || /\s/.test(url);

    let tileUrl: string;
    if (cachedTileUrl && !hasMalformedTemplate(cachedTileUrl)) {
      console.log(`[Overlay] Cache HIT for ${cacheKey} — reusing tile URL, skipping API`);
      tileUrl = cachedTileUrl;
    } else {
      const base = environment.apiUrl.replace('/api/v1', '');
      if (config.source === 'ACSPO GOES-19 (Regional)') {
        const regionCode = this.getAcspoRegionCode(config.region);
        tileUrl = `${base}/api/v1/observations/acspo/tiles/${regionCode}/{z}/{x}/{y}/?date=${dateStr}`;
      } else {
        tileUrl = `${base}/api/v1/observations/tiles/ocean/${sourceCode}/{z}/{x}/{y}/?date=${dateStr}`;
      }
      this.overlayDataCache[cacheKey] = tileUrl;
      console.log(`[Overlay] Cache MISS for ${cacheKey} — fetching tile layer: `, tileUrl);
    }
    // ─────────────────────────────────────────────────────────────────────────

    try {
      if (this.map.getSource(sourceId)) {
        // Update tiles if source already exists
        (this.map.getSource(sourceId) as maplibregl.RasterTileSource).setTiles([tileUrl]);
      } else {
        this.map.addSource(sourceId, {
          type: 'raster',
          tiles: [tileUrl],
          tileSize: 256,
        });
        // Attempt to place overlay below trip tracks
        let beforeId: string | undefined = undefined;
        if (this.map.getLayer(`${this.tripTrackLineLayerId} -shadow`)) {
          beforeId = `${this.tripTrackLineLayerId} -shadow`;
        } else if (this.map.getLayer(this.tripTrackLineLayerId)) {
          beforeId = this.tripTrackLineLayerId;
        }

        this.map.addLayer({
          id: layerId,
          type: 'raster',
          source: sourceId,
          paint: {
            'raster-opacity': (config.opacity ?? 70) / 100,
          },
        }, beforeId);
      }

      if (config.source === 'ACSPO GOES-19 (Regional)') {
        this.applyAcspoRegionViewport(config, dateStr);
      }
    } catch (err) {
      console.error(`[Overlay] Failed to add / update ${config.type} raster layer: `, err);
      if (shouldTrackRasterLoad) {
        this.submenuService.setOverlayLoading(false);
      }
    }
  }

  private getAcspoRegionCode(region: any): string {
    const label = String(region ?? '').trim().toLowerCase();
    if (label.includes('gulf')) return 'gulf_coast';
    if (label.includes('bahama')) return 'bahamas';
    return 'east_coast';
  }

  private applyAcspoRegionViewport(config: any, dateStr: string): void {
    if (!this.map || !config) return;
    const regionCode = this.getAcspoRegionCode(config.region);
    const bounds = this.acspoRegionBounds[regionCode];
    if (!bounds) return;

    const viewportKey = `${regionCode}| ${dateStr} `;
    if ((config as any).__acspoViewportKey === viewportKey) return;
    (config as any).__acspoViewportKey = viewportKey;

    this.map.fitBounds(bounds, {
      padding: { top: 44, right: 44, bottom: 44, left: 44 },
      duration: 650,
      maxZoom: 7,
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Combo overlay rendering + interactions
  // ─────────────────────────────────────────────────────────────────────────

  private setupComboInteractions(): void {
    if (!this.map) return;
    // Bare-map tap → probe (debounced in the service). Independent handler; it
    // only acts while Combo is active and not playing, so it never interferes
    // with the other click behaviours.
    this.map.on('click', (e) => this.ngZone.run(() => this.handleComboMapClick(e)));
    // Keep the anchored probe bubble glued to its lat/lon on pan/zoom.
    this.map.on('move', () => this.updateComboProbeAnchor());
  }

  private handleComboMapClick(e: maplibregl.MapMouseEvent): void {
    const s = this.combo.state;
    if (!s.config.enabled || s.isPlaying) return;
    const lat = e.lngLat.lat;
    const lon = e.lngLat.lng;
    const b = probeBounds(s);
    if (b && (lat < b.south || lat > b.north || lon < b.west || lon > b.east)) {
      this.combo.clearProbe();
      return;
    }
    this.combo.probeAt(lat, lon);
    // If the 5-model wind-comparison chart is open, live-update it for the new
    // tap location. Matches the POC behaviour Ryan flagged — the chart stays up
    // while the user clicks around and re-populates itself for each spot.
    if (this.comboShowChart && this.comboBackend.caps.wind) {
      this.combo.loadWindModels(lat, lon);
    }
  }

  private updateComboProbeAnchor(): void {
    const p = this.combo.state.probe;
    if (!this.map || p == null || !this.comboState.config.enabled) {
      this.comboProbeAnchor = null;
      return;
    }
    const pt = this.map.project([p.lon, p.lat]);
    this.comboProbeAnchor = { x: pt.x, y: pt.y };
  }

  /** Mirrors Flutter `buildComboMapLayers` — image/raster overlays per selection. */
  private renderComboLayers(state: ComboState): void {
    if (!this.map || !this.isMapLoaded) return;
    if (!state.config.enabled) {
      this.removeComboLayers();
      return;
    }
    const layer = state.config.layer;
    const opacity = Math.max(0, Math.min(1, state.config.opacity));

    // Bathymetry contour tiles — drawn ABOVE the combo value/wind layer so the
    // depth isobaths remain visible over the SST/CHL/Wind colour field. URL +
    // auth mirror the Flutter `ContourConstants.urlTemplate`; a missing tile /
    // 404 is transparent (client-side fallback), matching mobile behaviour.
    this.upsertComboContourLayer();

    if (layer === 'wind') {
      // Remove SST/CHL layers; render the baked forecast frame.
      this.removeComboImageSlot('value');
      this.removeComboImageSlot('fronts');
      const url = currentWindFrameUrl(state);
      const f = state.windField;
      if (url == null || f == null) {
        this.removeComboWindLayer();
        return;
      }
      const west = f.lons[0];
      const east = f.lons[f.lons.length - 1];
      const south = f.lats[0];
      const north = f.lats[f.lats.length - 1];
      const coords: [[number, number], [number, number], [number, number], [number, number]] = [
        [west, north], [east, north], [east, south], [west, south],
      ];
      const key = `wind:${state.windFrameIndex}`;
      this.upsertDataImageLayer(this.comboWindSourceId, this.comboWindLayerId, url, coords, opacity, key, 'wind');
      return;
    }

    // SST / CHL — remove the wind layer first.
    this.removeComboWindLayer();
    const view = currentView(state);
    if (view == null) {
      this.removeComboImageSlot('value');
      this.removeComboImageSlot('fronts');
      return;
    }
    // Always use `/render` (server recolor → PNG bytes inline) — even for the
    // "default" range — instead of `/frames/<png>`, which 302-redirects to a GCS
    // signed URL. A blob fetch through the redirect drops `Authorization` on
    // cross-origin (correct per fetch spec) and then fails CORS at GCS, leaving
    // the value layer blank until the user dragged the gradient (which already
    // hit `/render`). `/render` returns the PNG body directly, so no redirect /
    // CORS issue.
    //
    // IMPORTANT: use the **debounced** committed range (`renderVmin/renderVmax`)
    // — not `cmin/cmax` (which read the LIVE `state.config.customVmin/max`
    // updated on every drag tick). Otherwise the URL changes every ~16 ms
    // while dragging → we spam `/render` 60×/sec, and out-of-order responses
    // can leave the map on a stale range ("sometimes it is not re-calling the
    // API"). Falling back to `manifest.vmin/vmax` when no custom range keeps
    // the URL stable outside of a drag.
    const rV = state.renderVmin;
    const rM = state.renderVmax;
    const vmin = rV ?? state.manifest?.vmin ?? cmin(state);
    const vmax = rM ?? state.manifest?.vmax ?? cmax(state);
    const valueUrl = this.comboBackend.renderUrl(layer, view.time, vmin, vmax);
    if (valueUrl == null) {
      this.removeComboImageSlot('value');
      this.removeComboImageSlot('fronts');
      return;
    }
    const b = view.bounds;
    const coords: [[number, number], [number, number], [number, number], [number, number]] = [
      [b.west, b.north], [b.east, b.north], [b.east, b.south], [b.west, b.south],
    ];
    this.applyComboAuthedImage('value', valueUrl, coords, opacity);

    const showFronts = state.config.frontsOn && layer === 'sst' && view.frontPng != null;
    if (showFronts) {
      const frontUrl = this.comboBackend.frameUrl(layer, view.frontPng!);
      this.applyComboAuthedImage('fronts', frontUrl, coords, 0.9);
    } else {
      this.removeComboImageSlot('fronts');
    }

    // During SST/CHL autoplay, each frame image is only fetched from the
    // network once the play timer lands on it (700ms cadence). If that fetch
    // takes longer than the cadence, the request is superseded by the next
    // tick before it resolves and the frame silently never paints — the map
    // then appears frozen until a fetch finally wins the race. Warming the
    // next couple of frames into the blob cache ahead of time means playback
    // usually finds them already cached by the time it gets there.
    if (state.isPlaying) {
      this.prefetchUpcomingComboFrames(state, vmin, vmax);
    }
  }

  private prefetchUpcomingComboFrames(state: ComboState, vmin: number, vmax: number, count: number = 2): void {
    const layer = state.config.layer;
    const m = state.manifest;
    if (m == null || m.frames.length === 0) return;
    const last = m.frames.length - 1;
    let idx = resolvedFrameIndex(state);
    for (let i = 0; i < count; i++) {
      idx = idx >= last ? playStart(state) : idx + 1;
      const synthetic: ComboState = { ...state, config: { ...state.config, frameIndex: idx, compositeChecked: false } };
      const view = currentView(synthetic);
      if (view == null) continue;
      const url = this.comboBackend.renderUrl(layer, view.time, vmin, vmax);
      if (url) this.comboBackend.fetchImageObjectUrl(url);
      if (state.config.frontsOn && layer === 'sst' && view.frontPng != null) {
        this.comboBackend.fetchImageObjectUrl(this.comboBackend.frameUrl(layer, view.frontPng));
      }
    }
  }

  /** Fetch an authed PNG → object URL, then upsert the image layer (deduped). */
  private async applyComboAuthedImage(
    slot: 'value' | 'fronts',
    rawUrl: string,
    coords: [[number, number], [number, number], [number, number], [number, number]],
    opacity: number,
  ): Promise<void> {
    const sourceId = slot === 'value' ? this.comboValueSourceId : this.comboFrontsSourceId;
    const layerId = slot === 'value' ? this.comboValueLayerId : this.comboFrontsLayerId;
    const key = `${rawUrl}|${coords.map((c) => c.join(',')).join(';')}`;
    const prevKey = slot === 'value' ? this.comboValueKey : this.comboFrontsKey;
    if (key === prevKey) {
      // Only opacity changed — update in place.
      if (this.map?.getLayer(layerId)) this.map.setPaintProperty(layerId, 'raster-opacity', opacity);
      return;
    }
    if (slot === 'value') this.comboValueKey = key;
    else this.comboFrontsKey = key;

    // SYNCHRONOUS cache-hit fast path — the whole point of the LRU cache. If
    // this URL was fetched before (SST → CHL → SST within cache lifetime),
    // paint it in this same task tick with no `await`, no in-flight indicator
    // and no microtask hop. This is what makes switching feel instant.
    const cachedObj = this.comboBackend.getCachedObjectUrl(rawUrl);
    if (cachedObj != null) {
      this.upsertDataImageLayer(sourceId, layerId, cachedObj, coords, opacity, key, slot);
      if (slot === 'value') this.comboValueObjUrl = cachedObj;
      else this.comboFrontsObjUrl = cachedObj;
      return;
    }

    this.combo.incImageInFlight();
    let obj: string | null = null;
    try {
      obj = await this.comboBackend.fetchImageObjectUrl(rawUrl);
    } finally {
      this.combo.decImageInFlight();
    }
    // Superseded while in flight, or fetch failed → keep the previous frame.
    // NOTE: the object URL is now owned by the backend's LRU image cache; we
    // DO NOT `URL.revokeObjectURL` on swap here (that would kill the cache
    // entry). The cache revokes on its own eviction / on `clearImageCache`.
    const stillCurrent = (slot === 'value' ? this.comboValueKey : this.comboFrontsKey) === key;
    if (!stillCurrent || obj == null) return;
    this.upsertDataImageLayer(sourceId, layerId, obj, coords, opacity, key, slot);
    if (slot === 'value') this.comboValueObjUrl = obj;
    else this.comboFrontsObjUrl = obj;
  }

  /** Add/update a MapLibre `image` source + raster layer from a ready URL. */
  private upsertDataImageLayer(
    sourceId: string,
    layerId: string,
    url: string,
    coords: [[number, number], [number, number], [number, number], [number, number]],
    opacity: number,
    key: string,
    slot: 'value' | 'fronts' | 'wind',
  ): void {
    if (!this.map) return;
    if (slot === 'wind') {
      if (key === this.comboWindKey) {
        if (this.map.getLayer(layerId)) this.map.setPaintProperty(layerId, 'raster-opacity', opacity);
        // fall through to updateImage so a frame swap still applies
      }
      this.comboWindKey = key;
    }
    try {
      const existing = this.map.getSource(sourceId) as any;
      if (existing) {
        if (typeof existing.updateImage === 'function') {
          existing.updateImage({ url, coordinates: coords });
        } else {
          if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
          this.map.removeSource(sourceId);
          this.map.addSource(sourceId, { type: 'image', url, coordinates: coords });
        }
      } else {
        this.map.addSource(sourceId, { type: 'image', url, coordinates: coords });
      }
      if (!this.map.getLayer(layerId)) {
        let beforeId: string | undefined;
        if (this.map.getLayer(`${this.tripTrackLineLayerId} -shadow`)) {
          beforeId = `${this.tripTrackLineLayerId} -shadow`;
        } else if (this.map.getLayer(this.tripTrackLineLayerId)) {
          beforeId = this.tripTrackLineLayerId;
        }
        this.map.addLayer(
          { id: layerId, type: 'raster', source: sourceId, paint: { 'raster-opacity': opacity } },
          beforeId,
        );
      } else {
        this.map.setPaintProperty(layerId, 'raster-opacity', opacity);
      }
    } catch (err) {
      console.error('[Combo] Failed to add/update image layer:', err);
    }
  }

  private removeComboImageSlot(slot: 'value' | 'fronts'): void {
    const sourceId = slot === 'value' ? this.comboValueSourceId : this.comboFrontsSourceId;
    const layerId = slot === 'value' ? this.comboValueLayerId : this.comboFrontsLayerId;
    if (this.map?.getLayer(layerId)) this.map.removeLayer(layerId);
    if (this.map?.getSource(sourceId)) this.map.removeSource(sourceId);
    // The object URL is owned by the backend's LRU cache — don't revoke it
    // here, or a later reuse (SST → CHL → SST) would serve a dead URL.
    if (slot === 'value') {
      this.comboValueKey = '';
      this.comboValueObjUrl = null;
    } else {
      this.comboFrontsKey = '';
      this.comboFrontsObjUrl = null;
    }
  }

  private removeComboWindLayer(): void {
    if (this.map?.getLayer(this.comboWindLayerId)) this.map.removeLayer(this.comboWindLayerId);
    if (this.map?.getSource(this.comboWindSourceId)) this.map.removeSource(this.comboWindSourceId);
    this.comboWindKey = '';
  }

  /**
   * Add / ensure the bathymetry contour tile layer is drawn ON TOP of the combo
   * value/wind image. Mirrors `ContourConstants.urlTemplate` (v3, z6–z13
   * native, transparent on 404). Auth is injected via the map's transformRequest.
   */
  private upsertComboContourLayer(): void {
    if (!this.map) return;
    if (!this.map.getSource(this.comboContourSourceId)) {
      try {
        this.map.addSource(this.comboContourSourceId, {
          type: 'raster',
          tiles: [this.comboContourUrl],
          tileSize: 256,
          minzoom: 6,
          maxzoom: 13,
        });
      } catch (err) {
        console.error('[Combo] Failed to add contour source:', err);
        return;
      }
    }
    if (!this.map.getLayer(this.comboContourLayerId)) {
      try {
        // Place BELOW trip tracks / waypoints (so those stay on top) but ABOVE
        // the combo value/wind layer. Using the trip-track shadow layer as the
        // beforeId keeps contours above combo but below routes/markers.
        let beforeId: string | undefined;
        if (this.map.getLayer(`${this.tripTrackLineLayerId} -shadow`)) {
          beforeId = `${this.tripTrackLineLayerId} -shadow`;
        } else if (this.map.getLayer(this.tripTrackLineLayerId)) {
          beforeId = this.tripTrackLineLayerId;
        }
        this.map.addLayer(
          {
            id: this.comboContourLayerId,
            type: 'raster',
            source: this.comboContourSourceId,
            minzoom: 6,
            paint: { 'raster-opacity': 1.0 },
          },
          beforeId,
        );
      } catch (err) {
        console.error('[Combo] Failed to add contour layer:', err);
      }
    } else {
      // Ensure the contour layer stays above the current combo value/wind layer
      // (which may have been added AFTER the contour on layer switch).
      try {
        this.map.moveLayer(this.comboContourLayerId);
      } catch { /* ignore */ }
    }
  }

  private removeComboContourLayer(): void {
    if (this.map?.getLayer(this.comboContourLayerId)) this.map.removeLayer(this.comboContourLayerId);
    if (this.map?.getSource(this.comboContourSourceId)) this.map.removeSource(this.comboContourSourceId);
  }

  private removeComboLayers(): void {
    this.removeComboImageSlot('value');
    this.removeComboImageSlot('fronts');
    this.removeComboWindLayer();
    this.removeComboContourLayer();
  }

  /** The × on the slider card — disables Combo entirely. */
  onComboClose(): void {
    this.combo.disable();
    this.removeComboLayers();
  }

  /** ⚡ Auto-view — pass the live map viewport to /api/stretch. */
  onComboAutoView(): void {
    if (!this.map) return;
    const b = this.map.getBounds();
    this.combo.autoStretch({
      west: b.getWest(),
      south: b.getSouth(),
      east: b.getEast(),
      north: b.getNorth(),
    });
  }

  /** Open the 5-model wind chart for the current probe. */
  onComboOpenModels(): void {
    if (!this.comboBackend.caps.wind) return;
    const p = this.combo.state.probe;
    if (p != null) this.combo.loadWindModels(p.lat, p.lon);
    this.comboShowChart = true;
  }

  onComboCloseChart(): void {
    this.comboShowChart = false;
    this.combo.clearWindModels();
  }

  /**
   * Backdrop press for the wind-chart modal. Dismisses ONLY when the press
   * actually starts on the backdrop (target === currentTarget) — not when a
   * scrub drag inside the chart happens to release outside, which previously
   * fired the backdrop's `click` and killed the chart mid-interaction.
   */
  onChartBackdropDown(ev: MouseEvent): void {
    if (ev.target === ev.currentTarget) this.onComboCloseChart();
  }

  private removeMapOverlayLayer(type: string): void {
    if (!this.map) return;
    const sourceId = `${this.rasterOverlaySourceId} -${type} `;
    const layerId = `${this.rasterOverlayLayerId} -${type} `;
    const outlineLayerId = `${layerId} -outline`;

    // If this overlay was toggled off while still performing its initial load,
    // its source is removed before the map's `data`/`isSourceLoaded` event can
    // fire — which would otherwise leave the overlay loader stuck on. Stop
    // tracking it and clear the loader explicitly.
    if (this.currentRasterOverlayInitialLoadSourceId === sourceId) {
      this.currentRasterOverlayInitialLoadSourceId = null;
      this.submenuService.setOverlayLoading(false);
    }

    if (this.map.getLayer(outlineLayerId)) {
      this.map.removeLayer(outlineLayerId);
    }
    if (this.map.getLayer(layerId)) {
      this.map.removeLayer(layerId);
    }
    if (this.map.getSource(sourceId)) {
      this.map.removeSource(sourceId);
    }
    if (type === 'wind') {
      this.releaseWeatherOverlayZoomLock();
    }
  }

  updateOverlayOpacity(type: string, value: number): void {
    const config = this.activeOverlayConfigs.find(c => c.type === type);
    if (!config) return;

    config.opacity = value;
    const layerId = `${this.rasterOverlayLayerId} -${type} `;
    if (this.map && this.map.getLayer(layerId)) {
      if (type === 'wind') {
        this.map.setPaintProperty(layerId, 'fill-opacity', this.getWeatherOverlayFillOpacity(value));
        const outlineLayerId = `${layerId} -outline`;
        if (this.map.getLayer(outlineLayerId)) {
          this.map.setPaintProperty(outlineLayerId, 'line-opacity', this.getWeatherOverlayLineOpacity(value));
        }
      } else {
        this.map.setPaintProperty(layerId, 'raster-opacity', value / 100);
      }
    }
  }

  setActiveOverlayWidgetType(type: string): void {
    if (!type) return;
    if (!this.activeOverlayConfigs.some(c => c.type === type)) return;
    this.activeOverlayWidgetType = type;
    if (!this.isAcspoPointQueryOverlayActive()) {
      this.dismissAcspoPointPopup();
    }
    if (this.map && this.isMapLoaded) {
      this.syncMapOverlayLayers(this.activeOverlayConfigs);
    }
  }

  getActiveOverlayWidgetConfig(): any | null {
    if (!this.activeOverlayConfigs.length) return null;
    if (this.activeOverlayWidgetType) {
      const selected = this.activeOverlayConfigs.find(c => c.type === this.activeOverlayWidgetType);
      if (selected) return selected;
    }
    return this.activeOverlayConfigs[0];
  }

  updateActiveOverlayWidgetOpacity(value: number): void {
    const active = this.getActiveOverlayWidgetConfig();
    if (!active?.type) return;
    this.updateOverlayOpacity(active.type, value);
  }

  closeMapOverlay(type: string): void {
    if (this.activeOverlayWidgetType === type) {
      const remaining = this.activeOverlayConfigs.filter(c => c.type !== type);
      this.activeOverlayWidgetType = remaining.length ? remaining[0].type : null;
    }
    this.submenuService.removeMapOverlay(type);
  }

  updateAllOverlayOpacity(value: number): void {
    this.activeOverlayConfigs.forEach(config => {
      config.opacity = value;
      const layerId = `${this.rasterOverlayLayerId} -${config.type} `;
      if (this.map && this.map.getLayer(layerId)) {
        if (config.type === 'wind') {
          this.map.setPaintProperty(layerId, 'fill-opacity', this.getWeatherOverlayFillOpacity(value));
          const outlineLayerId = `${layerId} -outline`;
          if (this.map.getLayer(outlineLayerId)) {
            this.map.setPaintProperty(outlineLayerId, 'line-opacity', this.getWeatherOverlayLineOpacity(value));
          }
        } else {
          this.map.setPaintProperty(layerId, 'raster-opacity', value / 100);
        }
      }
    });
  }

  closeAllMapOverlays(): void {
    this.activeOverlayWidgetType = null;
    [...this.activeOverlayConfigs].forEach(config => {
      this.submenuService.removeMapOverlay(config.type);
    });
  }

  loadWeatherZonesFromBbox(): void {
    if (!this.map) return;
    const active = this.getActiveOverlayWidgetConfig();
    if (!active || active.type !== 'wind') return;

    const bounds = this.getWeatherOverlayFetchBounds();
    const bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()} `;
    const params = new HttpParams()
      .set('bbox', bbox)
      .set('zone_types', 'coastal,offshore');

    const cacheKey = this.buildOverlayCacheKey(active);
    const applyPayload = (payload: any) => {
      const featureCollection = this.toMarineZoneFeatureCollection(payload);
      this.overlayDataCache[cacheKey] = featureCollection;
      this.applyWeatherOverlayLayer(active, false, featureCollection);
    };

    this.beginWeatherZonesLoad();
    this.http.get<any>(this.weatherZonesBboxApiBaseUrl, { params }).subscribe({
      next: (payload: any) => {
        applyPayload(payload);
        this.endWeatherZonesLoad();
      },
      error: () => {
        // Fallback to the existing marine-zones endpoint if bbox endpoint is unavailable.
        this.http.get<any>(this.weatherZonesApiBaseUrl, { params }).subscribe({
          next: (payload: any) => {
            applyPayload(payload);
            this.endWeatherZonesLoad();
          },
          error: (err: any) => {
            console.error('[Overlay] Failed to load weather zones via bbox action:', err);
            this.endWeatherZonesLoad();
          },
        });
      },
    });
  }

  private applyWeatherOverlayLayer(config: any, fetchIfMissing: boolean = false, prefetchedFeatureCollection?: GeoJSON.FeatureCollection): void {
    if (!this.map) return;
    console.log('[HomePage] Sidebar activation - shouldSnap:', config.shouldSnap, 'isLayerOpen:', this.isLayerOpen);
    const viewportAnimating = this.applyWeatherOverlayDefaultViewport(config, config.shouldSnap === true && this.isLayerOpen);
    if (viewportAnimating && fetchIfMissing && !prefetchedFeatureCollection) {
      if ((config as any).__weatherFetchAfterViewportPending) return;
      (config as any).__weatherFetchAfterViewportPending = true;

      const handleMoveEnd = () => this.ngZone.run(() => {
        if (!this.map) return;
        this.map.off('moveend', handleMoveEnd);
        (config as any).__weatherFetchAfterViewportPending = false;

        const stillActive = this.activeOverlayConfigs.some((c: any) => c?.type === config?.type);
        if (!stillActive) return;
        this.applyWeatherOverlayLayer(config, true);
      });
      this.map.on('moveend', handleMoveEnd);
      return;
    }

    const sourceId = `${this.rasterOverlaySourceId} -${config.type} `;
    const layerId = `${this.rasterOverlayLayerId} -${config.type} `;
    const outlineLayerId = `${layerId} -outline`;
    const opacity = Number(config.opacity ?? 65);

    const applyFeatureCollection = (featureCollection: any) => {
      if (!featureCollection.features.length) {
        this.removeMapOverlayLayer(config.type);
        return;
      }

      if (this.map!.getSource(sourceId)) {
        (this.map!.getSource(sourceId) as maplibregl.GeoJSONSource).setData(featureCollection as any);
      } else {
        this.map!.addSource(sourceId, {
          type: 'geojson',
          data: featureCollection as any,
        });
      }

      if (!this.map!.getLayer(layerId)) {
        this.map!.addLayer({
          id: layerId,
          type: 'fill',
          source: sourceId,
          paint: {
            'fill-color': '#2F54EB',
            'fill-opacity': this.getWeatherOverlayFillOpacity(opacity),
          },
        });
      } else {
        this.map!.setPaintProperty(layerId, 'fill-opacity', this.getWeatherOverlayFillOpacity(opacity));
      }

      if (!this.map!.getLayer(outlineLayerId)) {
        this.map!.addLayer({
          id: outlineLayerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': '#1D4ED8',
            'line-width': 2,
            'line-opacity': this.getWeatherOverlayLineOpacity(opacity),
          },
        });
      } else {
        this.map!.setPaintProperty(outlineLayerId, 'line-opacity', this.getWeatherOverlayLineOpacity(opacity));
      }
    };

    const bindMarineZoneInteractionHandlers = () => {
      this.map!.off('click', layerId, this.onMarineZoneClick);
      this.map!.off('click', outlineLayerId, this.onMarineZoneClick);
      this.map!.off('mouseenter', layerId, this.onMarineZoneMouseEnter);
      this.map!.off('mouseenter', outlineLayerId, this.onMarineZoneMouseEnter);
      this.map!.off('mouseleave', layerId, this.onMarineZoneMouseLeave);
      this.map!.off('mouseleave', outlineLayerId, this.onMarineZoneMouseLeave);

      this.map!.on('click', layerId, this.onMarineZoneClick);
      this.map!.on('click', outlineLayerId, this.onMarineZoneClick);
      this.map!.on('mouseenter', layerId, this.onMarineZoneMouseEnter);
      this.map!.on('mouseenter', outlineLayerId, this.onMarineZoneMouseEnter);
      this.map!.on('mouseleave', layerId, this.onMarineZoneMouseLeave);
      this.map!.on('mouseleave', outlineLayerId, this.onMarineZoneMouseLeave);
    };

    if (prefetchedFeatureCollection) {
      applyFeatureCollection(prefetchedFeatureCollection);
      bindMarineZoneInteractionHandlers();
      return;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Get current map bounds to fetch relevant zones dynamically
    const bounds = this.getWeatherOverlayFetchBounds();
    const bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()} `;
    const params = new HttpParams()
      .set('bbox', bbox)
      .set('zone_types', 'coastal,offshore');

    // ── Session cache check ───────────────────────────────────────────────────
    const cacheKey = this.buildOverlayCacheKey(config);
    const cachedGeoJson = this.overlayDataCache[cacheKey] as object | undefined;

    if (cachedGeoJson) {
      console.log(`[Overlay] Cache HIT for ${cacheKey} — reusing weather GeoJSON, skipping API`);
      applyFeatureCollection(cachedGeoJson);
      bindMarineZoneInteractionHandlers();
      return;
    }

    if (!fetchIfMissing) {
      return;
    }
    // ─────────────────────────────────────────────────────────────────────────

    console.log(`[Overlay] Cache MISS for ${cacheKey} — fetching weather zones for dynamic bbox: ${bbox} `);

    // Use HttpClient so authInterceptor attaches Bearer token (API requires auth).
    this.beginWeatherZonesLoad();
    this.http.get<any>(this.weatherZonesApiBaseUrl, { params }).subscribe({
      next: (payload: any) => {
        const featureCollection = this.toMarineZoneFeatureCollection(payload);
        this.overlayDataCache[cacheKey] = featureCollection;
        applyFeatureCollection(featureCollection);
        bindMarineZoneInteractionHandlers();
        this.endWeatherZonesLoad();
      },
      error: (err: any) => {
        console.error('[Overlay] Failed to load NOAA Weather marine zones:', err);
        this.endWeatherZonesLoad();
      }
    });
  }

  private applyWeatherOverlayDefaultViewport(config: any, shouldSnap: boolean = false): boolean {
    if (!this.map || !config) return false;
    if (this.weatherViewportSnapped) return false;
    this.weatherViewportSnapped = true;

    // Match mobile behavior: only enforce a minimum weather zoom.
    // When activated from the overlay sheet we pan to the user, but we do not
    // zoom further in than the minimum required for bounded polygon rendering.
    const LOCK_MIN_ZOOM = this.weatherOverlayMinZoom;

    const currentZoom = this.map.getZoom();
    if (!Number.isFinite(currentZoom)) return false;

    if (shouldSnap) {
      const targetZoom = Math.max(currentZoom, LOCK_MIN_ZOOM);

      // Leave zoom lock null during animation so setMinZoom(LOCK_MIN) doesn't conflict
      // with the easeTo. commitZoomLock() applies the floor once animation settles.
      this.weatherOverlayLockedMinZoom = null;
      this.weatherOverlayLockedMaxZoom = null;

      const commitZoomLock = () => {
        const stillActive = this.activeOverlayConfigs.some((c: any) => c?.type === 'wind');
        if (!stillActive) return;
        this.weatherOverlayLockedMinZoom = LOCK_MIN_ZOOM;
        this.weatherOverlayLockedMaxZoom = null;
        this.ensureWeatherOverlayZoomLock();
      };

      const snapToUser = (lng: number, lat: number) => {
        if (!this.map) return;
        if (!this.userLocationMarker) {
          const el = document.createElement('div');
          el.className = 'user-location-marker';
          this.userLocationMarker = new maplibregl.Marker({ element: el })
            .setLngLat([lng, lat])
            .addTo(this.map);
        } else {
          this.userLocationMarker.setLngLat([lng, lat]);
        }
        this.map.easeTo({ center: [lng, lat], zoom: targetZoom, duration: 500 });
        this.map.once('moveend', commitZoomLock);
      };

      if (this.userLocationMarker) {
        const { lng, lat } = this.userLocationMarker.getLngLat();
        this.map.easeTo({ center: [lng, lat], zoom: targetZoom, duration: 500 });
        this.map.once('moveend', commitZoomLock);
        return true;
      }

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (!this.map) return;
            if (!this.activeOverlayConfigs.some((c: any) => c?.type === 'wind')) return;
            snapToUser(pos.coords.longitude, pos.coords.latitude);
          },
          () => {
            if (this.map && currentZoom < LOCK_MIN_ZOOM) {
              this.map.easeTo({ zoom: LOCK_MIN_ZOOM, duration: 500 });
              this.map.once('moveend', commitZoomLock);
            } else {
              commitZoomLock();
            }
          },
          { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }
        );
        return true;
      }

      if (currentZoom < LOCK_MIN_ZOOM) {
        this.map.easeTo({ zoom: LOCK_MIN_ZOOM, duration: 500 });
        this.map.once('moveend', commitZoomLock);
        return true;
      }
      commitZoomLock();
      return true;
    } else {
      const commitZoomLock = () => {
        const stillActive = this.activeOverlayConfigs.some((c: any) => c?.type === 'wind');
        if (!stillActive) return;
        this.weatherOverlayLockedMinZoom = LOCK_MIN_ZOOM;
        this.weatherOverlayLockedMaxZoom = null;
        this.ensureWeatherOverlayZoomLock();
      };

      if (currentZoom < LOCK_MIN_ZOOM) {
        this.map.easeTo({ zoom: LOCK_MIN_ZOOM, duration: 500 });
        this.map.once('moveend', commitZoomLock);
        return true;
      } else {
        commitZoomLock();
        return false;
      }
    }
  }

  private syncWeatherOverlayZoomLock(weatherVisible: boolean): void {
    if (!this.map) return;
    if (weatherVisible) {
      this.ensureWeatherOverlayZoomLock();
    } else {
      this.releaseWeatherOverlayZoomLock();
    }
  }

  private ensureWeatherOverlayZoomLock(): void {
    if (!this.map) return;
    if (this.weatherOverlayPreviousMinZoom == null) {
      this.weatherOverlayPreviousMinZoom = this.map.getMinZoom();
    }
    if (this.weatherOverlayPreviousMaxZoom == null) {
      this.weatherOverlayPreviousMaxZoom = this.map.getMaxZoom();
    }
    if (this.weatherOverlayLockedMinZoom != null) {
      this.map.setMinZoom(this.weatherOverlayLockedMinZoom);
    }
    if (this.weatherOverlayLockedMaxZoom != null) {
      this.map.setMaxZoom(this.weatherOverlayLockedMaxZoom);
    }
  }

  private releaseWeatherOverlayZoomLock(): void {
    if (!this.map) return;
    if (this.weatherOverlayPreviousMinZoom != null) {
      this.map.setMinZoom(this.weatherOverlayPreviousMinZoom);
    }
    if (this.weatherOverlayPreviousMaxZoom != null) {
      this.map.setMaxZoom(this.weatherOverlayPreviousMaxZoom);
    }
    this.weatherOverlayPreviousMinZoom = null;
    this.weatherOverlayPreviousMaxZoom = null;
    this.weatherOverlayLockedMinZoom = null;
    this.weatherOverlayLockedMaxZoom = null;
    this.weatherViewportSnapped = false;
    this.activeOverlayConfigs.forEach((cfg: any) => {
      if (cfg?.type === 'wind' && cfg.__weatherFetchAfterViewportPending) {
        delete cfg.__weatherFetchAfterViewportPending;
      }
    });
  }

  private isWeatherOverlayMinZoomReached(): boolean {
    if (!this.map || this.weatherOverlayLockedMinZoom == null) return false;
    return this.map.getZoom() <= this.weatherOverlayLockedMinZoom + 0.001;
  }

  private getWeatherOverlayFetchBounds(): maplibregl.LngLatBounds {
    const rawBounds = this.map.getBounds();
    const currentZoom = this.map.getZoom();
    if (currentZoom >= this.weatherOverlayMinZoom) {
      return rawBounds;
    }

    // Match the mobile implementation by shrinking the query bbox toward the
    // current center as if the map were already at the enforced minimum zoom.
    const scale = Math.pow(2, currentZoom - this.weatherOverlayMinZoom);
    const center = this.map.getCenter();
    const west = center.lng + (rawBounds.getWest() - center.lng) * scale;
    const south = center.lat + (rawBounds.getSouth() - center.lat) * scale;
    const east = center.lng + (rawBounds.getEast() - center.lng) * scale;
    const north = center.lat + (rawBounds.getNorth() - center.lat) * scale;

    return new maplibregl.LngLatBounds([west, south], [east, north]);
  }

  private getWeatherOverlayFillOpacity(value: number): number {
    const normalized = Math.max(0, Math.min(1, Number(value) / 100));
    return Math.max(0.12, normalized * 0.45);
  }

  private getWeatherOverlayLineOpacity(value: number): number {
    const normalized = Math.max(0, Math.min(1, Number(value) / 100));
    return Math.max(0.2, normalized);
  }

  private beginWeatherZonesLoad(): void {
    this.weatherZonesLoadCounter += 1;
    this.isWeatherZonesLoading = this.weatherZonesLoadCounter > 0;
  }

  private endWeatherZonesLoad(): void {
    this.weatherZonesLoadCounter = Math.max(0, this.weatherZonesLoadCounter - 1);
    this.isWeatherZonesLoading = this.weatherZonesLoadCounter > 0;
  }

  // --- Marine Zone Interaction Handlers ---
  private onMarineZoneClick = (e: any) => {
    if (!e.features || e.features.length === 0) return;
    const feature = e.features[0];
    const zoneId = this.getMarineZoneIdFromFeature(feature);
    const zoneName = feature.properties?.name || feature.properties?.zone_name || `Zone ${zoneId} `;

    if (!zoneId) return;

    if (this.marineWeatherPopup) {
      this.marineWeatherPopup.remove();
    }

    const frame = this.createMarineForecastPopupFrame(zoneId, zoneName);
    this.renderMarineForecastLoading(frame.body);

    this.marineWeatherPopup = new maplibregl.Popup({ closeButton: true, maxWidth: '460px', className: 'marine-forecast-sheet-popup' })
      .setLngLat(e.lngLat)
      .setDOMContent(frame.root)
      .addTo(this.map!);

    const forecastUrl = `${this.weatherZonesApiBaseUrl}${encodeURIComponent(zoneId)} /forecast/`;

    // Use HttpClient so authInterceptor attaches Bearer token.
    this.http.get<any>(forecastUrl).subscribe({
      next: (data: any) => {
        if (!this.marineWeatherPopup) return;
        this.renderMarineForecastContent(frame.body, zoneId, zoneName, data);
      },
      error: (err: any) => {
        if (!this.marineWeatherPopup) return;
        const status = Number(err?.status || 0);
        if (status === 502 || status >= 500) {
          this.renderMarineForecastUnavailable(frame.body);
        } else {
          this.renderMarineForecastError(frame.body, 'Could not load forecast.');
        }
      }
    });
  };

  private createMarineForecastPopupFrame(zoneId: string, zoneName: string): { root: HTMLDivElement; body: HTMLDivElement } {
    const root = document.createElement('div');
    root.className = 'marine-forecast-popup';
    root.style.background = '#FFFFFF';
    root.style.borderRadius = '12px';
    root.style.overflow = 'hidden';
    root.style.boxShadow = '0 14px 30px rgba(15, 23, 42, 0.22)';
    root.style.fontFamily = "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
    root.style.width = '400px';
    root.style.maxWidth = 'calc(100vw - 18px)';

    const grabberWrap = document.createElement('div');
    grabberWrap.style.paddingTop = '8px';
    grabberWrap.style.display = 'flex';
    grabberWrap.style.justifyContent = 'center';
    const grabber = document.createElement('div');
    grabber.style.width = '36px';
    grabber.style.height = '5px';
    grabber.style.borderRadius = '2.5px';
    grabber.style.background = '#D1D1D6';
    grabberWrap.appendChild(grabber);
    root.appendChild(grabberWrap);

    const header = document.createElement('div');
    header.style.display = 'flex';
    header.style.alignItems = 'center';
    header.style.gap = '12px';
    header.style.padding = '14px 16px 12px 16px';

    const iconWrap = document.createElement('div');
    iconWrap.style.width = '36px';
    iconWrap.style.height = '36px';
    iconWrap.style.borderRadius = '10px';
    iconWrap.style.background = 'rgba(0, 122, 255, 0.12)';
    iconWrap.style.display = 'flex';
    iconWrap.style.alignItems = 'center';
    iconWrap.style.justifyContent = 'center';
    iconWrap.innerHTML = `
  < svg width = "18" height = "18" viewBox = "0 0 24 24" fill = "none" aria - hidden="true" >
    <path d="M7 18h10a4 4 0 0 0 .6-7.95A5.5 5.5 0 0 0 7 9.5a3.5 3.5 0 0 0 0 7Z" stroke = "#007AFF" stroke - width="1.8" stroke - linecap="round" stroke - linejoin="round" />
      </svg>
        `;

    const textWrap = document.createElement('div');
    textWrap.style.display = 'flex';
    textWrap.style.flexDirection = 'column';
    textWrap.style.minWidth = '0';

    const title = document.createElement('div');
    title.textContent = zoneName && zoneName.trim().length ? zoneName : zoneId;
    title.style.fontSize = '16px';
    title.style.fontWeight = '700';
    title.style.color = '#001A33';
    title.style.letterSpacing = '-0.3px';
    title.style.lineHeight = '1.25';

    textWrap.appendChild(title);
    if (zoneName && zoneName.trim().length) {
      const subtitle = document.createElement('div');
      subtitle.textContent = zoneId;
      subtitle.style.fontSize = '12px';
      subtitle.style.color = '#8E8E93';
      subtitle.style.marginTop = '2px';
      textWrap.appendChild(subtitle);
    }

    header.appendChild(iconWrap);
    header.appendChild(textWrap);
    root.appendChild(header);

    const divider = document.createElement('div');
    divider.style.height = '1px';
    divider.style.background = '#E5E5EA';
    root.appendChild(divider);

    const body = document.createElement('div');
    body.style.maxHeight = '310px';
    body.style.overflowY = 'auto';
    body.style.padding = '8px 0 12px 0';
    root.appendChild(body);

    return { root, body };
  }

  private renderMarineForecastLoading(body: HTMLElement): void {
    body.innerHTML = `
      < div style = "padding:34px 20px; display:flex; align-items:center; justify-content:center; gap:10px;" >
        <span style="width:16px;height:16px;border:2px solid #B9D8FF;border-top-color:#007AFF;border-radius:50%;display:inline-block;animation: marine-spin 0.9s linear infinite;" > </span>
          < span style = "font-size:14px;color:#3C3C43;" > Loading forecast...</span>
            </div>
              `;
    this.ensureMarinePopupSpinKeyframes();
  }

  private renderMarineForecastUnavailable(body: HTMLElement): void {
    body.innerHTML = `
            < div style = "padding:32px 20px; text-align:center;" >
              <div style="font-size:34px; line-height:1; color:#D1D1D6;" >☁</div>
                < div style = "margin-top:12px;font-size:15px;font-weight:500;color:#8E8E93;" > Forecast temporarily unavailable </div>
                  </div>
                    `;
  }

  private renderMarineForecastError(body: HTMLElement, message: string): void {
    body.innerHTML = `
                  < div style = "padding:30px 20px;text-align:center;" >
                    <div style="font-size:15px;color:#8E8E93;" > ${this.escapeHtml(message)} </div>
                      </div>
                        `;
  }

  private renderMarineForecastContent(body: HTMLElement, zoneId: string, _zoneName: string, data: any): void {
    body.replaceChildren();

    const periodsRaw = Array.isArray(data?.periods) ? data.periods : [];
    const periods = periodsRaw.filter((item: any) => item && typeof item === 'object');
    const rawText = String(data?.raw_text || data?.text || data?.forecast_text || data?.forecast || '').trim();
    const sourceUrl = String(data?.source_url || '').trim();

    if (periods.length) {
      periods.forEach((period: any, idx: number) => {
        const label = String(period?.label || period?.name || period?.period || '').trim();
        const text = String(period?.text || period?.forecast || period?.detailedForecast || '').trim();

        const row = document.createElement('div');
        row.style.padding = '10px 16px';

        if (label) {
          const labelEl = document.createElement('div');
          labelEl.textContent = label;
          labelEl.style.fontSize = '14px';
          labelEl.style.fontWeight = '700';
          labelEl.style.color = '#001A33';
          row.appendChild(labelEl);
        }

        if (text) {
          const textEl = document.createElement('div');
          textEl.textContent = text;
          textEl.style.marginTop = label ? '4px' : '0';
          textEl.style.fontSize = '13px';
          textEl.style.color = '#3C3C43';
          textEl.style.lineHeight = '1.45';
          textEl.style.whiteSpace = 'normal';
          textEl.style.overflowWrap = 'anywhere';
          textEl.style.wordBreak = 'break-word';
          row.appendChild(textEl);
        }

        if (label || text) {
          body.appendChild(row);
          if (idx < periods.length - 1) {
            const divider = document.createElement('div');
            divider.style.height = '1px';
            divider.style.background = '#E5E5EA';
            divider.style.margin = '2px 16px';
            body.appendChild(divider);
          }
        }
      });
    } else if (rawText) {
      const raw = document.createElement('pre');
      raw.textContent = rawText;
      raw.style.margin = '0';
      raw.style.padding = '8px 16px';
      raw.style.whiteSpace = 'pre-wrap';
      raw.style.overflowWrap = 'anywhere';
      raw.style.wordBreak = 'break-word';
      raw.style.fontFamily = "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace";
      raw.style.fontSize = '12px';
      raw.style.color = '#3C3C43';
      raw.style.lineHeight = '1.5';
      body.appendChild(raw);
    } else {
      const none = document.createElement('div');
      none.textContent = 'No forecast data available.';
      none.style.padding = '24px 16px 10px';
      none.style.fontSize = '14px';
      none.style.color = '#8E8E93';
      body.appendChild(none);
    }

    if (sourceUrl) {
      const sourceWrap = document.createElement('div');
      sourceWrap.style.padding = '10px 16px 4px';

      const link = document.createElement('a');
      link.href = sourceUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'inline-flex';
      link.style.alignItems = 'center';
      link.style.gap = '4px';
      link.style.fontSize = '13px';
      link.style.fontWeight = '500';
      link.style.color = '#007AFF';
      link.style.textDecoration = 'none';
      link.textContent = 'View on NOAA';

      const icon = document.createElement('span');
      icon.textContent = '↗';
      icon.style.fontSize = '12px';
      icon.style.lineHeight = '1';
      link.prepend(icon);

      sourceWrap.appendChild(link);
      body.appendChild(sourceWrap);
    } else {
      const syntheticSource = document.createElement('div');
      syntheticSource.style.padding = '10px 16px 4px';
      syntheticSource.style.fontSize = '12px';
      syntheticSource.style.color = '#8E8E93';
      syntheticSource.textContent = `Zone ${zoneId} `;
      body.appendChild(syntheticSource);
    }
  }

  private ensureMarinePopupSpinKeyframes(): void {
    const styleId = 'marine-forecast-popup-spin-style';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `@keyframes marine - spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } `;
    document.head.appendChild(style);
  }

  private fitMapToFeatureCollection(featureCollection: GeoJSON.FeatureCollection): void {
    if (!this.map || !featureCollection?.features?.length) return;

    const bounds = new maplibregl.LngLatBounds();
    let hasCoordinates = false;

    const walkCoords = (node: any) => {
      if (!Array.isArray(node)) return;
      if (node.length >= 2 && typeof node[0] === 'number' && typeof node[1] === 'number') {
        const lng = Number(node[0]);
        const lat = Number(node[1]);
        if (Number.isFinite(lng) && Number.isFinite(lat)) {
          bounds.extend([lng, lat]);
          hasCoordinates = true;
        }
        return;
      }
      node.forEach((child) => walkCoords(child));
    };

    featureCollection.features.forEach((feature: any) => {
      walkCoords(feature?.geometry?.coordinates);
    });

    if (!hasCoordinates) return;

    this.map.fitBounds(bounds, {
      padding: { top: 32, right: 32, bottom: 32, left: 32 },
      duration: 700,
      maxZoom: 9,
    });
  }

  private onMarineZoneMouseEnter = () => {
    if (this.map) this.map.getCanvas().style.cursor = 'pointer';
  };

  private onMarineZoneMouseLeave = () => {
    if (this.map) this.map.getCanvas().style.cursor = '';
  };

  private formatDms(value: number, isLat: boolean): { line: string; hemi: string } {
    const hemi = isLat ? (value >= 0 ? 'N' : 'S') : (value >= 0 ? 'E' : 'W');
    const abs = Math.abs(value);
    const deg = Math.floor(abs);
    const minFloat = (abs - deg) * 60;
    const min = Math.floor(minFloat);
    const sec = (minFloat - min) * 60;
    // one decimal like the screenshot (e.g. 7.2")
    const secStr = sec.toFixed(1);
    const line = `${deg}° ${min} ' ${secStr}" ${hemi}`;
    return { line, hemi };
  }

  private closeLayerPopupsForProfileOpen(): void {
    this.closeBuoyDetailScreen();
    this.closeTideDetailScreen();

    if (this.activeWreckPopup) {
      try { this.activeWreckPopup.remove(); } catch (_) { }
      this.activeWreckPopup = null;
      this.updateWreckMarkerStacking(undefined);
    }
  }

  private dismissAcspoPointPopup(): void {
    this.acspoPointRequestSeq += 1;
    try { this.acspoPointPopup?.remove(); } catch (_) { }
    this.acspoPointPopup = null;
  }

  private isAcspoPointQueryOverlayActive(configs: any[] = this.activeOverlayConfigs): boolean {
    if (!configs.length) return false;
    const selectedType = this.activeOverlayWidgetType && configs.some(c => c.type === this.activeOverlayWidgetType)
      ? this.activeOverlayWidgetType
      : configs[0].type;
    const selectedConfig = configs.find(c => c.type === selectedType);
    return !!selectedConfig && selectedConfig.type === 'sst' && selectedConfig.source === 'ACSPO GOES-19 (Regional)';
  }

  private tryShowAcspoPointPopup(lngLat: { lng: number; lat: number } | any): void {
    if (!this.map || !lngLat) return;

    const active = this.getActiveOverlayWidgetConfig();
    if (!active || active.type !== 'sst' || active.source !== 'ACSPO GOES-19 (Regional)') return;

    const lon = Number(lngLat.lng);
    const lat = Number(lngLat.lat);
    if (!isFinite(lon) || !isFinite(lat)) return;
    if (!this.isPointWithinAcspoRegionBounds(lon, lat, active)) {
      this.dismissAcspoPointPopup();
      return;
    }

    // Close any previous ACSPO popup before showing a new one
    try { this.acspoPointPopup?.remove(); } catch (_) { }
    this.acspoPointPopup = null;

    const dateStr = active.date instanceof Date
      ? active.date.toISOString().slice(0, 10)
      : String(active.date ?? '').slice(0, 10) || new Date().toISOString().slice(0, 10);

    const regionCode = this.getAcspoRegionCode(active.region);
    const seq = ++this.acspoPointRequestSeq;
    const params = new HttpParams()
      .set('lat', String(lat))
      .set('lon', String(lon))
      .set('date', dateStr)
      .set('region', regionCode);

    // Show loader right away before making the request
    const loadingEl = document.createElement('div');
    loadingEl.className = 'acspo-point-popup';
    loadingEl.innerHTML = `
      <div style="background:#fff;border-radius:12px;width:56px;height:56px;display:flex;align-items:center;justify-content:center;box-shadow:0 10px 28px rgba(0,0,0,0.15);">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" style="animation: acspo-spin 1s linear infinite;">
          <circle cx="12" cy="12" r="10" stroke="#F97316" stroke-width="2.5" stroke-dasharray="45" stroke-linecap="round" opacity="0.3"></circle>
          <path d="M12 2a10 10 0 0 1 10 10" stroke="#F97316" stroke-width="2.5" stroke-linecap="round"></path>
        </svg>
        <style>
          @keyframes acspo-spin {
            100% { transform: rotate(360deg); }
          }
        </style>
      </div>
    `;

    this.acspoPointPopup = new maplibregl.Popup({ closeButton: false, maxWidth: '320px', anchor: 'bottom', offset: [0, -10] })
      .setLngLat([lon, lat])
      .setDOMContent(loadingEl)
      .addTo(this.map);

    this.http.get<any>(this.acspoPointApiUrl, { params }).subscribe({
      next: (res: any) => {
        if (seq !== this.acspoPointRequestSeq) return; // stale

        const sstC = typeof res?.sst_c === 'number' ? res.sst_c : null;
        // Outside the true SST footprint often returns no numeric SST.
        // Do not keep highlight/popup in those areas.
        if (sstC == null) {
          this.dismissAcspoPointPopup();
          return;
        }

        const tempF = sstC != null ? (sstC * 9 / 5 + 32) : null;
        const confidence = typeof res?.confidence === 'number' ? res.confidence : null;

        const displayTemp = tempF != null ? `${tempF.toFixed(1)} °F` : '—';
        const displayConf = confidence != null ? `${Math.round(confidence * 100)}%` : '—';
        const latDms = this.formatDms(lat, true).line;
        const lonDms = this.formatDms(lon, false).line;

        const content = document.createElement('div');
        content.className = 'acspo-point-popup';
        content.innerHTML = `
          <div style="background:#fff;border-radius:12px;padding:12px 14px;box-shadow:0 10px 28px rgba(0,0,0,0.15);font-family: system-ui, -apple-system, 'Segoe UI', Roboto, Arial;min-width:220px;">
            <div style="display:flex;align-items:flex-start;gap:10px;">
              <span style="display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;margin-top:2px;">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M14 14.76V5a2 2 0 10-4 0v9.76a4 4 0 104 0z" stroke="#F97316" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M12 17a1.5 1.5 0 01-1.5-1.5c0-.7.5-1.3 1.2-1.45V8" stroke="#F97316" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <div style="display:flex;flex-direction:column;gap:6px;">
                <div style="font-size:18px;font-weight:800;color:#111;line-height:1.1;">${displayTemp}</div>
                <div style="font-size:13px;color:#111;line-height:1.25;">
                  <div>${latDms}</div>
                  <div>${lonDms}</div>
                </div>
                <div style="font-size:12px;color:#9CA3AF;">Confidence: ${displayConf}</div>
              </div>
            </div>
          </div>
        `;

        if (!this.acspoPointPopup) return;
        this.acspoPointPopup.setDOMContent(content);
      },
      error: () => {
        if (seq !== this.acspoPointRequestSeq) return;
        // Keep strict behavior: no popup when ACSPO has no point result.
        this.dismissAcspoPointPopup();
      }
    });
  }

  private isPointWithinAcspoRegionBounds(lon: number, lat: number, activeConfig: any): boolean {
    const regionCode = this.getAcspoRegionCode(activeConfig?.region);
    const bounds = this.acspoRegionBounds[regionCode];
    if (!bounds) return true;

    const tolerance = this.acspoPointRegionBoundaryToleranceDeg;
    const [[west, south], [east, north]] = bounds;
    return (
      lon >= west - tolerance &&
      lon <= east + tolerance &&
      lat >= south - tolerance &&
      lat <= north + tolerance
    );
  }

  private handleZoomBasedLayerVisibility(): void {
    if (!this.map) return;
    const zoom = this.map.getZoom();
    if (zoom < this.layerAutoHideZoomThreshold) {
      if (this.activeMapLayerIds.length > 0 && this.layersHiddenByZoom.length === 0) {
        this.layersHiddenByZoom = [...this.activeMapLayerIds];
        this.submenuService.setActiveMapLayers([]);
      }
    } else if (this.layersHiddenByZoom.length > 0) {
      const toRestore = this.layersHiddenByZoom;
      this.layersHiddenByZoom = [];
      this.submenuService.setActiveMapLayers(toRestore);
    }
  }

  private syncMapDataLayers(): void {
    const buoyEnabled = this.activeMapLayerIds.includes('buoy');
    if (buoyEnabled) {
      this.ensureBuoyMarkers();
    } else {
      this.clearBuoyMarkers();
    }

    const wrecksEnabled = this.activeMapLayerIds.includes('wrecks');
    if (wrecksEnabled) {
      this.ensureWreckMarkers();
    } else {
      this.clearWreckMarkers();
    }

    const localTidesEnabled = this.activeMapLayerIds.includes('local-tides');
    if (localTidesEnabled) {
      this.ensureTideMarkers();
    } else {
      this.clearTideMarkers();
      this.closeTideDetailScreen();
    }
  }

  private ensureBuoyMarkers(): void {
    if (!this.map) return;
    const nearby = this.getBuoyNearbyRequest();
    if (!nearby) return;
    if (this.buoysLoaded && this.buoyMarkers.length > 0 && this.lastBuoyFetchKey === nearby.cacheKey) return;

    fetch(nearby.url)
      .then((res) => res.ok ? res.json() : Promise.reject(new Error('buoy nearby request failed')))
      .then((payload: any) => {
        const buoyRows = Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload?.buoys)
            ? payload.buoys
            : Array.isArray(payload?.data)
              ? payload.data
              : Array.isArray(payload)
                ? payload
                : [];

        this.clearBuoyMarkers();

        const mapCenter = this.map!.getCenter();
        const centerLngLat = new maplibregl.LngLat(mapCenter.lng, mapCenter.lat);
        const normalizedRows = buoyRows
          .map((row: any) => {
            const coord = this.extractBuoyCoordinate(row);
            if (!coord) return null;
            const distanceNm = this.haversineNauticalMiles(centerLngLat, new maplibregl.LngLat(coord.lng, coord.lat));
            return { row, coord, distanceNm };
          })
          .filter((item: any) => !!item && Number.isFinite(item.distanceNm))
          .sort((a: any, b: any) => a.distanceNm - b.distanceNm)
          .slice(0, this.buoyMarkersRenderLimit);

        // Build markers outside Angular's zone (pure DOM/map work); the click
        // handler re-enters the zone via ngZone.run, so interactions still work.
        this.ngZone.runOutsideAngular(() => {
          normalizedRows.forEach((item: any) => {
            const row = item.row;
            const coord = item.coord;
            const buoyId = this.getBuoyId(row);

            const el = document.createElement('img');
            el.src = '/assets/home/layer/buoy_marker.png';
            el.alt = 'Buoy';
            el.style.width = '28px';
            el.style.height = '28px';
            el.style.objectFit = 'contain';
            el.style.display = 'block';
            el.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.35))';
            // Keep buoy markers underneath popups/modals.
            el.style.zIndex = '1';
            el.style.cursor = 'pointer';
            if (buoyId) {
              el.setAttribute('data-buoy-id', buoyId);
            }
            el.onerror = () => {
              el.src = '/assets/home/layer/buoy_overlay 1.png';
            };
            el.addEventListener('click', (event: MouseEvent) => {
              event.stopPropagation();
              this.ngZone.run(() => this.openBuoyDetailScreen(row));
            });

            const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
              .setLngLat([coord.lng, coord.lat])
              .addTo(this.map!);

            this.buoyMarkers.push(marker);
          });
          // Attach only on-screen buoys; off-screen ones stay detached until panned in.
          this.buoyMarkers.forEach((m) => this.applyVesselMarkerViewportCull(m));
        });

        this.buoysLoaded = true;
        this.lastBuoyFetchKey = nearby.cacheKey;
      })
      .catch((err) => {
        console.error('[Layers] Failed to load buoy markers:', err);
        this.lastBuoyFetchKey = null;
      });
  }

  private clearBuoyMarkers(): void {
    this.buoyMarkers.forEach((marker) => marker.remove());
    this.buoyMarkers = [];
    this.buoysLoaded = false;
    this.lastBuoyFetchKey = null;
  }

  private getBuoyNearbyRequest(): { url: string; cacheKey: string } | null {
    if (!this.map) return null;

    // Always use the vessel's last known position so the API is only called once
    // when the layer is applied, regardless of subsequent map panning/zooming.
    const vesselLat = this.latestObservation?.lat;
    const vesselLng = this.latestObservation?.lng;
    const lat = Number.isFinite(vesselLat) ? vesselLat! : this.map.getCenter().lat;
    const lng = Number.isFinite(vesselLng) ? vesselLng! : this.map.getCenter().lng;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    // Fixed 500nm radius for buoys so a wide area around the vessel is covered.
    const radiusNm = 500;

    // cacheKey is based on rounded vessel position so it stays stable during map panning.
    const cacheKey = [
      lat.toFixed(1),
      lng.toFixed(1),
      `r${radiusNm}`,
    ].join(',');
    const url = `${this.buoyNearbyApiBaseUrl}?lat=${lat.toFixed(4)}&lon=${lng.toFixed(4)}&radius_nm=${radiusNm}`;
    return { url, cacheKey };
  }

  private ensureTideMarkers(): void {
    if (!this.map) return;
    if (this.map.getZoom() < this.tideMarkerMinZoom) {
      this.clearTideMarkers();
      return;
    }
    const nearby = this.getTideNearbyRequest();
    if (!nearby) return;
    if (this.tidesLoaded && this.tideMarkers.length > 0 && this.lastTideFetchKey === nearby.cacheKey) return;
    const requestSeq = ++this.tidesRequestSeq;

    const tideToken = this.authService.getAccessToken();
    const tideHeaders: HeadersInit = tideToken ? { Authorization: `Bearer ${tideToken}` } : {};
    fetch(nearby.url, { headers: tideHeaders })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error('local tides nearby request failed')))
      .then((payload: any) => {
        if (requestSeq !== this.tidesRequestSeq) return;
        const rows = Array.isArray(payload?.stations)
          ? payload.stations
          : Array.isArray(payload?.results)
            ? payload.results
            : Array.isArray(payload?.data)
              ? payload.data
              : Array.isArray(payload)
                ? payload
                : [];

        this.clearTideMarkers(false);

        const centerLngLat = new maplibregl.LngLat(nearby.centerLng, nearby.centerLat);
        const normalized = rows
          .map((row: any) => {
            const coord = this.extractTideCoordinate(row);
            if (!coord) return null;
            const stationKey = this.getNoaaStationId(row) ?? `${coord.lat.toFixed(4)},${coord.lng.toFixed(4)}`;
            const distanceNm = this.haversineNauticalMiles(centerLngLat, new maplibregl.LngLat(coord.lng, coord.lat));
            return { row, coord, stationKey, distanceNm };
          })
          .filter((item: any) => !!item && Number.isFinite(item.distanceNm) && item.distanceNm <= nearby.radiusNm + 0.01);

        const dedupedByStation: Record<string, any> = {};
        normalized.forEach((item: any) => {
          const existing = dedupedByStation[item.stationKey];
          if (!existing || item.distanceNm < existing.distanceNm) {
            dedupedByStation[item.stationKey] = item;
          }
        });

        const rowsToRender = Object.values(dedupedByStation)
          .sort((a, b) => a.distanceNm - b.distanceNm)
          .slice(0, this.tideMarkersRenderLimit);

        // Build markers outside Angular's zone (pure DOM/map work); the click
        // handler re-enters the zone via ngZone.run, so interactions still work.
        this.ngZone.runOutsideAngular(() => {
          rowsToRender.forEach((item: any) => {
            const row = item.row;
            const coord = item.coord;

            const el = document.createElement('img');
            el.src = '/assets/home/layer/tide_marker.png';
            el.alt = 'Tide Station';
            el.style.width = '36px';
            el.style.height = '36px';
            el.style.objectFit = 'contain';
            el.style.display = 'block';
            el.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.35))';
            el.style.zIndex = '2';
            el.style.cursor = 'pointer';
            el.onerror = () => {
              el.src = '/assets/home/layer/tide_overlay 1.png';
            };

            const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
              .setLngLat([coord.lng, coord.lat])
              .addTo(this.map!);

            el.addEventListener('click', (event: MouseEvent) => {
              event.stopPropagation();
              this.ngZone.run(() => this.openTideDetailScreen(row));
            });

            this.tideMarkers.push(marker);
          });
          // Attach only on-screen tide stations; the rest stay detached until panned in.
          this.tideMarkers.forEach((m) => this.applyVesselMarkerViewportCull(m));
        });

        this.tidesLoaded = true;
        this.lastTideFetchKey = nearby.cacheKey;
      })
      .catch((err) => {
        console.error('[Layers] Failed to load local tide markers:', err);
        this.lastTideFetchKey = null;
      });
  }

  private clearTideMarkers(cancelPendingRequest: boolean = true): void {
    if (cancelPendingRequest) {
      this.tidesRequestSeq += 1;
    }
    this.tideMarkers.forEach((marker) => marker.remove());
    this.tideMarkers = [];
    this.tidesLoaded = false;
    this.lastTideFetchKey = null;
  }

  private getTideNearbyRequest(): { url: string; cacheKey: string; radiusNm: number; centerLat: number; centerLng: number } | null {
    if (!this.map) return null;

    // Always use the vessel's last known position so the API is only called once
    // when the layer is applied, regardless of subsequent map panning/zooming.
    const vesselLat = this.latestObservation?.lat;
    const vesselLng = this.latestObservation?.lng;
    const centerLat = Number.isFinite(vesselLat) ? vesselLat! : this.map.getCenter().lat;
    const centerLng = Number.isFinite(vesselLng) ? vesselLng! : this.map.getCenter().lng;
    if (!Number.isFinite(centerLat) || !Number.isFinite(centerLng)) return null;

    // Fixed 25nm radius for local tides.
    const radiusNm = 25;

    // cacheKey is based on rounded vessel position so it stays stable during map panning.
    const cacheKey = [
      centerLat.toFixed(1),
      centerLng.toFixed(1),
      `r${radiusNm}`,
    ].join(',');
    const url = `${this.localTidesNearbyApiBaseUrl}?lat=${centerLat.toFixed(4)}&lon=${centerLng.toFixed(4)}&radius_nm=${radiusNm}`;
    return { url, cacheKey, radiusNm, centerLat, centerLng };
  }

  private extractTideCoordinate(row: any): { lat: number; lng: number } | null {
    if (!row) return null;
    const direct = this.normalizeCoordinatePair(
      row?.lat ?? row?.latitude ?? row?.lat_dd ?? row?.position?.lat ?? row?.position?.latitude
      ?? row?.location?.lat ?? row?.geometry?.lat ?? row?.centroid?.lat,
      row?.lon ?? row?.lng ?? row?.longitude ?? row?.long ?? row?.position?.lon ?? row?.position?.lng
      ?? row?.position?.longitude ?? row?.location?.lng ?? row?.location?.lon ?? row?.geometry?.lng
      ?? row?.geometry?.lon ?? row?.centroid?.lng ?? row?.centroid?.lon
    );
    if (direct) return direct;

    const geometryCoords = row?.geometry?.coordinates || row?.coordinates;
    if (Array.isArray(geometryCoords) && geometryCoords.length >= 2) {
      const geojsonOrder = this.normalizeCoordinatePair(geometryCoords[1], geometryCoords[0]);
      if (geojsonOrder) return geojsonOrder;
      const reversedOrder = this.normalizeCoordinatePair(geometryCoords[0], geometryCoords[1]);
      if (reversedOrder) return reversedOrder;
    }
    return null;
  }

  closeTideDetailScreen(): void {
    this.isTideDetailOpen = false;
    this.tideDetailLoading = false;
    this.tideSheetError = null;
    this.selectedTide = null;
    this.tideDetailRequestSeq += 1;
  }

  private openTideDetailScreen(row: any): void {
    const stationId = this.getNoaaStationId(row);
    if (!stationId) return;
    const requestSeq = ++this.tideDetailRequestSeq;
    const shell = this.toTideDetailShell(row, stationId);

    this.isTideDetailOpen = true;
    this.tideDetailLoading = true;
    this.tideSheetError = null;
    this.selectedTide = shell;
    this.closeBuoyDetailScreen();

    this.fetchNoaaTideSheet(stationId)
      .then((data) => {
        this.ngZone.run(() => {
          if (requestSeq !== this.tideDetailRequestSeq || !this.selectedTide) return;
          this.selectedTide = {
            ...this.selectedTide,
            ...data,
          };
          if (data.currentLevel === null && data.events.length === 0) {
            this.tideSheetError = 'Data unavailable for this tide station.';
          }
        });
      })
      .catch((err) => {
        console.error('[Tides] NOAA fetch failed:', err);
        this.ngZone.run(() => {
          if (requestSeq !== this.tideDetailRequestSeq) return;
          this.tideSheetError = 'Unable to load data, please try again later.';
        });
      })
      .finally(() => {
        this.ngZone.run(() => {
          if (requestSeq !== this.tideDetailRequestSeq) return;
          this.tideDetailLoading = false;
        });
      });
  }

  private toTideDetailShell(row: any, stationId: string): TideDetailView {
    const name = String(row?.name || row?.station_name || `Station ${stationId}`).trim() || `Station ${stationId}`;
    const state = String(row?.state || row?.region || '').trim();
    const distanceNm = this.toFiniteNumber(row?.distance_nm);
    const subtitle = this.formatTideSubtitleLine(stationId, distanceNm, state);

    return {
      id: stationId,
      name,
      subtitle,
      locationTag: state || 'Station',
      dateLabel: this.formatTodayLabel(),
      events: [],
      currentLevel: null,
      trend: null,
      levelTimestamp: null,
    };
  }

  private async fetchNoaaTideSheet(stationId: string): Promise<{
    currentLevel: number | null;
    trend: number | null;
    levelTimestamp: string | null;
    events: TideEventView[];
  }> {
    const utcNow = new Date();
    const twoHoursAgo = new Date(utcNow.getTime() - 2 * 60 * 60 * 1000);
    const fmt = (dt: Date) => {
      const y = dt.getUTCFullYear();
      const mo = String(dt.getUTCMonth() + 1).padStart(2, '0');
      const d = String(dt.getUTCDate()).padStart(2, '0');
      const h = String(dt.getUTCHours()).padStart(2, '0');
      const mi = String(dt.getUTCMinutes()).padStart(2, '0');
      return `${y}${mo}${d} ${h}:${mi}`;
    };
    const todayStr = `${utcNow.getUTCFullYear()}${String(utcNow.getUTCMonth() + 1).padStart(2, '0')}${String(utcNow.getUTCDate()).padStart(2, '0')}`;

    const levelUrl =
      `${this.coOpsBase}?product=water_level&station=${encodeURIComponent(stationId)}` +
      `&begin_date=${encodeURIComponent(fmt(twoHoursAgo))}` +
      `&end_date=${encodeURIComponent(fmt(utcNow))}` +
      '&datum=MLLW&time_zone=lst_ldt&units=english&format=json&application=EsperWave';

    const predUrl =
      `${this.coOpsBase}?product=predictions&station=${encodeURIComponent(stationId)}` +
      `&begin_date=${todayStr}&end_date=${todayStr}` +
      '&datum=MLLW&time_zone=lst_ldt&interval=hilo&units=english&format=json&application=EsperWave';

    const [levelResp, predResp] = await Promise.all([fetch(levelUrl), fetch(predUrl)]);
    const [levelBody, predBody] = await Promise.all([
      levelResp.ok ? levelResp.json().catch(() => ({})) : Promise.resolve({}),
      predResp.ok ? predResp.json().catch(() => ({})) : Promise.resolve({}),
    ]);

    let currentLevel: number | null = null;
    let trend: number | null = null;
    let levelTimestamp: string | null = null;
    const events: TideEventView[] = [];

    const levelData = levelBody?.data as Array<{ v?: string; t?: string }> | undefined;
    if (levelData && levelData.length > 0) {
      const last = levelData[levelData.length - 1];
      currentLevel = this.toFiniteNumber(last?.v);
      levelTimestamp = typeof last?.t === 'string' ? last.t : null;
      if (levelData.length >= 2) {
        const firstVal = this.toFiniteNumber(levelData[0]?.v);
        if (firstVal !== null && currentLevel !== null) {
          trend = currentLevel - firstVal;
        }
      }
    }

    const preds = predBody?.predictions as Array<{ type?: string; v?: string; t?: string }> | undefined;
    if (preds && Array.isArray(preds)) {
      for (const p of preds) {
        const isHigh = p.type === 'H';
        const val = this.toFiniteNumber(p.v);
        events.push({
          type: isHigh ? 'High' : 'Low',
          level: val !== null ? `${val.toFixed(2)} ft` : '—',
          timeLabel: this.formatNoaaPredTime(typeof p.t === 'string' ? p.t : null),
        });
      }
    }

    return { currentLevel, trend, levelTimestamp, events };
  }

  tideTrendLabel(): string {
    const t = this.selectedTide?.trend;
    if (t === null || t === undefined) return '';
    if (t > 0.05) return ' ↑ Rising';
    if (t < -0.05) return ' ↓ Falling';
    return ' → Steady';
  }

  tideTrendClass(): string {
    const t = this.selectedTide?.trend;
    if (t === null || t === undefined) return '';
    if (t > 0.05) return 'tide-trend--rise';
    if (t < -0.05) return 'tide-trend--fall';
    return 'tide-trend--steady';
  }

  tideLevelFooterDisplay(): string {
    return this.formatNoaaWaterLevelFooter(this.selectedTide?.levelTimestamp ?? null);
  }

  private formatNoaaPredTime(t: string | null): string {
    if (!t) return '—';
    const parts = t.split(' ');
    if (parts.length < 2) return t;
    const timeParts = parts[1].split(':');
    if (timeParts.length < 2) return parts[1];
    const h = parseInt(timeParts[0], 10);
    const m = timeParts[1];
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${m} ${period}`;
  }

  private formatNoaaWaterLevelFooter(t: string | null): string {
    if (!t) return '';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const parts = t.split(' ');
    if (parts.length < 2) return t;
    const dateParts = parts[0].split('-');
    const timeParts = parts[1].split(':');
    if (dateParts.length < 3 || timeParts.length < 2) return t;
    const month = parseInt(dateParts[1], 10);
    const day = parseInt(dateParts[2], 10);
    const h = parseInt(timeParts[0], 10);
    const m = timeParts[1];
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    const monthStr = month >= 1 && month <= 12 ? months[month - 1] : String(month);
    return `${monthStr} ${day}  ${h12}:${m} ${period}`;
  }

  private formatTideSubtitleLine(tideId: string, distanceNm: number | null, state: string): string {
    const parts: string[] = [tideId];
    if (distanceNm !== null) {
      parts.push(`${distanceNm.toFixed(1)} nm`);
    }
    if (state) {
      parts.push(state);
    }
    return parts.join(' · ');
  }

  /** NOAA CO-OPS station id (numeric string from backend / nearby feed). */
  private getNoaaStationId(row: any): string | null {
    const raw =
      row?.noaa_station_id ??
      row?.noaa_id ??
      row?.station_id ??
      row?.station ??
      row?.id ??
      row?._id ??
      row?.identifier ??
      null;
    if (raw === null || raw === undefined) return null;
    const s = String(raw).trim();
    return s || null;
  }

  private formatTodayLabel(): string {
    const now = new Date();
    return now.toLocaleString('en-US', { month: 'short', day: 'numeric' });
  }

  private ensureWreckMarkers(): void {
    if (!this.map) return;
    // Run all wreck fetch/process/render work OUTSIDE Angular's zone so it never
    // triggers a change-detection pass. It runs on every moveend and again on each
    // refetch when the viewport leaves the cached area, with a main-thread
    // map/sort over the whole response. The wreck layer's click handler is
    // registered on the map (already outside the zone), so interactions are
    // unaffected.
    this.ngZone.runOutsideAngular(() => this.ensureWreckMarkersInner());
  }

  private ensureWreckMarkersInner(): void {
    if (!this.map) return;
    const nearby = this.getWreckNearbyRequest();
    if (!nearby) return;

    // Already loaded wrecks for an area that still covers the current viewport?
    // Reuse them — zooming in and small pans stay inside the cached region, so
    // we skip the network request (and the marker reload it caused). We only
    // refetch when the viewport escapes the cached area (pan/zoom-out to new
    // water). Note: the seq is bumped *after* this guard so a skip never
    // cancels a fetch that is still in flight for the same area.
    if (
      this.wrecksLoaded &&
      this.wreckFeatures.length > 0 &&
      this.lastWreckFetchBounds &&
      this.boundsContain(this.lastWreckFetchBounds, this.map.getBounds())
    ) {
      return;
    }

    const requestSeq = ++this.wreckMarkersRequestSeq;
    const requestUrl = nearby.url;

    fetch(requestUrl)
      .then((res) => res.ok ? res.json() : Promise.reject(new Error('wreck nearby request failed')))
      .then((payload: any) => {
        if (requestSeq !== this.wreckMarkersRequestSeq) return;
        const wreckRows = Array.isArray(payload?.features)
          ? payload.features
          : Array.isArray(payload?.results)
            ? payload.results
            : Array.isArray(payload?.wrecks)
              ? payload.wrecks
              : Array.isArray(payload?.data)
                ? payload.data
                : Array.isArray(payload)
                  ? payload
                  : [];

        // Load the wreck icon into the GL context (once), then publish features.
        return this.ensureWreckIconImage().then((iconOk) => {
          if (!iconOk || requestSeq !== this.wreckMarkersRequestSeq || !this.map || !this.activeMapLayerIds.includes('wrecks')) return;

          const mapCenter = this.map.getCenter();
          const centerLngLat = new maplibregl.LngLat(mapCenter.lng, mapCenter.lat);
          this.wreckFeatures = wreckRows
            .map((row: any) => {
              const coord = this.extractWreckCoordinate(row);
              if (!coord) return null;
              const distanceNm = this.haversineNauticalMiles(centerLngLat, new maplibregl.LngLat(coord.lng, coord.lat));
              return { row, coord, distanceNm };
            })
            .filter((item: any) => !!item && Number.isFinite(item.distanceNm))
            .sort((a: any, b: any) => a.distanceNm - b.distanceNm)
            .slice(0, this.wreckMarkersRenderLimit)
            .map((item: any) => ({
              type: 'Feature' as const,
              geometry: { type: 'Point' as const, coordinates: [item.coord.lng, item.coord.lat] },
              properties: {
                wid: this.extractWreckId(item.row),
                name: this.getWreckName(item.row),
              },
            }));

          this.renderWreckLayer();
          this.wrecksLoaded = true;
          this.lastWreckFetchBounds = nearby.bounds;
        });
      })
      .catch((err) => {
        console.error('[Layers] Failed to load wreck markers:', err);
        this.lastWreckFetchBounds = null;
      });
  }

  /** Push the current wreck FeatureCollection to the GL source, creating the source/layer on first use. */
  private renderWreckLayer(): void {
    if (!this.map) return;
    const data = { type: 'FeatureCollection' as const, features: this.wreckFeatures };
    const src = this.map.getSource(this.wreckSourceId) as maplibregl.GeoJSONSource | undefined;
    if (src) {
      src.setData(data as any);
    } else {
      this.map.addSource(this.wreckSourceId, { type: 'geojson', data: data as any });
    }

    if (!this.map.getLayer(this.wreckSymbolLayerId)) {
      const naturalWidth = this.wreckIconNaturalWidth || this.wreckMarkerBaseSizePx;
      const base = this.wreckMarkerBaseSizePx;
      this.map.addLayer({
        id: this.wreckSymbolLayerId,
        type: 'symbol',
        source: this.wreckSourceId,
        layout: {
          'icon-image': this.wreckIconImageId,
          // Teardrop pin: tip sits on the coordinate (like the DOM pins), not the pin's centre.
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          // Mirror the previous DOM sizing: getZoomScale(zoom, 4, 15, 0.45, 1.25) * 25px.
          // Done on the GPU here, so zoom no longer touches the DOM per marker.
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            4, (base * 0.45) / naturalWidth,
            15, (base * 1.25) / naturalWidth,
          ],
        },
      });
      this.registerWreckLayerInteractions();
    }
  }

  /** Click / hover handlers for the wreck symbol layer (registered once). */
  private registerWreckLayerInteractions(): void {
    if (this.wreckInteractionsRegistered || !this.map) return;
    this.wreckInteractionsRegistered = true;

    // NOTE: the wreck symbol layer deliberately has NO dedicated 'click' handler.
    // A per-layer click fires whenever a wreck is under the point regardless of an
    // overlapping waypoint pin / place / track dot, which let the wreck popup hijack
    // clicks the user aimed at a neighbouring icon. Wreck clicks are now resolved by
    // the main map-click handler via pickNearestClickable(), which opens only the
    // single icon nearest the click. Hover cursor feedback stays here.
    this.map.on('mouseenter', this.wreckSymbolLayerId, () => {
      if (this.map) this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', this.wreckSymbolLayerId, () => {
      if (this.map) this.map.getCanvas().style.cursor = '';
    });
  }

  /**
   * Resolve overlapping "point" icons to the SINGLE one nearest the click so a click
   * opens exactly the icon the user aimed at, at any zoom — never a neighbour a few
   * pixels away. Considers visible waypoint DOM markers (pins, places, spots, fish
   * caught, …) and wreck GL symbols, all measured in viewport pixels. Returns the
   * nearest within MAX_PX, or null when the click isn't near any icon (so the caller
   * falls through to trip-track / background handling).
   *
   * Clustered-away waypoints are detached from the DOM (el.isConnected === false),
   * so they're never candidates — clicking a cluster still hits the cluster, not the
   * individual pins hidden inside it.
   */
  private pickNearestClickable(
    e: any,
  ):
    | { kind: 'waypoint'; marker: maplibregl.Marker; dist: number }
    | { kind: 'wreck'; row: { _id: any; name: any }; coord: { lng: number; lat: number }; dist: number }
    | null {
    if (!this.map) return null;
    const oe = e.originalEvent as MouseEvent | undefined;
    if (!oe || typeof oe.clientX !== 'number' || typeof oe.clientY !== 'number') return null;
    const cx = oe.clientX, cy = oe.clientY;
    const MAX_PX = 24; // must click reasonably near an icon

    let best:
      | { kind: 'waypoint'; marker: maplibregl.Marker; dist: number }
      | { kind: 'wreck'; row: { _id: any; name: any }; coord: { lng: number; lat: number }; dist: number }
      | null = null;
    let bestDist = Infinity;

    // Visible waypoint DOM markers — distance from the click to each icon's box
    // (0 when the click is inside the box, so a direct hit always wins).
    for (const marker of this.displayedMarkers) {
      const el = marker.getElement();
      if (!el || !el.isConnected) continue;
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) continue;
      const inside = cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom;
      const d = inside ? 0 : Math.hypot((r.left + r.right) / 2 - cx, (r.top + r.bottom) / 2 - cy);
      if (d < bestDist) {
        bestDist = d;
        best = { kind: 'waypoint', marker, dist: d };
      }
    }

    // Wreck symbols (GL) near the click — projected into viewport pixels so they
    // compare in the same space as the DOM markers above.
    if (this.map.getLayer(this.wreckSymbolLayerId)) {
      const cvRect = this.map.getCanvas().getBoundingClientRect();
      const box: [[number, number], [number, number]] = [
        [e.point.x - MAX_PX, e.point.y - MAX_PX],
        [e.point.x + MAX_PX, e.point.y + MAX_PX],
      ];
      const feats = this.map.queryRenderedFeatures(box, { layers: [this.wreckSymbolLayerId] }) as any[];
      for (const f of feats) {
        const c = f?.geometry?.coordinates;
        if (!Array.isArray(c) || c.length < 2) continue;
        const p = this.map.project({ lng: c[0], lat: c[1] } as any);
        const d = Math.hypot(p.x + cvRect.left - cx, p.y + cvRect.top - cy);
        if (d < bestDist) {
          bestDist = d;
          best = { kind: 'wreck', row: { _id: f.properties?.wid, name: f.properties?.name }, coord: { lng: c[0], lat: c[1] }, dist: d };
        }
      }
    }

    if (!best || bestDist > MAX_PX) return null;
    return best;
  }

  /** Load + register the wreck icon image into the GL context exactly once. */
  private ensureWreckIconImage(): Promise<boolean> {
    if (!this.map) return Promise.resolve(false);
    if (this.map.hasImage(this.wreckIconImageId) && this.wreckIconNaturalWidth) return Promise.resolve(true);
    if (this.wreckIconImagePromise) return this.wreckIconImagePromise;

    this.wreckIconImagePromise = this.getWreckMarkerIconUrl().then((iconUrl) => new Promise<boolean>((resolve) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        try {
          if (this.map && !this.map.hasImage(this.wreckIconImageId)) {
            this.map.addImage(this.wreckIconImageId, img, { pixelRatio: 1 });
          }
          this.wreckIconNaturalWidth = img.naturalWidth || img.width || this.wreckMarkerBaseSizePx;
          resolve(true);
        } catch (err) {
          console.error('[Layers] Failed to register wreck icon image:', err);
          resolve(false);
        } finally {
          this.wreckIconImagePromise = null;
        }
      };
      img.onerror = () => {
        this.wreckIconImagePromise = null;
        resolve(false);
      };
      img.src = iconUrl;
    }));

    return this.wreckIconImagePromise;
  }

  private extractWreckId(row: any): string {
    return String(
      row?._id || row?.id || row?.waypoint_id ||
      row?.properties?._id || row?.properties?.id || row?.properties?.waypoint_id || ''
    ).trim();
  }

  private clearWreckMarkers(cancelPendingRequest: boolean = true): void {
    if (cancelPendingRequest) {
      this.wreckMarkersRequestSeq += 1;
    }
    this.wreckFeatures = [];
    if (this.map) {
      const src = this.map.getSource(this.wreckSourceId) as maplibregl.GeoJSONSource | undefined;
      if (src) src.setData({ type: 'FeatureCollection', features: [] } as any);
    }
    this.wrecksLoaded = false;
    this.lastWreckFetchBounds = null;
    if (this.activeWreckPopup) {
      try { this.activeWreckPopup.remove(); } catch (_) { /* noop */ }
      this.activeWreckPopup = null;
    }
  }

  private extractWreckCoordinate(row: any): { lat: number; lng: number } | null {
    if (!row) return null;

    const lat = Number(row?.lat ?? row?.latitude ?? row?.position?.lat ?? row?.position?.latitude);
    const lng = Number(row?.lon ?? row?.lng ?? row?.longitude ?? row?.position?.lon ?? row?.position?.lng ?? row?.position?.longitude);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      return { lat, lng };
    }

    const geometryCoords = row?.geometry?.coordinates || row?.coordinates;
    const firstPair = this.findFirstLngLatPair(geometryCoords);
    if (firstPair) {
      return { lat: firstPair[1], lng: firstPair[0] };
    }

    return null;
  }

  private findFirstLngLatPair(coords: any): [number, number] | null {
    if (!Array.isArray(coords)) return null;
    if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      const lng = Number(coords[0]);
      const lat = Number(coords[1]);
      if (!Number.isNaN(lng) && !Number.isNaN(lat)) {
        return [lng, lat];
      }
      return null;
    }

    for (const child of coords) {
      const pair = this.findFirstLngLatPair(child);
      if (pair) return pair;
    }
    return null;
  }

  private getWreckNearbyRequest(): { url: string; bounds: maplibregl.LngLatBounds } | null {
    if (!this.map) return null;

    // Fetch a padded superset of the viewport so one request covers a wider area
    // than the screen. Subsequent zoom-ins and small pans then stay inside this
    // cached region and reuse the loaded wrecks instead of re-fetching.
    const bounds = this.padBounds(this.map.getBounds(), this.wreckFetchPadFactor);
    const west = bounds.getWest().toFixed(3);
    const south = bounds.getSouth().toFixed(3);
    const east = bounds.getEast().toFixed(3);
    const north = bounds.getNorth().toFixed(3);
    const zoom = Math.max(this.map.getZoom(), 12).toFixed(1);
    const url = `${this.wreckNearbyApiBaseUrl}?bbox=${west},${south},${east},${north}&zoom=${zoom}`;

    return { url, bounds };
  }

  /** Expand a bounds box outward by `factor` of its width/height on every side. */
  private padBounds(b: maplibregl.LngLatBounds, factor: number): maplibregl.LngLatBounds {
    const west = b.getWest();
    const south = b.getSouth();
    const east = b.getEast();
    const north = b.getNorth();
    const dx = (east - west) * factor;
    const dy = (north - south) * factor;
    return new maplibregl.LngLatBounds([west - dx, south - dy], [east + dx, north + dy]);
  }

  /** True when `outer` fully contains `inner` (used to reuse cached wrecks). */
  private boundsContain(outer: maplibregl.LngLatBounds, inner: maplibregl.LngLatBounds): boolean {
    return outer.getWest() <= inner.getWest()
      && outer.getEast() >= inner.getEast()
      && outer.getSouth() <= inner.getSouth()
      && outer.getNorth() >= inner.getNorth();
  }

  private getWreckMarkerIconUrl(): Promise<string> {
    if (this.wreckMarkerIconUrlCache) {
      return Promise.resolve(this.wreckMarkerIconUrlCache);
    }
    if (this.wreckMarkerIconPromise) {
      return this.wreckMarkerIconPromise;
    }

    const rawSrc = '/assets/waypoint_icons/pins/wreck.png';
    this.wreckMarkerIconPromise = new Promise<string>((resolve) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx || canvas.width <= 0 || canvas.height <= 0) {
            this.wreckMarkerIconUrlCache = rawSrc;
            resolve(rawSrc);
            return;
          }

          ctx.drawImage(img, 0, 0);
          const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
          const bounds = this.getNonTransparentBounds(data, canvas.width, canvas.height);

          if (!bounds) {
            this.wreckMarkerIconUrlCache = rawSrc;
            resolve(rawSrc);
            return;
          }

          const trimmedCanvas = document.createElement('canvas');
          trimmedCanvas.width = bounds.width;
          trimmedCanvas.height = bounds.height;
          const trimmedCtx = trimmedCanvas.getContext('2d');
          if (!trimmedCtx) {
            this.wreckMarkerIconUrlCache = rawSrc;
            resolve(rawSrc);
            return;
          }

          trimmedCtx.drawImage(
            img,
            bounds.left,
            bounds.top,
            bounds.width,
            bounds.height,
            0,
            0,
            bounds.width,
            bounds.height
          );
          const trimmedUrl = trimmedCanvas.toDataURL('image/png');
          this.wreckMarkerIconUrlCache = trimmedUrl;
          resolve(trimmedUrl);
        } catch (err) {
          console.error('[Layers] Wreck icon trim failed:', err);
          this.wreckMarkerIconUrlCache = rawSrc;
          resolve(rawSrc);
        } finally {
          this.wreckMarkerIconPromise = null;
        }
      };
      img.onerror = () => {
        this.wreckMarkerIconUrlCache = '/assets/home/filter/wreck.svg';
        this.wreckMarkerIconPromise = null;
        resolve(this.wreckMarkerIconUrlCache);
      };
      img.src = rawSrc;
    });

    return this.wreckMarkerIconPromise;
  }

  private getNonTransparentBounds(
    rgba: Uint8ClampedArray,
    width: number,
    height: number
  ): { left: number; top: number; width: number; height: number } | null {
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = rgba[(y * width + x) * 4 + 3];
        if (alpha <= 8) continue;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }

    if (maxX < minX || maxY < minY) return null;
    return {
      left: minX,
      top: minY,
      width: maxX - minX + 1,
      height: maxY - minY + 1,
    };
  }

  private openWreckPopup(row: any, coord: { lat: number; lng: number }, selectedEl?: HTMLElement): void {
    if (!this.map) return;

    if (this.activeWreckPopup) {
      try { this.activeWreckPopup.remove(); } catch (_) { /* noop */ }
      this.activeWreckPopup = null;
    }

    const name = this.getWreckName(row);
    const latText = this.formatWreckCoordinate(coord.lat, 'lat');
    const lngText = this.formatWreckCoordinate(coord.lng, 'lng');
    const popupIconSrc = this.wreckMarkerIconUrlCache || '/assets/waypoint_icons/pins/wreck.png';

    const wreckId = String(
      row?._id || row?.id || row?.waypoint_id ||
      row?.properties?._id || row?.properties?.id || row?.properties?.waypoint_id || ''
    ).trim();

    const eyeOffSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
      fill="none" stroke="#555" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <circle cx="12" cy="12" r="3"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>`;

    const hideActionsHtml = wreckId ? `
      <div class="popup-actions" id="wreck-popup-actions-${wreckId}">
        <button class="share-btn popup-hide-btn" data-action="wreck-hide"
          data-wid="${wreckId}" title="Hide from me">${eyeOffSvg}</button>
      </div>` : '';

    const html = `
      <div class="waypoint-popup wreck-waypoint-popup">
        <div class="popup-header">
          <img src="${popupIconSrc}" class="header-icon" alt="Wreck">
          <div class="header-text-col">
            <span class="header-title">${this.escapeHtml(name)}</span>
          </div>
        </div>
        <div class="popup-body">
          <div class="coords">
            <div class="coord-line">${latText}</div>
            <div class="coord-line">${lngText}</div>
          </div>
        </div>
        ${hideActionsHtml}
      </div>
    `;

    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: true,
      offset: 16,
      className: 'wreck-map-popup',
    })
      .setLngLat([coord.lng, coord.lat])
      .setHTML(html)
      .addTo(this.map);

    if (wreckId) {
      const popupEl = popup.getElement();
      if (popupEl) {
        popupEl.addEventListener('click', (e: MouseEvent) => {
          const btn = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
          if (!btn) return;
          e.stopPropagation();
          const action = btn.dataset['action'];
          const actionsId = `wreck-popup-actions-${wreckId}`;
          const actionsEl = popupEl.querySelector(`#${actionsId}`) as HTMLElement | null;

          if (action === 'wreck-hide') {
            if (actionsEl) {
              actionsEl.innerHTML = `
                <span class="popup-hide-label">Delete from your map?</span>
                <button class="share-btn popup-hide-yes" data-action="wreck-hide-confirm" data-wid="${wreckId}">Yes</button>
                <button class="share-btn popup-hide-no" data-action="wreck-hide-cancel">No</button>`;
            }
          } else if (action === 'wreck-hide-confirm') {
            this.ngZone.run(() => {
              try { popup.remove(); } catch (_) { }
              this.activeWreckPopup = null;
              // Remove the wreck from the GL source so it disappears immediately
              this.wreckFeatures = this.wreckFeatures.filter((feat) => String(feat?.properties?.wid || '') !== wreckId);
              this.renderWreckLayer();
              // Signal watch feed to hide matching posts
              this.submenuService.notifyWatchItemHidden({ waypointId: wreckId });
              // Persist on backend
              this.waypointService.hideForMe(undefined, wreckId).subscribe({ error: () => { } });
            });
          } else if (action === 'wreck-hide-cancel') {
            if (actionsEl) {
              actionsEl.innerHTML = `
                <button class="share-btn popup-hide-btn" data-action="wreck-hide"
                  data-wid="${wreckId}" title="Hide from me">${eyeOffSvg}</button>`;
            }
          }
        });
      }
    }

    this.updateWreckMarkerStacking(selectedEl);
    popup.on('close', () => {
      this.updateWreckMarkerStacking(undefined);
      this.activeWreckPopup = null;
    });
    this.activeWreckPopup = popup;
  }

  private updateWreckMarkerStacking(selectedEl?: HTMLElement): void {
    this.wreckMarkers.forEach((marker) => {
      const el = marker.getElement();
      if (!el) return;
      el.style.zIndex = selectedEl && el === selectedEl ? '10' : '6';
    });
  }

  private getWreckName(row: any): string {
    const raw = row?.properties?.name ?? row?.name ?? row?.title ?? 'Wreck';
    if (typeof raw !== 'string' || !raw.trim()) return 'WRECK';
    return raw.trim().toUpperCase();
  }

  private formatWreckCoordinate(value: number, type: 'lat' | 'lng'): string {
    const abs = Math.abs(value);
    const degrees = Math.floor(abs);
    const minutesFloat = (abs - degrees) * 60;
    const minutes = Math.floor(minutesFloat);
    const seconds = ((minutesFloat - minutes) * 60).toFixed(1);
    const direction = type === 'lat' ? (value >= 0 ? 'N' : 'S') : (value >= 0 ? 'E' : 'W');
    return `${degrees}° ${minutes}' ${seconds}" ${direction}`;
  }

  private escapeHtml(input: string): string {
    return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private updateZoomResponsiveMarkerSizes(
    targetMarker?: maplibregl.Marker,
    applyVesselStyle = true,
  ): void {
    if (!this.map) return;
    const zoom = this.map.getZoom();

    if (targetMarker) {
      const el = targetMarker.getElement();
      this.applyPlaceMarkerZoomSize(el, zoom);
      this.applyWreckMarkerZoomSize(el, zoom);
      if (applyVesselStyle) {
        this.applyVesselMarkerZoomStyle(el, zoom);
      }
      if (this.activeWaypointCardWaypointId === String(el?.getAttribute('data-wpid') || '').trim()) {
        if (this.activeWaypointCardMarker) {
          this.applyWaypointCardFlip(targetMarker);
        } else {
          this.mountWaypointCard(targetMarker);
        }
      }
      if (el?.classList.contains('waypoint-marker-highlight')) {
        this.updateWaypointFocusRing(el);
      }
      return;
    }

    this.displayedMarkers.forEach((marker) => {
      const el = marker.getElement();
      this.applyPlaceMarkerZoomSize(el, zoom);
      if (this.activeWaypointCardWaypointId === String(el?.getAttribute('data-wpid') || '').trim()) {
        if (this.activeWaypointCardMarker) {
          this.applyWaypointCardFlip(marker);
        } else {
          this.mountWaypointCard(marker);
        }
      }
      if (el?.classList.contains('waypoint-marker-highlight')) {
        this.updateWaypointFocusRing(el);
      }
    });
    this.wreckMarkers.forEach((marker) => {
      this.applyWreckMarkerZoomSize(marker.getElement(), zoom);
    });
    if (applyVesselStyle) {
      this.vesselMarkers.forEach((marker) => {
        this.applyVesselMarkerZoomStyle(marker.getElement(), zoom);
      });
      this.dockmateMarkers.forEach((marker) => {
        this.applyVesselMarkerZoomStyle(marker.getElement(), zoom);
      });
    }
  }

  private applyVesselMarkerZoomStyle(el: HTMLElement | null, zoom: number): void {
    if (!el) return;
    const vesselEl = el.classList.contains('vessel-boat-marker')
      ? el
      : this.getDockmateVesselElement(el);
    if (!vesselEl) return;
    const ring = vesselEl.querySelector('.dockmate-vessel-ring') as HTMLElement | null;
    if (!ring) return;

    // Keep ring geometry stable while aggregation is active — toggling styles
    // during zoom changes the visual anchor and markers appear to drift downward.
    // Past the cut-off nothing aggregates, so the zoomed-in ring is safe.
    ring.classList.toggle(
      'vessel-zoomed-in',
      !MapClusteringService.isClusteringActive(zoom),
    );
  }

  private getWaypointPopupOffset(marker: maplibregl.Marker): [number, number] {
    const el = marker.getElement();
    const markerKind = String(el?.getAttribute('data-marker-kind') || 'pin').trim();
    const rect = el?.getBoundingClientRect();
    const height = Math.max(
      rect?.height || 0,
      el?.offsetHeight || 0,
      markerKind === 'pin' ? 51 : markerKind === 'place-icon' ? 40 : 24
    );
    const visualEl =
      (el?.querySelector('.main-marker-image') as HTMLElement | null) ||
      (el?.querySelector('.waypoint-icon') as HTMLElement | null) ||
      (el?.querySelector('img') as HTMLElement | null) ||
      (el?.querySelector('svg') as HTMLElement | null);
    const visualRect = visualEl?.getBoundingClientRect();
    const visualHeight = Math.max(
      visualRect?.height || 0,
      visualEl?.offsetHeight || 0,
      markerKind === 'pin' ? 51 : markerKind === 'place-icon' ? 40 : 24
    );

    if (markerKind === 'place-label') {
      return [0, -(Math.round(height / 2) + 2)];
    }

    if (markerKind === 'place-icon') {
      return [0, -(Math.round(height / 2) + 2)];
    }

    // Lift the popup card above the pin icon so the icon stays visible.
    // The pin marker uses offset [0, 14]: the element sits 14px lower than normal
    // so the coordinate is inside the wider pin body. Subtract 14 here so the
    // popup tail aligns just above the pin's visual top.
    return [0, -(visualHeight - 14)];
  }

  /**
   * Decide whether the waypoint detail card should open above the icon (default)
   * or below it. Mirrors the vessel detail card: prefer above, but flip below
   * when the waypoint is too close to the top of the viewport for the card to
   * fit — so it never hides behind the top edge. Returns the matching MapLibre
   * marker anchor + pixel offset.
   */
  private getWaypointCardPlacement(originMarker: maplibregl.Marker): { anchor: 'top' | 'bottom'; offset: [number, number] } {
    const aboveOffset = this.getWaypointPopupOffset(originMarker); // card above the icon
    // Measure the live card height when available; fall back to a safe estimate
    // before the first layout pass.
    const cardEl = this.activeWaypointCardMarker?.getElement();
    const cardH = cardEl?.offsetHeight || 200;

    if (!this.map) return { anchor: 'bottom', offset: aboveOffset };

    const pt = this.map.project(originMarker.getLngLat());
    const margin = 8;
    const gap = Math.abs(aboveOffset[1]);
    const roomAbove = pt.y - gap - margin >= cardH;
    if (roomAbove) {
      return { anchor: 'bottom', offset: aboveOffset };
    }

    // Not enough room above → open below the icon, clearing its lower extent.
    const markerKind = String(originMarker.getElement()?.getAttribute('data-marker-kind') || 'pin').trim();
    const belowGap = markerKind === 'pin' ? 20 : 8;
    return { anchor: 'top', offset: [aboveOffset[0], belowGap] };
  }

  /**
   * Apply the correct above/below placement to the active waypoint card. MapLibre
   * markers can't change anchor after creation, so the marker is re-created (re-using
   * the same element, preserving its click handlers) only when the anchor must flip;
   * otherwise just the offset is updated.
   */
  private applyWaypointCardFlip(originMarker: maplibregl.Marker): void {
    if (!this.map || !this.activeWaypointCardMarker) return;
    const el = this.activeWaypointCardMarker.getElement();
    if (!el) return;

    const placement = this.getWaypointCardPlacement(originMarker);
    const currentAnchor: 'top' | 'bottom' = el.classList.contains('waypoint-popup-overlay--below') ? 'top' : 'bottom';

    if (placement.anchor === currentAnchor) {
      this.activeWaypointCardMarker.setOffset(placement.offset);
      return;
    }

    const lngLat = this.activeWaypointCardMarker.getLngLat();
    try { this.activeWaypointCardMarker.remove(); } catch (_) { }
    const newMarker = new maplibregl.Marker({ element: el, anchor: placement.anchor, offset: placement.offset })
      .setLngLat(lngLat)
      .addTo(this.map);
    this.activeWaypointCardMarker = newMarker;
    el.classList.toggle('waypoint-popup-overlay--below', placement.anchor === 'top');
    this.syncWaypointPresentationVisibility();
  }

  private applyPlaceMarkerZoomSize(el: HTMLElement | null, zoom: number): void {
    if (!el) return;
    if (el.classList.contains('waypoint-marker-container--place-label')) {
      this.applyPlaceLabelZoomStyle(el, zoom);
      return;
    }
    if (!el.classList.contains('waypoint-marker-container--place-icon')) return;

    const scale = this.getZoomScale(zoom, 4, 15, 0.45, 1.25);
    const markerSize = Math.round(this.placeMarkerBaseSizePx * scale);
    const placeType = String(el.getAttribute('data-place-type') || '').toLowerCase();
    const glyphBaseSize = placeType === 'inlet' ? 28 : this.placeMarkerGlyphBaseSizePx;
    const glyphSize = Math.round(glyphBaseSize * scale);

    el.style.width = `${markerSize}px`;
    el.style.height = `${markerSize}px`;

    const img = el.querySelector('img') as HTMLImageElement | null;
    if (img) {
      img.style.width = `${glyphSize}px`;
      img.style.height = `${glyphSize}px`;
    }

    const svg = el.querySelector('svg') as SVGElement | null;
    if (svg) {
      svg.style.width = `${glyphSize}px`;
      svg.style.height = `${glyphSize}px`;
    }
  }

  private applyWreckMarkerZoomSize(el: HTMLElement | null, zoom: number): void {
    if (!el) return;
    if (!el.classList.contains('wreck-map-marker')) return;

    // Match place-waypoint zoom scaling so wreck layer and wreck waypoints stay visually consistent.
    const scale = this.getZoomScale(zoom, 4, 15, 0.45, 1.25);
    const size = Math.round(this.wreckMarkerBaseSizePx * scale);
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
  }

  private applyPlaceLabelZoomStyle(el: HTMLElement, zoom: number): void {
    const label = el.querySelector('.waypoint-place-label') as HTMLElement | null;
    if (!label) return;

    const opacity = this.getZoomScale(
      zoom,
      this.placeLabelHiddenZoom,
      this.placeLabelFadeStartZoom,
      0,
      1
    );
    const isHidden = zoom <= this.placeLabelHiddenZoom;
    const labelScale = this.getZoomScale(zoom, 4, 15, 0.78, 1.08);

    el.style.opacity = `${opacity}`;
    el.style.pointerEvents = isHidden ? 'none' : '';
    el.style.display = isHidden ? 'none' : 'block';
    label.style.opacity = `${opacity}`;
    label.style.transform = `scale(${labelScale})`;
    label.style.transformOrigin = 'center center';
  }

  private getZoomScale(
    zoom: number,
    minZoom: number,
    maxZoom: number,
    minScale: number,
    maxScale: number
  ): number {
    if (!Number.isFinite(zoom)) return 1;
    if (zoom <= minZoom) return minScale;
    if (zoom >= maxZoom) return maxScale;
    const t = (zoom - minZoom) / (maxZoom - minZoom);
    return minScale + t * (maxScale - minScale);
  }

  @HostListener('document:keydown.escape')
  onEscapeCloseBuoyDetail(): void {
    if (this.isBuoyDetailOpen) this.closeBuoyDetailScreen();
  }

  closeBuoyDetailScreen(): void {
    this.isBuoyDetailOpen = false;
    this.buoyDetailLoading = false;
    this.buoyHistoryLoading = false;
    this.buoyHistoryExpanded = false;
    this.selectedBuoy = null;
    this.buoyHistoryObservations = [];
    this.buoyDetailRequestSeq += 1;
  }

  loadBuoyHistory(): void {
    if (!this.selectedBuoy || this.buoyHistoryLoading || this.buoyHistoryExpanded) return;

    const buoyId = this.selectedBuoy.id;
    this.buoyHistoryLoading = true;
    const currentReq = this.buoyDetailRequestSeq;
    const historyApiUrl = `${environment.apiUrl}/observations/buoys/${encodeURIComponent(buoyId)}/history/`;

    fetch(historyApiUrl)
      .then((res) => res.ok ? res.json() : Promise.reject(new Error('buoy history request failed')))
      .then((payload: any) => {
        if (currentReq !== this.buoyDetailRequestSeq) return;
        const rows = Array.isArray(payload?.history)
          ? payload.history
          : Array.isArray(payload?.results)
            ? payload.results
            : Array.isArray(payload?.data)
              ? payload.data
              : Array.isArray(payload)
                ? payload
                : [];

        this.buoyHistoryObservations = rows
          .slice(0, 5)
          .map((obs: any) => this.toBuoyObservationView(obs, payload?.units, 'history'));
        this.buoyHistoryExpanded = true;
      })
      .catch((err) => {
        console.error('[Buoy] Failed to load buoy history:', err);
        if (currentReq !== this.buoyDetailRequestSeq) return;
        this.buoyHistoryObservations = [];
        this.buoyHistoryExpanded = true;
      })
      .finally(() => {
        if (currentReq !== this.buoyDetailRequestSeq) return;
        this.buoyHistoryLoading = false;
      });
  }

  private openBuoyDetailScreen(row: any): void {
    const buoyId = this.getBuoyId(row);
    if (!buoyId) return;

    const title = this.getBuoyDisplayName(row, buoyId);
    const subtitle = this.getBuoySubtitle(row, buoyId);
    const requestSeq = ++this.buoyDetailRequestSeq;

    this.closeTideDetailScreen();
    this.isBuoyDetailOpen = true;
    this.buoyDetailLoading = true;
    this.buoyHistoryLoading = false;
    this.buoyHistoryExpanded = false;
    this.buoyHistoryObservations = [];
    this.selectedBuoy = {
      id: buoyId,
      name: title,
      subtitle,
      currentObservation: null,
    };

    const detailApiUrl = `${environment.apiUrl}/observations/buoys/${encodeURIComponent(buoyId)}/`;
    fetch(detailApiUrl)
      .then((res) => res.ok ? res.json() : Promise.reject(new Error('buoy detail request failed')))
      .then((payload: any) => {
        if (requestSeq !== this.buoyDetailRequestSeq || !this.selectedBuoy) return;
        this.selectedBuoy = {
          ...this.selectedBuoy,
          currentObservation: this.toBuoyObservationView(payload, payload?.units, 'current'),
        };
      })
      .catch((err) => {
        console.error('[Buoy] Failed to load buoy detail:', err);
        if (requestSeq !== this.buoyDetailRequestSeq || !this.selectedBuoy) return;
        this.selectedBuoy = {
          ...this.selectedBuoy,
          currentObservation: this.toBuoyObservationView({}, undefined, 'current'),
        };
      })
      .finally(() => {
        if (requestSeq !== this.buoyDetailRequestSeq) return;
        this.buoyDetailLoading = false;
      });
  }

  private toBuoyObservationView(
    rawObservation: any,
    unitsFromPayload: Record<string, string> | undefined,
    mode: 'current' | 'history'
  ): BuoyObservationView {
    const units: Record<string, string> = {
      wave_height: unitsFromPayload?.['wave_height'] || rawObservation?.units?.wave_height || 'ft',
      wave_period: unitsFromPayload?.['wave_period'] || rawObservation?.units?.wave_period || 's',
      air_temp: unitsFromPayload?.['air_temp'] || rawObservation?.units?.air_temp || '°F',
      sea_temp: unitsFromPayload?.['sea_temp'] || rawObservation?.units?.sea_temp || '°F',
      wind_speed: unitsFromPayload?.['wind_speed'] || rawObservation?.units?.wind_speed || 'kt',
    };

    const windDirection = this.toFiniteNumber(rawObservation?.wind_direction);
    const wavePeriodLabel = mode === 'current' ? 'Period' : 'WVP';

    return {
      timestampLabel: this.formatBuoyTimestamp(rawObservation?.timestamp),
      metricCards: [
        {
          label: 'WVHT',
          value: this.formatNumberWithUnit(rawObservation?.wave_height, units['wave_height'], 1),
          iconSrc: '/assets/common_icons/watch_conditions.svg',
          iconAlt: 'Wave height',
        },
        {
          label: wavePeriodLabel,
          value: this.formatNumberWithUnit(rawObservation?.wave_period, units['wave_period'], 1),
          iconSrc: '/assets/common_icons/watch_duration.svg',
          iconAlt: 'Wave period',
        },
        {
          label: 'Sea Temp',
          value: this.formatNumberWithUnit(rawObservation?.sea_temp, units['sea_temp'], 1),
          iconSrc: '/assets/common_icons/watch_temp.svg',
          iconAlt: 'Sea temperature',
        },
        {
          label: 'Air Temp',
          value: this.formatNumberWithUnit(rawObservation?.air_temp, units['air_temp'], 1),
          iconSrc: '/assets/home/filter/tempbreak.svg',
          iconAlt: 'Air temperature',
        },
        {
          label: 'Wind Speed',
          value: this.formatNumberWithUnit(rawObservation?.wind_speed, units['wind_speed'], 1),
          iconSrc: '/assets/common_icons/watch_conditions.svg',
          iconAlt: 'Wind speed',
        },
        {
          label: 'Wind from',
          value: this.formatWindDirection(windDirection, true),
          iconSrc: '/assets/home/profile/compassicon.svg',
          iconAlt: 'Wind direction',
        },
      ],
    };
  }

  private formatBuoyTimestamp(timestamp: unknown): string {
    if (typeof timestamp !== 'string' || !timestamp) return '--';
    const parsed = new Date(timestamp);
    if (isNaN(parsed.getTime())) return '--';
    return parsed.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  private formatNumberWithUnit(value: unknown, unit: string, precision: number): string {
    const numeric = this.toFiniteNumber(value);
    if (numeric === null) return '--';
    const unitText = unit ? ` ${unit}` : '';
    return `${numeric.toFixed(precision)}${unitText}`;
  }

  private formatWindDirection(value: number | null, includeCardinal: boolean): string {
    if (value === null) return '--';
    const normalized = ((value % 360) + 360) % 360;
    const deg = `${Math.round(normalized)}°`;
    if (!includeCardinal) return deg;
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const idx = Math.round(normalized / 22.5) % 16;
    return `${directions[idx]} ${deg}`;
  }

  private getBuoyId(row: any): string | null {
    const buoyId = row?.id ?? row?._id ?? row?.buoy_id ?? row?.station_id ?? row?.station ?? row?.identifier ?? null;
    if (typeof buoyId !== 'string') return null;
    const trimmed = buoyId.trim();
    return trimmed ? trimmed : null;
  }

  private getBuoyDisplayName(row: any, fallbackId: string): string {
    const name = row?.name ?? row?.station_name ?? row?.title ?? null;
    if (typeof name === 'string' && name.trim()) return name.trim().toUpperCase();
    return fallbackId.toUpperCase();
  }

  private getBuoySubtitle(row: any, buoyId: string): string {
    const distance = this.toFiniteNumber(row?.distance_nm);
    if (distance !== null) {
      // Same "<id> · <distance> nm" meta line as Flutter's buoy sheet header.
      return `${buoyId.toUpperCase()} · ${distance.toFixed(1)} nm`;
    }
    return buoyId.toUpperCase();
  }

  private toFiniteNumber(value: unknown): number | null {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private extractBuoyCoordinate(row: any): { lat: number; lng: number } | null {
    if (!row) return null;

    const direct = this.normalizeCoordinatePair(
      row?.lat ?? row?.latitude ?? row?.position?.lat ?? row?.position?.latitude,
      row?.lon ?? row?.lng ?? row?.longitude ?? row?.position?.lon ?? row?.position?.lng ?? row?.position?.longitude
    );
    if (direct) return direct;

    const geometryCoords = row?.geometry?.coordinates || row?.coordinates;
    if (Array.isArray(geometryCoords) && geometryCoords.length >= 2) {
      // GeoJSON is usually [lng, lat], but some feeds emit [lat, lng].
      const geojsonOrder = this.normalizeCoordinatePair(geometryCoords[1], geometryCoords[0]);
      if (geojsonOrder) return geojsonOrder;
      const reversedOrder = this.normalizeCoordinatePair(geometryCoords[0], geometryCoords[1]);
      if (reversedOrder) return reversedOrder;
    }

    return null;
  }

  private normalizeCoordinatePair(rawLat: any, rawLng: any): { lat: number; lng: number } | null {
    const lat = Number(rawLat);
    const lng = Number(rawLng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    // Standard valid range.
    if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng };
    }

    // Some upstream payloads accidentally swap lat/lng.
    if (lng >= -90 && lng <= 90 && lat >= -180 && lat <= 180) {
      return { lat: lng, lng: lat };
    }

    return null;
  }

  private toMarineZoneFeatureCollection(payload: any): GeoJSON.FeatureCollection {
    if (payload?.type === 'FeatureCollection' && Array.isArray(payload.features)) {
      const normalizedFeatures = payload.features
        .map((feature: any) => this.normalizeMarineZoneFeature(feature))
        .filter((feature: GeoJSON.Feature | null): feature is GeoJSON.Feature => !!feature);
      return {
        type: 'FeatureCollection',
        features: normalizedFeatures,
      } as GeoJSON.FeatureCollection;
    }

    const rows = Array.isArray(payload?.results)
      ? payload.results
      : Array.isArray(payload?.data?.results)
        ? payload.data.results
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [];

    const features = rows
      .map((row: any) => {
        const geometry = row?.geometry || row?.geom || row?.zone_geometry || row?.shape || null;
        if (!geometry || !geometry.type || !Array.isArray(geometry.coordinates)) return null;
        const id = this.getMarineZoneIdFromProps(row);
        return {
          type: 'Feature',
          properties: {
            id,
            name: row?.name || row?.zone_name || row?.label || '',
            zone_type: row?.zone_type || row?.type || '',
          },
          geometry,
        } as GeoJSON.Feature;
      })
      .filter((feature: GeoJSON.Feature | null): feature is GeoJSON.Feature => !!feature);

    return {
      type: 'FeatureCollection',
      features,
    };
  }

  private normalizeMarineZoneFeature(feature: any): GeoJSON.Feature | null {
    if (!feature?.geometry?.type || !Array.isArray(feature?.geometry?.coordinates)) return null;
    const props = feature?.properties || {};
    const zoneId = this.getMarineZoneIdFromProps(props) || this.getMarineZoneIdFromProps(feature);
    const name = props?.name || props?.zone_name || props?.label || feature?.id || '';
    return {
      type: 'Feature',
      properties: {
        ...props,
        id: zoneId,
        name,
      },
      geometry: feature.geometry,
    } as GeoJSON.Feature;
  }

  private getMarineZoneIdFromProps(obj: any): string | null {
    const raw =
      obj?.zoneId ??
      obj?.zone_id ??
      obj?.zone ??
      obj?.id ??
      obj?._id ??
      obj?.identifier ??
      obj?.widget?.zoneId ??
      null;
    if (raw === null || raw === undefined) return null;
    const value = String(raw).trim();
    return value || null;
  }

  private getMarineZoneIdFromFeature(feature: any): string | null {
    if (!feature) return null;
    return this.getMarineZoneIdFromProps(feature.properties) || this.getMarineZoneIdFromProps(feature);
  }

  private startDockmatesTracking(): void {
    console.debug('[HomePage] Fetching initial dockmates live positions');

    // Initial fetch only, no continuous polling of this endpoint
    this.vesselService.getDockmatesLivePositions().pipe(catchError(() => of({ results: [] }))).subscribe((data: any) => {
      if (data && Array.isArray(data.results)) {
        this.updateDockmateMarkers(data.results);
      }
    });
  }

  private updateDockmateMarkers(vessels: any[]): void {
    if (!this.map) return;

    vessels.forEach(v => {
      // `community/live-positions/` carries `boat_skin`; learn it before the art is chosen below.
      this.skins.remember(v.vessel_id, v.boat_skin);
      const existing = this.dockmateMarkers.find(m => m.getElement().getAttribute('data-vessel-id') === v.vessel_id);
      const opacity = this.getMarkerOpacity(v.last_seen);

      if (existing) {
        existing.setLngLat([v.lon, v.lat]);
        const root = existing.getElement();
        const vesselEl = this.getDockmateVesselElement(root) || root;
        vesselEl.dataset['naturalOpacity'] = opacity.toString();
        // Fade only the boat icon image so the white ring and blue glow always
        // remain visible as a location anchor (matching Flutter's behaviour where
        // the PNG image fades while the surrounding ring stays opaque).
        if (!vesselEl.dataset['revealing']) {
          const iconImg = vesselEl.querySelector('.boat-icon img') as HTMLElement | null;
          if (iconImg) iconImg.style.opacity = opacity.toString();
        }
        const iconEl = vesselEl.querySelector('.boat-icon') as HTMLElement;
        if (iconEl && v.cog != null) this.rotateBoatIcon(iconEl, v.vessel_id, v.cog);
      } else {
        const { root, vesselEl } = this.createDockmateMarkerRoot(v.vessel_id, opacity);

        // Fixed-size root is what MapLibre anchors — inner ring can restyle without shifting.
        const marker = new maplibregl.Marker({
          element: root,
          anchor: 'center',
          subpixelPositioning: true,
        })
          .setLngLat([v.lon, v.lat])
          .addTo(this.map);

        const iconImg = vesselEl.querySelector('.boat-icon img') as HTMLElement | null;
        if (iconImg) iconImg.style.opacity = opacity.toString();

        if (v.cog != null) {
          const iconEl = vesselEl.querySelector('.boat-icon') as HTMLElement;
          if (iconEl) this.rotateBoatIcon(iconEl, v.vessel_id, v.cog);
        }

        root.addEventListener('click', (e) => {
          e.stopPropagation();
          if (!this.map) return;

          const currentZoom = this.map.getZoom();
          const TARGET_ZOOM = 13;

          // Always clear any stale observation marker first.
          this.clearDockmateObservationMarker();

          if (currentZoom >= TARGET_ZOOM) {
            // Already zoomed in enough — open the card immediately.
            this.openDockmateCard(v);
            return;
          }

          // Zoom in toward the vessel first, then open the card once the
          // animation settles so the user sees the map context before the popup.
          this.map.easeTo({
            center: [v.lon, v.lat],
            zoom: TARGET_ZOOM,
            duration: 600,
          });

          const pendingId = ++this._dockmateZoomPendingId;
          this.map.once('zoomend', () => {
            if (pendingId !== this._dockmateZoomPendingId) return;
            this.openDockmateCard(v);
            requestAnimationFrame(() => this.updateWaypointAggregation());
          });
        });

        this.dockmateMarkers.push(marker);
      }
    });

    // Apply the current zoom style immediately so ring visibility is correct
    // even when markers are created while the map is already zoomed in.
    this.updateZoomResponsiveMarkerSizes();

    // Register all dockmate vessels in the clustering store (stable vessel_id key).
    vessels.forEach((v: any) => {
      const vesselId = String(v.vessel_id || '').trim();
      if (!vesselId) return;
      this.liveVesselStore.set(vesselId, {
        id: vesselId,
        lat: v.lat,
        lng: v.lon,
        heading: null,
        speed: null,
        isPrimary: false,
        cog: v.cog ?? null,
        lastSeen: v.last_seen || null,
      });
    });
    this.triggerVesselClustering();
  }

  private getMarkerOpacity(lastSeen: any, isPrimary: boolean = false): number {
    if (isPrimary) return 1.0;
    if (!lastSeen) return 0.5;

    let lastSeenDate: Date;
    const num = Number(lastSeen);
    if (!isNaN(num) && num > 0) {
      lastSeenDate = new Date(num);
    } else {
      lastSeenDate = this.parseUtc(lastSeen) || new Date(lastSeen);
    }

    const diffMs = Date.now() - lastSeenDate.getTime();
    if (isNaN(diffMs)) return 0.4;

    // Negative-clock guard: treat future timestamps as brand-new.
    const ageSec = Math.max(0, Math.floor(diffMs / 1000));

    // Matches Flutter's _fetchDockmateLivePositions opacity segments:
    if (ageSec < 300) return 1.0;          // < 5 min: full brightness
    if (ageSec < 3600) return 0.75;        // 5 min – 1 hr: flat 0.75
    if (ageSec < 21600) {
      // 1 hr – 6 hr: 0.75 → 0.65 linear over 18 000 s
      const t = (ageSec - 3600) / 18000;
      return 0.75 - 0.1 * t;
    }
    if (ageSec < 86400) {
      // 6 hr – 24 hr: 0.65 → 0.55 linear over 64 800 s
      const t = (ageSec - 21600) / 64800;
      return 0.65 - 0.1 * t;
    }
    // ≥ 24 hr: 0.55 minus 0.05 per full extra day, hard floor of 0.4
    const extraDays = Math.floor((ageSec - 86400) / 86400);
    return Math.max(0.4, 0.55 - 0.05 * extraDays);
  }

  private openDockmateCard(v: any): void {
    console.debug('[HomePage] Opening dockmate card for', v.vessel_name);

    // Close any open obs-trip popup so only one vessel UI is visible at a time
    if (this.activeObsTripPopup) {
      try { this.activeObsTripPopup.remove(); } catch (e) { /* ignore */ }
      this.activeObsTripPopup = null;
    }

    // Stop any existing polling for a dockmate
    if (this.dockmateDetailSub) {
      this.dockmateDetailSub.unsubscribe();
    }

    // Anchor the card to the vessel's actual on-map marker position so it can be
    // placed right next to the icon. Fall back to the passed-in coords if the
    // marker can't be found.
    const cardMarker = this.dockmateMarkers.find(
      m => m.getElement().getAttribute('data-vessel-id') === String(v.vessel_id || '')
    );
    const markerLngLat = cardMarker?.getLngLat();
    this.dockmateCardLngLat = markerLngLat
      ? [markerLngLat.lng, markerLngLat.lat]
      : (v.lon != null && v.lat != null ? [Number(v.lon), Number(v.lat)] : null);

    // Open + render the card inside Angular's zone so the *ngIf element is created
    // immediately (the marker click can fire outside the zone). Without this the
    // card only rendered when the first poll response arrived ~2s later, so it
    // appeared at the old bottom-pinned spot and then jumped to the vessel.
    this.ngZone.run(() => {
      this.selectedDockmateVessel = { ...v };
      // The card is the Flutter boat card: it loads the latest reading and the ride-along
      // crew itself, from the same arguments Flutter's dockmate tap passes.
      this.dockmateCardParams = {
        vesselId: v.vessel_id ?? null,
        vesselName: v.vessel_name ?? null,
        ownerName: v.owner_name ?? null,
        lastSeen: v.last_seen ?? v.lastSeen ?? null,
        fallbackLat: v.lat ?? null,
        fallbackLon: v.lon ?? null,
        fallbackCog: v.cog ?? null,
      };
      this.dockmateCardContext = this.vesselCardContext();
      this.isDockmateCardOpen = true;
    });

    // Position the card next to the marker as soon as the element exists. It stays
    // hidden (visibility:hidden in SCSS) until positioned, so it never flashes at
    // the bottom of the screen first.
    this.revealDockmateCardWhenReady();

    // Fetch initial latest observation, then poll every 7 seconds
    const fetchLatest = () => this.vesselService.getLatestObservation(v.vessel_id).pipe(catchError(() => of(null)));

    this.dockmateDetailSub = this.pollScheduler
      .poll$(fetchLatest, VESSEL_TRACKING_POLL_MS, { skipInitialFetch: true, changeKey: latestObservationKey })
      .subscribe(obs => this.handleDockmateDetailUpdate(v.vessel_id, obs));

    // Also trigger immediately once
    fetchLatest().subscribe(obs => this.handleDockmateDetailUpdate(v.vessel_id, obs));
  }

  /**
   * Keeps the vessel's marker, its heading and the card anchor on the latest fix. The card
   * reads its own data, so nothing here touches what it displays.
   */
  private handleDockmateDetailUpdate(vesselId: string, obs: any): void {
    if (!obs || !obs.pgns || !this.selectedDockmateVessel) return;

    // Since we open the card for a specific vessel, make sure we only update if it is still the selected one.
    if (this.selectedDockmateVessel.vessel_id !== vesselId) return;

    let lat: number | null = null;
    let lon: number | null = null;
    let heading: number | null = null;

    for (const p of obs.pgns) {
      const f = p.fields || {};
      if ((p.name === 'GNSSPositionData' || p.pgn === 129029) && f.lat_deg != null && f.lon_deg != null) {
        lat = Number(f.lat_deg);
        lon = Number(f.lon_deg);
      }
      if ((p.name === 'VesselHeading' || p.pgn === 127250) && f.heading_deg != null) {
        heading = Number(f.heading_deg);
      }
      if (heading === null && (p.name === 'COGSOGRapidUpdate' || p.pgn === 129026) && f.cog_deg != null) {
        heading = Number(f.cog_deg);
      }
    }

    if (lat !== null && lon !== null) {
      // Keep the card anchored to the vessel's latest position and reposition it.
      this.dockmateCardLngLat = [lon, lat];
      this.positionDockmateCard();

      // Update marker on map
      const existingMarker = this.dockmateMarkers.find(m => m.getElement().getAttribute('data-vessel-id') === vesselId);
      if (existingMarker) {
        existingMarker.setLngLat([lon, lat]);
        const root = existingMarker.getElement();
        const vesselEl = this.getDockmateVesselElement(root) || root;
        existingMarker.setOpacity(1);

        if (heading !== null) {
          const iconEl = vesselEl.querySelector('.boat-icon') as HTMLElement;
          if (iconEl) this.rotateBoatIcon(iconEl, vesselId, heading);
        }
      }
    }
  }

  closeDockmateCard(): void {
    if (this.dockmateDetailSub) {
      this.dockmateDetailSub.unsubscribe();
    }
    this.isDockmateCardOpen = false;
    this.selectedDockmateVessel = null;
    this.dockmateCardParams = null;
    this.dockmateCardContext = null;
    this.dockmateCardLngLat = null;
  }

  /** Chat button on the dockmate card. */
  onDockmateCardChat(args: ChatArgs): void {
    if (!this.dockmateCardParams || !this.dockmateCardContext) return;
    void this.vesselCardChat.open(args, this.dockmateCardParams, this.dockmateCardContext.identity);
  }

  /**
   * Position the dockmate detail card next to its vessel marker instead of pinning
   * it to the bottom of the screen. The card is centred horizontally on the marker
   * and opens below it when there is room (vessel near the top of the screen),
   * above it otherwise (vessel near the bottom), clamped to stay fully on screen
   * and offset by `iconGap` so it never overlaps the 44px vessel icon.
   *
   * Writes inline styles directly (no Angular binding) so it can run on every map
   * `move`/`zoom` frame without triggering change detection — keeping drag smooth.
   */
  private positionDockmateCard(): void {
    if (!this.map || !this.isDockmateCardOpen) return;

    const card = document.querySelector('.dockmate-detail-card') as HTMLElement | null;
    if (!card) return;

    // No anchor coordinate available — reveal the card at its SCSS default rather
    // than leaving it hidden forever.
    if (!this.dockmateCardLngLat) {
      card.style.visibility = 'visible';
      return;
    }

    const pt = this.map.project(this.dockmateCardLngLat);
    const container = this.map.getContainer();
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const cardW = card.offsetWidth;
    const cardH = card.offsetHeight;

    // Clear the actual on-map vessel icon so the card never sits on top of it.
    // Measure the live marker element (the observation marker, else the dockmate
    // marker) instead of assuming a fixed 44px — selected/zoomed icons are larger.
    const vid = String(this.selectedDockmateVessel?.vessel_id || '').trim();
    const iconEl =
      this.dockmateObservationMarker?.getElement() ||
      (vid
        ? (this.dockmateMarkers.find(m => m.getElement()?.getAttribute('data-vessel-id') === vid)?.getElement() ?? null)
        : null);
    const iconHeight = iconEl?.offsetHeight || 44;
    const iconGap = Math.round(iconHeight / 2) + 14; // half the icon + breathing gap
    const margin = 8;   // keep the card off the viewport edges

    // Horizontal: centre on the marker, then clamp inside the viewport.
    let left = pt.x - cardW / 2;
    left = Math.max(margin, Math.min(left, cw - cardW - margin));

    // Vertical: prefer below the icon; flip above when there isn't room below.
    let top: number;
    const spaceBelow = ch - pt.y;
    const spaceAbove = pt.y;
    if (spaceBelow >= cardH + iconGap + margin) {
      top = pt.y + iconGap;                 // open below the marker
    } else if (spaceAbove >= cardH + iconGap + margin) {
      top = pt.y - iconGap - cardH;         // open above the marker
    } else {
      top = pt.y + iconGap;                 // tight on both sides — clamp below
    }
    top = Math.max(margin, Math.min(top, ch - cardH - margin));

    // Override the SCSS bottom/left/transform pinning with marker-relative coords.
    card.style.left = `${Math.round(left)}px`;
    card.style.top = `${Math.round(top)}px`;
    card.style.right = 'auto';
    card.style.bottom = 'auto';
    card.style.transform = 'none';
    // Now that it's correctly placed, reveal it (SCSS keeps it hidden until here).
    card.style.visibility = 'visible';
  }

  /**
   * Wait for Angular to render the dockmate card element, then position it next to
   * the marker. Retries across a few animation frames because the *ngIf element may
   * not exist on the frame the card is opened.
   */
  private revealDockmateCardWhenReady(attempt: number = 0): void {
    if (!this.isDockmateCardOpen) return;
    const card = document.querySelector('.dockmate-detail-card') as HTMLElement | null;
    if (card) {
      this.positionDockmateCard();
      return;
    }
    if (attempt < 15) {
      requestAnimationFrame(() => this.revealDockmateCardWhenReady(attempt + 1));
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Distance Measurement Tool
  // ═══════════════════════════════════════════════════════════════════════════

  /** Toggle measurement mode on/off from the sidebar button */
  toggleMeasurementMode(): void {
    if (this.isMeasurementMode) {
      this.clearMeasurement();
    } else {
      this.isMeasurementMode = true;
      this.measureDistance = null;
      this.showMeasureToast = true;
      setTimeout(() => { this.showMeasureToast = false; }, 3500);
    }
  }

  /** Called from the map click handler when measurement mode is active */
  private handleMeasurementClick(lngLat: maplibregl.LngLat): void {
    if (!this.isMeasurementMode) return;

    if (!this.measurePointA) {
      // Place Point A
      this.measurePointA = this.createMeasureMarker(lngLat, 'A');
      this.measurePointASet = true;
    } else if (!this.measurePointB) {
      // Place Point B → draw line, compute distance
      this.measurePointB = this.createMeasureMarker(lngLat, 'B');
      this.drawMeasureLine(
        this.measurePointA.getLngLat(),
        this.measurePointB.getLngLat()
      );
      const dist = this.haversineNauticalMiles(
        this.measurePointA.getLngLat(),
        this.measurePointB.getLngLat()
      );
      this.measureDistance = dist.toFixed(2) + ' nmi';
    }
    // If both points already exist, ignore further clicks until reset
  }

  /** Create a styled measurement marker at the given position */
  private createMeasureMarker(lngLat: maplibregl.LngLat, label: 'A' | 'B'): maplibregl.Marker {
    const el = document.createElement('div');
    el.className = 'measure-point-marker';
    el.innerHTML = `<span class="measure-point-label">${label}</span>`;
    // Keep free A/B points underneath waypoint markers when they overlap.
    el.style.zIndex = '1';

    const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
      .setLngLat(lngLat)
      .addTo(this.map);
    return marker;
  }

  private createGhostMeasureMarker(lngLat: maplibregl.LngLat): maplibregl.Marker {
    const ghost = document.createElement('div');
    ghost.style.width = '0';
    ghost.style.height = '0';
    return new maplibregl.Marker({ element: ghost, anchor: 'center' })
      .setLngLat(lngLat)
      .addTo(this.map);
  }

  private applyMeasureSelectionToWaypoint(markerEl: HTMLElement, pointIndex: 1 | 2): void {
    markerEl.classList.remove('waypoint-marker-highlight');
    void markerEl.offsetWidth;
    markerEl.classList.add('waypoint-marker-highlight');
    markerEl.setAttribute('data-measure-point', String(pointIndex));

    try {
      this.setSvgOutline(markerEl, true);
    } catch (e) { /* ignore */ }
    try {
      this.setRasterHighlightedIcon(markerEl, true);
    } catch (e) { /* ignore */ }
    try {
      this.updateWaypointFocusRing(markerEl);
    } catch (e) { /* ignore */ }
  }

  /** Draw the straight red line between the two points using a GeoJSON source/layer */
  private drawMeasureLine(a: maplibregl.LngLat, b: maplibregl.LngLat): void {
    if (!this.map) return;
    // When an endpoint is a pin-style waypoint (anchor:'bottom'), the waypoint
    // tip does not fully cover the line's rounded cap. Instead of trimming
    // along the segment direction (which can create a visible gap), nudge the
    // endpoint slightly "into" the pin so the cap is hidden behind the icon.
    const startBase = this.tuckMeasureEndpointUnderWaypoint(a, this.measureHighlightedElA);
    const endBase = this.tuckMeasureEndpointUnderWaypoint(b, this.measureHighlightedElB);
    const [start, end] = this.getTrimmedMeasureEndpoints(startBase, endBase, 0, 0);
    const geojson: GeoJSON.Feature<GeoJSON.LineString> = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: [
          [start.lng, start.lat],
          [end.lng, end.lat],
        ],
      },
    };

    if (this.map.getSource(this.measureSourceId)) {
      (this.map.getSource(this.measureSourceId) as maplibregl.GeoJSONSource).setData(geojson);
    } else {
      this.map.addSource(this.measureSourceId, { type: 'geojson', data: geojson });
      this.map.addLayer({
        id: this.measureLayerId,
        type: 'line',
        source: this.measureSourceId,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#E8342A',
          'line-width': 2.5,
          'line-dasharray': [1, 0],
        },
      });
    }
  }

  private tuckMeasureEndpointUnderWaypoint(lngLat: maplibregl.LngLat, markerEl: HTMLElement | null): maplibregl.LngLat {
    if (!this.map || !markerEl) return lngLat;
    const kind = String(markerEl.getAttribute('data-marker-kind') || '').trim();
    // Only pin-style markers use anchor:'bottom' and need the tuck.
    if (kind !== 'pin') return lngLat;

    const rect = markerEl.getBoundingClientRect?.();
    const h = rect && Number.isFinite(rect.height) ? rect.height : 0;
    // Clamp so it works across zoom-resized pins without creating a visible kink.
    const tuckPx = Math.max(3, Math.min(6, Math.round(h * 0.14) || 4));

    const p = this.map.project([lngLat.lng, lngLat.lat]);
    // Screen-space "up" is negative y; this pushes the endpoint into the pin body.
    return this.map.unproject([p.x, p.y - tuckPx]);
  }

  /**
   * Trim the segment in pixel space so the line visually ends under marker edges
   * instead of running through marker centers.
   */
  private getTrimmedMeasureEndpoints(
    a: maplibregl.LngLat,
    b: maplibregl.LngLat,
    trimStartPx: number,
    trimEndPx: number
  ): [maplibregl.LngLat, maplibregl.LngLat] {
    if (!this.map) return [a, b];

    const p1 = this.map.project([a.lng, a.lat]);
    const p2 = this.map.project([b.lng, b.lat]);
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const dist = Math.hypot(dx, dy);

    const totalTrim = Math.max(0, trimStartPx) + Math.max(0, trimEndPx);
    if (!Number.isFinite(dist) || dist <= (totalTrim + 1)) {
      return [a, b];
    }

    const ux = dx / dist;
    const uy = dy / dist;

    const spx: [number, number] = [p1.x + ux * trimStartPx, p1.y + uy * trimStartPx];
    const epx: [number, number] = [p2.x - ux * trimEndPx, p2.y - uy * trimEndPx];

    return [this.map.unproject(spx), this.map.unproject(epx)];
  }

  /** Remove measurement markers, line layer/source, and reset all state */
  clearMeasurement(): void {
    this.isMeasurementMode = false;
    this.measureDistance = null;
    this.showMeasureToast = false;
    this.measurePointASet = false;

    if (this.measurePointA) { try { this.measurePointA.remove(); } catch (_) { } this.measurePointA = null; }
    if (this.measurePointB) { try { this.measurePointB.remove(); } catch (_) { } this.measurePointB = null; }
    // Clear measurement highlights from any selected waypoint markers
    if (this.measureHighlightedElA) {
      this.measureHighlightedElA.classList.remove(
        'waypoint-marker-highlight',
        'measure-waypoint-selected',
        'measure-waypoint-selected--a',
        'measure-waypoint-selected--b',
        'measure-waypoint-selected--point-1',
        'measure-waypoint-selected--point-2'
      );
      this.measureHighlightedElA.removeAttribute('data-measure-point');
      try { this.setSvgOutline(this.measureHighlightedElA, false); } catch (e) { /* ignore */ }
      try { this.setRasterHighlightedIcon(this.measureHighlightedElA, false); } catch (e) { /* ignore */ }
      const badgeA = this.measureHighlightedElA.querySelector('.measure-wp-badge');
      if (badgeA) badgeA.remove();
      this.measureHighlightedElA = null;
    }
    if (this.measureHighlightedElB) {
      this.measureHighlightedElB.classList.remove(
        'waypoint-marker-highlight',
        'measure-waypoint-selected',
        'measure-waypoint-selected--a',
        'measure-waypoint-selected--b',
        'measure-waypoint-selected--point-1',
        'measure-waypoint-selected--point-2'
      );
      this.measureHighlightedElB.removeAttribute('data-measure-point');
      try { this.setSvgOutline(this.measureHighlightedElB, false); } catch (e) { /* ignore */ }
      try { this.setRasterHighlightedIcon(this.measureHighlightedElB, false); } catch (e) { /* ignore */ }
      const badgeB = this.measureHighlightedElB.querySelector('.measure-wp-badge');
      if (badgeB) badgeB.remove();
      this.measureHighlightedElB = null;
    }

    if (this.map) {
      if (this.map.getLayer(this.measureLayerId)) this.map.removeLayer(this.measureLayerId);
      if (this.map.getSource(this.measureSourceId)) this.map.removeSource(this.measureSourceId);
    }
  }

  /**
   * Haversine formula — returns distance between two LngLat points in nautical miles.
   * 1 nautical mile = 1 arc-minute of latitude ≈ 1.852 km
   */
  private haversineNauticalMiles(a: maplibregl.LngLat, b: maplibregl.LngLat): number {
    const R = 3440.065; // Earth radius in nautical miles
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const sinDLat = Math.sin(dLat / 2);
    const sinDLng = Math.sin(dLng / 2);
    const h = sinDLat * sinDLat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinDLng * sinDLng;
    return 2 * R * Math.asin(Math.sqrt(h));
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // REPLAY FEATURE
  // ═══════════════════════════════════════════════════════════════════════════

  /** Opens the pre-replay filter sheet then enters replay mode on confirm. */
  openReplay(): void {
    // Defence in depth: the nav hides the entry, but any other trigger must obey the same gate.
    if (!this.userRoles.canAccessReplay) return;
    const ref = this.dialog.open(ReplayFilterModalComponent, {
      panelClass: 'replay-filter-dialog',
      maxWidth: '100vw',
    });

    ref.afterClosed().subscribe((result: ReplayFilterParams | null) => {
      if (!result) return;

      // Remember filter state so the settings sheet can reopen with them
      if (result.historyDays != null) {
        const days = result.historyDays;
        this._replayPresetIndex = days === 1 ? 0 : days === 7 ? 1 : 2;
        this._replayCustomRange = null;
      } else {
        this._replayPresetIndex = -1;
        this._replayCustomRange = result.customRange ?? null;
      }
      this._replaySourceIndex = result.showMine && result.showShared ? 0
        : result.showMine ? 1 : 2;

      // Ensure replay map layers exist before loading trips
      if (this.isMapLoaded) this._ensureReplayLayers();

      // Always arm initial zoom so the camera zooms to the first mine-boat
      // regardless of which filter the user picked (matches Flutter's
      // _replayNeedsInitialZoom = true default).  The zoom only fires when a
      // mine boat actually appears, so there is no harm for shared-only filters.
      this._replayNeedsInitialZoom = true;
      this._replayAutoFollow = false;
      this._replayHadMyBoats = false;

      this.submenuService.setReplayMode(true);
      this.replayService.setHistoryDays(result.historyDays ?? 30);
      this.replayService.fetchAndLoadTrips({
        showMine: result.showMine,
        showShared: result.showShared,
        dateRange: result.customRange ?? null,
      });
    });
  }

  /** Called by the overlay's (closed) output — exits replay mode entirely. */
  exitReplay(): void {
    this.replayService.reset();
    this.submenuService.setReplayMode(false);
    this._replayAutoFollow = false;
    this._replayNeedsInitialZoom = false;
    this._replayHadMyBoats = false;
    this._clearReplayMap();
  }

  /** Called by the overlay's (filterChanged) output — keeps filter state in sync. */
  onReplayFilterChanged(evt: { presetIndex: number; sourceIndex: number; customRange: { start: Date; end: Date } | null }): void {
    this._replayPresetIndex = evt.presetIndex;
    this._replaySourceIndex = evt.sourceIndex;
    this._replayCustomRange = evt.customRange;

    // Re-arm initial zoom so the camera zooms to the first mine boat of the
    // newly-loaded data, exactly as Flutter does when filter changes mid-session.
    this._replayNeedsInitialZoom = true;
    this._replayHadMyBoats = false;
    this._replayAutoFollow = false;
  }

  // ── Map Layer Lifecycle ────────────────────────────────────────────────────

  private _ensureReplayLayers(): void {
    if (!this.map) return;

    if (!this.map.getSource(this.replayPolylinesSourceId)) {
      this.map.addSource(this.replayPolylinesSourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    // Own trips — solid blue line
    if (!this.map.getLayer(this.replayPolylinesLineLayerId)) {
      this.map.addLayer({
        id: this.replayPolylinesLineLayerId,
        type: 'line',
        source: this.replayPolylinesSourceId,
        filter: ['==', ['get', 'isMine'], true],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#007AFF',
          'line-width': 4,
          'line-opacity': 0.9,
        },
      });
    }

    // Shared trips — solid teal line
    if (!this.map.getLayer(this.replayPolylinesSharedLayerId)) {
      this.map.addLayer({
        id: this.replayPolylinesSharedLayerId,
        type: 'line',
        source: this.replayPolylinesSourceId,
        filter: ['==', ['get', 'isMine'], false],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#00CED1',
          'line-width': 4,
          'line-opacity': 0.9,
        },
      });
    }

    // Waypoint circles — WebGL, same pipeline as track line (no HTML-vs-WebGL offset)
    if (!this.map.getSource(this.replayWaypointsSourceId)) {
      this.map.addSource(this.replayWaypointsSourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
    if (!this.map.getLayer(this.replayWaypointsLayerId)) {
      this.map.addLayer({
        id: this.replayWaypointsLayerId,
        type: 'symbol',
        source: this.replayWaypointsSourceId,
        layout: {
          'icon-image': ['get', 'iconKey'] as any,
          'icon-size': 1,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
      this._replayWaypointClickHandler = (e: any) => {
        if (!e.features?.length || !this.map) return;
        const props = e.features[0].properties as { id: string };
        const wp = this.replayState?.activeWaypoints?.find(w => w.id === props['id']);
        if (!wp) return;
        const rawWp = wp.raw ?? {
          position: { latitude: wp.lat, longitude: wp.lng },
          name: wp.name,
          audit_data: { create_ts: wp.createTs.toISOString() },
        };
        const popup = new maplibregl.Popup({ closeButton: true, offset: [0, -14], anchor: 'bottom' })
          .setLngLat([wp.lng, wp.lat])
          .setHTML(this.getPopupHtml(rawWp))
          .addTo(this.map);
        popup.on('open', () => {
          const expandEl = popup.getElement()?.querySelector('[data-action="expand"]') as HTMLElement | null;
          if (expandEl) expandEl.style.display = 'none';
        });
      };
      this.map.on('click', this.replayWaypointsLayerId, this._replayWaypointClickHandler);
      this.map.on('mouseenter', this.replayWaypointsLayerId, () => { if (this.map) this.map.getCanvas().style.cursor = 'pointer'; });
      this.map.on('mouseleave', this.replayWaypointsLayerId, () => { if (this.map) this.map.getCanvas().style.cursor = ''; });
    }
  }

  // ── Realtime Map Update (called on every playback tick) ────────────────────

  private _updateReplayMap(state: ReplayState): void {
    if (!this.map || !this.isMapLoaded) return;

    // Ensure layers exist (map may have just become ready)
    this._ensureReplayLayers();

    // ── Update polyline GeoJSON ──
    const features: GeoJSON.Feature[] = state.currentPolylines.map(pl => ({
      type: 'Feature',
      properties: { isMine: pl.isMine, tripIndex: pl.tripIndex },
      geometry: { type: 'LineString', coordinates: pl.points as any },
    }));

    const src = this.map.getSource(this.replayPolylinesSourceId) as maplibregl.GeoJSONSource | undefined;
    if (src) {
      src.setData({ type: 'FeatureCollection', features });
    }

    // ── Update boat markers ──
    const activeTripIndices = new Set(state.activeBoats.map(b => b.tripIndex));

    // Remove markers for trips no longer active
    for (const idx of Object.keys(this._replayBoatMarkers).map(Number)) {
      if (!activeTripIndices.has(idx)) {
        try { this._replayBoatMarkers[idx].remove(); } catch (_) { }
        delete this._replayBoatMarkers[idx];
      }
    }

    // Add / update markers for active boats
    for (const boat of state.activeBoats) {
      let marker = this._replayBoatMarkers[boat.tripIndex];

      if (!marker) {
        const el = this._createBoatMarkerElement(boat);
        marker = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([boat.lng, boat.lat])
          .addTo(this.map);
        this._replayBoatMarkers[boat.tripIndex] = marker;
      } else {
        marker.setLngLat([boat.lng, boat.lat]);
      }

      // Update speed label — two lines: name (white) + kts (blue own, teal shared)
      const el = marker.getElement();
      const labelEl = el?.querySelector?.('.replay-boat-label') as HTMLElement | null;
      const iconEl = el?.querySelector?.('.replay-boat-icon') as HTMLImageElement | null;
      if (iconEl) {
        iconEl.src = boat.isMine ? '/assets/common_icons/boat_self.png' : '/assets/common_icons/boat_other_user.png';
        iconEl.style.width = boat.isMine ? '50px' : '44px';
        iconEl.style.height = boat.isMine ? '50px' : '44px';
        const headingDeg = (boat.bearing || 0) + 30 - (this.map?.getBearing() || 0);
        iconEl.style.transform = `translate(-50%, -50%) rotate(${headingDeg}deg)`;
      }
      if (labelEl) {
        // Mirror Flutter fallback: 'My Vessel' for mine, 'Unknown Vessel' for others
        const name = (boat.vesselName?.trim()) || (boat.isMine ? 'My Vessel' : 'Unknown Vessel');
        // Always show speed (even 0.0 kts) — matches Flutter behaviour
        const ktsStr = `${boat.speedKnots.toFixed(1)} kts`;
        const ktsColor = boat.isMine ? '#007AFF' : '#5EEAD4';
        labelEl.innerHTML = `
          <span class="rbl-name" style="display:block;color:#fff;font-weight:600;font-size:12px;line-height:1.3;">${name}</span>
          <span class="rbl-kts" style="display:block;color:${ktsColor};font-weight:500;font-size:11px;line-height:1.2;">${ktsStr}</span>
        `;
        // Pin the label just above the boat icon (fixed screen-space tooltip,
        // like the app) so it never swings to variable spots as the boat turns.
        const labelOffset = this._computeLabelOffset(boat);
        labelEl.style.left = `calc(50% + ${labelOffset[0]}px)`;
        labelEl.style.top = `calc(50% + ${labelOffset[1]}px)`;
        labelEl.style.visibility = 'visible';
      }
    }

    // ── Update waypoint WebGL symbol layer ──────────────────────────────────
    const wpSrc = this.map.getSource(this.replayWaypointsSourceId) as maplibregl.GeoJSONSource | undefined;
    if (wpSrc) {
      const wpFeatures: GeoJSON.Feature[] = state.activeWaypoints.map(wp => {
        const rawWp = wp.raw ?? { position: { latitude: wp.lat, longitude: wp.lng }, name: wp.name, audit_data: { create_ts: wp.createTs.toISOString() } };
        const iconKey = this.getIconSrc(rawWp);
        this._ensureWaypointImage(iconKey);
        return {
          type: 'Feature' as const,
          properties: { id: wp.id, name: wp.name, iconKey },
          geometry: { type: 'Point' as const, coordinates: [wp.lng, wp.lat] },
        };
      });
      wpSrc.setData({ type: 'FeatureCollection', features: wpFeatures });
    }

    // ── Update boat icon symbol layer (WebGL, pixel-perfect rotation) ────────
    // ── Camera auto-follow: mirror Flutter's _replayAutoFollow logic ─────────
    // On first appearance of a mine boat → zoom in once to a good detail level.
    // On every subsequent tick → pan to keep the boat centred (preserve zoom).
    const targetBoats = state.activeBoats;
    const hasTargetBoats = targetBoats.length > 0;

    if (!this._replayHadMyBoats && hasTargetBoats && this._replayNeedsInitialZoom) {
      // Use jumpTo (instant) to match Flutter's _mapController.move(position, 13.0).
      // flyTo animates over ~1 s but auto-follow fires jumpTo every 16 ms tick,
      // which would cancel the fly animation before it completes.
      if (targetBoats.length === 1) {
        this.map.jumpTo({ center: [targetBoats[0].lng, targetBoats[0].lat], zoom: 13 });
      } else {
        const lngs = targetBoats.map(b => b.lng), lats = targetBoats.map(b => b.lat);
        this.map.fitBounds(
          [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
          { padding: 64, maxZoom: 14, animate: false }
        );
      }
      this._replayNeedsInitialZoom = false;
      this._replayAutoFollow = true;
    }

    if (this._replayHadMyBoats && !hasTargetBoats) {
      // Mine trip just ended — re-arm zoom for the next trip
      this._replayNeedsInitialZoom = true;
    }

    this._replayHadMyBoats = hasTargetBoats;

    // Pan to keep mine boat centred; never force the zoom (let user control it).
    if (hasTargetBoats && this._replayAutoFollow) {
      if (targetBoats.length === 1) {
        this.map.jumpTo({ center: [targetBoats[0].lng, targetBoats[0].lat] });
      } else {
        const midLng = targetBoats.reduce((s, b) => s + b.lng, 0) / targetBoats.length;
        const midLat = targetBoats.reduce((s, b) => s + b.lat, 0) / targetBoats.length;
        this.map.jumpTo({ center: [midLng, midLat] });
      }
    }
  }

  /**
   * Pixel offset that glues the speed label to the boat's STERN (back), following
   * the icon's on-screen heading — exactly like the Flutter app. The distance is a
   * FIXED constant (not a measured offsetHeight, which could misreport and fling the
   * label far away), so the box always rides just off the back of the boat at the
   * same gap no matter the heading, speed, or map rotation.
   */
  private _computeLabelOffset(boat: ActiveBoatState): [number, number] {
    // Keep speed/name label placement statically below the vessel without rotation.
    const boatHalf = boat.isMine ? 18 : 16;
    const gap = 4;
    const labelHalf = 17;
    const dist = boatHalf + gap + labelHalf;
    return [0, dist];
  }

  /** Creates the vessel marker element for click events, icon and speed label. */
  private _createBoatMarkerElement(boat: ActiveBoatState): HTMLElement {
    // Mirror Flutter fallback: 'My Vessel' for mine, 'Unknown Vessel' for others
    const name = (boat.vesselName?.trim()) || (boat.isMine ? 'My Vessel' : 'Unknown Vessel');
    // Always show speed (even 0.0 kts) — matches Flutter behaviour
    const kts = `${boat.speedKnots.toFixed(1)} kts`;

    // Transparent 72×72 click-capture div — matches icon-size exactly so
    // anchor:'center' places wrapper center at the same pixel as the symbol layer.
    const wrapper = document.createElement('div');
    wrapper.className = 'replay-boat-wrapper';
    wrapper.style.cssText = `
      position: relative;
      width: 96px; height: 96px;
      cursor: pointer; pointer-events: auto;
    `;

    // Boat icon and speed label live in one marker so they move as a unit.
    const icon = document.createElement('img');
    icon.className = 'replay-boat-icon';
    icon.src = boat.isMine ? '/assets/common_icons/boat_self.png' : '/assets/common_icons/boat_other_user.png';
    icon.alt = '';
    const iconSize = boat.isMine ? 50 : 44;
    const headingDeg = (boat.bearing || 0) + 30 - (this.map?.getBearing() || 0);
    icon.style.cssText = `
      position: absolute;
      left: 50%;
      top: 50%;
      width: ${iconSize}px;
      height: ${iconSize}px;
      object-fit: contain;
      transform: translate(-50%, -50%) rotate(${headingDeg}deg);
      transform-origin: center center;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
      pointer-events: none;
    `;
    wrapper.appendChild(icon);

    const ktsColor = boat.isMine ? '#007AFF' : '#5EEAD4';
    const label = document.createElement('div');
    label.className = 'replay-boat-label';
    label.innerHTML = `
      <span class="rbl-name" style="display:block;color:#fff;font-weight:600;font-size:12px;line-height:1.3;">${name}</span>
      <span class="rbl-kts" style="display:block;color:${ktsColor};font-weight:500;font-size:11px;line-height:1.2;">${kts}</span>
    `;
    // Initial placement just above the boat (kept in sync every tick in
    // _updateReplayMap).
    const _off = this._computeLabelOffset(boat);
    label.style.cssText = `
      position: absolute;
      left: calc(50% + ${_off[0]}px);
      top: calc(50% + ${_off[1]}px);
      transform: translate(-50%, -50%);
      background: rgba(0,0,0,0.72);
      padding: 4px 10px; border-radius: 10px;
      text-align: center; white-space: nowrap; pointer-events: none;
      font-family: Inter, sans-serif;
      visibility: visible;
    `;
    wrapper.appendChild(label);

    // Click → show app-style popup: distance (nmi) + current coordinates
    wrapper.addEventListener('click', (e) => {
      e.stopPropagation();
      this.ngZone.run(() => {
        if (this._replayBoatPopup) { this._replayBoatPopup.remove(); this._replayBoatPopup = null; }

        // Resolve live boat state so coordinates/speed are current at click time
        const liveBoat = this.replayState?.activeBoats?.find(b => b.tripIndex === boat.tripIndex) ?? boat;

        // Cumulative track distance from polyline
        const polyline = this.replayState?.currentPolylines?.find(p => p.tripIndex === boat.tripIndex);
        let distNmi = 0;
        if (polyline && polyline.points.length >= 2) {
          for (let i = 0; i < polyline.points.length - 1; i++) {
            const [lng1, lat1] = polyline.points[i];
            const [lng2, lat2] = polyline.points[i + 1];
            const R = 6371000, toRad = (d: number) => d * Math.PI / 180;
            const φ1 = toRad(lat1), φ2 = toRad(lat2);
            const Δφ = toRad(lat2 - lat1), Δλ = toRad(lng2 - lng1);
            const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
            distNmi += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 0.000539957;
          }
        }

        // Format with spaces matching the app: 39° 43' 5.9" N
        const fmtCoord = (val: number, type: 'lat' | 'lng'): string => {
          if (val === undefined || val === null || isNaN(val)) return '--';
          const absVal = Math.abs(val);
          const deg = Math.floor(absVal);
          const min = Math.floor((absVal - deg) * 60);
          const sec = ((absVal - deg - min / 60) * 3600).toFixed(1);
          const dir = type === 'lat' ? (val >= 0 ? 'N' : 'S') : (val >= 0 ? 'E' : 'W');
          return `${deg}° ${min}' ${sec}" ${dir}`;
        };
        const latStr = fmtCoord(liveBoat.lat, 'lat');
        const lngStr = fmtCoord(liveBoat.lng, 'lng');

        const html = `
          <div style="font-family:Inter,sans-serif;background:#fff;border-radius:12px;
                      padding:12px 14px;display:inline-block;box-shadow:0 4px 20px rgba(0,0,0,0.15);">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;white-space:nowrap;">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                fill="none" stroke="#475466" stroke-width="2" stroke-linecap="square" stroke-linejoin="miter"
                style="flex-shrink:0;">
                <rect x="1" y="10" width="22" height="8" rx="1"/>
                <line x1="4.5" y1="10" x2="4.5" y2="13.5"/>
                <line x1="7.5" y1="10" x2="7.5" y2="13.5"/>
                <line x1="10.5" y1="10" x2="10.5" y2="13.5"/>
                <line x1="13.5" y1="10" x2="13.5" y2="13.5"/>
                <line x1="16.5" y1="10" x2="16.5" y2="13.5"/>
                <line x1="19.5" y1="10" x2="19.5" y2="13.5"/>
              </svg>
              <span style="font-weight:700;font-size:18px;color:#111;line-height:1;">
                ${distNmi.toFixed(1)} nmi
              </span>
            </div>
            <div style="font-size:13px;color:#555;line-height:1.8;">
              ${latStr}<br>${lngStr}
            </div>
          </div>`;

        this._replayBoatPopup = new maplibregl.Popup({
          closeButton: true, anchor: 'bottom', offset: [0, -8],
          maxWidth: '260px',
        })
          .setLngLat([liveBoat.lng, liveBoat.lat])
          .setHTML(html)
          .addTo(this.map);
      });
    });

    return wrapper;
  }

  private _replayBoatPopup: maplibregl.Popup | null = null;

  /** Loads a waypoint icon URL into MapLibre's image store (PNG or SVG via canvas). */
  private _ensureWaypointImage(url: string): void {
    if (!this.map || this.map.hasImage(url) || this._loadingWaypointImages.has(url)) return;
    this._loadingWaypointImages.add(url);
    if (!url.toLowerCase().endsWith('.svg')) {
      this.map.loadImage(url).then(result => {
        if (this.map && !this.map.hasImage(url)) this.map.addImage(url, result.data);
      }).catch(() => { });
      return;
    }
    // SVG: draw to canvas to produce ImageData
    const size = 44;
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const img = new Image(size, size);
    img.onload = () => {
      ctx.drawImage(img, 0, 0, size, size);
      if (this.map && !this.map.hasImage(url)) {
        try { this.map.addImage(url, ctx.getImageData(0, 0, size, size)); } catch (_) { }
      }
    };
    img.src = url;
  }

  // ── Cleanup ────────────────────────────────────────────────────────────────

  private _clearReplayMap(): void {
    if (!this.map) return;

    // Dismiss any open boat popup
    if (this._replayBoatPopup) { try { this._replayBoatPopup.remove(); } catch (_) { } this._replayBoatPopup = null; }

    // Remove boat markers
    for (const m of Object.values(this._replayBoatMarkers)) {
      try { m.remove(); } catch (_) { }
    }
    this._replayBoatMarkers = {};

    // Remove waypoint circle layer
    try {
      if (this._replayWaypointClickHandler) {
        this.map.off('click', this.replayWaypointsLayerId, this._replayWaypointClickHandler);
        this._replayWaypointClickHandler = null;
      }
      if (this.map.getLayer(this.replayWaypointsLayerId)) this.map.removeLayer(this.replayWaypointsLayerId);
      if (this.map.getSource(this.replayWaypointsSourceId)) this.map.removeSource(this.replayWaypointsSourceId);
    } catch (_) { }

    // Remove polyline layers and source
    try {
      if (this.map.getLayer(this.replayPolylinesLineLayerId)) this.map.removeLayer(this.replayPolylinesLineLayerId);
      if (this.map.getLayer(this.replayPolylinesSharedLayerId)) this.map.removeLayer(this.replayPolylinesSharedLayerId);
      if (this.map.getSource(this.replayPolylinesSourceId)) this.map.removeSource(this.replayPolylinesSourceId);
    } catch (_) { /* map may be mid-teardown */ }

  }

  // ── Watermark ──────────────────────────────────────────────────────────────
  private initWatermark(): void {
    // Extract the login email from the JWT token (this is the email used to log in)
    const email = this.getLoginEmailFromToken();
    console.log('[Watermark] login email from token:', email);
    // Do not block watermark if email is missing; use fallback text.
    this.watermarkEmail = email || 'Waterproof Intel Service';

    // Check if user belongs to 'Waterproof intel service' fleet
    this.socialService.myFleets(1, 100).subscribe({
      next: (res: any) => {
        const fleets = Array.isArray(res?.results) ? res.results
          : Array.isArray(res?.data?.results) ? res.data.results
            : Array.isArray(res?.data) ? res.data
              : Array.isArray(res) ? res
                : [];
        console.log('[Watermark] fleets:', fleets.map((f: any) => f?.name || f?.community?.name || f?.display_name));

        const normalize = (value: any) => String(value || '')
          .toLowerCase()
          .replace(/\s+/g, ' ')
          .trim();
        const targetFleetName = 'waterproof intel service';
        const isWaterproof = fleets.some((f: any) =>
          normalize(f?.name || f?.community?.name || f?.community_name || f?.display_name) === targetFleetName
        );
        console.log('[Watermark] isWaterproof:', isWaterproof);
        this.showWatermark = isWaterproof;
      },
      error: (err) => {
        console.error('[Watermark] Error fetching fleets:', err);
      }
    });
  }

  private getLoginEmailFromToken(): string | null {
    try {
      const token = this.authService.getAccessToken();
      if (!token) return null;
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
      const payload = JSON.parse(atob(padded));
      return payload.email || payload.preferred_username || null;
    } catch {
      return null;
    }
  }
}
